
// src/App.js — نظام بريتاو للسكنر مع Firebase + FCM Push Notifications
// npm install firebase recharts

import { useState, useEffect, useRef } from "react";
import React from "react";
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";

// ═══════════════════════════════════════════════
//  FIREBASE — ضع مفاتيحك هنا مباشرة
// ═══════════════════════════════════════════════
import { initializeApp, getApps } from "firebase/app";
import {
  getFirestore, initializeFirestore, collection, doc,
  addDoc, setDoc, updateDoc, getDocs,
  onSnapshot, query, orderBy, where,
  serverTimestamp, deleteDoc, writeBatch,
} from "firebase/firestore";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";

const FB_CONFIG = {
  apiKey:            "AIzaSyDqIaKfjwbqRu09JanovlUaaIH-5laYY2A",
  authDomain:        "britao-scanner.firebaseapp.com",
  projectId:         "britao-scanner",
  storageBucket:     "britao-scanner.firebasestorage.app",
  messagingSenderId: "1035251738179",
  appId:             "1:1035251738179:web:d1ff6026ae6fde415a56f6",
};

const fbApp = getApps().length ? getApps()[0] : initializeApp(FB_CONFIG);
// استخدام long polling لتجنب INTERNAL ASSERTION على الموبايل
let db;
try {
  db = initializeFirestore(fbApp, {
    experimentalAutoDetectLongPolling: true,
  });
} catch(e) {
  // إذا كان قد تم تهيئته مسبقاً
  db = getFirestore(fbApp);
}
const DB_READY = true;

// ═══ FCM PUSH NOTIFICATIONS ═══
const VAPID_KEY = "BC07ZdPAShT3OblqPnJqN7WEgbjkqk8zmINc0CDj7FNGiH_TUn5aTxbsWTOEAg7vqFY5d4q4EUjm9n5-6c3Our4";
const messagingReadyPromise = isSupported()
  .then(ok => {
    if (!ok) {
      console.warn("FCM not supported in this browser");
      return null;
    }
    try {
      const m = getMessaging(fbApp);
      console.log("✅ FCM messaging initialized");
      return m;
    } catch(e) {
      console.warn("FCM init failed:", e);
      return null;
    }
  })
  .catch(e => { console.warn("isSupported failed:", e); return null; });


// ═══════════════════════════════════════════════
//  كودات المحافظات — كل محافظة قاعدة بيانات منفصلة
//  السر: كل محافظة تستخدم collection prefix خاص
// ═══════════════════════════════════════════════
const GOV_CODES = {
  "BGD-7X4K": "بغداد",
  "NNW-2M9R": "نينوى",
  "BSR-5H3T": "البصرة",
  "SLD-8Q1P": "صلاح الدين",
  "DHK-4N6W": "دهوك",
  "ARB-3F7V": "أربيل",
  "SLM-9J2C": "السليمانية",
  "DYL-6B5Z": "ديالى",
  "WST-1R8G": "واسط",
  "MSN-7D4L": "ميسان",
  "ZQR-2K9E": "ذي قار",
  "MTN-5X3A": "المثنى",
  "BBL-8H1Y": "بابل",
  "KRB-4W6U": "كربلاء",
  "NJF-3T5S": "النجف",
  "ANB-9P2F": "الأنبار",
  "QDS-6C7M": "القادسية",
  "KRK-1V4J": "كركوك",
  "HLB-8N3Q": "حلبجة",
  "ADMIN-00": "admin_all", // المدير العام — يرى كل شيء
};
const IS_ADMIN_CODE = (code) => code === "ADMIN-00";
// مساعد: اسم collection مع prefix المحافظة
const col = (govCode, name) => `${govCode}_${name}`;

// إحداثيات مراكز المحافظات العراقية (للتركيز التلقائي على الخريطة)
const GOV_CENTERS = {
  "BGD-7X4K": { lat: 33.3152, lng: 44.3661, zoom: 12 }, // بغداد
  "NNW-2M9R": { lat: 36.3450, lng: 43.1450, zoom: 12 }, // نينوى - الموصل
  "BSR-5H3T": { lat: 30.5085, lng: 47.7804, zoom: 12 }, // البصرة
  "SLD-8Q1P": { lat: 34.6000, lng: 43.6800, zoom: 11 }, // صلاح الدين - تكريت
  "DHK-4N6W": { lat: 36.8669, lng: 42.9886, zoom: 12 }, // دهوك
  "ARB-3F7V": { lat: 36.1911, lng: 44.0091, zoom: 12 }, // أربيل
  "SLM-9J2C": { lat: 35.5641, lng: 45.4326, zoom: 12 }, // السليمانية
  "DYL-6B5Z": { lat: 33.7700, lng: 44.6200, zoom: 11 }, // ديالى - بعقوبة
  "WST-1R8G": { lat: 32.5160, lng: 45.8260, zoom: 11 }, // واسط - الكوت
  "MSN-7D4L": { lat: 31.8400, lng: 47.1450, zoom: 11 }, // ميسان - العمارة
  "ZQR-2K9E": { lat: 31.0080, lng: 46.2570, zoom: 11 }, // ذي قار - الناصرية
  "MTN-5X3A": { lat: 31.3160, lng: 45.2740, zoom: 11 }, // المثنى - السماوة
  "BBL-8H1Y": { lat: 32.4830, lng: 44.4220, zoom: 12 }, // بابل - الحلة
  "KRB-4W6U": { lat: 32.6160, lng: 44.0240, zoom: 12 }, // كربلاء
  "NJF-3T5S": { lat: 32.0000, lng: 44.3360, zoom: 12 }, // النجف
  "ANB-9P2F": { lat: 33.4200, lng: 43.3100, zoom: 11 }, // الأنبار - الرمادي
  "QDS-6C7M": { lat: 31.9890, lng: 44.9240, zoom: 11 }, // القادسية - الديوانية
  "KRK-1V4J": { lat: 35.4680, lng: 44.3920, zoom: 12 }, // كركوك
  "HLB-8N3Q": { lat: 35.1750, lng: 45.9860, zoom: 12 }, // حلبجة
  "ADMIN-00": { lat: 33.2232, lng: 43.6793, zoom: 7  }, // المدير العام - مركز العراق
};



// ═══════════════════════════════════════════════
//  SEED — معطّل (لا توجد بيانات افتراضية)
// ═══════════════════════════════════════════════
const SEED_REPS = [];
const SEED_DOCTORS = [];

// IDs البيانات الافتراضية القديمة — ستُحذف تلقائياً عند أول تشغيل
const OLD_SEED_REP_IDS = ["rep1","rep2","rep3"];
const OLD_SEED_DOC_IDS = ["dr1","dr2","dr3"];
const OLD_SEED_APT_IDS = ["A-1001"];

async function seedIfEmpty(govCode) {
  if (!DB_READY || !govCode) return;
  const prefix = govCode;
  try {
    // ═══ تنظيف البيانات الافتراضية القديمة (مرّة واحدة لكل محافظة) ═══
    const cleanupKey = `britao_seed_cleanup_${prefix}`;
    if (!localStorage.getItem(cleanupKey)) {
      console.log("🧹 تنظيف البيانات الافتراضية القديمة من", prefix);
      try {
        for (const id of OLD_SEED_REP_IDS) await deleteDoc(doc(db,`${prefix}_reps`,id)).catch(()=>{});
        for (const id of OLD_SEED_DOC_IDS) await deleteDoc(doc(db,`${prefix}_doctors`,id)).catch(()=>{});
        for (const id of OLD_SEED_APT_IDS) await deleteDoc(doc(db,`${prefix}_appointments`,id)).catch(()=>{});
        // حذف رسالة الترحيب الافتراضية
        try {
          const msgs = await getDocs(collection(db,`${prefix}_messages`));
          for (const m of msgs.docs) {
            const d = m.data();
            if (d.userId==="admin" && d.text && d.text.includes("مرحباً بالجميع في نظام بريتاو")) {
              await deleteDoc(doc(db,`${prefix}_messages`,m.id)).catch(()=>{});
            }
          }
        } catch(e){}
        localStorage.setItem(cleanupKey, "done");
        console.log("✅ تم حذف البيانات الافتراضية");
      } catch(e) { console.warn("cleanup error:", e); }
    }
    // ❌ لا يتم إضافة بيانات افتراضية جديدة — التطبيق يبدأ فارغاً
  } catch(e) { console.error("seed/cleanup error:", e); }
}

// ═══════════════════════════════════════════════
//  USERS / ROLES
// ═══════════════════════════════════════════════
const USERS = {
  "ADMIN":  {role:"admin", id:"admin",name:"مدير النظام",  code:"ADMIN", password:"admin123"},
  "SEC-01": {role:"sec",   id:"sec1", name:"مروة",code:"SEC-01", password:"1234"},
  "DR-02":  {role:"doctor",id:"dr2",  name:"د. نور الدين", code:"DR-02"},
};
const ROLES = {
  admin: {label:"مدير النظام",color:"#22d3a0",icon:"🏛",hint:"ADMIN"},
  sec:   {label:"كول سنتر",    color:"#f472b6",icon:"💼",hint:"SEC-01"},
  rep:   {label:"مندوب سكنر", color:"#60a5fa",icon:"🏃",hint:"M-001"},
  doctor:{label:"طبيب",       color:"#38bdf8",icon:"👨‍⚕️",hint:"DR-01"},
};
const APTS = {
  "جديدة":              {bg:"#2a1f08",c:"#60a5fa", step:0},
  "محوّلة":             {bg:"#1a1035",c:"#a78bfa", step:1},
  "مقبولة":             {bg:"#0a2e1e",c:"#22d3a0", step:2},
  "مقبولة قيد الانتظار":{bg:"#1f2a00",c:"#fbbf24", step:2},
  "في الطريق":          {bg:"#2d1f00",c:"#fbbf24", step:3},
  "وصلت":               {bg:"#0d2035",c:"#38bdf8", step:4},
  "جاري التصوير":       {bg:"#1a0d30",c:"#e879f9", step:5},
  "مكتملة":             {bg:"#0a2e1e",c:"#22d3a0", step:6},
  "مرفوضة":             {bg:"#2e0a0a",c:"#f87171", step:-1},
};
const SC  = {active:"#22d3a0",away:"#fbbf24"};
const now = () => new Date().toLocaleTimeString("ar-IQ",{hour:"2-digit",minute:"2-digit"});

// تحويل الوقت من 24 ساعة إلى 12 ساعة بصيغة عربية (ص/م)
function fmt12(time) {
  if (!time) return "";
  const parts = String(time).split(":");
  let h = parseInt(parts[0]);
  const m = (parts[1]||"00").padStart(2,"0");
  if (isNaN(h)) return time;
  const period = h >= 12 ? "م" : "ص";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${m} ${period}`;
}

// ═══════════════════════════════════════════════════════════════════════════
//  ARABIC FUZZY MATCHING — مطابقة ذكيّة للأسماء العربية مع تسامح للأخطاء
// ═══════════════════════════════════════════════════════════════════════════

// تطبيع النصّ العربي: إزالة التشكيل + توحيد الهمزات + توحيد الياء
function arabicNormalize(s) {
  return (s || "")
    .toString()
    .toLowerCase()
    .replace(/[\u064B-\u065F\u0670]/g, "") // إزالة التشكيل (فتحة، كسرة، إلخ)
    .replace(/[إأآا]/g, "ا")               // توحيد الألف بكل أشكالها
    .replace(/ى/g, "ي")                    // ألف مقصورة → ياء
    .replace(/ة/g, "ه")                    // تاء مربوطة → هاء
    .replace(/ؤ/g, "و")                    // واو بهمزة → واو
    .replace(/ئ/g, "ي")                    // ياء بهمزة → ياء
    .replace(/ـ/g, "")                     // إزالة التطويل (كشيدة)
    .replace(/[\u200f\u200e]/g, "")        // إزالة علامات الاتجاه RTL/LTR
    .trim();
}

// مسافة Levenshtein — عدد التغييرات اللازمة لتحويل نصّ لآخر
function levenshtein(a, b) {
  const m = a.length, n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
    }
  }
  return dp[m][n];
}

// مطابقة ذكيّة لكلمة عربية واحدة: prefix + contains + fuzzy
// مثال: "رنج" يطابق "رانج" (مسافة 1)
function fuzzyArabicMatch(typed, target) {
  const t = arabicNormalize(typed);
  const tg = arabicNormalize(target);
  if (t.length === 0) return false;
  // 1) تطابق تامّ
  if (t === tg) return true;
  // 2) prefix بعد التطبيع
  if (tg.startsWith(t)) return true;
  // 3) النصّ المُدخَل موجود ضمن الكلمة المستهدفة
  if (tg.includes(t)) return true;
  // 4) مسافة Levenshtein للأخطاء الإملائية
  // الحدّ المسموح يعتمد على طول النصّ المُدخَل
  const maxDist = t.length <= 3 ? 1 : t.length <= 6 ? 2 : 3;
  if (Math.abs(t.length - tg.length) <= maxDist + 1) {
    const dist = levenshtein(t, tg);
    if (dist <= maxDist) return true;
    // أيضاً قارن مع بداية الكلمة المستهدفة (للأسماء الطويلة)
    if (tg.length > t.length) {
      const distPrefix = levenshtein(t, tg.substring(0, t.length));
      if (distPrefix <= maxDist) return true;
    }
  }
  return false;
}

// تنظيف اسم الطبيب وإرجاع كلمات نظيفة (تجاهل "د." و "دكتور")
function drCleanWords(s) {
  const cleaned = (s || "").toString().trim().replace(/\s+/g, " ").toLowerCase();
  return cleaned.split(" ").filter(w => {
    const x = w.replace(/\./g, "").replace(/\u200f/g, "");
    return x.length > 0 && x !== "د" && x !== "دكتور" && x !== "دكتورة";
  });
}

// ═══════════════════════════════════════════════════════════════════════════
//  SafeInput — حقل إدخال آمن للعربية (يحلّ مشكلة IME composition على Samsung)
//  المشكلة: React's controlled input يتعارض مع وضع composition للكيبورد العربي
//          → الأحرف تختفي مباشرة عند الكتابة
//  الحل: استعمال defaultValue + ref + composition events بدلاً من value
//        ← الكيبورد يملك التحكّم الكامل، نقرأ القيمة عند الحاجة
// ═══════════════════════════════════════════════════════════════════════════
const SafeInput = React.forwardRef(function SafeInput({ value, onChange, type = "text", ...rest }, externalRef) {
  const innerRef = React.useRef(null);
  const composingRef = React.useRef(false);

  // ربط الـ refs (الخارجي والداخلي)
  React.useImperativeHandle(externalRef, () => innerRef.current, []);

  // عند تغيُّر value الخارجية (مثلاً اختيار اقتراح من القائمة) — نُحدّث الـ DOM مباشرة
  // (لا نستعمل value prop لتجنّب إعادة الـ render في كل keystroke)
  React.useEffect(() => {
    if (innerRef.current && innerRef.current.value !== (value || "") && !composingRef.current) {
      innerRef.current.value = value || "";
    }
  }, [value]);

  // معالج التغيير: يُمرّر القيمة للأب — مع تخطّي أحداث composition الوسيطة
  const handleChange = (e) => {
    if (composingRef.current) return; // أثناء composition: الكيبورد يملك السيطرة
    if (onChange) onChange(e);
  };

  return (
    <input
      ref={innerRef}
      type={type}
      defaultValue={value || ""}
      onChange={handleChange}
      onCompositionStart={() => { composingRef.current = true; }}
      onCompositionEnd={(e) => {
        composingRef.current = false;
        if (onChange) onChange(e);
      }}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck={false}
      {...rest}
    />
  );
});

// نسخة من SafeInput للـ textarea
const SafeTextarea = React.forwardRef(function SafeTextarea({ value, onChange, ...rest }, externalRef) {
  const innerRef = React.useRef(null);
  const composingRef = React.useRef(false);
  React.useImperativeHandle(externalRef, () => innerRef.current, []);
  React.useEffect(() => {
    if (innerRef.current && innerRef.current.value !== (value || "") && !composingRef.current) {
      innerRef.current.value = value || "";
    }
  }, [value]);
  const handleChange = (e) => {
    if (composingRef.current) return;
    if (onChange) onChange(e);
  };
  return (
    <textarea
      ref={innerRef}
      defaultValue={value || ""}
      onChange={handleChange}
      onCompositionStart={() => { composingRef.current = true; }}
      onCompositionEnd={(e) => {
        composingRef.current = false;
        if (onChange) onChange(e);
      }}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck={false}
      {...rest}
    />
  );
});

// ═══════════════════════════════════════════════
//  LOGIN MATCHING HELPERS — تحقّق توسعي من اسم المستخدم/الهاتف/كلمة المرور
//  يحلّ مشاكل: المسافات الزائدة، الأرقام العربية ٠١٢٣، حروف RTL/ZWJ المخفية،
//  تنوّع كتابة الهاتف (+964/00964/07/7)، حساسية حالة الأحرف الإنجليزية، إلخ.
// ═══════════════════════════════════════════════
function loginNormText(s) {
  if (s == null) return "";
  return String(s)
    // إزالة الأحرف الخفية: zero-width + علامات اتجاه RTL/LTR
    .replace(/[\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g, "")
    // تحويل الأرقام العربية الهندية إلى لاتينية
    .replace(/[٠-٩]/g, c => "٠١٢٣٤٥٦٧٨٩".indexOf(c).toString())
    // تحويل الأرقام الفارسية إلى لاتينية
    .replace(/[۰-۹]/g, c => "۰۱۲۳۴۵۶۷۸۹".indexOf(c).toString())
    // توحيد الهمزات والتاء المربوطة (لتطابق الاسم العربي بمرونة)
    .replace(/[إأآا]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ة/g, "ه")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

function loginNormPhone(s) {
  if (s == null) return "";
  let digits = String(s)
    .replace(/[\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]/g, "")
    .replace(/[٠-٩]/g, c => "٠١٢٣٤٥٦٧٨٩".indexOf(c).toString())
    .replace(/[۰-۹]/g, c => "۰۱۲۳۴۵۶۷۸۹".indexOf(c).toString())
    .replace(/[^\d]/g, ""); // أرقام فقط
  // إزالة كود البلد العراقي 964 إن وُجد
  if (digits.startsWith("00964")) digits = digits.slice(5);
  else if (digits.startsWith("964") && digits.length > 10) digits = digits.slice(3);
  // إزالة الصفر البادئ
  if (digits.startsWith("0")) digits = digits.slice(1);
  return digits;
}

// تحقّق إن كان الإدخال يطابق هذا المستخدم (اسم/معرّف/هاتف بأي صيغة)
// ملاحظة: fullName/nickname (اللقب العربي) لا يُستعمل في تسجيل الدخول — للعرض فقط
function loginMatchUser(u, input) {
  const inN = loginNormText(input);
  const inP = loginNormPhone(input);
  if (!inN) return false;
  // الحقول المسموح بها للدخول: اسم المستخدم الإنجليزي + الـ docId
  const textFields = [u.id, u.name, u.username, u.docId];
  for (const f of textFields) {
    if (f && loginNormText(f) === inN) return true;
  }
  // مطابقة الهاتف (تتقبّل +964 / 00964 / 07.../ 7...)
  if (inP && inP.length >= 7) {
    if (loginNormPhone(u.phone) === inP) return true;
    if (loginNormPhone(u.phone2) === inP) return true; // هاتف ثاني (لو موجود)
  }
  return false;
}

// تحقّق من كلمة المرور (مع مرونة بسيطة للمسافات/الأرقام العربية)
function loginMatchPassword(u, input) {
  if (!u.password || input == null) return false;
  // مطابقة صارمة أولاً (الأسرع والأكثر شيوعاً)
  if (u.password === input) return true;
  // مطابقة بعد تنظيف المسافات
  if (String(u.password).trim() === String(input).trim()) return true;
  // مطابقة مع تحويل الأرقام العربية والمسافات
  return loginNormText(u.password) === loginNormText(input);
}

// ═══════════════════════════════════════════════
//  DEVICE FINGERPRINT — معرفة نوع الجهاز من User Agent
// ═══════════════════════════════════════════════
function getDeviceId() {
  let id = localStorage.getItem("britao_device_id");
  if (!id) {
    id = "dev_" + Math.random().toString(36).substring(2,12) + "_" + Date.now().toString(36);
    localStorage.setItem("britao_device_id", id);
  }
  return id;
}

function getDeviceInfo() {
  const ua = navigator.userAgent || "";
  const isTouch = (navigator.maxTouchPoints || 0) > 0;
  const isMobile = /Mobi|Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(ua) && isTouch;

  // ═══ iPhone / iPad ═══
  if (/iPhone/.test(ua)) {
    const ver = ua.match(/OS (\d+)_(\d+)/);
    return { name: `iPhone${ver ? ` (iOS ${ver[1]}.${ver[2]})` : ""}`, type: "mobile", os: "iOS", icon: "📱" };
  }
  if (/iPad/.test(ua)) {
    const ver = ua.match(/OS (\d+)_(\d+)/);
    return { name: `iPad${ver ? ` (iOS ${ver[1]}.${ver[2]})` : ""}`, type: "tablet", os: "iOS", icon: "📱" };
  }

  // ═══ Android ═══
  const androidMatch = ua.match(/Android\s+(\d+)[^;]*;\s*([^;)]+)/);
  if (androidMatch) {
    const ver = androidMatch[1];
    let model = androidMatch[2].trim().replace(/\s*Build\/.*/, "");
    // خريطة بأسماء موديلات شائعة
    const map = {
      // Samsung S series
      "SM-S928B":"Galaxy S24 Ultra","SM-S921B":"Galaxy S24","SM-S918B":"Galaxy S23 Ultra",
      "SM-S911B":"Galaxy S23","SM-S908B":"Galaxy S22 Ultra","SM-S901B":"Galaxy S22",
      "SM-G998B":"Galaxy S21 Ultra","SM-G991B":"Galaxy S21","SM-G988B":"Galaxy S20 Ultra",
      // Samsung A series
      "SM-A546B":"Galaxy A54","SM-A536B":"Galaxy A53","SM-A526B":"Galaxy A52",
      "SM-A156B":"Galaxy A15","SM-A256B":"Galaxy A25","SM-A356B":"Galaxy A35",
      // Samsung Note/Fold
      "SM-N986B":"Galaxy Note 20 Ultra","SM-F946B":"Galaxy Z Fold 5","SM-F731B":"Galaxy Z Flip 5",
      // Xiaomi / Redmi
      "23021RAA2Y":"Redmi Note 12 Pro+","2201117TG":"Redmi Note 11","23090RA98G":"Redmi Note 13 Pro",
      "2210132G":"Xiaomi 13","2306EPN60G":"Xiaomi 13T","2310FPCA4G":"Xiaomi 14",
      // Huawei
      "ELS-NX9":"Huawei P40 Pro","NOH-NX9":"Mate 40 Pro",
      // Google Pixel
      "Pixel 8 Pro":"Pixel 8 Pro","Pixel 8":"Pixel 8","Pixel 7":"Pixel 7","Pixel 6":"Pixel 6",
    };
    model = map[model] || model;
    return { name: `${model}`, type: "mobile", os: `Android ${ver}`, icon: "📱" };
  }

  // ═══ Desktop ═══
  const browser = /Edg\//.test(ua) ? "Edge" :
                  /OPR|Opera/.test(ua) ? "Opera" :
                  /Chrome/.test(ua) ? "Chrome" :
                  /Firefox/.test(ua) ? "Firefox" :
                  /Safari/.test(ua) ? "Safari" : "متصفّح";
  const os = /Windows NT 10/.test(ua) ? "Windows 10/11" :
             /Windows NT/.test(ua) ? "Windows" :
             /Mac OS X/.test(ua) ? "macOS" :
             /Linux/.test(ua) ? "Linux" : "كمبيوتر";
  return { name: `${browser} على ${os}`, type: "desktop", os, icon: "🖥️" };
}

// ═══════════════════════════════════════════════
//  PDF EXPORT HELPER (طباعة → حفظ كـ PDF)
// ═══════════════════════════════════════════════
// Modal طباعة الجدول داخل التطبيق (Native Android)
function NativePrintTableModal({data, onClose}) {
  const { title, subtitle, columns, rows } = data;
  const dateStr = new Date().toLocaleDateString("ar-IQ");
  return (
    <div onClick={onClose} style={{
      position:"fixed",inset:0,background:"#000d",zIndex:99999,
      display:"flex",alignItems:"center",justifyContent:"center",padding:8
    }}>
      <div onClick={e=>e.stopPropagation()} style={{
        background:"#fff",color:"#1a1a1a",borderRadius:14,
        width:"100%",maxWidth:600,maxHeight:"95vh",
        display:"flex",flexDirection:"column",
        border:"3px solid #d4af37",
        fontFamily:"'Cairo',sans-serif",direction:"rtl"
      }}>
        {/* Header */}
        <div style={{
          padding:"12px 14px",
          background:"linear-gradient(135deg,#fefaee,#fff5d8)",
          borderBottom:"2px solid #d4af37",
          borderRadius:"12px 12px 0 0",
          display:"flex",justifyContent:"space-between",alignItems:"center"
        }}>
          <div>
            <div style={{
              fontSize:18,fontWeight:900,
              background:"linear-gradient(135deg,#8b6914,#d4af37,#f0c040)",
              WebkitBackgroundClip:"text",backgroundClip:"text",color:"transparent"
            }}>مختبر بريتاو</div>
            <div style={{fontSize:9,color:"#8b6914"}}>{dateStr}</div>
          </div>
          <button onClick={onClose} style={{
            background:"#2e0a0a",color:"#f87171",border:"none",
            width:32,height:32,borderRadius:8,cursor:"pointer",fontSize:14,fontWeight:800
          }}>✕</button>
        </div>
        {/* Content scrollable */}
        <div style={{flex:1,overflow:"auto",padding:14}}>
          <h2 style={{margin:"4px 0",fontSize:16,fontWeight:900}}>{title}</h2>
          {subtitle && <div style={{fontSize:11,color:"#5a4518",background:"#fdf6e3",borderRight:"3px solid #d4af37",borderRadius:6,padding:"6px 10px",marginBottom:10,fontWeight:600}}>{subtitle}</div>}
          <div style={{overflow:"auto",border:"1px solid #d4af3744",borderRadius:8}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
              <thead>
                <tr style={{background:"linear-gradient(135deg,#d4af37,#f0c040)",color:"#1a1208"}}>
                  {columns.map((c,i)=><th key={i} style={{padding:"8px 6px",textAlign:"right",fontWeight:900,whiteSpace:"nowrap"}}>{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.length===0 && <tr><td colSpan={columns.length} style={{padding:20,textAlign:"center",color:"#888"}}>لا توجد بيانات</td></tr>}
                {rows.map((r,i)=>(
                  <tr key={i} style={{background:i%2?"#fffbf0":"#fff",borderTop:"1px solid #e5dcc0"}}>
                    {r.map((c,j)=><td key={j} style={{padding:"7px 6px",color:"#222",fontWeight:600}}>{c==null?"—":c}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{marginTop:12,padding:10,borderTop:"2px solid #d4af3766",fontSize:10,color:"#8b6914",textAlign:"center",fontWeight:700}}>
            عدد السجلات: {rows.length} • نظام بريتاو
          </div>
        </div>
      </div>
    </div>
  );
}

// Modal طباعة الخريطة داخل التطبيق (Native Android) — مرقّمة + Legend جانبي
function NativePrintMapModal({data, onClose, govCode}) {
  const { title, subtitle, locations } = data;
  const dateStr = new Date().toLocaleDateString("ar-IQ");
  return (
    <div onClick={onClose} style={{
      position:"fixed",inset:0,background:"#000d",zIndex:99999,
      display:"flex",alignItems:"center",justifyContent:"center",padding:8
    }}>
      <div onClick={e=>e.stopPropagation()} style={{
        background:"#fff",color:"#1a1a1a",borderRadius:14,
        width:"100%",maxWidth:700,maxHeight:"95vh",
        display:"flex",flexDirection:"column",
        border:"3px solid #d4af37",
        fontFamily:"'Cairo',sans-serif",direction:"rtl"
      }}>
        {/* Header */}
        <div style={{
          padding:"12px 14px",
          background:"linear-gradient(135deg,#fefaee,#fff5d8)",
          borderBottom:"2px solid #d4af37",
          borderRadius:"12px 12px 0 0",
          display:"flex",justifyContent:"space-between",alignItems:"center"
        }}>
          <div>
            <div style={{
              fontSize:18,fontWeight:900,
              background:"linear-gradient(135deg,#8b6914,#d4af37,#f0c040)",
              WebkitBackgroundClip:"text",backgroundClip:"text",color:"transparent"
            }}>مختبر بريتاو — خريطة</div>
            <div style={{fontSize:9,color:"#8b6914"}}>{dateStr} • {locations.length} موقع</div>
          </div>
          <button onClick={onClose} style={{
            background:"#2e0a0a",color:"#f87171",border:"none",
            width:32,height:32,borderRadius:8,cursor:"pointer",fontSize:14,fontWeight:800
          }}>✕</button>
        </div>
        {/* Content */}
        <div style={{flex:1,overflow:"auto",padding:12,background:"#0a0600"}}>
          <h2 style={{margin:"4px 0 8px",fontSize:14,fontWeight:900,color:"#f0c040"}}>{title}</h2>
          {subtitle && <div style={{fontSize:10,color:"#c9b37e",marginBottom:10}}>{subtitle}</div>}
          <NumberedDoctorsMap locations={locations} height={300} govCode={govCode}/>
        </div>
      </div>
    </div>
  );
}

function exportToPdf({title, columns, rows, subtitle=""}) {
  // اكتشاف Native Android — افتح modal داخل التطبيق
  const isNative = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
  if (isNative) {
    window.dispatchEvent(new CustomEvent("britao:openTablePrintModal", {
      detail: { title, subtitle, columns, rows }
    }));
    return;
  }
  const w = window.open("about:blank", "_blank");
  if (!w) { alert("الرجاء السماح للنوافذ المنبثقة لحفظ PDF"); return; }
  const dateStr = new Date().toLocaleDateString("ar-IQ");
  const timeStr = new Date().toLocaleTimeString("ar-IQ",{hour:"2-digit",minute:"2-digit",hour12:true});
  // رابط اللوجو (يعمل من النشر على Cloudflare أو أي مكان)
  const logoUrl = `${window.location.origin}/logo192.png`;
  const html = `
<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="UTF-8">
  <title>${title}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@600;700;800;900&family=Tajawal:wght@500;700;900&display=swap" rel="stylesheet">
  <style>
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    body {
      font-family: 'Tajawal','Cairo',sans-serif;
      margin: 22px;
      color: #1a1a1a;
      direction: rtl;
      font-weight: 600;
    }
    .header {
      display: flex; justify-content: space-between; align-items: center;
      padding: 14px 18px; margin-bottom: 18px;
      background: linear-gradient(135deg,#fefaee,#fff);
      border-radius: 14px;
      border: 2px solid #d4af37;
      box-shadow: 0 2px 8px rgba(212,175,55,0.15);
    }
    .brand-block {
      display: flex; align-items: center; gap: 14px;
    }
    .brand-block img {
      width: 64px; height: 64px; object-fit: contain;
      border-radius: 12px; background: #000; padding: 4px;
    }
    .brand-text .name {
      font-size: 24px; font-weight: 900;
      background: linear-gradient(135deg,#8b6914,#d4af37,#f0c040);
      -webkit-background-clip: text; background-clip: text; color: transparent;
      letter-spacing: 0.5px;
    }
    .brand-text .sub {
      font-size: 12px; color: #8b6914; font-weight: 700; margin-top: 2px;
    }
    .meta {
      font-size: 12px; color: #4a4a4a; text-align: left;
      background: #fff8e1; padding: 8px 14px; border-radius: 10px;
      border: 1px solid #d4af3744; font-weight: 700;
    }
    .meta div { margin: 2px 0; }
    h1 {
      font-size: 20px; margin: 4px 0 6px; color: #1a1a1a;
      font-weight: 900; padding: 0 4px;
    }
    .subtitle {
      font-size: 13px; color: #5a4518; margin-bottom: 16px;
      padding: 6px 12px; background: #fdf6e3; border-right: 4px solid #d4af37;
      border-radius: 6px; font-weight: 600;
    }
    table {
      width: 100%; border-collapse: collapse; font-size: 13px;
      box-shadow: 0 2px 6px rgba(0,0,0,0.08);
      border-radius: 10px; overflow: hidden;
    }
    thead tr {
      background: linear-gradient(135deg,#d4af37,#f0c040);
      color: #1a1208;
    }
    th {
      padding: 12px 10px; text-align: right; font-weight: 900;
      font-size: 12px; letter-spacing: 0.3px;
    }
    td {
      padding: 10px 10px; border-bottom: 1px solid #e5dcc0; color: #2a2a2a;
      font-weight: 600;
    }
    tbody tr:nth-child(even) { background: #fffbf0; }
    tbody tr:hover { background: #fff5d6; }
    .footer {
      margin-top: 24px; padding: 12px;
      border-top: 2px solid #d4af3766;
      font-size: 11px; color: #8b6914; text-align: center;
      font-weight: 700;
    }
    @media screen {
      .print-controls {
        position: fixed; top: 12px; left: 12px;
        background: #15100a; color: #f0c040; padding: 10px 14px;
        border-radius: 10px; display: flex; gap: 8px; z-index: 100;
        box-shadow: 0 4px 12px rgba(0,0,0,0.4);
      }
      .print-controls button {
        padding: 8px 14px; border-radius: 6px; border: none; cursor: pointer;
        font-family: 'Cairo'; font-weight: 800; font-size: 12px;
      }
      .print-controls .btn-print { background: #d4af37; color: #000; }
      .print-controls .btn-close { background: #2e0a0a; color: #f87171; }
    }
    @media print {
      body { margin: 10px; }
      .print-controls { display: none !important; }
      thead { display: table-header-group; }
      tr { page-break-inside: avoid; }
      .header { box-shadow: none; }
    }
  </style>
</head>
<body>
  <div class="print-controls">
    <button class="btn-print" onclick="window.print()">🖨 طباعة</button>
    <button class="btn-close" onclick="window.close()">✕</button>
  </div>
  <div class="header">
    <div class="brand-block">
      <img src="${logoUrl}" alt="بريتاو" onerror="this.style.display='none'"/>
      <div class="brand-text">
        <div class="name">مختبر بريتاو</div>
        <div class="sub">Prettau Dental Lab</div>
      </div>
    </div>
    <div class="meta">
      <div>📅 ${dateStr}</div>
      <div>🕐 ${timeStr}</div>
    </div>
  </div>
  <h1>${title}</h1>
  ${subtitle ? `<div class="subtitle">${subtitle}</div>` : ""}
  <table>
    <thead>
      <tr>${columns.map(c=>`<th>${c}</th>`).join("")}</tr>
    </thead>
    <tbody>
      ${rows.length===0
        ? `<tr><td colspan="${columns.length}" style="padding:30px;text-align:center;color:#888">لا توجد بيانات</td></tr>`
        : rows.map(r=>`<tr>${r.map(c=>`<td>${c==null?"—":c}</td>`).join("")}</tr>`).join("")}
    </tbody>
  </table>
  <div class="footer">
    عدد السجلات: ${rows.length} • تم التوليد بواسطة نظام بريتاو • ${dateStr} ${timeStr}
  </div>
  <script>
    // الطباعة لا تبدأ تلقائياً — المستخدم يضغط الزر بنفسه
    // هذا يمنع فقدان السيطرة على التطبيق الأصلي
  </script>
</body>
</html>`;
  w.document.write(html);
  w.document.close();
  w.focus();
}

// زر تحميل PDF موحّد
function PdfBtn({onClick, label="📄 حفظ PDF"}) {
  return (
    <button onClick={onClick} style={{
      background:"linear-gradient(135deg,#b8860b,#f0c040)", color:"#000",
      border:"none", borderRadius:8, padding:"6px 14px",
      fontSize:11, fontWeight:800, cursor:"pointer", fontFamily:"Cairo,sans-serif",
      whiteSpace:"nowrap"
    }}>{label}</button>
  );
}

// تقييم الطبيب حسب عدد الزيارات/الحالات
function getDoctorRating(visitsCount) {
  const v = visitsCount || 1;
  if (v >= 10) return { label: "قوي",   color: "#22d3a0", icon: "⭐⭐⭐" };
  if (v >= 5)  return { label: "متوسط", color: "#fbbf24", icon: "⭐⭐"   };
  return                { label: "ضعيف", color: "#f87171", icon: "⭐"     };
}

// مكوّن خريطة الأطباء المرقّمة + Legend جانبي
function NumberedDoctorsMap({ locations, height = 360, govCode = null, onPrint }) {
  const [highlighted, setHighlighted] = useState(null); // ID الطبيب المُحدَّد
  const divRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const [ready, setReady] = useState(false);

  // إنشاء الخريطة
  useEffect(() => {
    if (!document.getElementById("lf-css")) {
      const l = document.createElement("link");
      l.id = "lf-css"; l.rel = "stylesheet";
      l.href = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css";
      document.head.appendChild(l);
    }
    const init = () => {
      if (!divRef.current || mapRef.current) return;
      try {
        const center = (govCode && GOV_CENTERS[govCode]) || GOV_CENTERS["ADMIN-00"];
        const map = window.L.map(divRef.current, {
          center: [center.lat, center.lng],
          zoom: center.zoom || 11,
          zoomControl: true,
          attributionControl: false,
        });
        window.L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(map);
        mapRef.current = map;
        setReady(true);
      } catch(e){ console.warn("Map init:", e); }
    };
    if (window.L) init();
    else if (!document.getElementById("lf-js")) {
      const s = document.createElement("script");
      s.id = "lf-js";
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
      s.onload = init; document.head.appendChild(s);
    } else {
      const t = setInterval(() => { if (window.L) { clearInterval(t); init(); } }, 100);
    }
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markersRef.current = {};
        setReady(false);
      }
    };
  }, []);

  // وضع العلامات المرقّمة
  useEffect(() => {
    if (!ready || !mapRef.current || !locations) return;
    const L = window.L;
    // امسح القديم
    Object.values(markersRef.current).forEach(m => { try { mapRef.current.removeLayer(m); } catch(e){} });
    markersRef.current = {};
    // مع التقريب على المواقع
    const valid = locations.filter(l => l.lat && l.lng);
    if (valid.length === 0) return;
    if (valid.length === 1) {
      mapRef.current.setView([valid[0].lat, valid[0].lng], 14);
    } else {
      try {
        const bounds = L.latLngBounds(valid.map(l => [l.lat, l.lng]));
        mapRef.current.fitBounds(bounds, { padding: [40,40], maxZoom: 14 });
      } catch(e){}
    }
    // أضف العلامات المرقّمة
    valid.forEach((loc, idx) => {
      const num = idx + 1;
      const isHi = highlighted === loc.id;
      const bg = isHi ? "#3b82f6" : "#ef4444";
      const ring = isHi ? "0 0 0 6px rgba(59,130,246,0.35), 0 4px 12px rgba(59,130,246,0.6)"
                        : "0 0 0 3px rgba(239,68,68,0.25), 0 2px 8px rgba(0,0,0,0.4)";
      const size = isHi ? 36 : 30;
      const icon = L.divIcon({
        html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${bg};border:3px solid #fff;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:900;font-size:${isHi?16:14}px;font-family:'Cairo',sans-serif;box-shadow:${ring};transition:all .25s ease;">${num}</div>`,
        className: "",
        iconSize: [size, size],
        iconAnchor: [size/2, size/2],
      });
      const marker = L.marker([loc.lat, loc.lng], { icon, zIndexOffset: isHi ? 9999 : 100 }).addTo(mapRef.current);
      marker.on("click", () => setHighlighted(loc.id));
      markersRef.current[loc.id] = marker;
    });
  }, [ready, locations, highlighted]);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      {/* الخريطة */}
      <div style={{position:"relative",height,borderRadius:12,overflow:"hidden",border:"1px solid #3d2f15"}}>
        <div ref={divRef} style={{width:"100%",height:"100%"}}/>
        {!ready && <div style={{position:"absolute",inset:0,background:"#040d18",display:"flex",alignItems:"center",justifyContent:"center",color:"#7a6841",fontSize:12}}>جاري تحميل الخريطة...</div>}
      </div>

      {/* Legend جانبي مع الأرقام والتقييم */}
      <div style={{
        background:"#15100a",border:"1px solid #3d2f15",borderRadius:12,
        padding:10,maxHeight:380,overflowY:"auto"
      }}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,padding:"4px 6px"}}>
          <div style={{fontSize:11,fontWeight:800,color:"#f0c040"}}>📍 قائمة العيادات ({locations.filter(l=>l.lat&&l.lng).length})</div>
          {highlighted && (
            <button onClick={()=>setHighlighted(null)}
              style={{background:"#15100a",border:"1px solid #3b82f6",borderRadius:6,padding:"3px 8px",color:"#3b82f6",fontSize:9,cursor:"pointer",fontFamily:"Cairo,sans-serif"}}>
              ✕ إلغاء التحديد
            </button>
          )}
        </div>
        {locations.filter(l=>l.lat&&l.lng).length === 0 && (
          <div style={{padding:20,textAlign:"center",color:"#5a4518",fontSize:11}}>
            لا توجد مواقع لعرضها
          </div>
        )}
        {locations.filter(l=>l.lat&&l.lng).map((loc, idx) => {
          const num = idx + 1;
          const rating = getDoctorRating(loc.visitsCount);
          const isHi = highlighted === loc.id;
          return (
            <div key={loc.id || idx}
              onClick={() => setHighlighted(isHi ? null : loc.id)}
              style={{
                display:"flex",alignItems:"center",gap:10,
                padding:"8px 10px",marginBottom:5,
                background: isHi ? "#1e3a8a33" : "#0a0600",
                border: `1.5px solid ${isHi ? "#3b82f6" : "#3d2f15"}`,
                borderRadius:8,
                cursor:"pointer",
                transition:"all .2s ease"
              }}>
              <div style={{
                width:32,height:32,borderRadius:"50%",
                background: isHi ? "#3b82f6" : "#ef4444",
                color:"#fff",fontWeight:900,fontSize:14,
                display:"flex",alignItems:"center",justifyContent:"center",
                flexShrink:0,fontFamily:"Cairo,sans-serif"
              }}>{num}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:11,fontWeight:800,color:"#f0f9ff",overflow:"hidden",textOverflow:"ellipsis"}}>
                  🦷 {loc.drName || "—"}
                </div>
                <div style={{fontSize:9,color:"#c9b37e",marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  📍 {loc.address || loc.govName || "—"}
                </div>
                <div style={{display:"flex",alignItems:"center",gap:6,marginTop:4,flexWrap:"wrap"}}>
                  <span style={{
                    fontSize:9,fontWeight:800,
                    padding:"2px 8px",borderRadius:10,
                    background: `${rating.color}22`,
                    color: rating.color,
                    border: `1px solid ${rating.color}55`
                  }}>
                    {rating.icon} {rating.label}
                  </span>
                  <span style={{fontSize:9,color:"#7a6841"}}>
                    {loc.visitsCount || 1} حالة
                  </span>
                  {loc.repName && (
                    <span style={{fontSize:9,color:"#60a5fa"}}>
                      • {loc.repName}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* زر طباعة */}
      {onPrint && (
        <button onClick={onPrint}
          style={{
            background:"linear-gradient(135deg,#1a3a2a,#22d3a0)",color:"#000",
            border:"none",borderRadius:8,padding:"10px",fontSize:12,fontWeight:800,
            cursor:"pointer",fontFamily:"Cairo,sans-serif"
          }}>
          🗺 طباعة الخريطة كاملة
        </button>
      )}
    </div>
  );
}

// مكوّن فلتر التاريخ + الفهرسة (يُستخدم في كل الجداول)
function DateSortFilter({dateFrom, setDateFrom, dateTo, setDateTo, sortBy, setSortBy, sortOptions=[]}) {
  const todayStr = () => new Date().toISOString().slice(0,10);
  const dateNDaysAgo = (n) => { const d=new Date(); d.setDate(d.getDate()-n); return d.toISOString().slice(0,10); };
  const setQuick = (kind) => {
    if (kind === "today")     { setDateFrom(todayStr()); setDateTo(todayStr()); }
    else if (kind === "yesterday") { const y=dateNDaysAgo(1); setDateFrom(y); setDateTo(y); }
    else if (kind === "week") { setDateFrom(dateNDaysAgo(7)); setDateTo(todayStr()); }
    else if (kind === "month"){ setDateFrom(dateNDaysAgo(30)); setDateTo(todayStr()); }
    else                      { setDateFrom(""); setDateTo(""); }
  };
  return (
    <div style={{background:"#15100a",border:"1px solid #3d2f15",borderRadius:10,padding:10,display:"flex",flexDirection:"column",gap:8}}>
      <div style={{fontSize:10,color:"#c9b37e",fontWeight:700}}>📅 فلتر التاريخ والفهرسة</div>
      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
        {[["today","اليوم","#22d3a0"],["yesterday","أمس","#fbbf24"],["week","أسبوع","#60a5fa"],["month","شهر","#a78bfa"],["all","الكل","#94a3b8"]].map(([k,l,c])=>(
          <button key={k} onClick={()=>setQuick(k)}
            style={{padding:"5px 10px",borderRadius:8,border:`1px solid ${c}44`,background:`${c}11`,color:c,fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"Cairo,sans-serif"}}>{l}</button>
        ))}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
        <div>
          <div style={{fontSize:9,color:"#7a6841",marginBottom:3}}>من تاريخ</div>
          <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)}
            style={{width:"100%",background:"#0a0600",border:"1px solid #3d2f15",borderRadius:8,padding:"6px 8px",color:"#f0f9ff",fontSize:11,fontFamily:"Cairo,sans-serif",boxSizing:"border-box"}}/>
        </div>
        <div>
          <div style={{fontSize:9,color:"#7a6841",marginBottom:3}}>إلى تاريخ</div>
          <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)}
            style={{width:"100%",background:"#0a0600",border:"1px solid #3d2f15",borderRadius:8,padding:"6px 8px",color:"#f0f9ff",fontSize:11,fontFamily:"Cairo,sans-serif",boxSizing:"border-box"}}/>
        </div>
      </div>
      {sortOptions.length>0 && (
        <div>
          <div style={{fontSize:9,color:"#7a6841",marginBottom:3}}>🔢 الفهرسة</div>
          <select value={sortBy} onChange={e=>setSortBy(e.target.value)}
            style={{width:"100%",background:"#0a0600",border:"1px solid #3d2f15",borderRadius:8,padding:"6px 8px",color:"#f0f9ff",fontSize:11,fontFamily:"Cairo,sans-serif"}}>
            {sortOptions.map(([v,l])=><option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}

// تصدير الخريطة كـ PDF — يفتح نافذة الطباعة بحجم كامل
function exportMapPdf({title, locations, subtitle=""}) {
  if (!locations || locations.length===0) { alert("لا توجد مواقع للطباعة"); return; }
  // اكتشاف Native Android — فتح نافذة modal داخل التطبيق
  const isNative = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
  if (isNative) {
    window.dispatchEvent(new CustomEvent("britao:openMapPrintModal", {
      detail: { title, subtitle, locations }
    }));
    return;
  }
  const w = window.open("about:blank", "_blank");
  if (!w) { alert("الرجاء السماح للنوافذ المنبثقة"); return; }
  // احسب bounds لتقريب الخريطة على المواقع
  const lats = locations.map(l=>l.lat).filter(Boolean);
  const lngs = locations.map(l=>l.lng).filter(Boolean);
  const minLat = Math.min(...lats) - 0.01;
  const maxLat = Math.max(...lats) + 0.01;
  const minLng = Math.min(...lngs) - 0.01;
  const maxLng = Math.max(...lngs) + 0.01;
  const dateStr = new Date().toLocaleDateString("ar-IQ");
  const timeStr = new Date().toLocaleTimeString("ar-IQ",{hour:"2-digit",minute:"2-digit",hour12:true});
  // نُحسّب التقييم لكل عيادة
  const ratedLocs = locations.map((l, i) => {
    const v = l.visitsCount || 1;
    const rating = v >= 10 ? {label:"قوي",color:"#22d3a0",icon:"⭐⭐⭐"}
                : v >= 5  ? {label:"متوسط",color:"#fbbf24",icon:"⭐⭐"}
                          : {label:"ضعيف",color:"#f87171",icon:"⭐"};
    return {
      num: i+1,
      lat: l.lat, lng: l.lng,
      name: (l.drName||l.name||"موقع").replace(/['"<>]/g,""),
      addr: (l.address||l.govName||"").replace(/['"<>]/g,""),
      rep:  (l.repName||"").replace(/['"<>]/g,""),
      visits: v,
      rating
    };
  });
  const markersJSON = JSON.stringify(ratedLocs);
  const html = `
<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
<meta charset="UTF-8">
<title>${title}</title>
<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css">
<link href="https://fonts.googleapis.com/css2?family=Tajawal:wght@600;700;900&display=swap" rel="stylesheet">
<style>
  *{box-sizing:border-box;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
  body{font-family:'Tajawal',sans-serif;margin:14px;direction:rtl;color:#1a1a1a;}
  .header{display:flex;justify-content:space-between;align-items:center;padding:10px 16px;margin-bottom:12px;
    background:linear-gradient(135deg,#fefaee,#fff);border-radius:12px;border:2px solid #d4af37;}
  .brand{font-size:22px;font-weight:900;background:linear-gradient(135deg,#8b6914,#d4af37,#f0c040);
    -webkit-background-clip:text;background-clip:text;color:transparent;}
  .meta{font-size:11px;color:#5a4518;font-weight:700;text-align:left;}
  h1{font-size:18px;margin:6px 0;font-weight:900;}
  .subtitle{font-size:12px;color:#5a4518;margin-bottom:10px;font-weight:600;}
  .container{display:grid;grid-template-columns:1fr 320px;gap:14px;}
  #map{width:100%;height:72vh;border-radius:12px;border:2px solid #d4af37;}
  .legend{background:#fffbf0;border:2px solid #d4af37;border-radius:12px;padding:10px;max-height:72vh;overflow-y:auto;}
  .legend-title{font-size:13px;font-weight:900;color:#5a4518;margin-bottom:10px;padding-bottom:8px;border-bottom:2px solid #d4af3744;}
  .legend-item{display:flex;align-items:flex-start;gap:8px;padding:8px;margin-bottom:6px;
    background:#fff;border:1px solid #d4af3733;border-radius:8px;cursor:pointer;transition:all .2s;}
  .legend-item:hover,.legend-item.active{background:#dbeafe;border-color:#3b82f6;}
  .legend-num{width:30px;height:30px;border-radius:50%;background:#ef4444;color:#fff;
    display:flex;align-items:center;justify-content:center;font-weight:900;font-size:13px;flex-shrink:0;}
  .legend-item.active .legend-num{background:#3b82f6;}
  .legend-text{flex:1;min-width:0;}
  .legend-name{font-size:11px;font-weight:900;color:#1a1208;}
  .legend-addr{font-size:9px;color:#8b6914;margin-top:2px;}
  .legend-rating{display:inline-block;font-size:9px;font-weight:800;padding:1px 6px;border-radius:8px;margin-top:3px;}
  .legend-rep{font-size:9px;color:#3b82f6;margin-top:2px;}
  .footer{margin-top:14px;padding:10px;border-top:2px solid #d4af3766;font-size:11px;color:#8b6914;text-align:center;font-weight:700;}
  .controls{position:fixed;top:8px;left:8px;background:#15100a;color:#f0c040;padding:8px;border-radius:10px;z-index:9999;display:flex;gap:6px;}
  .controls button{padding:6px 12px;border-radius:6px;border:none;font-family:'Tajawal';font-weight:800;cursor:pointer;}
  .controls .print{background:#d4af37;color:#000;}
  .controls .close{background:#2e0a0a;color:#f87171;}
  @media print {
    .controls{display:none!important;}
    body{margin:5px;}
    #map,.legend{height:80vh;max-height:80vh;}
    .header{box-shadow:none;}
  }
  @media (max-width:768px){
    .container{grid-template-columns:1fr;}
    #map{height:50vh;}
    .legend{max-height:none;}
  }
</style>
</head>
<body>
<div class="controls">
  <button class="print" onclick="window.print()">🖨 طباعة</button>
  <button class="close" onclick="window.close()">✕</button>
</div>
<div class="header">
  <div>
    <div class="brand">مختبر بريتاو</div>
    <div style="font-size:11px;color:#8b6914;">Prettau Dental Lab</div>
  </div>
  <div class="meta">
    <div>📅 ${dateStr}</div>
    <div>🕐 ${timeStr}</div>
    <div>📍 ${locations.length} موقع</div>
  </div>
</div>
<h1>${title}</h1>
${subtitle?`<div class="subtitle">${subtitle}</div>`:""}
<div class="container">
  <div id="map"></div>
  <div class="legend">
    <div class="legend-title">📋 قائمة العيادات (${locations.length})</div>
    <div id="legend-list"></div>
  </div>
</div>
<div class="footer">عدد المواقع: ${locations.length} • نظام بريتاو • ${dateStr} ${timeStr}</div>
<script src="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js"></script>
<script>
window.onload = function(){
  var locs = ${markersJSON};
  var map = L.map('map',{zoomControl:true,attributionControl:false});
  L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",{maxZoom:19}).addTo(map);
  if(locs.length===1){
    map.setView([locs[0].lat,locs[0].lng],15);
  } else {
    var bounds = L.latLngBounds(locs.map(function(l){return [l.lat,l.lng];}));
    map.fitBounds(bounds,{padding:[40,40],maxZoom:14});
  }

  var markers = {};
  var highlighted = null;

  function makeIcon(num, isHi){
    var bg = isHi ? "#3b82f6" : "#ef4444";
    var size = isHi ? 38 : 32;
    var ring = isHi ? "0 0 0 7px rgba(59,130,246,0.35), 0 4px 12px rgba(59,130,246,0.7)"
                    : "0 0 0 3px rgba(239,68,68,0.25), 0 2px 8px rgba(0,0,0,0.4)";
    return L.divIcon({
      html:'<div style="width:'+size+'px;height:'+size+'px;border-radius:50%;background:'+bg+';border:3px solid #fff;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:900;font-size:'+(isHi?16:14)+'px;font-family:Tajawal,sans-serif;box-shadow:'+ring+';transition:all .25s ease;">'+num+'</div>',
      className:"",iconSize:[size,size],iconAnchor:[size/2,size/2]
    });
  }

  function highlight(num){
    // Reset previous
    if(highlighted !== null && markers[highlighted]){
      markers[highlighted].setIcon(makeIcon(highlighted, false));
      var prev = document.getElementById('item-'+highlighted);
      if(prev) prev.classList.remove('active');
    }
    if(highlighted === num){
      highlighted = null;
      return;
    }
    highlighted = num;
    if(markers[num]){
      markers[num].setIcon(makeIcon(num, true));
      markers[num].setZIndexOffset(9999);
    }
    var item = document.getElementById('item-'+num);
    if(item){
      item.classList.add('active');
      item.scrollIntoView({behavior:"smooth",block:"nearest"});
    }
  }

  // أنشئ العلامات
  locs.forEach(function(l){
    var m = L.marker([l.lat,l.lng],{icon:makeIcon(l.num,false)}).addTo(map);
    m.on('click',function(){ highlight(l.num); });
    markers[l.num] = m;
  });

  // أنشئ Legend
  var listEl = document.getElementById('legend-list');
  locs.forEach(function(l){
    var div = document.createElement('div');
    div.className = 'legend-item';
    div.id = 'item-'+l.num;
    div.onclick = function(){ highlight(l.num); map.setView([l.lat,l.lng],16); };
    var inner = '<div class="legend-num">'+l.num+'</div>'
              + '<div class="legend-text">'
              + '<div class="legend-name">🦷 '+l.name+'</div>';
    if(l.addr) inner += '<div class="legend-addr">📍 '+l.addr+'</div>';
    inner += '<div>'
           + '<span class="legend-rating" style="background:'+l.rating.color+'22;color:'+l.rating.color+';border:1px solid '+l.rating.color+'55;">'+l.rating.icon+' '+l.rating.label+'</span>'
           + ' <span style="font-size:9px;color:#7a6841;">('+l.visits+' حالة)</span>'
           + '</div>';
    if(l.rep) inner += '<div class="legend-rep">👤 '+l.rep+'</div>';
    inner += '</div>';
    div.innerHTML = inner;
    listEl.appendChild(div);
  });
};
</script>
</body>
</html>`;
  w.document.write(html);
  w.document.close();
  w.focus();
}

const tFmt = ts => {
  if (!ts) return "";
  if (ts.toDate) return ts.toDate().toLocaleTimeString("ar-IQ",{hour:"2-digit",minute:"2-digit"});
  return ts;
};

// ═══════════════════════════════════════════════
//  STYLES
// ═══════════════════════════════════════════════
const G = `
  @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800;900&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Cairo',sans-serif;background:#0a0600;direction:rtl}
  ::-webkit-scrollbar{width:4px}::-webkit-scrollbar-track{background:transparent}
  ::-webkit-scrollbar-thumb{background:#3d2f15;border-radius:2px}
  @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:.3}}
  @keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}
  @keyframes slideIn{from{opacity:0;transform:translateX(14px)}to{opacity:1;transform:translateX(0)}}
  @keyframes blink{0%,100%{box-shadow:0 0 6px #f472b6}50%{box-shadow:0 0 20px #f472b6,0 0 40px #f472b622}}
  @keyframes slideDownBounce{0%{opacity:0;transform:translate(-50%,-80px)}60%{opacity:1;transform:translate(-50%,10px)}100%{transform:translate(-50%,0)}}
  @keyframes pulseBig{0%,100%{box-shadow:0 10px 40px rgba(34,211,160,0.5), 0 0 80px rgba(34,211,160,0.3)}50%{box-shadow:0 10px 50px rgba(34,211,160,0.7), 0 0 120px rgba(34,211,160,0.5)}}
  @keyframes ring{0%{transform:rotate(-8deg)}100%{transform:rotate(8deg)}}
  input::placeholder,textarea::placeholder{color:#5a4518}
  input:focus,textarea:focus,select:focus{outline:none}
`;

// ═══════════════════════════════════════════════
//  SHARED COMPONENTS
// ═══════════════════════════════════════════════
function SBadge({s}) {
  return <span style={{fontSize:9,padding:"3px 10px",borderRadius:20,fontWeight:700,background:APTS[s]?.bg||"#3d2f15",color:APTS[s]?.c||"#94a3b8",whiteSpace:"nowrap"}}>{s}</span>;
}
// ═══════════════════════════════════════════════════════════════════════════
//  PROFESSIONAL SVG ICONS — نمط Brand Icons (Filled, Simple, Distinctive)
// ═══════════════════════════════════════════════════════════════════════════
function RoleIcon({type, size=32, color="#fff"}) {
  const props = {
    width: size, height: size, viewBox:"0 0 24 24",
    fill: color, xmlns:"http://www.w3.org/2000/svg"
  };
  switch (type) {
    case "doctor":  // ━━ سماعة طبية فاخرة (filled) ━━
      return (
        <svg {...props}>
          <path d="M11 2.5a1 1 0 0 0-1 1V8a3 3 0 1 1-6 0V3.5a1 1 0 0 0-2 0V8a5 5 0 0 0 4 4.9V15a5 5 0 0 0 9.9 1A4 4 0 1 0 17 8v-.04A4 4 0 0 0 17 0a4 4 0 0 0-4 4 4 4 0 0 0 2 3.46V8a2 2 0 1 1 0 4 1 1 0 0 0-1 1v2a3 3 0 0 1-6 0v-2.1A5 5 0 0 0 12 8V3.5a1 1 0 0 0-1-1z" transform="translate(0 1)"/>
          <circle cx="17" cy="4" r="1.6" fill="currentColor" opacity=".15"/>
        </svg>
      );
    case "rep":  // ━━ دراجة توصيل (motorcycle filled) ━━
      return (
        <svg {...props}>
          <path d="M5 17a3 3 0 1 0 .001 6.001A3 3 0 0 0 5 17zm0 4.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zM19 17a3 3 0 1 0 .001 6.001A3 3 0 0 0 19 17zm0 4.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z" transform="translate(0 -3)"/>
          <path d="M19.44 9.03L15.41 5H10v2h4.59l2.21 2.21A6.96 6.96 0 0 0 13.5 12H7l-2-2H1v2h3.17l1.83 1.83V15c0 .55.45 1 1 1h3.5l-2.21 2.21A4 4 0 0 1 5 19a4 4 0 0 1-4-4 1 1 0 0 1 2 0 2 2 0 0 0 2 2c.55 0 1.04-.22 1.41-.59L7 16.83V12h6.5c.27 0 .53.05.77.13l1.5-1.5A4.97 4.97 0 0 1 19 11a5 5 0 0 1 5 5 5 5 0 0 1-5 5 5 5 0 0 1-5-5 5 5 0 0 1 .13-1.13l-1.46 1.46c-.16.55-.17 1.13-.17 1.67a3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3c-.55 0-1.04.22-1.41.59L13 16.83V12.5l1.91-1.91A2.99 2.99 0 0 1 17 10a3 3 0 0 1 0 6 3 3 0 0 1-3-3l1.5-1.5L19.44 9.03z" transform="translate(0 -2) scale(0.95)"/>
        </svg>
      );
    case "sec":  // ━━ سماعة Headset (filled bold) ━━
      return (
        <svg {...props}>
          <path d="M12 1C7.03 1 3 5.03 3 10v7a3 3 0 0 0 3 3h2v-9H5V10a7 7 0 0 1 14 0v1h-3v9h2a3 3 0 0 0 3-3V10c0-4.97-4.03-9-9-9z"/>
          <rect x="4" y="13" width="4" height="6" rx="1" fill="currentColor" opacity=".7"/>
          <rect x="16" y="13" width="4" height="6" rx="1" fill="currentColor" opacity=".7"/>
        </svg>
      );
    case "supervisor":  // ━━ حقيبة عمل (briefcase filled) ━━
      return (
        <svg {...props}>
          <path d="M9 2a2 2 0 0 0-2 2v2H4a2 2 0 0 0-2 2v3h20V8a2 2 0 0 0-2-2h-3V4a2 2 0 0 0-2-2H9zm0 2h6v2H9V4z"/>
          <path d="M2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6h-9v2h-2v-2H2z"/>
        </svg>
      );
    case "marketing":  // ━━ مكبّر صوت (megaphone filled) ━━
      return (
        <svg {...props}>
          <path d="M21 4a1 1 0 0 0-1.45-.89L7.34 9.57c-.16-.04-.32-.07-.49-.07H4a3 3 0 0 0-3 3 3 3 0 0 0 3 3h.59l1.62 5.56A2 2 0 0 0 8.13 22h.78a2 2 0 0 0 1.94-2.51L9.7 16h.66l9.19 4.46A1 1 0 0 0 21 19.5V4zM7 15.5l1.5 5h-.5l-1.5-5H7z"/>
          <path d="M22 8a1 1 0 0 0 0 8 1 1 0 0 0 0-8z" opacity=".5"/>
        </svg>
      );
    case "mushref":  // ━━ درع مع علامة صحّ (shield-check filled) ━━
      return (
        <svg {...props}>
          <path d="M12 1L3 5v6c0 5.5 3.84 10.74 9 12 5.16-1.26 9-6.5 9-12V5l-9-4z"/>
          <path d="M10 16l-3.5-3.5L8 11l2 2 5-5 1.5 1.5L10 16z" fill="#fff" opacity=".95"/>
        </svg>
      );
    default:
      return null;
  }
}

function Card({children,style={}}) {
  // ═══ بطاقة معاصرة بـ glassmorphism احترافي على خلفية سوداء ═══
  return <div style={{
    background:"linear-gradient(180deg, rgba(28,28,36,0.7), rgba(20,20,26,0.7))",
    backdropFilter:"blur(20px) saturate(180%)",
    WebkitBackdropFilter:"blur(20px) saturate(180%)",
    border:"1px solid rgba(255,255,255,0.08)",
    borderRadius:16,
    boxShadow:"0 1px 0 rgba(255,255,255,0.04) inset, 0 8px 32px rgba(0,0,0,0.35)",
    color:"#fafafa",
    transition:"all 0.25s cubic-bezier(0.4,0,0.2,1)",
    ...style
  }}>{children}</div>;
}

function Btn({children,color="#d4af37",onClick,disabled,small=false,style={}}) {
  // ═══ زرّ معاصر — مستوحى من Linear و Vercel على خلفية سوداء ═══
  const isGold = color==="#d4af37" || color==="#f0c040" || color==="#fbbf24";
  return <button onClick={onClick} disabled={disabled} style={{
    padding: small?"7px 14px":"10px 18px",
    borderRadius: small?10:12,
    fontSize: small?11:13,
    fontWeight: 600,
    letterSpacing: -0.01,
    cursor: disabled?"not-allowed":"pointer",
    fontFamily:"'Tajawal','Inter',sans-serif",
    background: disabled
      ? "rgba(255,255,255,0.04)"
      : `linear-gradient(180deg, ${color} 0%, ${color}d8 100%)`,
    border: disabled
      ? "1px solid rgba(255,255,255,0.06)"
      : `1px solid ${color}80`,
    color: disabled
      ? "rgba(255,255,255,0.3)"
      : (isGold?"#0a0a0e":"#fff"),
    opacity: disabled?.7:1,
    boxShadow: disabled
      ? "none"
      : `0 1px 0 rgba(255,255,255,0.15) inset, 0 4px 12px ${color}30, 0 1px 2px rgba(0,0,0,0.5)`,
    textShadow: disabled || isGold ? "none" : "0 1px 0 rgba(0,0,0,0.2)",
    transition: "all 0.18s cubic-bezier(0.4,0,0.2,1)",
    ...style
  }}>{children}</button>;
}
function TopBar({user,color,nc,onNotif,onLogout,title}) {
  // ═══ TopBar معاصر مستوحى من Linear / Vercel على خلفية سوداء ═══
  return (
    <div style={{
      height:60,
      background:"rgba(7, 7, 10, 0.85)",
      backdropFilter:"blur(24px) saturate(180%)",
      WebkitBackdropFilter:"blur(24px) saturate(180%)",
      borderBottom:"1px solid rgba(255,255,255,0.06)",
      display:"flex",alignItems:"center",padding:"0 16px",gap:12,
      flexShrink:0,position:"sticky",top:0,zIndex:100
    }}>
      {/* اللوغو */}
      <div style={{
        width:36,height:36,borderRadius:10,
        background:"linear-gradient(135deg,#1a1a22,#0e0e13)",
        padding:4,
        display:"flex",alignItems:"center",justifyContent:"center",
        boxShadow:"0 1px 0 rgba(255,255,255,0.04) inset, 0 4px 12px rgba(0,0,0,0.5)",
        flexShrink:0
      }}>
        <img src="/logo192.png" alt="بريتاو" style={{width:"100%",height:"100%",objectFit:"contain",borderRadius:8}}/>
      </div>

      {/* اسم المستخدم */}
      <div style={{flex:1,minWidth:0}}>
        <div style={{
          fontSize:13,fontWeight:700,
          color:"#fafafa",
          whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",
          letterSpacing:-0.01
        }}>{user.fullName||user.name}</div>
        <div style={{
          fontSize:10,color:"#d4af37",marginTop:1,fontWeight:800
        }}>{user.govName||""}</div>
      </div>

      {/* شارة الدور */}
      {title&&<div style={{
        fontSize:10,fontWeight:600,
        color:"#fafafa",
        background:`linear-gradient(135deg, ${color}40, ${color}20)`,
        border:`1px solid ${color}50`,
        padding:"5px 11px",borderRadius:8,flexShrink:0,
        letterSpacing:0.2
      }}>{title}</div>}

      {/* زرّ الإشعارات */}
      <button onClick={onNotif} style={{
        position:"relative",
        background:"rgba(255,255,255,0.04)",
        border:"1px solid rgba(255,255,255,0.08)",
        borderRadius:10,width:38,height:38,
        fontSize:16,display:"flex",alignItems:"center",justifyContent:"center",
        color:"#d4d4d8",flexShrink:0,
        transition:"all 0.18s cubic-bezier(0.4,0,0.2,1)"
      }}
      onMouseOver={e=>{e.currentTarget.style.background="rgba(255,255,255,0.08)";e.currentTarget.style.borderColor="rgba(255,255,255,0.15)";}}
      onMouseOut={e=>{e.currentTarget.style.background="rgba(255,255,255,0.04)";e.currentTarget.style.borderColor="rgba(255,255,255,0.08)";}}>
        🔔
        {nc>0&&<div style={{
          position:"absolute",top:-3,right:-3,
          minWidth:18,height:18,padding:"0 5px",borderRadius:9,
          background:"linear-gradient(180deg,#ef4444,#dc2626)",
          fontSize:9,color:"#fff",fontWeight:700,
          display:"flex",alignItems:"center",justifyContent:"center",
          boxShadow:"0 2px 6px rgba(239,68,68,0.5), 0 0 0 2px #07070a",
          fontFamily:"'Inter',sans-serif"
        }}>{nc}</div>}
      </button>

      {/* زرّ الخروج */}
      <button onClick={onLogout} style={{
        background:"rgba(239,68,68,0.08)",
        border:"1px solid rgba(239,68,68,0.20)",
        borderRadius:10,padding:"8px 14px",
        color:"#f87171",fontSize:11,fontWeight:600,
        whiteSpace:"nowrap",
        transition:"all 0.18s cubic-bezier(0.4,0,0.2,1)"
      }}
      onMouseOver={e=>{e.currentTarget.style.background="rgba(239,68,68,0.15)";e.currentTarget.style.borderColor="rgba(239,68,68,0.35)";}}
      onMouseOut={e=>{e.currentTarget.style.background="rgba(239,68,68,0.08)";e.currentTarget.style.borderColor="rgba(239,68,68,0.20)";}}>
        خروج
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  ConnectivityWatcher — مراقبة الإنترنت + GPS وإظهار تحذير عند انقطاع الخدمة
// ═══════════════════════════════════════════════════════════════════════════
function ConnectivityWatcher({user}) {
  const [isOnline, setIsOnline]   = useState(navigator.onLine !== false);
  const [gpsState, setGpsState]   = useState("granted"); // granted | denied | prompt | unknown | error
  const [vibrated, setVibrated]   = useState(false);

  // ─── مراقبة الإنترنت ───
  useEffect(() => {
    const onOnline  = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    setIsOnline(navigator.onLine !== false);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  // ─── مراقبة GPS (إذن الموقع الجغرافي) ───
  useEffect(() => {
    let watchId = null;
    let permissionStatus = null;

    const checkGps = async () => {
      // أولاً: تحقق من الإذن
      if (navigator.permissions) {
        try {
          permissionStatus = await navigator.permissions.query({ name: "geolocation" });
          setGpsState(permissionStatus.state); // granted | denied | prompt
          permissionStatus.onchange = () => setGpsState(permissionStatus.state);
        } catch(e) { setGpsState("unknown"); }
      }
      // ثانياً: جرّب جلب الموقع للتأكد فعلياً (يكشف GPS مغلق على مستوى النظام)
      if (navigator.geolocation) {
        watchId = navigator.geolocation.watchPosition(
          () => setGpsState("granted"),
          (err) => {
            // err.code: 1=PERMISSION_DENIED, 2=POSITION_UNAVAILABLE, 3=TIMEOUT
            if (err.code === 1) setGpsState("denied");
            else if (err.code === 2) setGpsState("error");
          },
          { enableHighAccuracy: false, maximumAge: 60000, timeout: 30000 }
        );
      }
    };
    checkGps();

    return () => {
      try { if (watchId !== null) navigator.geolocation.clearWatch(watchId); } catch(e){}
      try { if (permissionStatus) permissionStatus.onchange = null; } catch(e){}
    };
  }, []);

  // ─── اهتزاز تنبيهي عند تغيّر الحالة لأول مرة (للموبايل) ───
  useEffect(() => {
    if ((!isOnline || gpsState === "denied") && !vibrated) {
      try {
        if (navigator.vibrate) navigator.vibrate([300, 150, 300, 150, 300]);
      } catch(e) {}
      setVibrated(true);
    } else if (isOnline && gpsState === "granted") {
      setVibrated(false); // إعادة تعيين الاهتزاز للمرة القادمة
    }
  }, [isOnline, gpsState, vibrated]);

  // ─── طلب إذن الموقع مجدّداً ───
  const requestGps = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        () => setGpsState("granted"),
        (err) => {
          if (err.code === 1) {
            alert("⚠️ يجب السماح بالوصول للموقع من إعدادات الهاتف:\n\nالإعدادات → التطبيقات → بريتاو → الأذونات → الموقع → السماح طوال الوقت");
          }
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  };

  // إذا كل شيء يعمل — لا يظهر شيء
  if (isOnline && (gpsState === "granted" || gpsState === "prompt" || gpsState === "unknown")) return null;

  // ─── البانر التحذيري ───
  const showOffline = !isOnline;
  const showGpsOff  = gpsState === "denied" || gpsState === "error";

  return (
    <div style={{
      position:"fixed", top:0, left:0, right:0, zIndex:9998,
      background:"linear-gradient(135deg,#7f1d1d,#991b1b)",
      borderBottom:"3px solid #f87171",
      padding:"12px 14px",
      animation:"connWarnPulse 1.5s ease-in-out infinite",
      boxShadow:"0 4px 20px rgba(239,68,68,0.5)",
      direction:"rtl", fontFamily:"Cairo,sans-serif"
    }}>
      <style>{`
        @keyframes connWarnPulse {
          0%, 100% { background: linear-gradient(135deg,#7f1d1d,#991b1b); }
          50%      { background: linear-gradient(135deg,#991b1b,#dc2626); }
        }
      `}</style>

      {showOffline && (
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom: showGpsOff ? 8 : 0}}>
          <span style={{fontSize:24}}>📡</span>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:900,color:"#fff"}}>
              ⚠️ لا يوجد اتصال بالإنترنت!
            </div>
            <div style={{fontSize:10,color:"#fecaca",marginTop:2,lineHeight:1.5}}>
              يُرجى تشغيل البيانات أو WiFi لتسجيل المواعيد والتتبّع
            </div>
          </div>
        </div>
      )}

      {showGpsOff && (
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:24}}>📍</span>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:900,color:"#fff"}}>
              ⚠️ خدمة الموقع (GPS) معطّلة!
            </div>
            <div style={{fontSize:10,color:"#fecaca",marginTop:2,lineHeight:1.5}}>
              تشغيل GPS مطلوب لتتبّع موقعك أثناء الزيارات
            </div>
          </div>
          <button onClick={requestGps}
            style={{background:"#fff",color:"#991b1b",border:"none",borderRadius:8,
              padding:"8px 14px",fontSize:11,fontWeight:900,cursor:"pointer",
              fontFamily:"Cairo,sans-serif",whiteSpace:"nowrap"}}>
            ✓ تفعيل
          </button>
        </div>
      )}
    </div>
  );
}

// ═══ Banner لتفعيل الإشعارات ═══
function NotifBanner({notifPerm, requestNotifs}) {
  const [dismissed, setDismissed] = useState(false);
  // محاولة تلقائية لتفعيل الإشعارات بعد 1.5 ثانية من تحميل التطبيق
  useEffect(() => {
    if (notifPerm === "default" && typeof requestNotifs === "function") {
      const t = setTimeout(() => {
        try { requestNotifs(); } catch(e){}
      }, 1500);
      return () => clearTimeout(t);
    }
  }, [notifPerm]);
  // البانر مخفي دائماً — التفعيل تلقائي
  return null;
  // الكود السابق محفوظ تحت لكن غير مستخدم
  // eslint-disable-next-line no-unreachable
  if (dismissed) return null;
  if (notifPerm === "granted") return null;
  const isBlocked = notifPerm === "denied";
  return (
    <div style={{background:isBlocked?"linear-gradient(135deg,#2e0a0a,#1a0505)":"linear-gradient(135deg,#1a1300,#0d0a00)",
      borderBottom:`2px solid ${isBlocked?"#f87171":"#fbbf24"}`,
      padding:"12px 14px",display:"flex",alignItems:"center",gap:10,
      animation: isBlocked ? "none" : "pulse 2.5s infinite"}}>
      <span style={{fontSize:24,flexShrink:0}}>{isBlocked?"🔕":"🔔"}</span>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:12,fontWeight:800,color:isBlocked?"#f87171":"#fbbf24",marginBottom:2}}>
          {isBlocked ? "الإشعارات محجوبة في المتصفح" : "⚡ فعّل التنبيهات الآن"}
        </div>
        <div style={{fontSize:10,color:"#94a3b8",lineHeight:1.4}}>
          {isBlocked
            ? "اضغط 🔒 بجانب الرابط → Notifications → Allow"
            : "صوت + اهتزاز + تنبيه على شاشة القفل"}
        </div>
      </div>
      {!isBlocked && (
        <button onClick={requestNotifs}
          style={{background:"linear-gradient(135deg,#22d3a0,#0891b2)",border:"none",
            borderRadius:10,padding:"8px 14px",color:"#fff",fontSize:11,fontWeight:800,
            cursor:"pointer",fontFamily:"Cairo,sans-serif",whiteSpace:"nowrap",
            boxShadow:"0 4px 14px rgba(34,211,160,0.4)"}}>
          ✓ تفعيل
        </button>
      )}
      <button onClick={()=>setDismissed(true)}
        style={{background:"transparent",border:"none",color:"#64748b",fontSize:18,cursor:"pointer",padding:"0 4px"}}>✕</button>
    </div>
  );
}
function BNav({items,active,onChange}) {
  // ═══ Bottom Navigation معاصر مستوحى من iOS / Material You — خلفية سوداء ═══
  return (
    <div style={{
      background:"rgba(7, 7, 10, 0.85)",
      backdropFilter:"blur(24px) saturate(180%)",
      WebkitBackdropFilter:"blur(24px) saturate(180%)",
      borderTop:"1px solid rgba(255,255,255,0.06)",
      display:"flex",padding:"10px 4px 14px",
      flexShrink:0,position:"sticky",bottom:0,zIndex:100
    }}>
      {items.map(n=>{
        const on = active===n.id;
        return (
          <button key={n.id} onClick={()=>onChange(n.id)} style={{
            flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4,
            background:"none",border:"none",padding:"4px 2px",position:"relative"
          }}>
            {/* Pill indicator */}
            <div style={{
              width: on?52:42,
              height:32,borderRadius:16,
              display:"flex",alignItems:"center",justifyContent:"center",
              background: on ? `${n.c}22` : "transparent",
              transition:"all 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)",
            }}>
              <span style={{
                fontSize:18,
                filter: on ? `drop-shadow(0 0 4px ${n.c}88)` : "none",
                opacity: on ? 1 : 0.55,
                transition:"all 0.25s cubic-bezier(0.4,0,0.2,1)"
              }}>{n.icon}</span>
            </div>
            <span style={{
              fontSize:10,
              color: on ? n.c : "#d4af37",
              fontWeight: on?800:700,
              whiteSpace:"nowrap",
              letterSpacing:0.1,
              transition:"all 0.2s cubic-bezier(0.4,0,0.2,1)",
              fontFamily:"'Tajawal','Inter',sans-serif"
            }}>{n.label}</span>
          </button>
        );
      })}
    </div>
  );
}
function NPanel({notifs,color,onClose,onMarkAll}) {
  return (
    <div style={{position:"fixed",inset:0,zIndex:99999,background:"#000000aa"}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{position:"absolute",top:0,right:0,width:300,height:"100%",background:"#0d0a00",borderLeft:"1px solid #3d2f15",display:"flex",flexDirection:"column",animation:"slideIn .2s ease",zIndex:1}}>
        <div style={{padding:"14px 16px",borderBottom:"1px solid #3d2f15",display:"flex",justifyContent:"space-between"}}>
          <span style={{fontSize:13,fontWeight:800,color:"#f0f9ff"}}>الإشعارات</span>
          <button onClick={()=>{onMarkAll();onClose();}} style={{background:"none",border:"none",color,fontSize:10,cursor:"pointer",fontFamily:"Cairo,sans-serif"}}>تعليم الكل</button>
        </div>
        <div style={{flex:1,overflowY:"auto"}}>
          {notifs.length===0
            ?<div style={{textAlign:"center",padding:40,color:"#334155",fontSize:12}}>لا توجد إشعارات</div>
            :notifs.map((n,i)=>(
              <div key={i} style={{display:"flex",gap:10,padding:"12px 16px",borderBottom:"1px solid #0f1829",background:!n.read?"#0a182988":"transparent"}}>
                <div style={{width:34,height:34,borderRadius:10,background:"#0a1829",display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,flexShrink:0}}>{n.icon}</div>
                <div style={{flex:1}}><div style={{fontSize:12,color:!n.read?"#fafafa":"#d4af37",fontWeight:!n.read?800:700,lineHeight:1.5}}>{n.text}</div><div style={{fontSize:10,color:"#d4af37",fontWeight:700,marginTop:3}}>{n.time}</div></div>
                {!n.read&&<div style={{width:7,height:7,borderRadius:"50%",background:color,marginTop:4,flexShrink:0}}/>}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════
//  LEAFLET MAP (خريطة حقيقية)
// ═══════════════════════════════════════════════
function LeafletMap({reps,appointments,height=400,filterRepId=null,centerOn=null,doctorLocs=null,visitLocs=null,compareLocs=null,govCode=null}) {
  const divRef  = useRef(null);
  const mapRef  = useRef(null);
  const rMkrs   = useRef({});
  const aMkrs   = useRef({});
  const dMkrs   = useRef({});
  const vMkrs   = useRef({});
  const cMkrs   = useRef({});
  const meMkr   = useRef(null);
  const centeredOnce = useRef(false);
  const [ready,setReady] = useState(false);
  const [info,setInfo]   = useState(null);

  useEffect(()=>{
    if(!document.getElementById("lf-css")){
      const l=document.createElement("link");l.id="lf-css";l.rel="stylesheet";
      l.href="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css";
      document.head.appendChild(l);
    }
    // CSS لنبض المؤشرات
    if(!document.getElementById("lf-pulse-css")){
      const st=document.createElement("style");st.id="lf-pulse-css";
      st.textContent=`
        @keyframes mapPulseGold  { 0%{box-shadow:0 0 0 0 rgba(212,175,55,.85);} 70%{box-shadow:0 0 0 18px rgba(212,175,55,0);} 100%{box-shadow:0 0 0 0 rgba(212,175,55,0);} }
        @keyframes mapPulseRed   { 0%{box-shadow:0 0 0 0 rgba(239,68,68,.85);}  70%{box-shadow:0 0 0 22px rgba(239,68,68,0);} 100%{box-shadow:0 0 0 0 rgba(239,68,68,0);} }
        @keyframes mapPulseBlue  { 0%{box-shadow:0 0 0 0 rgba(96,165,250,.85);} 70%{box-shadow:0 0 0 22px rgba(96,165,250,0);} 100%{box-shadow:0 0 0 0 rgba(96,165,250,0);} }
        @keyframes mapPulseBlack { 0%{box-shadow:0 0 0 0 rgba(0,0,0,.9), 0 0 0 0 rgba(167,139,250,.7);} 70%{box-shadow:0 0 0 14px rgba(0,0,0,0), 0 0 0 22px rgba(167,139,250,0);} 100%{box-shadow:0 0 0 0 rgba(0,0,0,0), 0 0 0 0 rgba(167,139,250,0);} }
        @keyframes mapPulseGreen { 0%{box-shadow:0 0 0 0 rgba(34,211,160,.9);} 70%{box-shadow:0 0 0 22px rgba(34,211,160,0);} 100%{box-shadow:0 0 0 0 rgba(34,211,160,0);} }
        .pulse-rep   { animation: mapPulseGold  1.4s infinite; }
        .pulse-doc   { animation: mapPulseRed   1.6s infinite; }
        .pulse-me    { animation: mapPulseBlue  1.4s infinite; }
        .pulse-visit { animation: mapPulseBlack 1.8s infinite; }
        @keyframes mapPulsePink   { 0%{box-shadow:0 0 0 0 rgba(251,113,133,.85);} 70%{box-shadow:0 0 0 22px rgba(251,113,133,0);} 100%{box-shadow:0 0 0 0 rgba(251,113,133,0);} }
        .pulse-visit-mkt { animation: mapPulsePink 1.6s infinite; }
        .pulse-visit-msh { animation: mapPulseGreen 1.8s infinite; }
        .pulse-cmp-y { animation: mapPulseGreen 1.4s infinite; }
        .pulse-cmp-n { animation: mapPulseRed   1.6s infinite; }
      `;
      document.head.appendChild(st);
    }
    const init=()=>{
      if(!divRef.current||mapRef.current) return;
      try{
        // مركز الخريطة الافتراضي حسب محافظة المستخدم
        const center = (govCode && GOV_CENTERS[govCode]) || GOV_CENTERS["ADMIN-00"];
        const map=window.L.map(divRef.current,{center:[center.lat,center.lng],zoom:center.zoom||11,zoomControl:true,attributionControl:false});
        window.L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",{maxZoom:18}).addTo(map);
        mapRef.current=map; setReady(true);
      }catch(e){console.warn("Map init:",e);}
    };
    if(window.L) init();
    else if(!document.getElementById("lf-js")){
      const s=document.createElement("script");s.id="lf-js";
      s.src="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
      s.onload=init; document.head.appendChild(s);
    } else { const t=setInterval(()=>{if(window.L){clearInterval(t);init();}},100); }
    return()=>{ if(mapRef.current){mapRef.current.remove();mapRef.current=null;rMkrs.current={};aMkrs.current={};dMkrs.current={};meMkr.current=null;centeredOnce.current=false;setReady(false);} };
  },[]);

  // تركيز الخريطة على موقع المستخدم الحالي
  useEffect(()=>{
    if(!ready||!mapRef.current||!centerOn||!centerOn.lat||!centerOn.lng) return;
    const L=window.L;
    if(!centeredOnce.current){
      mapRef.current.setView([centerOn.lat,centerOn.lng],15);
      centeredOnce.current=true;
    }
    const ic=L.divIcon({html:`<div class="pulse-me" style="width:20px;height:20px;border-radius:50%;background:#60a5fa;border:3px solid #fff;"></div>`,className:"",iconSize:[20,20],iconAnchor:[10,10]});
    if(meMkr.current){ meMkr.current.setLatLng([centerOn.lat,centerOn.lng]); meMkr.current.setIcon(ic); }
    else{ meMkr.current=L.marker([centerOn.lat,centerOn.lng],{icon:ic,zIndexOffset:2000}).addTo(mapRef.current).bindTooltip("📍 موقعي الآن",{permanent:false}); }
  },[ready,centerOn?.lat,centerOn?.lng]);

  useEffect(()=>{
    if(!ready||!mapRef.current) return;
    const L=window.L;
    const list=filterRepId?reps.filter(r=>r.id===filterRepId):reps;
    // معرّفات المناديب الذين حالتهم "جاري التصوير" حالياً
    const shootingRepIds = new Set(
      (appointments||[]).filter(a=>a.status==="جاري التصوير"&&a.repId).map(a=>a.repId)
    );
    // ═══ احتساب الحالة الحقيقية بناءً على lastSeen ═══
    const computeLiveStatus = (r) => {
      const ls = r.lastSeen;
      if (!ls) return "offline"; // لم يفتح التطبيق أبداً
      const date = ls.toDate ? ls.toDate() : new Date(ls);
      const ageSec = (Date.now() - date.getTime()) / 1000;
      if (ageSec < 120)  return "active";   // آخر دقيقتين
      if (ageSec < 600)  return "idle";     // 2-10 دقائق
      return "offline";                     // > 10 دقائق
    };

    list.forEach(r=>{
      if(!r.lat||!r.lng) return;
      const isShooting = shootingRepIds.has(r.id);
      const liveStatus = computeLiveStatus(r);
      // اللون حسب الحالة الحقيقية
      let c;
      if (isShooting)               c = "#fbbf24"; // ذهبي - يصوّر
      else if (liveStatus==="active") c = "#22d3a0"; // أخضر - نشط
      else if (liveStatus==="idle")   c = "#fbbf24"; // أصفر - خامل
      else                           c = "#ef4444"; // أحمر - منقطع (GPS/إنترنت)
      const cls = isShooting ? "pulse-rep" : (liveStatus==="offline" ? "pulse-doc" : "");
      const ic=L.divIcon({html:`<div class="${cls}" style="width:18px;height:18px;border-radius:50%;background:${c};border:3px solid #fff;box-shadow:0 0 12px ${c}88"></div>`,className:"",iconSize:[18,18],iconAnchor:[9,9]});
      // مرّر الحالة الحيّة للـ data
      const rWithLive = {...r, _liveStatus: liveStatus};
      if(rMkrs.current[r.id]){ rMkrs.current[r.id].setLatLng([r.lat,r.lng]); rMkrs.current[r.id].setIcon(ic); }
      else{
        const m=L.marker([r.lat,r.lng],{icon:ic,zIndexOffset:1000}).addTo(mapRef.current);
        m.on("click",()=>setInfo({type:"rep",data:rWithLive}));
        rMkrs.current[r.id]=m;
      }
    });
  },[ready,reps,appointments,filterRepId]);

  useEffect(()=>{
    if(!ready||!mapRef.current) return;
    const L=window.L;
    appointments.forEach(a=>{
      if(!a.lat||!a.lng) return;
      const c=APTS[a.status]?.c||"#60a5fa";
      const ic=L.divIcon({html:`<div style="width:14px;height:14px;border-radius:3px;background:${c};border:2px solid #fff;transform:rotate(45deg);box-shadow:0 2px 6px #00000066"></div>`,className:"",iconSize:[14,14],iconAnchor:[7,7]});
      if(aMkrs.current[a.id]){ aMkrs.current[a.id].setIcon(ic); }
      else{
        const m=L.marker([a.lat,a.lng],{icon:ic}).addTo(mapRef.current);
        m.on("click",()=>setInfo({type:"apt",data:a}));
        aMkrs.current[a.id]=m;
      }
    });
  },[ready,appointments]);

  // مواقع العيادات الثابتة (لقسم مواقع الأطباء)
  useEffect(()=>{
    if(!ready||!mapRef.current||!doctorLocs) return;
    const L=window.L;
    // احذف العلامات التي خرجت من الفلتر
    const currentKeys = new Set(doctorLocs.filter(d=>d.lat&&d.lng).map(d=>"doc_"+d.id));
    Object.keys(dMkrs.current).forEach(key => {
      if (!currentKeys.has(key)) {
        try { mapRef.current.removeLayer(dMkrs.current[key]); } catch(e) {}
        delete dMkrs.current[key];
      }
    });
    doctorLocs.forEach(d=>{
      if(!d.lat||!d.lng) return;
      const ic=L.divIcon({html:`<div class="pulse-doc" style="width:22px;height:22px;border-radius:50%;background:#ef4444;border:3px solid #fff;display:flex;align-items:center;justify-content:center;font-size:11px;color:#fff">🦷</div>`,className:"",iconSize:[22,22],iconAnchor:[11,11]});
      const key="doc_"+d.id;
      if(dMkrs.current[key]){ dMkrs.current[key].setLatLng([d.lat,d.lng]); dMkrs.current[key].setIcon(ic); }
      else{
        const m=L.marker([d.lat,d.lng],{icon:ic,zIndexOffset:500}).addTo(mapRef.current);
        m.on("click",()=>setInfo({type:"doc",data:d}));
        dMkrs.current[key]=m;
      }
    });
  },[ready,doctorLocs]);

  // مواقع زيارات الفريق — تميّز حسب دور المُزوار (تيم ليدر / مندوب تسويق / مشرف)
  useEffect(()=>{
    if(!ready||!mapRef.current||!visitLocs) return;
    const L=window.L;
    // احذف العلامات التي خرجت من الفلتر
    const currentKeys = new Set(visitLocs.filter(v=>v.lat&&v.lng).map(v=>"visit_"+v.id));
    Object.keys(vMkrs.current).forEach(key => {
      if (!currentKeys.has(key)) {
        try { mapRef.current.removeLayer(vMkrs.current[key]); } catch(e) {}
        delete vMkrs.current[key];
      }
    });
    visitLocs.forEach(v=>{
      if(!v.lat||!v.lng) return;
      // ═══ تمييز اللون حسب دور المُزوار ═══
      let bgColor, borderColor, icon, pulseClass;
      if (v.leaderRole === "marketing") {
        bgColor = "#1a0507"; borderColor = "#fb7185"; icon = "🌹"; pulseClass = "pulse-visit-mkt";
      } else if (v.leaderRole === "mushref") {
        bgColor = "#0d0a1f"; borderColor = "#22d3a0"; icon = "🛡"; pulseClass = "pulse-visit-msh";
      } else {
        // افتراضي: تيم ليدر (supervisor) أو غير محدّد
        bgColor = "#000"; borderColor = "#a78bfa"; icon = "👁"; pulseClass = "pulse-visit";
      }
      const ic=L.divIcon({html:`<div class="${pulseClass}" style="width:24px;height:24px;border-radius:50%;background:${bgColor};border:3px solid ${borderColor};display:flex;align-items:center;justify-content:center;font-size:12px;color:${borderColor}">${icon}</div>`,className:"",iconSize:[24,24],iconAnchor:[12,12]});
      const key="visit_"+v.id;
      if(vMkrs.current[key]){ vMkrs.current[key].setLatLng([v.lat,v.lng]); vMkrs.current[key].setIcon(ic); }
      else{
        const m=L.marker([v.lat,v.lng],{icon:ic,zIndexOffset:600}).addTo(mapRef.current);
        m.on("click",()=>setInfo({type:"visit",data:v}));
        vMkrs.current[key]=m;
      }
    });
  },[ready,visitLocs]);

  // المقارنة — مواقع الأطباء مع تمييز بالأخضر/الأحمر حسب الزيارة
  useEffect(()=>{
    if(!ready||!mapRef.current||!compareLocs) return;
    const L=window.L;

    // ═══ احذف العلامات التي لم تعد ضمن الفلتر الحالي ═══
    const currentKeys = new Set(compareLocs.filter(c=>c.lat&&c.lng).map(c=>"cmp_"+c.id));
    Object.keys(cMkrs.current).forEach(key => {
      if (!currentKeys.has(key)) {
        try { mapRef.current.removeLayer(cMkrs.current[key]); } catch(e) {}
        delete cMkrs.current[key];
      }
    });

    // أضف/حدّث العلامات الموجودة
    compareLocs.forEach(c=>{
      if(!c.lat||!c.lng) return;
      const visited = !!c._visitedByLeader;
      const cls = visited ? "pulse-cmp-y" : "pulse-cmp-n";
      const bg  = visited ? "#22d3a0" : "#ef4444";
      const ic=L.divIcon({html:`<div class="${cls}" style="width:24px;height:24px;border-radius:50%;background:${bg};border:3px solid #fff;display:flex;align-items:center;justify-content:center;font-size:12px;color:#fff">${visited?"✓":"!"}</div>`,className:"",iconSize:[24,24],iconAnchor:[12,12]});
      const key="cmp_"+c.id;
      if(cMkrs.current[key]){ cMkrs.current[key].setLatLng([c.lat,c.lng]); cMkrs.current[key].setIcon(ic); }
      else{
        const m=L.marker([c.lat,c.lng],{icon:ic,zIndexOffset:700}).addTo(mapRef.current);
        m.on("click",()=>setInfo({type:"cmp",data:c}));
        cMkrs.current[key]=m;
      }
    });
  },[ready,compareLocs]);

  return (
    <div style={{position:"relative",height,borderRadius:16,overflow:"hidden",border:"1px solid #3d2f15"}}>
      <div ref={divRef} style={{width:"100%",height:"100%"}}/>
      {!ready&&<div style={{position:"absolute",inset:0,background:"#040d18",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:10}}>
        <div style={{width:28,height:28,borderRadius:"50%",border:"3px solid #22d3a0",borderTopColor:"transparent",animation:"spin 1s linear infinite"}}/>
        <div style={{color:"#334155",fontSize:12}}>جاري تحميل الخريطة...</div>
      </div>}
      <div style={{position:"absolute",bottom:10,right:10,background:"#0d0a00ee",border:"1px solid #3d2f15",borderRadius:10,padding:"8px 12px",zIndex:500}}>
        {[{c:"#22d3a0",l:"مندوب نشط"},{c:"#fbbf24",l:"مندوب بعيد"},{c:"#60a5fa",l:"موعد جديد"},{c:"#22d3a0",l:"مكتمل"}].map((x,i)=>(
          <div key={i} style={{display:"flex",alignItems:"center",gap:5,marginBottom:i<3?4:0}}>
            <div style={{width:7,height:7,borderRadius:"50%",background:x.c}}/><span style={{fontSize:9,color:"#94a3b8"}}>{x.l}</span>
          </div>
        ))}
      </div>
      {info&&(
        <div style={{position:"absolute",top:10,left:10,background:"#0d0a00ee",border:"1px solid #3d2f15",borderRadius:12,padding:"12px 14px",zIndex:500,minWidth:160,animation:"fadeUp .2s ease"}}>
          <button onClick={()=>setInfo(null)} style={{position:"absolute",top:6,left:6,background:"none",border:"none",color:"#334155",cursor:"pointer",fontSize:14}}>✕</button>
          {info.type==="rep"&&(()=>{
            const ls = info.data._liveStatus || "offline";
            const conf = ls==="active"?{c:"#22d3a0",t:"🟢 نشط الآن"}:ls==="idle"?{c:"#fbbf24",t:"🟡 خامل"}:{c:"#ef4444",t:"🔴 منقطع (GPS/إنترنت)"};
            return <div>
              <div style={{fontSize:12,fontWeight:800,color:"#f0f9ff",marginBottom:3}}>{info.data.name}</div>
              <div style={{fontSize:10,color:"#334155"}}>{info.data.area||""}</div>
              <div style={{fontSize:10,color:"#334155"}}>{info.data.phone||""}</div>
              <div style={{marginTop:6,display:"flex",alignItems:"center",gap:5}}>
                <div style={{width:7,height:7,borderRadius:"50%",background:conf.c}}/>
                <span style={{fontSize:10,color:conf.c,fontWeight:700}}>{conf.t}</span>
              </div>
              {info.data.accuracy && <div style={{fontSize:9,color:info.data.gpsQuality==="exact"?"#22d3a0":"#fbbf24",marginTop:2}}>📡 GPS: {info.data.accuracy}m</div>}
            </div>;
          })()}
          {info.type==="apt"&&<div><div style={{fontSize:12,fontWeight:800,color:"#f0f9ff",marginBottom:3}}>{info.data.drName}</div><div style={{fontSize:10,color:"#334155",marginBottom:4}}>{info.data.address}</div><SBadge s={info.data.status}/></div>}
          {info.type==="doc"&&<div><div style={{fontSize:12,fontWeight:800,color:"#ef4444",marginBottom:3}}>🦷 {info.data.drName||"عيادة"}</div><div style={{fontSize:10,color:"#94a3b8",marginBottom:2}}>📍 {info.data.govName||""}</div><div style={{fontSize:9,color:"#64748b",marginBottom:2}}>👤 {info.data.repName||"-"}</div><div style={{fontSize:9,color:"#64748b"}}>🗓 {info.data.lastVisitDate||"-"} {info.data.lastVisitTime||""}</div><div style={{fontSize:9,color:"#fbbf24",marginTop:4}}>زيارات: {info.data.visitsCount||1}</div></div>}
          {info.type==="cmp"&&<div style={{minWidth:200}}>
            <div style={{fontSize:12,fontWeight:800,color:info.data._visitedByLeader?"#22d3a0":"#ef4444",marginBottom:3}}>
              {info.data._visitedByLeader?"✓ مُزار من تيم ليدر":"! لم يُزَر بعد"}
            </div>
            <div style={{fontSize:11,fontWeight:700,color:"#f0f9ff"}}>{info.data.drName}</div>
            <div style={{fontSize:10,color:"#c9b37e"}}>{info.data.clinic||""}</div>
            <div style={{fontSize:9,color:"#f0c040",marginTop:2}}>📍 {info.data.govName||"-"}</div>
            <div style={{fontSize:9,color:"#7a6841",marginTop:2}}>👤 المندوب: {info.data.repName||"-"}</div>
            {info.data._visitedByLeader && <>
              <div style={{fontSize:9,color:"#a78bfa",marginTop:4}}>👁 التيم ليدر: {info.data._leaderName||"-"}</div>
              <div style={{fontSize:9,color:"#60a5fa"}}>🗓 تاريخ الزيارة: {info.data._visitDate||"-"}</div>
              {info.data._visitResult && <div style={{fontSize:9,color:info.data._visitResult==="زيارة إيجابية"?"#22d3a0":"#f87171",marginTop:2,fontWeight:700}}>{info.data._visitResult}</div>}
            </>}
          </div>}
          {info.type==="visit"&&(()=>{
            // ═══ تمييز عنوان البطاقة حسب دور المُزوار ═══
            const role = info.data.leaderRole;
            let titleColor, titleIcon, titleText;
            if (role === "marketing") {
              titleColor = "#fb7185"; titleIcon = "🌹"; titleText = "زيارة مندوب تسويق";
            } else if (role === "mushref") {
              titleColor = "#22d3a0"; titleIcon = "🛡"; titleText = "زيارة مشرف";
            } else {
              titleColor = "#a78bfa"; titleIcon = "👁"; titleText = "زيارة تيم ليدر";
            }
            return (
              <div style={{minWidth:200}}>
                <div style={{fontSize:12,fontWeight:800,color:titleColor,marginBottom:3}}>{titleIcon} {titleText}</div>
                <div style={{fontSize:11,fontWeight:700,color:"#f0f9ff"}}>{info.data.drName}</div>
                <div style={{fontSize:10,color:"#c9b37e"}}>{info.data.clinic}</div>
                {info.data._gov && <div style={{fontSize:9,color:"#f0c040",marginTop:2}}>📍 {info.data._gov}</div>}
                <div style={{fontSize:9,color:"#7a6841",marginTop:2}}>👤 {info.data.leaderName||"-"}</div>
                <div style={{fontSize:9,color:"#7a6841"}}>📌 {info.data.reason}{info.data.reason==="أخرى"&&info.data.reasonOther?": "+info.data.reasonOther:""}</div>
                {/* ═══ المبلغ المُستحصَل (لو الزيارة من نوع استحصال نقود) ═══ */}
                {info.data.collectedAmount > 0 && (
                  <div style={{fontSize:10,color:"#22d3a0",marginTop:4,fontWeight:800,padding:"3px 8px",background:"#0a2e1e",borderRadius:6,display:"inline-block"}}>
                    💰 {Number(info.data.collectedAmount).toLocaleString()} د.ع
                  </div>
                )}
                {info.data.date && <div style={{fontSize:9,color:"#60a5fa",marginTop:4}}>🗓 {info.data.date} {info.data.time||""}</div>}
                {info.data.result && <div style={{fontSize:9,color:info.data.result==="زيارة إيجابية"?"#22d3a0":"#f87171",marginTop:4,fontWeight:700}}>{info.data.result}</div>}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════
//  CHAT
// ═══════════════════════════════════════════════
function Chat({user,messages,onSend,color}) {
  const [txt,setTxt]=useState("");
  const btm=useRef(null);
  const RC={admin:"#22d3a0",sec:"#f472b6",rep:"#60a5fa",doctor:"#38bdf8"};
  useEffect(()=>btm.current?.scrollIntoView({behavior:"smooth"}),[messages]);
  const send=()=>{ if(!txt.trim()) return; onSend(txt.trim()); setTxt(""); };
  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%"}}>
      <div style={{flex:1,overflowY:"auto",padding:"12px 12px 0",display:"flex",flexDirection:"column",gap:8}}>
        {messages.map((m,i)=>{
          const mine=m.userId===user.id; const mc=RC[m.role]||"#94a3b8";
          return(
            <div key={i} style={{display:"flex",flexDirection:mine?"row-reverse":"row",gap:8,alignItems:"flex-end"}}>
              <div style={{width:26,height:26,borderRadius:"50%",background:`${mc}22`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:mc,flexShrink:0}}>{(m.userName||"?")[0]}</div>
              <div style={{maxWidth:"74%"}}>
                {!mine&&<div style={{fontSize:9,color:mc,marginBottom:2,paddingRight:8}}>{m.userName}</div>}
                <div style={{background:mine?`${color}22`:"#0a1829",border:`1px solid ${mine?color+"44":"#3d2f15"}`,borderRadius:mine?"14px 14px 4px 14px":"14px 14px 14px 4px",padding:"8px 12px"}}>
                  <div style={{fontSize:12,color:"#e2e8f0",lineHeight:1.5}}>{m.text}</div>
                </div>
                <div style={{fontSize:9,color:"#5a4518",marginTop:2,textAlign:mine?"left":"right",padding:"0 4px"}}>{m.time||tFmt(m.ts)}</div>
              </div>
            </div>
          );
        })}
        <div ref={btm}/>
      </div>
      <div style={{padding:12,borderTop:"1px solid #3d2f15",display:"flex",gap:8,background:"#0d0a00"}}>
        <input value={txt} onChange={e=>setTxt(e.target.value)} onKeyDown={e=>e.key==="Enter"&&send()} placeholder="اكتب رسالتك..."
          style={{flex:1,background:"#15100a",border:"1px solid #3d2f15",borderRadius:12,padding:"10px 14px",color:"#f0f9ff",fontSize:12,fontFamily:"Cairo,sans-serif",direction:"rtl"}} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} />
        <button onClick={send} disabled={!txt.trim()} style={{width:40,height:40,borderRadius:12,background:txt.trim()?`linear-gradient(135deg,${color},${color}99)`:"#3d2f15",border:"none",cursor:txt.trim()?"pointer":"default",fontSize:16,flexShrink:0}}>📤</button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════
//  NOTES
// ═══════════════════════════════════════════════
function Notes({apt,user,onAdd}) {
  const [txt,setTxt]=useState("");
  return (
    <div>
      <div style={{fontSize:11,fontWeight:700,color:"#94a3b8",marginBottom:8}}>📝 الملاحظات</div>
      {(apt.notes||[]).length===0&&<div style={{fontSize:10,color:"#334155",marginBottom:8}}>لا توجد ملاحظات</div>}
      {(apt.notes||[]).map((n,i)=>(
        <div key={i} style={{background:"#0d0a00",borderRadius:8,padding:"8px 10px",marginBottom:6}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
            <span style={{fontSize:10,fontWeight:700,color:"#60a5fa"}}>{n.user}</span>
            <span style={{fontSize:9,color:"#5a4518"}}>{n.time}</span>
          </div>
          <div style={{fontSize:11,color:"#cbd5e1"}}>{n.text}</div>
        </div>
      ))}
      <div style={{display:"flex",gap:8,marginTop:8}}>
        <input value={txt} onChange={e=>setTxt(e.target.value)} onKeyDown={e=>e.key==="Enter"&&txt&&(onAdd(apt.id,{user:user.name,text:txt,time:now()}),setTxt(""))} placeholder="أضف ملاحظة..."
          style={{flex:1,background:"#0d0a00",border:"1px solid #3d2f15",borderRadius:8,padding:"7px 10px",color:"#f0f9ff",fontSize:11,fontFamily:"Cairo,sans-serif",direction:"rtl"}} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} />
        <button onClick={()=>{if(txt){onAdd(apt.id,{user:user.name,text:txt,time:now()});setTxt("");}}} style={{padding:"7px 12px",background:"#3d2f15",border:"none",borderRadius:8,color:"#60a5fa",fontSize:10,cursor:"pointer",fontFamily:"Cairo,sans-serif",whiteSpace:"nowrap"}}>+</button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════
//  APT CARD
// ═══════════════════════════════════════════════

/* ════ REP PICKER — الكول سنتر تختار المندوب ════ */
// قائمة منسدلة قابلة للبحث للأطباء — تستخدم في فورم الكول سنتر
function DoctorPicker({doctors, selectedId, onSelect}) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const selected = doctors.find(d=>d.id===selectedId);

  const norm = s => (s||"").toString().trim().toLowerCase();
  const s = norm(search);
  const filtered = !s
    ? doctors
    : doctors.filter(d =>
        norm(d.name).includes(s) ||
        norm(d.clinic).includes(s) ||
        norm(d.phone).includes(s) ||
        norm(d.address).includes(s)
      );

  return (
    <div>
      <div style={{fontSize:10,color:"#334155",marginBottom:5}}>اختر طبيباً ({doctors.length})</div>
      {selected && !open && (
        <div onClick={()=>setOpen(true)}
          style={{background:"#0d0a00",border:"1.5px solid #f472b6",borderRadius:10,padding:"10px 12px",cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{fontSize:12,fontWeight:800,color:"#f0f9ff"}}>🦷 {selected.name}</div>
            <div style={{fontSize:10,color:"#c9b37e"}}>{selected.clinic}{selected.phone?` • ${selected.phone}`:""}</div>
            {selected.address && <div style={{fontSize:9,color:"#7a6841",marginTop:2}}>📍 {selected.address}</div>}
          </div>
          <button onClick={(e)=>{e.stopPropagation();onSelect(null);setSearch("");setOpen(true);}}
            style={{background:"#3d2f15",border:"none",borderRadius:6,padding:"4px 10px",color:"#94a3b8",fontSize:10,cursor:"pointer",fontFamily:"Cairo,sans-serif"}}>
            تغيير
          </button>
        </div>
      )}
      {(!selected || open) && (
        <>
          <input value={search} onChange={e=>setSearch(e.target.value)} autoFocus
            placeholder="🔍 ابحث بالاسم / العيادة / الهاتف..."
            style={{width:"100%",background:"#0d0a00",border:"1px solid #3d2f15",borderRadius:10,padding:"10px",color:"#f0f9ff",fontSize:12,fontFamily:"Cairo,sans-serif",direction:"rtl",boxSizing:"border-box"}} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} />
          <div style={{maxHeight:240,overflowY:"auto",marginTop:6,background:"#0a0600",border:"1px solid #3d2f15",borderRadius:10}}>
            {filtered.length===0 && (
              <div style={{padding:14,textAlign:"center",color:"#7a6841",fontSize:11}}>لا توجد نتائج</div>
            )}
            {filtered.map(d => (
              <div key={d.id} onClick={()=>{onSelect(d);setOpen(false);setSearch("");}}
                style={{padding:"10px 12px",borderBottom:"1px solid #15100a",cursor:"pointer",
                  background: selectedId===d.id?"#f472b622":"transparent"}}>
                <div style={{fontSize:11,fontWeight:800,color:"#f0f9ff"}}>🦷 {d.name}</div>
                <div style={{fontSize:9,color:"#c9b37e"}}>
                  {d.clinic||"عيادة"}{d.phone?` • 📞 ${d.phone}`:""}
                </div>
                {d.address && <div style={{fontSize:9,color:"#7a6841",marginTop:2}}>📍 {d.address}</div>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function RepPicker({apt, act, user, allApts}) {
  const [picking, setPicking] = useState(false);
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  // أنواع التوجيه:
  // - new: لم يُوجَّه بعد (status === "جديدة")
  // - re-pending: مُوجَّه لكن لم يقبل بعد (status === "محوّلة")
  // - re-accepted: قَبِل المندوب وبدأ — تحويل لمندوب آخر
  const isReassign = ["محوّلة","مقبولة","مقبولة قيد الانتظار","في الطريق","وصلت"].includes(apt.status);
  const isAfterAccept = ["مقبولة","مقبولة قيد الانتظار","في الطريق","وصلت"].includes(apt.status);

  // استخدم محافظة المستخدم (الكول سنتر) كمصدر موثوق
  const govCode = user?.govCode || apt?.govCode || "";

  // حساب حالة المندوب بناءً على مواعيده
  const getRepStatus = (repId) => {
    const myApts = (allApts||[]).filter(a => a.repId === repId);
    // 1. أولاً: هل لديه موعد نشط (قَبِلَه بالفعل)؟
    const active = myApts.find(a => ["مقبولة","في الطريق","وصلت","جاري التصوير"].includes(a.status));
    if(active){
      if(active.status==="جاري التصوير") return {txt:`يصوّر (${active.casesCount||1})`,icon:"📷",color:"#e879f9",bg:"#e879f922"};
      if(active.status==="في الطريق")   return {txt:"في الطريق",icon:"🚗",color:"#fbbf24",bg:"#fbbf2422"};
      if(active.status==="وصلت")         return {txt:"وصل للموقع",icon:"📍",color:"#38bdf8",bg:"#38bdf822"};
      if(active.status==="مقبولة")       return {txt:"قبل موعداً",icon:"⏳",color:"#a78bfa",bg:"#a78bfa22"};
    }
    // 2. ثانياً: هل لديه موعد محوّل لم يوافق/يرفض عليه بعد؟
    const pending = myApts.find(a => a.status === "محوّلة");
    if(pending) return {txt:"قيد الموافقة/الرفض",icon:"⌛",color:"#fb923c",bg:"#fb923c22"};
    // 3. شاغر
    return {txt:"شاغر",icon:"🟢",color:"#22d3a0",bg:"#22d3a022"};
  };

  const loadReps = async () => {
    setLoading(true); setPicking(true);
    try {
      if (!govCode) {
        alert("خطأ: كود المحافظة مفقود — سجّل خروج ثم دخول مجدداً");
        setLoading(false); return;
      }
      const found = [];
      const seen = {};

      // ═══ مناديب السكنر فقط — لا تسويق ولا تيم ليدر ولا مشرف ═══

      // 1. من users collection — مناديب السكنر المسجّلون والمفعّلون
      try {
        const usersSnap = await getDocs(collection(db, `${govCode}_users`));
        usersSnap.docs.forEach(d => {
          const u = d.data();
          const uid = u.id || d.id;
          if (u.role === "rep" && u.approved !== false && !seen[uid]) {
            seen[uid] = true;
            found.push({...u, id: uid, _docId: d.id});
          }
        });
      } catch(e) { console.warn("users load:",e); }

      // 2. من reps collection — مناديب السكنر الأصليون فقط (تخطّي marketing)
      try {
        const repsSnap = await getDocs(collection(db, `${govCode}_reps`));
        repsSnap.docs.forEach(d => {
          const r = d.data();
          const rid = r.id || d.id;
          // ═══ مهم: استثناء marketing من collection reps ═══
          if (r.role === "marketing") return;
          if (!seen[rid]) {
            seen[rid] = true;
            found.push({...r, id: rid, _docId: d.id});
          }
        });
      } catch(e) { console.warn("reps load:",e); }

      setReps(found);
    } catch(e) {
      alert("خطأ في تحميل المناديب: " + e.message);
    }
    setLoading(false);
  };

  const pick = async (r) => {
    // تأكيد إضافي إذا كانت الحالة بعد القبول
    if (isAfterAccept) {
      const oldRep = apt.repName || "المندوب الحالي";
      const ok = window.confirm(
        `⚠️ تأكيد تحويل الموعد:\n\n` +
        `• المندوب الحالي: ${oldRep}\n` +
        `• الحالة الحالية: ${apt.status}\n` +
        `• المندوب الجديد: ${r.name}\n\n` +
        `الموعد سيُسحب من ${oldRep} ويُعطى لـ ${r.name} مع إعادة الحالة إلى "مقبولة".\n\n` +
        `هل تريد المتابعة؟`
      );
      if (!ok) return;
    }
    try {
      await act.forward(apt.id, r.id, r.name);
      setPicking(false);
    } catch(e) {
      alert("خطأ في التوجيه: " + e.message);
    }
  };

  if (!picking) return (
    <div>
      {isAfterAccept ? (
        <div style={{fontSize:10,color:"#f87171",marginBottom:6,background:"#2e0a0a",border:"1px solid #f8717155",borderRadius:8,padding:"8px 10px"}}>
          🚨 <b>تنبيه:</b> الموعد <b>قَبِله المندوب</b> {apt.repName?<span>({apt.repName})</span>:""} والحالة الحالية: <b style={{color:"#fbbf24"}}>{apt.status}</b>
          <div style={{fontSize:9,color:"#fecaca",marginTop:4,fontWeight:600}}>
            تغيير المندوب سيُعيد الحالة إلى "مقبولة" للمندوب الجديد
          </div>
        </div>
      ) : isReassign && (
        <div style={{fontSize:10,color:"#fbbf24",marginBottom:6,background:"#2a1f00",border:"1px solid #fbbf2444",borderRadius:6,padding:"6px 10px"}}>
          ⚠️ مُوجَّه حالياً للمندوب: <b>{apt.repName||"-"}</b>
        </div>
      )}
      <Btn color={isAfterAccept?"#f87171":isReassign?"#fbbf24":"#f472b6"} onClick={loadReps} style={{width:"100%"}}>
        {isAfterAccept ? "⚠️ تحويل لمندوب آخر (طارئ)" : isReassign ? "🔄 تغيير المندوب" : "📢 توجيه لمندوب محدد"}
      </Btn>
    </div>
  );

  // فلترة المناديب حسب البحث + استبعاد المندوب الحالي عند إعادة التوجيه
  const filteredReps = reps.filter(r => {
    if (isReassign && r.id === apt.repId) return false; // أخفِ المندوب الحالي
    if (!search.trim()) return true;
    const s = search.trim().toLowerCase();
    return (r.name||"").toLowerCase().includes(s)
      || (r.phone||"").toLowerCase().includes(s)
      || (r.area||"").toLowerCase().includes(s);
  });

  return (
    <div style={{background:"#0d0a00",border:"1px solid #3d2f15",borderRadius:10,padding:12,marginTop:8}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <div style={{fontSize:12,fontWeight:800,color:isReassign?"#fbbf24":"#f472b6"}}>
          {isReassign ? "🔄 اختر مندوباً آخر:" : "اختر المندوب:"}
        </div>
        <button onClick={()=>{setPicking(false);setSearch("");}}
          style={{background:"#3d2f15",border:"none",borderRadius:6,padding:"3px 8px",
            color:"#94a3b8",fontSize:10,cursor:"pointer",fontFamily:"Cairo,sans-serif"}}>✕</button>
      </div>
      <input value={search} onChange={e=>setSearch(e.target.value)}
        placeholder="🔍 ابحث بالاسم / الهاتف / المنطقة..."
        style={{width:"100%",background:"#0a0600",border:"1px solid #3d2f15",borderRadius:8,
          padding:"8px 10px",color:"#f0f9ff",fontSize:11,fontFamily:"Cairo,sans-serif",
          direction:"rtl",boxSizing:"border-box",marginBottom:10}} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} />
      {loading && <div style={{textAlign:"center",padding:10,color:"#334155",fontSize:11}}>⏳ جاري التحميل...</div>}
      {!loading && reps.length === 0 && (
        <div style={{textAlign:"center",padding:14,color:"#ef4444",fontSize:11}}>
          لا يوجد مناديب مسجلون في هذه المحافظة
        </div>
      )}
      {!loading && reps.length>0 && filteredReps.length===0 && (
        <div style={{textAlign:"center",padding:14,color:"#7a6841",fontSize:11}}>
          لا توجد نتائج للبحث
        </div>
      )}
      {!loading && filteredReps.map(r => {
        const st = getRepStatus(r.id);
        return (
          <div key={r.id||r._docId} onClick={()=>pick(r)}
            style={{display:"flex",alignItems:"center",gap:10,padding:"10px",
              background:"#15100a",borderRadius:8,marginBottom:6,cursor:"pointer",
              border:`1px solid ${st.color}33`}}>
            <div style={{position:"relative",flexShrink:0}}>
              <div style={{width:36,height:36,borderRadius:10,background:"#60a5fa22",
                display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:15,color:"#60a5fa",fontWeight:800}}>
                {(r.name||"?")[0]}
              </div>
              {/* نقطة حالة نابضة */}
              <div style={{position:"absolute",bottom:-2,right:-2,width:12,height:12,
                borderRadius:"50%",background:st.color,border:"2px solid #15100a",
                animation:st.txt!=="شاغر"?"pulse 1.5s infinite":"none"}}/>
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:12,fontWeight:800,color:"#f0f9ff"}}>{r.fullName||r.name}</div>
              <div style={{fontSize:9,color:"#334155",marginBottom:3}}>{r.area||""} {r.phone?"• "+r.phone:""}</div>
              {/* مربع الحالة الصغير */}
              <div style={{display:"inline-flex",alignItems:"center",gap:4,
                background:st.bg,border:`1px solid ${st.color}44`,borderRadius:6,
                padding:"2px 8px"}}>
                <span style={{fontSize:10}}>{st.icon}</span>
                <span style={{fontSize:9,fontWeight:800,color:st.color}}>{st.txt}</span>
              </div>
            </div>
            <div style={{fontSize:13,color:"#22d3a0",fontWeight:700}}>←</div>
          </div>
        );
      })}
    </div>
  );
}

// مُعدّل وقت/تاريخ الموعد (للكول سنتر)
function DateTimeEditor({apt, onSave}) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(apt.date || "");
  const [time, setTime] = useState(apt.time || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!date || !time) { alert("الرجاء اختيار التاريخ والوقت"); return; }
    setBusy(true);
    try {
      await onSave(date, time);
      setOpen(false);
    } catch(e) { alert("خطأ: "+e.message); }
    setBusy(false);
  };

  if (!open) {
    return (
      <button onClick={()=>setOpen(true)}
        style={{background:"#0d2035",border:"1px solid #38bdf855",borderRadius:8,padding:"7px 12px",color:"#38bdf8",fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"Cairo,sans-serif",display:"flex",alignItems:"center",gap:6}}>
        🗓 تعديل التاريخ والوقت
      </button>
    );
  }

  return (
    <div style={{background:"#0d0a00",border:"1px solid #38bdf855",borderRadius:10,padding:10,display:"flex",flexDirection:"column",gap:8}}>
      <div style={{fontSize:11,fontWeight:800,color:"#38bdf8"}}>🗓 تعديل التاريخ والوقت</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
        <div>
          <div style={{fontSize:9,color:"#7a6841",marginBottom:3}}>التاريخ</div>
          <input type="date" value={date} onChange={e=>setDate(e.target.value)}
            style={{width:"100%",background:"#0a0600",border:"1px solid #3d2f15",borderRadius:8,padding:"6px 8px",color:"#f0f9ff",fontSize:11,fontFamily:"Cairo,sans-serif",boxSizing:"border-box"}}/>
        </div>
        <div>
          <div style={{fontSize:9,color:"#7a6841",marginBottom:3}}>الوقت</div>
          <input type="time" value={time} onChange={e=>setTime(e.target.value)}
            style={{width:"100%",background:"#0a0600",border:"1px solid #3d2f15",borderRadius:8,padding:"6px 8px",color:"#f0f9ff",fontSize:11,fontFamily:"Cairo,sans-serif",boxSizing:"border-box"}}/>
        </div>
      </div>
      <div style={{display:"flex",gap:6}}>
        <Btn small color="#22d3a0" onClick={save} disabled={busy} style={{flex:1}}>{busy?"...":"✓ حفظ"}</Btn>
        <Btn small color="#5a4518" onClick={()=>setOpen(false)} style={{flex:0.5}}>إلغاء</Btn>
      </div>
    </div>
  );
}

function AptCard({apt,user,role,act,expanded,onExpand,allApts}) {
  return (
    <Card style={{padding:14,border:`1px solid ${APTS[apt.status]?.c||"#3d2f15"}22`,cursor:"pointer"}} onClick={onExpand}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
        <div>
          <div style={{fontSize:12,fontWeight:800,color:"#f0f9ff"}}>{apt.drName}</div>
          <div style={{fontSize:10,color:"#334155"}}>{apt.clinic}</div>
          <div style={{fontSize:10,color:"#334155",marginTop:2}}>📍 {apt.address}</div>
        </div>
        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4}}><SBadge s={apt.status}/><div style={{fontSize:9,color:"#334155"}}>{apt.id}</div></div>
      </div>
      <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
        <span style={{fontSize:10,color:"#60a5fa"}}>🗓 {apt.date}</span>
        <span style={{fontSize:10,color:"#a78bfa"}}>🕐 {fmt12(apt.time)}</span>
        {apt.repName&&<span style={{fontSize:10,color:"#22d3a0"}}>👤 {apt.repName}</span>}
      </div>
      {apt.rejectReason && (
        <div style={{marginTop:8,background:"#2e0a0a",border:"1px solid #f8717144",borderRadius:8,padding:"8px 10px"}}>
          <div style={{fontSize:9,color:"#f87171",fontWeight:700,marginBottom:2}}>
            ↩ رُفض من {apt.rejectedByName||"المندوب"} — {apt.status==="جديدة"?"يحتاج إعادة توجيه":"محفوظ كسجل"}
          </div>
          <div style={{fontSize:11,color:"#fecaca"}}>{apt.rejectReason}</div>
        </div>
      )}
      {apt.rejectionHistory && apt.rejectionHistory.length>0 && (
        <div style={{marginTop:6,background:"#15100a",border:"1px solid #3d2f15",borderRadius:8,padding:"6px 10px"}}>
          <div style={{fontSize:8,color:"#7a6841",fontWeight:700,marginBottom:2}}>📜 سجل رفضات سابقة ({apt.rejectionHistory.length})</div>
          {apt.rejectionHistory.map((r,i)=>(
            <div key={i} style={{fontSize:9,color:"#94a3b8",marginTop:2}}>
              • {r.byName||"مندوب"}: {r.reason||"بدون سبب"}
            </div>
          ))}
        </div>
      )}
      {apt.transferHistory && apt.transferHistory.length>0 && (
        <div style={{marginTop:6,background:"#1a1500",border:"1px solid #fbbf2444",borderRadius:8,padding:"6px 10px"}}>
          <div style={{fontSize:8,color:"#fbbf24",fontWeight:700,marginBottom:2}}>🔄 سجل تحويلات سابقة ({apt.transferHistory.length})</div>
          {apt.transferHistory.map((t,i)=>(
            <div key={i} style={{fontSize:9,color:"#c9b37e",marginTop:2}}>
              • من <b>{t.fromRepName||"-"}</b> إلى <b>{t.toRepName||"-"}</b> {t.atStatus?<span style={{color:"#7a6841"}}>(عند: {t.atStatus})</span>:""}
            </div>
          ))}
        </div>
      )}
      {expanded&&(
        <div onClick={e=>e.stopPropagation()} style={{marginTop:12,borderTop:"1px solid #3d2f15",paddingTop:12,animation:"fadeUp .2s ease",display:"flex",flexDirection:"column",gap:10}}>
          {role==="sec"&&["جديدة","محوّلة","مقبولة","مقبولة قيد الانتظار","في الطريق","وصلت"].includes(apt.status)&&<RepPicker apt={apt} act={act} user={user} allApts={allApts||[]}/>}
          {/* تعديل وقت/تاريخ الموعد — للكول سنتر، حتى بعد القبول */}
          {role==="sec"&&!["مكتملة","مرفوضة","جاري التصوير"].includes(apt.status)&&(
            <DateTimeEditor apt={apt} onSave={(d,t)=>act.updateAptDateTime(apt.id, d, t)}/>
          )}
          {role==="sec"&&apt.status==="جديدة"&&(
            <button onClick={()=>{ if(window.confirm(`حذف الطلب "${apt.drName}" نهائياً؟ (لم يتم توجيهه بعد)`)) act.secDeleteApt(apt); }}
              style={{background:"#2e0a0a",border:"1px solid #f8717155",borderRadius:8,padding:"7px 12px",color:"#f87171",fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"Cairo,sans-serif",marginTop:4}}>
              🗑 حذف الطلب
            </button>
          )}
          {role==="rep"&&apt.status==="محوّلة"&&(
            <div style={{display:"flex",gap:8}}><Btn color="#22d3a0" onClick={()=>act.accept(apt.id,user)} style={{flex:1}}>✓ قبول</Btn><Btn color="#f87171" onClick={()=>{ const r=prompt("يرجى كتابة سبب الرفض:"); if(r&&r.trim()) act.reject(apt.id, r.trim(), user); }} style={{flex:1}}>✕ رفض</Btn></div>
          )}
          {role==="rep"&&apt.repId===user.id&&(
            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
              {apt.status==="مقبولة"&&<Btn small color="#fbbf24" onClick={()=>act.upStatus(apt.id,"في الطريق")}>🚗 في الطريق</Btn>}
              {apt.status==="في الطريق"&&<Btn small color="#38bdf8" onClick={()=>act.upStatus(apt.id,"وصلت")}>📍 وصلت</Btn>}
              {apt.status==="وصلت"&&<Btn small color="#e879f9" onClick={()=>{ const n=prompt("كم عدد الحالات التي ستصورها لهذا الطبيب؟","1"); if(n&&!isNaN(n)&&Number(n)>0){ act.startShoot(apt.id,Number(n)); } }}>📷 بدء التصوير</Btn>}
              {apt.status==="جاري التصوير"&&<Btn small color="#22d3a0" onClick={()=>act.complete(apt.id,user)}>✅ انتهى التصوير</Btn>}
            </div>
          )}
          <div style={{display:"flex",alignItems:"center",gap:3,overflowX:"auto",paddingBottom:2}}>
            {["محوّلة","مقبولة","في الطريق","وصلت","جاري التصوير","مكتملة"].map((s,i)=>{
              const cur=APTS[apt.status]?.step||0;const done=APTS[s]?.step<cur;const act2=APTS[s]?.step===cur;
              return(<React.Fragment key={i}>
                {i>0&&<div style={{flex:1,height:2,background:done?"#22d3a033":"#0f1829",minWidth:8,borderRadius:1}}/>}
                <div style={{width:8,height:8,borderRadius:"50%",background:done||act2?APTS[s]?.c||"#22d3a0":"#0f1829",flexShrink:0,boxShadow:act2?`0 0 6px ${APTS[s]?.c}`:"none"}}/>
              </React.Fragment>);
            })}
          </div>
          <Notes apt={apt} user={user} onAdd={act.addNote}/>
          {role==="admin"&&(
            <button onClick={()=>{ if(window.confirm(`حذف الموعد ${apt.id} نهائياً؟`)) act.adminDeleteApt(apt); }}
              style={{background:"#2e0a0a",border:"1px solid #f8717155",borderRadius:8,padding:"8px 12px",color:"#f87171",fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"Cairo,sans-serif",marginTop:4}}>
              🗑 حذف هذا الموعد {apt._gov?`(${apt._gov})`:""}
            </button>
          )}
        </div>
      )}
    </Card>
  );
}

// ═══════════════════════════════════════════════
//  REPORTS
// ═══════════════════════════════════════════════
function ReportsPage({apts,reps,color}) {
  const [tab,setTab]=useState("daily");
  const TS={background:"#15100a",border:"1px solid #3d2f15",borderRadius:8,fontFamily:"Cairo,sans-serif",fontSize:10,color:"#e2e8f0",padding:"6px 10px"};
  const daily=[{d:"أحد",م:4,مكتمل:3},{d:"اثن",م:6,مكتمل:5},{d:"ثلا",م:3,مكتمل:3},{d:"أرب",م:8,مكتمل:6},{d:"خمي",م:5,مكتمل:4},{d:"جمع",م:2,مكتمل:2},{d:"اليوم",م:apts.length,مكتمل:apts.filter(a=>a.status==="مكتملة").length}];
  const rpf=reps.map(r=>({name:r.name.split(" ")[1]||r.name,م:apts.filter(a=>a.repId===r.id).length,مكتمل:apts.filter(a=>a.repId===r.id&&a.status==="مكتملة").length}));
  const sc=Object.keys(APTS).map(s=>({name:s,v:apts.filter(a=>a.status===s).length,c:APTS[s]?.c})).filter(x=>x.v>0);
  const exportReport = () => {
    if (tab==="daily") {
      exportToPdf({title:"التقرير اليومي — المواعيد الأسبوعية", subtitle:`الإجمالي: ${apts.length} • المكتملة: ${apts.filter(a=>a.status==="مكتملة").length}`,
        columns:["اليوم","المواعيد","المكتملة"], rows: daily.map(d=>[d.d, d.م, d.مكتمل])});
    } else if (tab==="reps") {
      exportToPdf({title:"تقرير أداء المناديب",
        columns:["المندوب","المواعيد الكلية","المكتملة","نسبة الإنجاز"],
        rows: rpf.map((r,i)=>{const all=reps[i]?apts.filter(a=>a.repId===reps[i].id).length:r.م; const done=r.مكتمل; return [r.name, all, done, all?Math.round(done/all*100)+"%":"0%"];})});
    } else {
      exportToPdf({title:"تقرير الحالات", subtitle:`المجموع: ${apts.length}`,
        columns:["الحالة","العدد","النسبة"],
        rows: sc.map(s=>[s.name, s.v, apts.length?Math.round(s.v/apts.length*100)+"%":"0%"])});
    }
  };
  return (
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10}}>
        <div style={{fontSize:14,fontWeight:800,color:"#f0f9ff"}}>📊 التقارير</div>
        <PdfBtn onClick={exportReport}/>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
        {[{l:"إجمالي",v:apts.length,c:"#60a5fa",icon:"📅"},{l:"مكتملة",v:apts.filter(a=>a.status==="مكتملة").length,c:"#22d3a0",icon:"✅"},{l:"معلّقة",v:apts.filter(a=>["جديدة","محوّلة"].includes(a.status)).length,c:"#f472b6",icon:"⏳"},{l:"نشطون",v:reps.filter(r=>r.status==="active").length,c:"#fbbf24",icon:"👥"}].map((s,i)=>(
          <Card key={i} style={{padding:12,border:`1px solid ${s.c}22`}}><div style={{display:"flex",justifyContent:"space-between"}}><div><div style={{fontSize:10,color:"#d4af37",fontWeight:800,marginBottom:4}}>{s.l}</div><div style={{fontSize:20,fontWeight:900,color:s.c}}>{s.v}</div></div><span style={{fontSize:20}}>{s.icon}</span></div></Card>
        ))}
      </div>
      <div style={{display:"flex",gap:6}}>
        {[["daily","يومي"],["reps","المناديب"],["status","الحالات"]].map(([id,l])=>(
          <button key={id} onClick={()=>setTab(id)} style={{padding:"6px 14px",borderRadius:20,border:"1px solid",borderColor:tab===id?color:"#3d2f15",background:tab===id?`${color}18`:"transparent",color:tab===id?color:"#334155",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"Cairo,sans-serif"}}>{l}</button>
        ))}
      </div>
      {tab==="daily"&&<Card style={{padding:14}}><div style={{fontSize:12,fontWeight:800,color:"#f0f9ff",marginBottom:12}}>المواعيد الأسبوعية</div><ResponsiveContainer width="100%" height={150}><BarChart data={daily} margin={{top:4,right:4,bottom:0,left:0}}><XAxis dataKey="d" tick={{fontSize:9,fill:"#334155",fontFamily:"Cairo,sans-serif"}} axisLine={false} tickLine={false}/><YAxis tick={{fontSize:9,fill:"#334155"}} axisLine={false} tickLine={false}/><Tooltip contentStyle={TS}/><Bar dataKey="م" fill={`${color}66`} radius={[3,3,0,0]}/><Bar dataKey="مكتمل" fill={color} radius={[3,3,0,0]}/></BarChart></ResponsiveContainer></Card>}
      {tab==="reps"&&<Card style={{padding:14}}><div style={{fontSize:12,fontWeight:800,color:"#f0f9ff",marginBottom:12}}>أداء المناديب</div><ResponsiveContainer width="100%" height={140}><BarChart data={rpf} margin={{top:4,right:4,bottom:0,left:0}}><XAxis dataKey="name" tick={{fontSize:9,fill:"#94a3b8",fontFamily:"Cairo,sans-serif"}} axisLine={false} tickLine={false}/><YAxis tick={{fontSize:9,fill:"#334155"}} axisLine={false} tickLine={false}/><Tooltip contentStyle={TS}/><Bar dataKey="م" fill="#60a5fa66" radius={[3,3,0,0]}/><Bar dataKey="مكتمل" fill="#22d3a0" radius={[3,3,0,0]}/></BarChart></ResponsiveContainer></Card>}
      {tab==="status"&&<Card style={{padding:14}}><div style={{fontSize:12,fontWeight:800,color:"#f0f9ff",marginBottom:12}}>توزيع الحالات</div>{sc.map((s,i)=><div key={i} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}><div style={{width:8,height:8,borderRadius:"50%",background:s.c}}/><div style={{width:72,fontSize:9,color:"#64748b",textAlign:"right"}}>{s.name}</div><div style={{flex:1,height:9,background:"#0a1829",borderRadius:5,overflow:"hidden"}}><div style={{height:"100%",width:`${(s.v/apts.length)*100}%`,background:s.c,borderRadius:5}}/></div><div style={{fontSize:11,fontWeight:800,color:s.c,width:16,textAlign:"left"}}>{s.v}</div></div>)}</Card>}
    </div>
  );
}

// ═══════════════════════════════════════════════
//  LOGIN
// ═══════════════════════════════════════════════
function Login({onLogin}) {
  const [step, setStep]   = useState(1);
  const [govInput, setGovInput] = useState("");
  const [govCode, setGovCode]   = useState("");
  const [govName, setGovName]   = useState("");
  const [uname, setUname] = useState("");
  const [pass,  setPass]  = useState("");
  const [err,   setErr]   = useState("");
  const [busy,  setBusy]  = useState(false);

  /* ─── تسجيل حساب جديد ─── */
  const [regStep, setRegStep]   = useState(0); // 0=مخفي 1=اختيار دور 2=نموذج
  const [regRole, setRegRole]   = useState("");
  const [regName, setRegName]   = useState("");        // اسم المستخدم — للدخول (إنجليزي)
  const [regNick, setRegNick]   = useState("");        // اللقب — للعرض داخل التطبيق (عربي)
  const [regPhone,setRegPhone]  = useState("");
  const [regPass, setRegPass]   = useState("");
  const [regPass2,setRegPass2]  = useState("");
  const [regArea, setRegArea]   = useState("");
  const [regClinic,setRegClinic]= useState("");
  const [regCity, setRegCity]   = useState("");
  const [regErr,  setRegErr]    = useState("");
  const [regBusy, setRegBusy]   = useState(false);
  const [regDone, setRegDone]   = useState(false);

  /* ─── خطوة 1: التحقق من كود المحافظة ─── */
  function checkGov() {
    var g = govInput.trim().toUpperCase();
    var n = GOV_CODES[g];
    if (!n) { setErr("كود المحافظة غير صحيح"); return; }
    setGovCode(g);
    setGovName(n);
    setErr("");
    setStep(2);
  }

  /* ─── خطوة 2: تسجيل الدخول مع تحقّق توسعي ─── */
  async function doLogin() {
    if (!uname.trim() || !pass.trim()) { setErr("أدخل اسم المستخدم وكلمة المرور"); return; }
    setBusy(true); setErr("");
    try {
      // ═══ فحص Firebase أولاً ═══
      if (DB_READY && govCode) {
        let snap;
        try {
          snap = await getDocs(collection(db, govCode + "_users"));
        } catch(netErr) {
          setErr("⚠️ فشل الاتصال بالإنترنت — تأكد من اتصالك ثم أعِد المحاولة");
          setBusy(false); return;
        }

        // ابحث عن كل المستخدمين المطابقين (حتى لو كلمة المرور خاطئة)
        let foundUser = null;
        let userMatchedButPasswordWrong = null;
        const userMatches = []; // كل المستخدمين الذين تطابقوا مع الإدخال (للتشخيص)

        snap.docs.forEach(function(d) {
          const u = { ...d.data(), docId: d.id };
          if (loginMatchUser(u, uname)) {
            userMatches.push(u);
            if (loginMatchPassword(u, pass)) {
              foundUser = u;
            } else {
              userMatchedButPasswordWrong = u;
            }
          }
        });

        // ═══ نجاح الدخول ═══
        if (foundUser) {
          // الأطباء يحتاجون موافقة المدير
          if (foundUser.role === "doctor" && !foundUser.approved) {
            setErr("حسابك بانتظار موافقة المدير"); setBusy(false); return;
          }
          // طبّع الـ id لو ناقص (للحسابات القديمة جداً)
          const cleanUser = {
            ...foundUser,
            id:       foundUser.id || foundUser.docId,
            name:     foundUser.name || foundUser.docId,
            govCode:  govCode,
            govName:  govName,
          };
          onLogin(cleanUser);
          setBusy(false); return;
        }

        // ═══ المستخدم موجود لكن كلمة المرور خاطئة ═══
        if (userMatchedButPasswordWrong) {
          setErr(`❌ كلمة المرور غير صحيحة\n(تم العثور على الحساب باسم: ${userMatchedButPasswordWrong.fullName||userMatchedButPasswordWrong.name})`);
          setBusy(false); return;
        }
      }

      // ═══ فحص المستخدمين الافتراضيين (USERS) ═══
      const keys = Object.keys(USERS);
      for (let i = 0; i < keys.length; i++) {
        const u = USERS[keys[i]];
        if (loginMatchUser({...u, name: u.name, id: u.code}, uname) && loginMatchPassword(u, pass)) {
          onLogin(Object.assign({}, u, {govCode: govCode, govName: govName}));
          setBusy(false); return;
        }
      }

      // ═══ لم يُعثر على المستخدم بأي طريقة ═══
      setErr("❌ لم نجد هذا المستخدم في محافظة \"" + govName + "\"\nتأكّد من:\n• كتابة الاسم أو الهاتف بشكل صحيح\n• اختيار كود المحافظة الصحيح");
    } catch(e) {
      setErr("⚠️ خطأ: " + (e?.message || "تعذّر تسجيل الدخول"));
      console.error("Login error:", e);
    }
    setBusy(false);
  }

  /* ─── تسجيل حساب جديد: اسم مستخدم (إنجليزي) + لقب (عربي للعرض) + كلمة مرور ─── */
  async function doRegister() {
    if (!regRole)        { setRegErr("اختر نوع الحساب"); return; }
    if (!regName.trim()) { setRegErr("أدخل اسم المستخدم"); return; }
    if (!regNick.trim()) { setRegErr("أدخل اللقب بالعربي"); return; }
    if (!regPass)        { setRegErr("أدخل كلمة المرور"); return; }
    if (!govCode)        { setRegErr("خطأ في كود المحافظة"); return; }

    // ═══ تنظيف اسم المستخدم: lowercase + إنجليزي/أرقام/_ فقط ═══
    const rawName = regName.trim().toLowerCase();
    const cleanUsername = rawName
      .replace(/\s+/g, "_")              // مسافة → _
      .replace(/[^a-z0-9_]/g, "");       // احذف أي حرف غير إنجليزي صغير/رقم/_

    if (cleanUsername.length < 3) {
      setRegErr("اسم المستخدم قصير جداً — يجب ٣ أحرف على الأقل\n(مثال: ahmed أو ali_q)");
      return;
    }
    if (cleanUsername !== rawName) {
      setRegErr(`الاسم يحوي أحرفاً غير مسموح بها — استخدم بدلاً منه:\n${cleanUsername}`);
      return;
    }

    // ═══ تحقّق اللقب — عربي حصراً (مع المسافات) ═══
    const cleanNick = regNick.trim();
    if (cleanNick.length < 2) {
      setRegErr("اللقب قصير جداً — يجب حرفين على الأقل");
      return;
    }
    // ٠٦٠٠–٠٦FF = نطاق Unicode للأحرف العربية الأساسية + التشكيل
    if (!/^[\u0600-\u06FF\s]+$/.test(cleanNick)) {
      setRegErr("اللقب يجب أن يكون بالعربي حصراً (بدون أرقام أو أحرف لاتينية)");
      return;
    }

    if (regPass.length < 4) {
      setRegErr("كلمة المرور قصيرة جداً — يجب ٤ أحرف على الأقل");
      return;
    }

    setRegBusy(true); setRegErr("");
    try {
      const colName = govCode + "_users";
      const existing = await getDocs(collection(db, colName));

      // ═══ فحص التكرار: نفس اسم المستخدم في نفس المحافظة ممنوع ═══
      let dup = false;
      existing.docs.forEach(function(d) {
        const data = d.data();
        const eId = (data.id || d.id || "").toLowerCase();
        const eName = (data.name || "").toLowerCase();
        if (eId === cleanUsername || eName === cleanUsername) dup = true;
      });
      if (dup) {
        setRegErr(`اسم المستخدم "${cleanUsername}" محجوز — اختر اسماً آخر أو سجّل الدخول`);
        setRegBusy(false); return;
      }

      const newUser = {
        id:       cleanUsername,        // = docId — للدخول
        name:     cleanUsername,        // اسم تسجيل الدخول
        fullName: cleanNick,            // اللقب العربي — يُعرَض داخل التطبيق
        nickname: cleanNick,            // مرادف صريح
        password: regPass,
        role:     regRole,
        govCode:  govCode,
        govName:  govName,
        status:   "active",
        approved: false,                // ⚠️ كل الحسابات الجديدة تحتاج موافقة المدير
        createdAt: serverTimestamp(),
        registeredVia: "app",
      };

      await setDoc(doc(db, colName, cleanUsername), newUser);

      // ═══ إشعار كل المدراء بطلب موافقة جديد ═══
      try {
        const roleLbl = regRole==="rep"?"مندوب سكنر":regRole==="marketing"?"مندوب تسويق":
                        regRole==="supervisor"?"تيم ليدر":regRole==="mushref"?"مشرف":
                        regRole==="sec"?"كول سنتر":regRole==="doctor"?"طبيب":"عضو";
        const govLbl = GOV_CODES[govCode] || govCode;
        const msg = `📝 طلب حساب جديد من ${roleLbl}: ${cleanNick} (${cleanUsername}) — يحتاج موافقتك`;

        // إشعار في Firestore لكل من في المحافظة عنده دور admin
        const allUsers = existing.docs.map(d => ({...d.data(), docId: d.id}));
        const adminIds = allUsers.filter(u => u.role === "admin").map(u => u.id || u.docId);
        adminIds.push("admin", "admin1"); // الافتراضي
        for (const aid of [...new Set(adminIds)]) {
          await addDoc(collection(db, `${govCode}_notifs`), {
            to: aid, icon: "📝",
            text: `[${govLbl}] ${msg}`,
            time: Date.now()+"", ts: serverTimestamp(), read: false,
          });
        }
        // إشعار للمدير العام أيضاً
        await addDoc(collection(db, `ADMIN-00_notifs`), {
          to: "admin", icon: "📝",
          text: `[${govLbl}] ${msg}`,
          time: Date.now()+"", ts: serverTimestamp(), read: false,
        });
      } catch(notifErr) {
        console.warn("Failed to notify admins:", notifErr);
      }

      setRegDone(true);
    } catch(e) {
      setRegErr("خطأ: " + (e?.message || "تعذّر التسجيل"));
      console.error("Register error:", e);
    }
    setRegBusy(false);
  }

  function resetReg() {
    setRegStep(0); setRegRole(""); setRegName(""); setRegNick(""); setRegPhone("");
    setRegPass(""); setRegPass2(""); setRegArea(""); setRegClinic("");
    setRegCity(""); setRegErr(""); setRegDone(false); setRegBusy(false);
  }

  var INP = {width:"100%",
    background:"linear-gradient(180deg, rgba(15,11,4,.9), rgba(10,6,0,.9))",
    border:"1px solid rgba(212,175,55,.22)",borderRadius:12,
    padding:"12px 16px",color:"#f8ecc8",fontSize:14,fontWeight:600,
    fontFamily:"Cairo,sans-serif",direction:"rtl",boxSizing:"border-box",
    transition:"all .25s ease"};

  return (
    <div style={{minHeight:"100vh",
      background:`
        radial-gradient(ellipse 1000px 700px at 50% -10%, rgba(212,175,55,.10), transparent 60%),
        radial-gradient(ellipse 700px 500px at 0% 110%, rgba(139,92,246,.06), transparent 55%),
        radial-gradient(ellipse 700px 500px at 100% 110%, rgba(16,185,129,.05), transparent 55%),
        #07070a
      `,
      display:"flex",flexDirection:"column",
      alignItems:"center",justifyContent:"center",padding:20,overflowY:"auto",
      position:"relative",overflow:"hidden"
    }}>
      <style>{G}</style>

      {/* تأثير حبيبات ذهبية في الخلفية */}
      <div style={{position:"absolute",inset:0,pointerEvents:"none",opacity:.4,
        background:`radial-gradient(circle at 20% 30%, #d4af3722 1px, transparent 2px),
                    radial-gradient(circle at 70% 60%, #f0c04022 1px, transparent 2px),
                    radial-gradient(circle at 40% 80%, #d4af3722 1px, transparent 2px)`,
        backgroundSize:"60px 60px, 80px 80px, 70px 70px"
      }}/>

      {/* Logo */}
      <div style={{textAlign:"center",marginBottom:30,position:"relative",zIndex:1,animation:"fadeInUp .6s ease"}}>
        <div style={{position:"relative",display:"inline-block",animation:"floatY 3.5s ease-in-out infinite"}}>
          <img src="/logo192.png" alt="بريتاو"
            style={{width:140,height:140,objectFit:"contain",borderRadius:24,
            margin:"0 auto 14px",display:"block",
            boxShadow:"0 0 50px rgba(212,175,55,.45), 0 0 100px rgba(212,175,55,.2), 0 10px 40px rgba(0,0,0,.5)"}}/>
        </div>
        <div style={{fontSize:30,fontWeight:900,letterSpacing:1.5,
          background:"linear-gradient(135deg, #f8ecc8, #f0c040 30%, #d4af37 60%, #b8860b)",
          WebkitBackgroundClip:"text",backgroundClip:"text",color:"transparent",
          textShadow:"0 2px 20px rgba(212,175,55,.30)"
        }}>مختبر بريتاو</div>
        <div style={{fontSize:13,marginTop:8,letterSpacing:2,fontWeight:600,
          fontStyle:"italic",
          background:"linear-gradient(135deg, #f8ecc8, #d4af37)",
          WebkitBackgroundClip:"text",backgroundClip:"text",color:"transparent"
        }}>Prettau Operating System</div>
        <div style={{marginTop:14,fontSize:11,color:"#0a0600",fontWeight:900,
          background:"linear-gradient(135deg, #f8ecc8 0%, #f0c040 25%, #d4af37 50%, #b8860b 75%, #f0c040 100%)",
          backgroundSize:"200% 100%",
          padding:"6px 18px",borderRadius:24,display:"inline-flex",alignItems:"center",gap:8,
          boxShadow:"0 4px 20px rgba(212,175,55,.5), inset 0 1px 0 rgba(255,255,255,.3)",
          letterSpacing:1.2,
          animation:"prettauShine 3s ease-in-out infinite, floatY 3s ease-in-out infinite"
        }}>
          <span style={{fontSize:12,animation:"prettauStar 2s ease-in-out infinite"}}>✨</span>
          <span>By Prettau Group</span>
          <span style={{fontSize:12,animation:"prettauStar 2s ease-in-out infinite",animationDelay:"1s"}}>✨</span>
        </div>
        <style>{`
          @keyframes prettauShine {
            0%, 100% { background-position: 0% 50%; }
            50%      { background-position: 100% 50%; }
          }
          @keyframes prettauStar {
            0%, 100% { transform: scale(1) rotate(0deg); opacity: 1; }
            50%      { transform: scale(1.3) rotate(180deg); opacity: .7; }
          }
        `}</style>
      </div>

      {/* ═══ خطوة 1: كود المحافظة ═══ */}
      {step === 1 && (
        <div style={{width:"100%",maxWidth:340}}>
          <Card style={{padding:22}}>
            <div style={{fontSize:14,fontWeight:900,color:"#f0f9ff",marginBottom:4}}>🔐 كود المحافظة</div>
            <div style={{fontSize:10,color:"#334155",marginBottom:16}}>
              أدخل كود محافظتك للوصول لبياناتها
            </div>
            <input
              value={govInput}
              onChange={function(e){ setGovInput(e.target.value.toUpperCase()); setErr(""); }}
              onKeyDown={function(e){ if(e.key==="Enter") checkGov(); }}
              placeholder="أدخل الكود"
              style={Object.assign({},INP,{fontSize:18,fontWeight:900,textAlign:"center",
                letterSpacing:3,direction:"ltr",
                borderColor:err?"#ef4444":"#3d2f15",color:"#22d3a0",marginBottom:6})}
             autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} />
            {err && <div style={{fontSize:11,color:"#ef4444",textAlign:"center",marginBottom:8,whiteSpace:"pre-line",lineHeight:1.5}}>{err}</div>}
            <Btn onClick={checkGov} disabled={!govInput} color="#22d3a0"
              style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginTop:8}}>
              التالي ←
            </Btn>
            <div style={{marginTop:14,textAlign:"center",fontSize:11,color:"#334155"}}>
              عضو جديد؟{" "}
              <button onClick={function(){
                var g = govInput.trim().toUpperCase();
                var n = GOV_CODES[g];
                if(!n){ setErr("أدخل كود المحافظة الصحيح أولاً"); return; }
                setGovCode(g); setGovName(n); setErr(""); setRegStep(1); setStep(3);
              }} style={{background:"none",border:"none",color:"#22d3a0",cursor:"pointer",
                fontFamily:"Cairo,sans-serif",fontWeight:800,fontSize:11}}>
                سجّل حساباً جديداً
              </button>
            </div>
          </Card>
        </div>
      )}

      {/* ═══ خطوة 2: تسجيل الدخول ═══ */}
      {step === 2 && (
        <div style={{width:"100%",maxWidth:340}}>
          <div style={{background:"#22d3a022",border:"1px solid #22d3a055",borderRadius:12,
            padding:"10px 16px",marginBottom:14,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{fontSize:12,fontWeight:800,color:"#22d3a0"}}>📍 {govName}</div>
            <button onClick={function(){ setStep(1); setUname(""); setPass(""); setErr(""); }}
              style={{background:"#3d2f15",border:"none",borderRadius:8,padding:"4px 10px",
                color:"#94a3b8",fontSize:10,cursor:"pointer",fontFamily:"Cairo,sans-serif"}}>
              تغيير
            </button>
          </div>
          <Card style={{padding:22}}>
            <div style={{fontSize:14,fontWeight:900,color:"#f0f9ff",marginBottom:16}}>تسجيل الدخول</div>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div>
                <div style={{fontSize:10,fontWeight:700,color:"#94a3b8",marginBottom:5}}>اسم المستخدم أو رقم الهاتف</div>
                <input value={uname}
                  onChange={function(e){ setUname(e.target.value); setErr(""); }}
                  onKeyDown={function(e){ if(e.key==="Enter") doLogin(); }}
                  placeholder="الاسم أو رقم الهاتف"
                  style={Object.assign({},INP,{borderColor:err?"#ef4444":"#3d2f15"})} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} />
              </div>
              <div>
                <div style={{fontSize:10,fontWeight:700,color:"#94a3b8",marginBottom:5}}>كلمة المرور</div>
                <input type="password" value={pass}
                  onChange={function(e){ setPass(e.target.value); setErr(""); }}
                  onKeyDown={function(e){ if(e.key==="Enter") doLogin(); }}
                  placeholder="••••••••"
                  style={Object.assign({},INP,{direction:"ltr",borderColor:err?"#ef4444":"#3d2f15"})}/>
              </div>
              {err && <div style={{fontSize:12,fontWeight:700,color:"#ef4444",textAlign:"center",
                background:"#ef444411",borderRadius:8,padding:"10px 12px",whiteSpace:"pre-line",lineHeight:1.6}}>{err}</div>}
              <Btn onClick={doLogin} disabled={!uname||!pass||busy} color="#22d3a0"
                style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"center",
                  gap:8,padding:"12px",marginTop:4}}>
                {busy ? <span style={{animation:"spin 1s linear infinite",display:"inline-block"}}>⟳</span> : "دخول ←"}
              </Btn>
              <div style={{textAlign:"center",fontSize:11,color:"#334155"}}>
                عضو جديد؟{" "}
                <button onClick={function(){ resetReg(); setRegStep(1); setStep(3); }}
                  style={{background:"none",border:"none",color:"#22d3a0",cursor:"pointer",
                    fontFamily:"Cairo,sans-serif",fontWeight:800,fontSize:11}}>
                  سجّل حساباً جديداً
                </button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* ═══ خطوة 3: تسجيل حساب جديد ═══ */}
      {step === 3 && (
        <div style={{width:"100%",maxWidth:360}}>
          <div style={{background:"#22d3a022",border:"1px solid #22d3a055",borderRadius:12,
            padding:"10px 16px",marginBottom:14,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{fontSize:12,fontWeight:800,color:"#22d3a0"}}>📍 {govName} — تسجيل جديد</div>
            <button onClick={function(){ resetReg(); setStep(govCode?2:1); }}
              style={{background:"#3d2f15",border:"none",borderRadius:8,padding:"4px 10px",
                color:"#94a3b8",fontSize:10,cursor:"pointer",fontFamily:"Cairo,sans-serif"}}>رجوع</button>
          </div>

          {regDone ? (
            <Card style={{padding:24,textAlign:"center"}}>
              <div style={{fontSize:32,marginBottom:10}}>✅</div>
              <div style={{fontSize:16,fontWeight:900,color:"#22d3a0",marginBottom:8}}>تم تسجيل حسابك!</div>
              <div style={{fontSize:11,color:"#94a3b8",lineHeight:1.8,marginBottom:16}}>
                {regRole === "doctor"
                  ? "يمكنك الدخول الآن مباشرةً"
                  : "حسابك بانتظار موافقة مدير المحافظة"}
              </div>
              <Btn onClick={function(){ resetReg(); setStep(2); }} color="#22d3a0" style={{width:"100%"}}>
                الذهاب لتسجيل الدخول
              </Btn>
            </Card>
          ) : (
            <Card style={{padding:20}}>
              {/* اختيار الدور */}
              {regStep === 1 && (
                <div>
                  <div style={{fontSize:13,fontWeight:900,color:"#f0f9ff",marginBottom:4}}>نوع الحساب</div>
                  <div style={{fontSize:10,color:"#334155",marginBottom:14}}>اختر دورك في المحافظة</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                    {[
                      {r:"doctor",    l:"طبيب",         color:"#0891b2"},
                      {r:"rep",       l:"مندوب سكنر",   color:"#2563eb"},
                      {r:"sec",       l:"كول سنتر",     color:"#db2777"},
                      {r:"supervisor",l:"تيم ليدر",     color:"#7c3aed"},
                      {r:"marketing", l:"مندوب تسويق",  color:"#dc2626"},
                      {r:"mushref",   l:"مشرف",         color:"#059669"},
                    ].map(function(item){
                      return (
                        <button key={item.r}
                          onClick={function(){ setRegRole(item.r); setRegStep(2); }}
                          style={{background:`linear-gradient(135deg, rgba(28,28,36,0.7), rgba(20,20,26,0.7))`,
                            border:`1px solid ${item.color}33`,borderRadius:16,
                            padding:"18px 10px",textAlign:"center",cursor:"pointer",
                            display:"flex",flexDirection:"column",alignItems:"center",gap:10,
                            fontFamily:"'Tajawal','Inter',sans-serif",
                            transition:"all .25s cubic-bezier(0.4,0,0.2,1)",
                            boxShadow:"0 4px 16px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.04)",
                            backdropFilter:"blur(20px)"}}
                          onMouseOver={e=>{e.currentTarget.style.transform="translateY(-3px)";e.currentTarget.style.boxShadow=`0 12px 28px ${item.color}30, 0 4px 12px rgba(0,0,0,0.40)`;e.currentTarget.style.borderColor=item.color;e.currentTarget.style.background=`linear-gradient(135deg, ${item.color}15, rgba(20,20,26,0.7))`;}}
                          onMouseOut={e=>{e.currentTarget.style.transform="";e.currentTarget.style.boxShadow="0 4px 16px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.04)";e.currentTarget.style.borderColor=`${item.color}33`;e.currentTarget.style.background="linear-gradient(135deg, rgba(28,28,36,0.7), rgba(20,20,26,0.7))";}}>
                          {/* أيقونة احترافية في دائرة ملوّنة 3D */}
                          <div style={{
                            width:54,height:54,borderRadius:"50%",
                            background:`linear-gradient(135deg, ${item.color}, ${item.color}cc)`,
                            display:"flex",alignItems:"center",justifyContent:"center",
                            boxShadow:`0 4px 12px ${item.color}50, inset 0 -2px 4px rgba(0,0,0,0.20), inset 0 2px 3px rgba(255,255,255,0.25)`,
                          }}>
                            <RoleIcon type={item.r} size={30} color="#fff"/>
                          </div>
                          <span style={{fontSize:13,fontWeight:700,color:item.color,letterSpacing:-0.01}}>{item.l}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* نموذج التسجيل */}
              {regStep === 2 && (
                <div>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
                    <button onClick={function(){ setRegStep(1); setRegErr(""); }}
                      style={{background:"#3d2f15",border:"none",borderRadius:8,width:30,height:30,
                        cursor:"pointer",color:"#94a3b8",fontSize:14}}>←</button>
                    <div style={{fontSize:13,fontWeight:800,color:"#22d3a0"}}>
                      {regRole==="doctor"?"🏥 طبيب":regRole==="rep"?"🚗 مندوب سكنر":regRole==="sec"?"💼 كول سنتر":regRole==="marketing"?"🌹 مندوب تسويق":regRole==="mushref"?"🛡 مشرف":"👁 تيم ليدر"}
                    </div>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:14}}>
                    <div>
                      <div style={{fontSize:10,fontWeight:700,color:"#94a3b8",marginBottom:5}}>اسم المستخدم *</div>
                      <input value={regName} onChange={function(e){setRegName(e.target.value);}}
                        placeholder="ahmed أو ali_q" style={Object.assign({},INP,{direction:"ltr",textAlign:"left"})} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} />
                      <div style={{fontSize:9,color:"#64748b",marginTop:5,lineHeight:1.6}}>
                        💡 أحرف إنجليزية صغيرة + أرقام + شرطة سفلية فقط (يُستخدم لتسجيل الدخول)
                      </div>
                    </div>
                    <div>
                      <div style={{fontSize:10,fontWeight:700,color:"#94a3b8",marginBottom:5}}>الاسم بالعربي (اللقب) *</div>
                      <input value={regNick}
                        onChange={function(e){
                          // اقبل الأحرف العربية + المسافات فقط (احذف أي شيء آخر تلقائياً)
                          const v = e.target.value.replace(/[^\u0600-\u06FF\s]/g, "");
                          setRegNick(v);
                        }}
                        placeholder="مثال: أحمد المنصور"
                        style={Object.assign({},INP,{direction:"rtl",textAlign:"right"})} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} />
                      <div style={{fontSize:9,color:"#64748b",marginTop:5,lineHeight:1.6}}>
                        🌹 يظهر داخل التطبيق فقط — لا يُستخدم في تسجيل الدخول
                      </div>
                    </div>
                    <div>
                      <div style={{fontSize:10,fontWeight:700,color:"#94a3b8",marginBottom:5}}>كلمة المرور *</div>
                      <input type="password" value={regPass} onChange={function(e){setRegPass(e.target.value);}}
                        placeholder="كلمة مرور قوية"
                        style={Object.assign({},INP,{direction:"ltr",textAlign:"left"})}/>
                    </div>
                    {regRole !== "doctor" && (
                      <div style={{background:"#2d1f0011",border:"1px solid #fbbf2433",borderRadius:10,
                        padding:"8px 12px",fontSize:10,fontWeight:600,color:"#fbbf24"}}>
                        ⚠️ حسابك يحتاج موافقة مدير المحافظة
                      </div>
                    )}
                    {regErr && <div style={{fontSize:11,color:"#ef4444",fontWeight:700,textAlign:"center",
                      background:"#ef444411",borderRadius:8,padding:"10px",whiteSpace:"pre-line",lineHeight:1.5}}>{regErr}</div>}
                    <Btn color="#22d3a0" onClick={doRegister}
                      disabled={regBusy||!regName||!regNick||!regPass}
                      style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginTop:4}}>
                      {regBusy ? <span style={{animation:"spin 1s linear infinite",display:"inline-block"}}>⟳</span> : "إنشاء الحساب ✓"}
                    </Btn>
                  </div>
                </div>
              )}
            </Card>
          )}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════
//  ROOT APP — Firebase Hub
// ═══════════════════════════════════════════════

// ═══════════ ERROR BOUNDARY — يعرض الخطأ بدل الصفحة البيضاء ═══════════
class ErrorBoundary extends React.Component {
  constructor(props){ super(props); this.state={err:null,info:null}; }
  static getDerivedStateFromError(err){ return {err}; }
  componentDidCatch(err,info){ this.setState({err,info}); console.error("🔴 App crashed:",err,info); }
  render(){
    if(this.state.err){
      return React.createElement("div",{style:{padding:20,background:"#0a0600",color:"#f0f9ff",minHeight:"100vh",fontFamily:"Cairo,sans-serif",direction:"rtl"}},
        React.createElement("div",{style:{fontSize:18,fontWeight:900,color:"#ef4444",marginBottom:14}},"⚠️ خطأ في التطبيق"),
        React.createElement("div",{style:{background:"#2e0a0a",padding:14,borderRadius:10,fontSize:12,fontFamily:"monospace",direction:"ltr",textAlign:"left",marginBottom:10,whiteSpace:"pre-wrap"}},String(this.state.err?.message||this.state.err)),
        this.state.info && React.createElement("div",{style:{background:"#3d2f15",padding:10,borderRadius:10,fontSize:10,fontFamily:"monospace",direction:"ltr",textAlign:"left",color:"#94a3b8",whiteSpace:"pre-wrap",maxHeight:300,overflow:"auto"}},String(this.state.info.componentStack||"")),
        React.createElement("button",{onClick:()=>{localStorage.clear();window.location.reload();},style:{marginTop:14,padding:"10px 20px",background:"#22d3a0",border:"none",borderRadius:8,color:"#000",fontWeight:800,cursor:"pointer",fontFamily:"Cairo,sans-serif"}},"🔄 امسح البيانات وأعد التحميل"));
    }
    return this.props.children;
  }
}

function AppInner() {
  // ── Session persistence ──
  const [user,setUser] = useState(()=>{
    try{ return JSON.parse(localStorage.getItem("britao_user")); }
    catch{ return null; }
  });
  // govCode stored with user session
  const govCode = user?.govCode || "";
  const govName = GOV_CODES[govCode] || "";
  const isAdminAll = govCode === "ADMIN-00";

  // ── Firestore live state ──
  const [apts,setApts]   = useState([]);
  const [reps,setReps]   = useState([]);
  const [msgs,setMsgs]   = useState([]);
  const [notifs,setNotifs]=useState([]);
  // مجموعة معرّفات الإشعارات التي اعتُبرت "مقروءة" محلياً قبل أن تكتمل الـ Firestore round-trip
  // (لإسقاط العدّاد إلى صفر فوراً عند الضغط على زر الجرس)
  const [optimisticReadIds, setOptimisticReadIds] = useState(new Set());
  const [drs,setDrs]     = useState([]);
  const [docLocs,setDocLocs] = useState([]);  // مواقع الأطباء الدائمة
  const [visits,setVisits]   = useState([]);  // زيارات التيم ليدر للأطباء
  const [bookingReqs,setBookingReqs] = useState([]);  // طلبات الحجز من مندوب التسويق/التيم ليدر
  const [loading,setLoading]=useState(true);
  // Modals للطباعة على Native Android
  const [printMapModal,setPrintMapModal]   = useState(null);
  const [printTableModal,setPrintTableModal] = useState(null);
  useEffect(()=>{
    const onMap   = (e) => setPrintMapModal(e.detail);
    const onTable = (e) => setPrintTableModal(e.detail);
    window.addEventListener("britao:openMapPrintModal", onMap);
    window.addEventListener("britao:openTablePrintModal", onTable);
    return () => {
      window.removeEventListener("britao:openMapPrintModal", onMap);
      window.removeEventListener("britao:openTablePrintModal", onTable);
    };
  },[]);


  // ── Init: seed + listeners ──
  useEffect(()=>{
    if(!DB_READY){ setLoading(false); return; }
    seedIfEmpty(govCode);

    const subs=[];

    if(!govCode){setLoading(false);return;}
    // كل محافظة لها collections منفصلة بـ prefix
    const GC = govCode; // مثال: BGD-7X4K

    if(isAdminAll){
      // ═══ المدير العام — realtime listeners لكل المحافظات ═══
      const allCodes = Object.keys(GOV_CODES).filter(c=>c!=="ADMIN-00");

      // مخازن لكل محافظة لكل نوع — نُجمّعها قبل setState
      const aptsByGov = {};   // {gov: [...]}
      const repsByGov = {};   // {gov: [...]}  من _reps
      const usersByGov = {};  // {gov: [...]}  من _users (role===rep)
      const drsByGov = {};
      const locsByGov = {};
      const visitsByGov = {};

      const flush = () => {
        const flatApts = [].concat(...Object.values(aptsByGov));
        // دمج _reps مع _users (role===rep) بدون تكرار
        const repsMap = {};
        Object.values(repsByGov).forEach(arr => arr.forEach(r => { repsMap[r.id] = r; }));
        Object.values(usersByGov).forEach(arr => arr.forEach(r => { repsMap[r.id] = r; })); // users يطغى لأن فيه آخر GPS
        const flatReps = Object.values(repsMap);
        const flatDrs = [].concat(...Object.values(drsByGov));
        const flatLocs = [].concat(...Object.values(locsByGov));
        const flatVisits = [].concat(...Object.values(visitsByGov));
        setApts(flatApts);
        setReps(flatReps);
        setDrs(flatDrs);
        setDocLocs(flatLocs);
        setVisits(flatVisits);
        setLoading(false);
      };

      // أنشئ listener لكل محافظة لكل نوع
      allCodes.forEach(c => {
        // مواعيد
        subs.push(onSnapshot(collection(db,`${c}_appointments`), snap => {
          aptsByGov[c] = snap.docs.map(d=>({...d.data(),id:d.id,govCode:c,_gov:GOV_CODES[c]}));
          flush();
        }, err => console.warn(`apts/${c}:`,err.message)));

        // مناديب من _reps
        subs.push(onSnapshot(collection(db,`${c}_reps`), snap => {
          repsByGov[c] = snap.docs.map(d=>({...d.data(),id:d.id,govCode:c,_gov:GOV_CODES[c]}));
          flush();
        }, err => console.warn(`reps/${c}:`,err.message)));

        // مناديب من _users (المسجّلون - فيهم آخر GPS)
        // نضمّ كل الأدوار التشغيلية (مندوب/تسويق/تيم ليدر/مشرف) ليراهم المدير على الخريطة
        subs.push(onSnapshot(collection(db,`${c}_users`), snap => {
          usersByGov[c] = snap.docs
            .map(d=>({...d.data(),id:d.id,_fromUsers:true,govCode:c,_gov:GOV_CODES[c]}))
            .filter(u=>["rep","marketing","supervisor","mushref"].includes(u.role));
          flush();
        }, err => console.warn(`users/${c}:`,err.message)));

        // أطباء
        subs.push(onSnapshot(collection(db,`${c}_doctors`), snap => {
          drsByGov[c] = snap.docs.map(d=>({...d.data(),id:d.id,govCode:c,_gov:GOV_CODES[c]}));
          flush();
        }, err => console.warn(`doctors/${c}:`,err.message)));

        // مواقع الأطباء
        subs.push(onSnapshot(collection(db,`${c}_doctor_locations`), snap => {
          locsByGov[c] = snap.docs.map(d=>({...d.data(),id:d.id,govCode:d.data().govCode||c,govName:d.data().govName||GOV_CODES[c]}));
          flush();
        }, err => console.warn(`doc_locs/${c}:`,err.message)));

        // زيارات تيم ليدر
        subs.push(onSnapshot(collection(db,`${c}_visits`), snap => {
          visitsByGov[c] = snap.docs.map(d=>({...d.data(),id:d.id,govCode:c,_gov:GOV_CODES[c]}));
          flush();
        }, err => console.warn(`visits/${c}:`,err.message)));
      });

      return ()=>subs.forEach(u=>{ try { u(); } catch(e){} });
    }

    // Cleanup any previous listeners before creating new ones
    subs.push(onSnapshot(
      collection(db,`${GC}_appointments`),
      snap => {
        setApts(snap.docs.map(d=>({...d.data(),id:d.id})));
        setLoading(false);
      },
      err => { console.warn("apts listener:", err.message); setLoading(false); }
    ));

    subs.push(onSnapshot(
      collection(db,`${GC}_reps`),
      snap => {
        const fromReps = snap.docs.map(d=>({...d.data(),id:d.id}));
        setReps(prev => {
          const byId = {};
          fromReps.forEach(r=>{byId[r.id]=r;});
          (prev||[]).filter(r=>r._fromUsers).forEach(r=>{byId[r.id]=r;});
          return Object.values(byId);
        });
      },
      err => console.warn("reps listener:", err.message)
    ));

    // Also load registered reps from users collection
    // نضمّ كل الأدوار التشغيلية (مندوب/تسويق/تيم ليدر/مشرف)
    subs.push(onSnapshot(
      collection(db,`${GC}_users`),
      snap => {
        const repUsers = snap.docs.map(d=>({...d.data(),id:d.id,_fromUsers:true}))
          .filter(u=>["rep","marketing","supervisor","mushref"].includes(u.role));
        setReps(prev => {
          const byId = {};
          (prev||[]).filter(r=>!r._fromUsers).forEach(r=>{byId[r.id]=r;});
          repUsers.forEach(r=>{byId[r.id]=r;});
          return Object.values(byId);
        });
      },
      err => console.warn("users listener:", err.message)
    ));

    subs.push(onSnapshot(
      collection(db,`${GC}_doctors`),
      snap => setDrs(snap.docs.map(d=>({...d.data(),id:d.id}))),
      err => console.warn("doctors listener:", err.message)
    ));

    subs.push(onSnapshot(
      collection(db,`${GC}_doctor_locations`),
      snap => setDocLocs(snap.docs.map(d=>({...d.data(),id:d.id,govCode:d.data().govCode||GC,govName:d.data().govName||GOV_CODES[GC]}))),
      err => console.warn("doc_locs listener:", err.message)
    ));

    subs.push(onSnapshot(
      collection(db,`${GC}_visits`),
      snap => setVisits(snap.docs.map(d=>({...d.data(),id:d.id,govCode:GC,_gov:GOV_CODES[GC]}))),
      err => console.warn("visits listener:", err.message)
    ));

    // ═══ Listener لطلبات الحجز ═══
    subs.push(onSnapshot(
      collection(db,`${GC}_booking_requests`),
      snap => setBookingReqs(snap.docs.map(d=>({...d.data(),id:d.id,govCode:GC,_gov:GOV_CODES[GC]}))),
      err => console.warn("bookingReqs listener:", err.message)
    ));

    return ()=>subs.forEach(u=>{ try { u(); } catch(e){} });
  },[govCode, isAdminAll]);

  // ═══════════════════════════════════════════════
  // نظام الإشعارات الهجين (داخل التطبيق + المتصفح)
  // ═══════════════════════════════════════════════
  const [toast, setToast] = useState(null);
  const [notifPerm, setNotifPerm] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [swReg, setSwReg] = useState(null);
  const [fcmSwReg, setFcmSwReg] = useState(null);
  const [messaging, setMessaging] = useState(null);
  const [fcmToken, setFcmToken] = useState(null);

  // 1️⃣ تسجيل Service Worker (للإشعارات عند إغلاق التطبيق)
  useEffect(()=>{
    if (!("serviceWorker" in navigator)) return;
    // سجّل SW العادي أولاً
    navigator.serviceWorker.register("/sw.js")
      .then(reg => {
        console.log("✅ Service Worker مُسجّل");
        setSwReg(reg);
      })
      .catch(err => console.warn("SW:", err));
    // سجّل FCM SW وخزّن الـ registration لتمريره لاحقاً لـ getToken
    navigator.serviceWorker.register("/firebase-messaging-sw.js")
      .then(reg => {
        console.log("✅ FCM SW مُسجّل");
        setFcmSwReg(reg);
      })
      .catch(err => console.warn("FCM SW:", err));
    // اربط messaging بحالة React بمجرد ما يجهز
    messagingReadyPromise.then(m => { if (m) setMessaging(m); });
  },[]);

  // 🔔 حفظ FCM Token في Firestore عند تسجيل الدخول (Web + Native)
  useEffect(()=>{
    if (!user || !govCode || !DB_READY) return;

    // ═══ Native Android (Capacitor) ═══
    const isNative = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());

    if (isNative) {
      console.log("📱 Native Android — تسجيل Push Notifications نيتيف");
      (async () => {
        try {
          const { PushNotifications } = await import("@capacitor/push-notifications");
          // اطلب الإذن
          let perm = await PushNotifications.checkPermissions();
          if (perm.receive !== "granted") perm = await PushNotifications.requestPermissions();
          if (perm.receive !== "granted") {
            console.warn("لم يُمنح إذن الإشعارات");
            return;
          }
          // سجّل
          await PushNotifications.register();
          // عند الحصول على token
          PushNotifications.addListener("registration", async (token) => {
            console.log("✅ Native FCM Token:", token.value.substring(0,20)+"...");
            setFcmToken(token.value);
            try {
              const tokenDocId = `${user.id}_${token.value.substring(0,40).replace(/[^a-zA-Z0-9]/g,"")}`;
              await setDoc(doc(db, `${govCode}_fcm_tokens`, tokenDocId), {
                userId: user.id,
                userName: user.name || "",
                role: user.role || "",
                token: token.value,
                platform: "android",
                govCode: govCode,
                updatedAt: serverTimestamp(),
              });
              console.log("✅ Native Token محفوظ في Firestore");
            } catch(e) { console.error("save native token:", e); }
          });
          PushNotifications.addListener("registrationError", (err) => {
            console.error("❌ Push registration error:", err);
          });
          // عند استلام إشعار والتطبيق مفتوح
          PushNotifications.addListener("pushNotificationReceived", (notif) => {
            console.log("📨 Push received (foreground):", notif);
            try {
              setToast({ icon: "🔔", text: notif.body || notif.title || "إشعار جديد" });
              setTimeout(() => setToast(null), 5000);
              if (typeof playAlertSound === "function") playAlertSound();
            } catch(e){}
          });
          // عند الضغط على الإشعار → افتح التطبيق + لوحة الإشعارات
          PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
            console.log("🔔 Push tapped:", action);
            try {
              // إعادة التطبيق للواجهة الأمامية
              if (window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.App) {
                // التأكد من أن التطبيق في الأمام
              }
              // افتح لوحة الإشعارات بطريقة عامة
              window.dispatchEvent(new CustomEvent("britao:openNotifs"));
              // مرّر بيانات الإشعار للواجهة لو فيها رابط/معرّف
              const data = action.notification?.data || {};
              if (data.aptId) {
                window.dispatchEvent(new CustomEvent("britao:openApt", { detail: { id: data.aptId } }));
              }
            } catch(e) { console.warn("notif action:", e); }
          });
        } catch(e) { console.error("Native push init:", e); }
      })();
      return;
    }

    // ═══ Web (المتصفح العادي) ═══
    if (!messaging || !fcmSwReg) return;
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;

    const saveFcmToken = async () => {
      try {
        const token = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: fcmSwReg,
        });
        if (!token) {
          console.warn("لم يتم الحصول على FCM token");
          return;
        }
        setFcmToken(token);
        console.log("✅ FCM Token:", token.substring(0,20)+"...");

        const tokenDocId = `${user.id}_${token.substring(0,40).replace(/[^a-zA-Z0-9]/g,"")}`;
        await setDoc(doc(db, `${govCode}_fcm_tokens`, tokenDocId), {
          userId: user.id,
          userName: user.name || "",
          role: user.role || "",
          token: token,
          platform: "web",
          govCode: govCode,
          updatedAt: serverTimestamp(),
        });
        console.log("✅ Token محفوظ في Firestore");
      } catch(e) {
        console.error("خطأ في حفظ FCM token:", e);
      }
    };

    saveFcmToken();
  },[user, govCode, notifPerm, messaging, fcmSwReg]);

  // استقبال الإشعارات عندما التطبيق مفتوح (FCM foreground)
  useEffect(()=>{
    if (!messaging) return;
    const unsub = onMessage(messaging, (payload) => {
      console.log("📩 FCM foreground:", payload);
      const icon = payload.data?.icon || "🔔";
      const text = payload.notification?.body || payload.data?.text || "إشعار جديد";
      fireNotif({icon, text, id: payload.messageId || String(Date.now())});
    });
    return () => { try { unsub(); } catch(e){} };
  },[messaging]);

  // 2️⃣ صوت تنبيه لطيف داخل التطبيق — Britao Chime
  // ثلاث نغمات هابطة (G6 → E6 → C6) — Major triad arpeggio
  // مع موجة triangle + lowpass filter لإحساس جرس ذهبي ناعم
  const playAlertSound = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const playBell = (freq, start, duration, vol = 0.18) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        const filter = ctx.createBiquadFilter();

        // فلتر تمرير منخفض → يلطّف الترددات العالية ويعطي دفئاً
        filter.type = "lowpass";
        filter.frequency.value = freq * 4;
        filter.Q.value = 0.7;

        // موجة مثلّثية أدفأ من sine وأقل خشونة من square
        osc.type = "triangle";
        osc.frequency.setValueAtTime(freq, ctx.currentTime + start);

        // مظروف ADSR شبيه بالجرس: هجوم سريع + اضمحلال أُسّي طبيعي
        gain.gain.setValueAtTime(0.0001, ctx.currentTime + start);
        gain.gain.exponentialRampToValueAtTime(vol, ctx.currentTime + start + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + duration);

        osc.connect(filter);
        filter.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + start);
        osc.stop(ctx.currentTime + start + duration + 0.1);
      };

      // ═══ Britao Chime — جرس ذهبي راقٍ ═══
      // أرپيج هابط في تونال C major: G6 (sparkle) → E6 (mid) → C6 (root, long decay)
      playBell(1568, 0.00, 0.45, 0.16);  // G6 — لمعة عالية قصيرة
      playBell(1318, 0.08, 0.65, 0.18);  // E6 — نغمة وسطى
      playBell(1047, 0.18, 0.95, 0.20);  // C6 — قاعدة بصدى أطول
    } catch(e) { console.warn("sound:", e); }
  };

  // 3️⃣ اهتزاز الموبايل
  const doVibrate = () => {
    try {
      if (navigator.vibrate) {
        navigator.vibrate([400, 150, 400, 150, 400]);
      }
    } catch(e) {}
  };

  // 4️⃣ إطلاق الإشعار الكامل
  const fireNotif = (notifData) => {
    // (أ) داخل التطبيق
    playAlertSound();
    doVibrate();
    setToast(notifData);
    setTimeout(()=>setToast(null), 6000);

    // (ب) إشعار المتصفح/النظام (يعمل حتى والتطبيق مغلق)
    if (notifPerm === "granted") {
      const title = "بريتاو " + (notifData.icon||"🔔");
      const options = {
        body: notifData.text || "",
        icon: "/logo192.png",
        badge: "/logo192.png",
        tag: notifData.id || String(Date.now()),
        vibrate: [400, 150, 400, 150, 400],
        requireInteraction: true,  // يبقى حتى يضغط المستخدم
        renotify: true,
        silent: false,
      };
      try {
        if (swReg && swReg.showNotification) {
          swReg.showNotification(title, options).catch(()=>{});
        } else if ("Notification" in window) {
          new Notification(title, options);
        }
      } catch(e) { console.warn("notif:",e); }
    }
  };

  // 5️⃣ Listener للإشعارات من Firestore
  useEffect(()=>{
    if(!user||!DB_READY) return;
    if(!govCode) return;
    const GC2 = govCode;

    const seenIds = new Set();
    let first = true;

    // ملاحظة: نتجنّب orderBy حتى لا نحتاج composite index في Firestore
    // ونرتّب محلّياً بدلاً من ذلك (أكثر مرونة وأقل أعطالاً)
    const unsub = onSnapshot(
      query(collection(db,`${GC2}_notifs`),where("to","==",user.id)),
      snap => {
        const list = snap.docs
          .map(d=>({...d.data(),id:d.id}))
          .sort((a,b)=>{
            const ta = a.ts?.toMillis?.() || a.ts?.seconds*1000 || 0;
            const tb = b.ts?.toMillis?.() || b.ts?.seconds*1000 || 0;
            return tb - ta;
          });
        setNotifs(list);
        if (!first) {
          snap.docChanges().forEach(change => {
            if (change.type === "added") {
              const n = change.doc.data();
              const nid = change.doc.id;
              if (seenIds.has(nid)) return;
              seenIds.add(nid);
              if (n.read) return;
              fireNotif({icon: n.icon||"🔔", text: n.text||"", id: nid});
            }
          });
        }
        if (first) {
          snap.docs.forEach(d => seenIds.add(d.id));
          first = false;
        }
      },
      err => console.warn("notifs listener:", err.message)
    );
    return ()=>{ try { unsub(); } catch(e){} };
  },[user,govCode]);

  // 6️⃣ دالة طلب الإذن (بضغطة زر — مهم للموبايل)
  const requestNotifs = async () => {
    // أولاً: تفعيل الصوت (يحتاج user gesture)
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx) {
        const ctx = new AudioCtx();
        if (ctx.state === "suspended") await ctx.resume();
      }
    } catch(e){}

    // ═══ Native Android (Capacitor) ═══
    const isNative = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
    if (isNative) {
      try {
        const { PushNotifications } = await import("@capacitor/push-notifications");
        let perm = await PushNotifications.checkPermissions();
        if (perm.receive !== "granted") {
          perm = await PushNotifications.requestPermissions();
        }
        if (perm.receive === "granted") {
          await PushNotifications.register();
          setNotifPerm("granted");
          fireNotif({icon:"✅", text:"تم تفعيل الإشعارات النيتيف!", id:"native_ok"});
        } else {
          fireNotif({icon:"⚠️", text:"يجب السماح بالإشعارات من إعدادات النظام", id:"native_deny"});
        }
      } catch(e) {
        console.error("Native notif request:", e);
        fireNotif({icon:"❌", text:"خطأ: "+e.message, id:"native_err"});
      }
      return;
    }

    // ثانياً: طلب إذن الإشعارات
    if (!("Notification" in window)) {
      fireNotif({icon:"🔔", text:"الإشعارات الصوتية مفعّلة!", id:"test1"});
      return;
    }
    try {
      const perm = await Notification.requestPermission();
      setNotifPerm(perm);
      // إشعار اختبار
      setTimeout(()=>{
        fireNotif({icon:"✅", text: perm === "granted"
          ? "تم تفعيل الإشعارات بالكامل! ستصلك حتى عند إغلاق التطبيق."
          : "تم تفعيل الصوت والاهتزاز داخل التطبيق", id:"test2"});
      }, 300);
    } catch(e) {
      alert("خطأ: " + e.message);
    }
  };

  // ── تسجيل الجهاز + heartbeat منتظم + استماع لـ force-logout عن بُعد ──
  useEffect(()=>{
    if(!user||!DB_READY||!govCode) return;
    const deviceId = getDeviceId();
    const info = getDeviceInfo();
    const deviceRef = doc(db, `${govCode}_devices`, deviceId);

    // تسجيل أوّلي كامل (مع كل المعلومات)
    const register = async () => {
      try {
        await setDoc(deviceRef, {
          deviceId,
          userId: user.id,
          userName: user.name || "",
          userRole: user.role || "",
          deviceName: info.name,
          deviceType: info.type,
          deviceOS: info.os,
          userAgent: (navigator.userAgent||"").substring(0,300),
          lastSeen: serverTimestamp(),
          active: true,
          govCode,
          govName: GOV_CODES[govCode]||"",
        }, { merge: true });
      } catch(e) { console.warn("device register:", e); }
    };

    // heartbeat خفيف — يحدّث lastSeen فقط
    const heartbeat = async () => {
      try {
        await updateDoc(deviceRef, { lastSeen: serverTimestamp() });
      } catch(e) {
        // لو فشل (مثلاً الوثيقة مُحذوفة) → أعد التسجيل
        register();
      }
    };

    register();

    // استماع: لو المدير ضغط "إخراج هذا الجهاز" → تسجيل خروج فوري
    const unsub = onSnapshot(deviceRef, (snap) => {
      if (!snap.exists()) return;
      const d = snap.data();
      if (d.active === false) {
        alert("تم تسجيل خروجك من هذا الجهاز عن بُعد من قبل المدير.");
        setUser(null);
        try { localStorage.removeItem("britao_user"); } catch(e){}
      }
    });

    // heartbeat كل 60 ثانية (بدلاً من 5 دقائق) — دقة أعلى
    const hb = setInterval(heartbeat, 60*1000);

    // عند عودة التبويب للظاهر → heartbeat فوري
    const onVisible = () => { if (!document.hidden) heartbeat(); };
    document.addEventListener("visibilitychange", onVisible);

    // عند نشاط المستخدم (focus، click) → heartbeat
    let lastActivityHb = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastActivityHb > 30000) { // كل 30 ثانية على الأكثر
        lastActivityHb = now;
        heartbeat();
      }
    };
    window.addEventListener("focus", onActivity);
    window.addEventListener("click", onActivity);

    return () => {
      try{unsub();}catch(e){}
      clearInterval(hb);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onActivity);
      window.removeEventListener("click", onActivity);
    };
  },[user, govCode]);

  // ═══════════════════════════════════════════════════════════════
  //  حماية الحقول من المسح التلقائي — JSX attributes الآن مُطبَّقة مباشرة في كل input/textarea
  //  لذلك أُزيل الـ MutationObserver لتجنّب أيّ تداخل غير ضروري مع React reconciliation
  // ═══════════════════════════════════════════════════════════════

  // ── Real GPS for reps + إشعارات فورية عند انقطاع GPS/الإنترنت + احتساب المدّة ──
  // يعمل للمندوب + مندوب التسويق + التيم ليدر + المشرف
  useEffect(()=>{
    if(!user||!["rep","marketing","supervisor","mushref"].includes(user.role)||!DB_READY||!govCode) return;
    if(!navigator.geolocation){console.warn("GPS غير مدعوم");return;}

    // ═══ منع الكمبيوتر من إرسال GPS وهمي ═══
    // الكمبيوتر يستخدم WiFi triangulation (موقع غير دقيق + يفسد بيانات الموبايل)
    const ua = navigator.userAgent || "";
    const isMobile = /Mobi|Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(ua);
    const hasTouchScreen = navigator.maxTouchPoints > 0;
    const isRealMobile = isMobile && hasTouchScreen;

    if (!isRealMobile) {
      console.log("🖥️  هذا الجهاز ليس موبايلاً — لن يُرسل GPS (تجنّب التداخل)");
      return; // الكمبيوترات لا تُرسل GPS
    }
    console.log("📱 جهاز موبايل — تفعيل GPS الحقيقي");

    // حالة الانقطاع المحلية
    const gpsKey = `britao_gps_off_${user.id}`;
    const netKey = `britao_net_off_${user.id}`;

    // مدّة بصيغة قابلة للقراءة
    const fmtDur = (ms)=> {
      const s = Math.max(1, Math.round(ms/1000));
      if (s < 60) return `${s} ثانية`;
      const m = Math.round(s/60);
      if (m < 60) return `${m} دقيقة`;
      const h = Math.floor(m/60), rm = m%60;
      return `${h} ساعة${rm?` و ${rm} دقيقة`:""}`;
    };

    // ═══ كاش معرّفات المشرفين في هذه المحافظة (لإرسال الإشعارات لكل المشرفين) ═══
    let mushrefIds = [];
    const refreshMushrefIds = async () => {
      try {
        const usersSnap = await getDocs(collection(db, `${govCode}_users`));
        mushrefIds = usersSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(u => u.role === "mushref" && u.id)
          .map(u => u.id);
      } catch(e) { console.warn("refreshMushrefIds:", e); }
    };
    refreshMushrefIds();
    // حدّث القائمة كل 5 دقائق (لو أُضيف مشرف جديد)
    const mushrefRefreshInterval = setInterval(refreshMushrefIds, 5 * 60 * 1000);

    // إرسال إشعار للكول سنتر + المدير المحلي + المدير العام + كل المشرفين
    const broadcast = async (icon, text) => {
      const now = Date.now();
      const data = (to)=>({to,icon,text,time:now+"",ts:serverTimestamp(),read:false});
      try {
        await addDoc(collection(db,`${govCode}_notifs`), data("sec1")); // الكول سنتر
        await addDoc(collection(db,`${govCode}_notifs`), data("admin")); // مدير المحافظة
        await addDoc(collection(db,`ADMIN-00_notifs`), {...data("admin"), text:`[${GOV_CODES[govCode]||govCode}] ${text}`}); // المدير العام
        // ═══ كل المشرفين في نفس المحافظة ═══
        for (const mid of mushrefIds) {
          await addDoc(collection(db, `${govCode}_notifs`), data(mid));
        }
      } catch(e) { console.warn("broadcast:", e); }
    };

    // ═══ GPS ═══
    let gpsOffSentAt = null; // وقت بدء الانقطاع (إذا أُرسل إشعار)

    const onGpsErr = async (err) => {
      console.warn("GPS error:", err.message, "code:", err.code);
      if (err.code !== 1 && err.code !== 2) return;
      const stored = localStorage.getItem(gpsKey);
      if (stored) return; // أرسلنا إشعار البدء بالفعل
      const now = Date.now();
      localStorage.setItem(gpsKey, String(now));
      gpsOffSentAt = now;
      const reason = err.code === 1 ? "أوقف GPS أو منع الإذن" : "GPS مغلق على الجهاز";
      await broadcast("📍", `⚠️ ${user.name||user.id} — ${reason}`);
    };

    const onGpsOk = async () => {
      const stored = localStorage.getItem(gpsKey);
      if (!stored) return;
      const startedAt = Number(stored);
      const dur = Date.now() - startedAt;
      localStorage.removeItem(gpsKey);
      gpsOffSentAt = null;
      await broadcast("✅", `${user.name||user.id} — عاد GPS بعد ${fmtDur(dur)}`);
    };

    // ═══ الإنترنت ═══
    // عند انقطاع الإنترنت لا نستطيع إرسال إشعار، لكن نسجّل وقت الانقطاع محلياً
    // عند العودة نُرسل إشعار البدء + النهاية + المدّة
    const onOffline = () => {
      if (localStorage.getItem(netKey)) return;
      localStorage.setItem(netKey, String(Date.now()));
      console.warn("📵 offline detected at", new Date().toLocaleTimeString());
    };
    const onOnline = async () => {
      const stored = localStorage.getItem(netKey);
      if (!stored) return;
      const startedAt = Number(stored);
      const dur = Date.now() - startedAt;
      localStorage.removeItem(netKey);
      const startStr = new Date(startedAt).toLocaleTimeString("ar",{hour:"2-digit",minute:"2-digit"});
      await broadcast("📵", `⚠️ ${user.name||user.id} — انقطع الإنترنت من ${startStr} لمدة ${fmtDur(dur)}`);
    };

    // إذا كان مفصول من قبل (آخر جلسة) — أرسل عند البدء
    if (!navigator.onLine && !localStorage.getItem(netKey)) {
      localStorage.setItem(netKey, String(Date.now()));
    }
    // إذا عاد بعد الانقطاع
    if (navigator.onLine && localStorage.getItem(netKey)) {
      onOnline();
    }

    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);

    // ═══ تحسين GPS — لتقليل تكلفة Firebase ═══
    // 1. حفظ كل 60 ثانية (بدلاً من 10 ثواني) → خفض 83% من الكتابات
    // 2. تخطّي الحفظ إذا لم يتحرّك المندوب أكثر من 25 متر
    // 3. حفظ إجباري كل 5 دقائق حتى لو لم يتحرّك (heartbeat)
    let lastSavedPos = null;
    let lastSavedAt  = 0;
    const MIN_DIST_METERS = 25;
    const FORCED_HEARTBEAT_MS = 5 * 60 * 1000; // 5 دقائق

    const distanceMeters = (a, b) => {
      if (!a || !b) return Infinity;
      const R = 6371000;
      const toRad = d => d * Math.PI / 180;
      const dLat = toRad(b.lat - a.lat);
      const dLng = toRad(b.lng - a.lng);
      const x = Math.sin(dLat/2)**2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng/2)**2;
      return 2 * R * Math.asin(Math.sqrt(x));
    };

    // عداد لمحاولات الدقة المنخفضة — للتشخيص
    let lowAccuracyAttempts = 0;

    const saveLocation = async (pos) => {
      const cur = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      const accuracy = pos.coords.accuracy || 9999;
      const now = Date.now();

      // ═══ فلتر الدقة — قبول GPS الحقيقي فقط ═══
      // GPS satellite: 5-30m | A-GPS مع SIM: 10-50m | WiFi: 50-200m | Cell tower: 200-2000m
      const STRICT_ACCURACY = 50;  // الدقة المثلى
      const MAX_ACCURACY    = 100; // الحد الأقصى المقبول

      if (accuracy > MAX_ACCURACY) {
        lowAccuracyAttempts++;
        console.warn(`⚠️ GPS غير دقيق جداً (${Math.round(accuracy)}m) — تخطّي [محاولة ${lowAccuracyAttempts}]`);
        // إذا فشل 5 محاولات متتالية → نبّه المستخدم
        if (lowAccuracyAttempts === 5) {
          console.error("📡 لم نحصل على GPS دقيق منذ 50 ثانية — تأكد من تشغيل الموقع وأنك في مكان مفتوح");
        }
        return;
      }
      lowAccuracyAttempts = 0;

      // إذا الدقة بين 50-100m → نحفظ لكن نُعلّم البيانات بأنها تقريبية
      const isExactGps = accuracy <= STRICT_ACCURACY;

      const moved = distanceMeters(lastSavedPos, cur);
      const elapsed = now - lastSavedAt;

      // تخطّي إذا: لم يتحرّك ولم يمر heartbeat
      if (lastSavedPos && moved < MIN_DIST_METERS && elapsed < FORCED_HEARTBEAT_MS) {
        // تحقق فقط من recovery لـ GPS بدون كتابة
        if (gpsOffSentAt || localStorage.getItem(gpsKey)) onGpsOk();
        return;
      }

      // معرّف الجهاز (لتتبّع أي جهاز أرسل آخر تحديث)
      let deviceId = localStorage.getItem("britao_device_id");
      if (!deviceId) {
        deviceId = "dev_" + Math.random().toString(36).substring(2,12);
        localStorage.setItem("britao_device_id", deviceId);
      }

      const data = {
        lat: cur.lat,
        lng: cur.lng,
        accuracy: Math.round(accuracy),
        gpsQuality: isExactGps ? "exact" : "approx", // exact = GPS satellite, approx = WiFi/Cell
        lastSeen: serverTimestamp(),
        lastDeviceId: deviceId,
        status: "active",
      };
      try { await updateDoc(doc(db,`${govCode}_users`,user.id), data); } catch(e){}
      try { await updateDoc(doc(db,`${govCode}_reps`,user.id), data); } catch(e){}
      // أيضاً نحفظ الموقع في وثيقة الجهاز نفسه (لقسم "الأجهزة")
      try {
        await updateDoc(doc(db,`${govCode}_devices`,deviceId),{
          lat: cur.lat, lng: cur.lng,
          accuracy: Math.round(accuracy),
          gpsQuality: data.gpsQuality,
          lastSeen: serverTimestamp(),
        });
      } catch(e){}
      lastSavedPos = cur;
      lastSavedAt  = now;
      // إذا كنّا في حالة GPS-off وعادت قراءة سليمة → أرسل "عاد"
      if (gpsOffSentAt || localStorage.getItem(gpsKey)) onGpsOk();
    };

    const wid = navigator.geolocation.watchPosition(
      pos => saveLocation(pos),
      onGpsErr,
      {enableHighAccuracy:true, maximumAge:0, timeout:30000}
    );

    // فحص الموقع كل 10 ثواني — موقع طازج دائماً (maximumAge:0)
    const interval = setInterval(()=>{
      navigator.geolocation.getCurrentPosition(
        pos => saveLocation(pos),
        onGpsErr,
        {enableHighAccuracy:true, maximumAge:0, timeout:15000}
      );
    }, 10000);

    return ()=>{
      navigator.geolocation.clearWatch(wid);
      clearInterval(interval);
      clearInterval(mushrefRefreshInterval);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
    };
  },[user, govCode]);

  // ── Helper: push notification to Firestore ──
  // كل الإشعارات تبدأ بـ [اسم المحافظة] لتحديد المصدر فوراً
  const pushNotif=async(to,icon,text)=>{
    if(!DB_READY||!govCode) return;
    const govLbl = GOV_CODES[govCode] || govCode;
    // لا تُكرّر البادئة إذا كانت مكتوبة سابقاً
    const finalText = (text||"").startsWith("[") ? text : `[${govLbl}] ${text||""}`;
    await addDoc(collection(db,`${govCode}_notifs`),{to,icon,text:finalText,time:now(),ts:serverTimestamp(),read:false});
  };

  // ── Actions (write to Firestore) ──
  const act = {

    addDoctor: async(d)=>{
      if(!govCode){ alert("خطأ: كود المحافظة مفقود"); return null; }
      try {
        // احفظ كل معلومات الطبيب لاستخدامها لاحقاً (هاتف، عنوان، إحداثيات)
        const data = {
          name: d.name||"",
          clinic: d.clinic||"عيادة",
          phone: d.phone||"",
          address: d.address||"",
          city: d.city||"بغداد",
          lat: d.lat||null,
          lng: d.lng||null,
          govCode,                              // ← إضافة govCode للترشيح المتعدّد
          govName: GOV_CODES[govCode] || "",
          createdAt: serverTimestamp(),
          createdBySource: "call_center_apt",   // ← مصدر الإضافة (للسجل)
        };
        const ref = await addDoc(collection(db,`${govCode}_doctors`), data);
        await updateDoc(doc(db,`${govCode}_doctors`,ref.id),{id:ref.id});
        console.log(`✅ طبيب جديد أُضيف لقاعدة الأطباء (الكول سنتر): ${data.name}`);
        return ref.id;
      } catch(e) { alert("خطأ في إضافة الطبيب: "+e.message); return null; }
    },
    updateDoctor: async(drId, d)=>{
      if(!govCode||!drId) return;
      try {
        const update = {};
        if (d.name!==undefined)    update.name = d.name;
        if (d.clinic!==undefined)  update.clinic = d.clinic;
        if (d.phone!==undefined)   update.phone = d.phone;
        if (d.address!==undefined) update.address = d.address;
        if (d.city!==undefined)    update.city = d.city;
        if (d.lat!==undefined)     update.lat = d.lat;
        if (d.lng!==undefined)     update.lng = d.lng;
        update.updatedAt = serverTimestamp();
        await updateDoc(doc(db,`${govCode}_doctors`,drId), update);
      } catch(e) { console.warn("updateDoctor:",e); }
    },

    // ═══ حذف طبيب من قاعدة البيانات (للكول سنتر) ═══
    deleteDoctor: async(drId)=>{
      if(!govCode||!drId) return;
      try {
        await deleteDoc(doc(db,`${govCode}_doctors`,drId));
      } catch(e) { alert("خطأ في حذف الطبيب: "+e.message); }
    },

    addApt: async(a)=>{
      if(!govCode){ alert("خطأ: كود المحافظة مفقود"); return; }
      try {
        const ref = await addDoc(collection(db,`${govCode}_appointments`),{...a,govCode:govCode,createdAt:serverTimestamp()});
        await updateDoc(doc(db,`${govCode}_appointments`,ref.id),{id:ref.id});
        // إشعار جميع المديرين والسكرتارية في هذه المحافظة
        const usersSnap = await getDocs(collection(db,`${govCode}_users`));
        const targets = ["admin","sec1"]; // القديمة
        usersSnap.docs.forEach(d=>{
          const u = d.data();
          if((u.role==="admin"||u.role==="sec")&&u.id) targets.push(u.id);
        });
        for(const tid of [...new Set(targets)]){
          await pushNotif(tid,"📅",`موعد جديد من ${a.drName}`);
        }
      } catch(e) {
        console.error("addApt error:",e);
        alert("خطأ في حفظ الموعد: " + e.message);
      }
    },

    // توجيه موعد لمندوب محدد فقط (داخل نفس المحافظة)
    forward: async(id, repId, repName)=>{
      try {
        const apt = apts.find(a=>a.id===id);
        // ═══ هل هذا تحويل بعد قبول المندوب؟ (تعارض مواعيد) ═══
        const ACTIVE = ["مقبولة","مقبولة قيد الانتظار","في الطريق","وصلت"];
        const wasActive = apt && ACTIVE.includes(apt.status);
        const oldRepId = apt?.repId || null;
        const oldRepName = apt?.repName || "";

        const update = {
          // إذا كانت الحالة نشطة → نُعيدها لـ "مقبولة" مباشرة (المندوب الجديد بدأ بقبول)
          // إذا جديدة/محوّلة → "محوّلة" (يحتاج المندوب يقبل)
          status: wasActive ? "مقبولة" : "محوّلة",
          repId: repId || null,
          repName: repName || null,
          reassignedAt: wasActive ? serverTimestamp() : null,
        };
        // سجل التحويل بعد القبول (للمحاسبة)
        if (wasActive && oldRepId && oldRepId !== repId) {
          const prevHistory = Array.isArray(apt.transferHistory) ? apt.transferHistory : [];
          update.transferHistory = [...prevHistory, {
            fromRepId: oldRepId,
            fromRepName: oldRepName,
            toRepId: repId,
            toRepName: repName,
            atStatus: apt.status,
            byName: "كول سنتر",
            at: new Date().toISOString(),
          }];
        }
        // عند إعادة التوجيه بعد رفض: احتفظ بسجل أنّ الطلب رُفض من قبل + امسح حقول الرفض الحالية
        if (apt?.rejectReason) {
          const prev = Array.isArray(apt.rejectionHistory) ? apt.rejectionHistory : [];
          update.rejectionHistory = [...prev, {
            byName: apt.rejectedByName||"",
            reason: apt.rejectReason||"",
            at: apt.rejectedAt || null,
          }];
          update.rejectReason = "";
          update.rejectedBy = "";
          update.rejectedByName = "";
        }
        await updateDoc(doc(db,`${govCode}_appointments`,id), update);
        // إشعار للمندوب الجديد
        if(repId){
          if (wasActive) {
            await pushNotif(repId,"⚡",`موعد جديد مُحَوَّل لك (${apt?.drName||""}) — مقبولة مباشرة`);
          } else {
            await pushNotif(repId,"📅",`موعد جديد — ${apt?.drName||""}`);
          }
        }
        // إشعار للمندوب القديم (لو كان مختلفاً)
        if (wasActive && oldRepId && oldRepId !== repId) {
          await pushNotif(oldRepId,"↪",`تم سحب الموعد ${apt?.drName||""} منك وتحويله لمندوب آخر`);
        }
        // إشعار للمدير
        if (wasActive && oldRepId !== repId) {
          await pushNotif("admin","🔄",`تحويل موعد بعد القبول: ${apt?.drName||""} من ${oldRepName||"مندوب"} إلى ${repName||"مندوب جديد"}`);
        }
      } catch(e) { alert("خطأ في التوجيه: "+e.message); }
    },

    accept: async(id,u)=>{
      const apt=apts.find(a=>a.id===id);
      // ═══ هل المندوب لديه حالة نشطة (في الطريق/وصلت/جاري التصوير)؟ ═══
      const ACTIVE = ["مقبولة","في الطريق","وصلت","جاري التصوير"];
      const myActive = apts.filter(a => a.repId===u.id && ACTIVE.includes(a.status));
      // إذا في حالة نشطة بالفعل → الجديد يُقبل لكن "قيد الانتظار"
      const newStatus = myActive.length > 0 ? "مقبولة قيد الانتظار" : "مقبولة";
      await updateDoc(doc(db,`${govCode}_appointments`,id),{
        status: newStatus,
        repId: u.id,
        repName: u.name,
        queuedAt: myActive.length > 0 ? serverTimestamp() : null,
      });
      const queuedNote = myActive.length > 0 ? " (قيد الانتظار — لديه حالة نشطة)" : "";
      const drLabel = apt?.drName || "—";
      await pushNotif("admin","✅",`${u.name} قبل الموعد من الطبيب ${drLabel}${queuedNote}`);
      await pushNotif("sec1","✅",`${u.name} قبل الموعد من الطبيب ${drLabel}${queuedNote}`);
      if(apt?.drId) await pushNotif(apt.drId,"✅",`مندوب في طريقه لموعدك مع الطبيب ${drLabel}`);
    },

    reject: async(id, reason, byUser)=>{
      const apt=apts.find(a=>a.id===id);
      // الرفض يُعيد الطلب لحالة "جديدة" + يمسح المندوب → يظهر للكول سنتر لإعادة التوجيه
      // مع حفظ سبب الرفض واسم المندوب الذي رفض (للسجل)
      await updateDoc(doc(db,`${govCode}_appointments`,id),{
        status:"جديدة",
        repId: null,
        repName: null,
        rejectReason: reason || "",
        rejectedBy: byUser?.id || "",
        rejectedByName: byUser?.name || "",
        rejectedAt: serverTimestamp(),
      });
      // إشعار الكول سنتر والمدير بالرفض + السبب
      const repName = byUser?.name || "المندوب";
      const reasonShort = reason ? ` — السبب: ${reason.substring(0,60)}` : "";
      await pushNotif("sec1","↩",`${repName} رفض ${apt?.drName||id}${reasonShort} — يحتاج إعادة توجيه`);
      await pushNotif("admin","↩",`${repName} رفض ${apt?.drName||id}${reasonShort}`);
    },

    upStatus: async(id,status)=>{
      const apt=apts.find(a=>a.id===id);
      // ═══ قاعدة صارمة: لا يمكن أن يكون لدى المندوب موعدان قيد التنفيذ بنفس الوقت ═══
      // التنفيذ = [في الطريق, وصلت, جاري التصوير]
      const EXEC = ["في الطريق", "وصلت", "جاري التصوير"];
      if (EXEC.includes(status) && apt?.repId) {
        // اقرأ آخر بيانات من Firestore لتجنّب race condition
        let liveApts = apts;
        try {
          const snap = await getDocs(collection(db, `${govCode}_appointments`));
          liveApts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch(e) {/* استخدم المحلّي */}
        const otherInExec = liveApts.find(a =>
          a.id !== id &&
          a.repId === apt.repId &&
          EXEC.includes(a.status)
        );
        if (otherInExec) {
          alert(
            `⚠️ لا يمكن بدء التنفيذ على هذا الموعد!\n\n` +
            `يوجد لديك موعد آخر قيد التنفيذ:\n` +
            `🦷 ${otherInExec.drName||"—"}\n` +
            `🏷 الحالة: ${otherInExec.status}\n\n` +
            `أنهِ ذلك الموعد أولاً، أو اضغط ⏸ تأجيل عليه.`
          );
          return;
        }
      }
      await updateDoc(doc(db,`${govCode}_appointments`,id),{status});
      const drNm = apt?.drName || "—";
      await pushNotif("sec1","📍",`موعد الطبيب ${drNm}: ${status}`);
      if(apt?.drId) await pushNotif(apt.drId,"📍",`موعدك مع الطبيب ${drNm}: ${status}`);
      // ملاحظة: حفظ موقع الطبيب يتم فقط عند الضغط على "بدء التصوير" (startShoot)
    },

    // ═══ تعديل وقت/تاريخ الموعد (للكول سنتر) — حتى بعد القبول ═══
    updateAptDateTime: async(aptId, newDate, newTime) => {
      if (!govCode || !aptId) return;
      try {
        const apt = apts.find(a=>a.id===aptId);
        await updateDoc(doc(db, `${govCode}_appointments`, aptId), {
          date: newDate,
          time: newTime,
          dateTimeUpdatedAt: serverTimestamp(),
          dateTimeUpdatedBy: "كول سنتر",
        });
        // إشعار المندوب لو موجود
        if (apt?.repId) {
          await pushNotif(apt.repId, "🗓", `تعديل موعد ${apt.drName||""}: ${newDate} ${fmt12(newTime)}`);
        }
      } catch(e) { alert("خطأ في التعديل: "+e.message); }
    },

    // ═══ تغيير المندوب الموجّه للموعد (للكول سنتر) ═══
    updateAptRep: async(aptId, newRepId) => {
      if (!govCode || !aptId) return;
      try {
        const apt = apts.find(a=>a.id===aptId);
        const newRep = reps.find(r=>r.id===newRepId);
        const oldRepId = apt?.repId || null;

        await updateDoc(doc(db, `${govCode}_appointments`, aptId), {
          repId: newRepId || null,
          repName: newRep?.name || "",
          // إذا كان الموعد سابقاً مرفوضاً/جديداً وأُسند مندوب — اعتبره "محوّل" مرة أخرى
          status: newRepId && (apt?.status === "جديدة" || apt?.status === "مرفوضة") ? "محوّلة" : apt?.status,
          repUpdatedAt: serverTimestamp(),
          repUpdatedBy: "كول سنتر",
        });

        // إشعار المندوب الجديد
        if (newRepId) {
          await pushNotif(newRepId, "🔄", `حُوِّل لك موعد ${apt?.drName||""} — ${apt?.date||""} ${fmt12(apt?.time||"")}`);
        }
        // إشعار المندوب القديم لو تغيّر
        if (oldRepId && oldRepId !== newRepId) {
          await pushNotif(oldRepId, "🔁", `تم تحويل موعد ${apt?.drName||""} لمندوب آخر`);
        }
      } catch(e) { alert("خطأ في تغيير المندوب: "+e.message); }
    },

    // ═══ ترقية موعد قيد الانتظار → مقبولة (يدوي/تلقائي) ═══
    // المبدأ: المندوب لديه حالة فعّالة واحدة فقط في كل وقت
    // أي موعد آخر "مقبولة/في الطريق/وصلت" يتم تأجيله تلقائياً
    // الاستثناء الوحيد: "جاري التصوير" — لا يمكن مقاطعته (المندوب فعلياً يصوّر)
    promoteQueued: async(apt) => {
      if (!apt || !apt.id || !govCode) return false;
      try {
        // ═══ اقرأ آخر حالة من Firestore مباشرةً (تجنّب stale closure) ═══
        let liveApts = apts;
        try {
          const snap = await getDocs(collection(db, `${govCode}_appointments`));
          liveApts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch(e) { console.warn("could not refresh apts, using local:", e.message); }

        // ═══ تحقّق من عدم وجود تصوير جارٍ ═══
        if (apt.repId) {
          const shooting = liveApts.find(a =>
            a.id !== apt.id &&
            a.repId === apt.repId &&
            a.status === "جاري التصوير"
          );
          if (shooting) {
            alert(`⚠️ لا يمكن التبديل الآن — يوجد تصوير قيد التنفيذ (${shooting.drName||""})\nأنهِ التصوير أولاً.`);
            return false;
          }
        }

        // ═══ 1) أجّل أي موعد فعّال آخر لنفس المندوب ═══
        // ACTIVE = جميع الحالات التي تعتبر "فعّالة" (المندوب ملتزم بها)
        const ACTIVE_TO_PAUSE = ["مقبولة", "في الطريق", "وصلت"];
        if (apt.repId) {
          const others = liveApts.filter(a =>
            a.id !== apt.id &&
            a.repId === apt.repId &&
            ACTIVE_TO_PAUSE.includes(a.status)
          );
          for (const other of others) {
            await updateDoc(doc(db, `${govCode}_appointments`, other.id), {
              status: "مقبولة قيد الانتظار",
              queuedAt: serverTimestamp(),
              pausedByUser: true,  // تمييز للأوتو-بروموت كي لا يُعيد تفعيلها فوراً
              previousStatus: other.status, // حفظ الحالة السابقة (للسجل)
            });
            console.log(`⏸ أُجِّل ${other.drName} (كان: ${other.status}) لتفعيل ${apt.drName}`);
          }
        }
        // ═══ 2) فعّل الموعد المُختار ═══
        await updateDoc(doc(db, `${govCode}_appointments`, apt.id), {
          status: "مقبولة",
          queuedAt: null,
          pausedByUser: null,
          promotedAt: serverTimestamp(),
        });
        if (apt.repId) {
          await pushNotif(apt.repId, "▶️", `تم تفعيل الموعد: ${apt.drName || ""}`);
          // إشعار الكول سنتر بالتبديل
          await pushNotif("sec1", "🔄", `${apt.repName||"مندوب"} بدّل لـ ${apt.drName||""}`);
        }
        console.log(`✅ ترقية الموعد ${apt.id} (${apt.drName}) → مقبولة`);
        return true;
      } catch(e) {
        console.error("promoteQueued error:", e);
        alert("خطأ في التفعيل: " + e.message);
        return false;
      }
    },

    // ═══ تأجيل موعد فعّال → قيد الانتظار (يدوي من المندوب) ═══
    // يعمل لكل الحالات الفعّالة (مقبولة/في الطريق/وصلت) — ما عدا "جاري التصوير"
    pauseAccepted: async(aptId, repId) => {
      if (!aptId || !govCode) return false;
      try {
        const apt = apts.find(a=>a.id===aptId);
        if (apt?.status === "جاري التصوير") {
          alert("⚠️ لا يمكن تأجيل موعد جارٍ تصويره — أنهِ التصوير أولاً.");
          return false;
        }
        await updateDoc(doc(db, `${govCode}_appointments`, aptId), {
          status: "مقبولة قيد الانتظار",
          queuedAt: serverTimestamp(),
          pausedByUser: true,
          pausedAt: serverTimestamp(),
          previousStatus: apt?.status || "مقبولة",
        });
        if (repId) await pushNotif(repId, "⏸", `تم تأجيل ${apt?.drName||""}`);
        console.log(`⏸ تأجيل الموعد ${aptId} (${apt?.drName}, كان: ${apt?.status})`);
        return true;
      } catch(e) {
        console.error("pauseAccepted error:", e);
        alert("خطأ في التأجيل: " + e.message);
        return false;
      }
    },

    // ═══ Helper مشترك: حفظ موقع الطبيب الدائم بكل الطرق الممكنة ═══
    // يُستدعى من: وصلت، startShoot، complete (كل مرحلة تحاول)
    saveDoctorLocation: async(apt, incrementVisit = false) => {
      if (!apt) return false;
      try {
        // مصادر الإحداثيات بالأولوية:
        // 1. GPS الحقيقي للمندوب الآن (الأدق)
        // 2. آخر موقع المندوب المسجّل في reps
        // 3. lat/lng في الموعد نفسه
        let lat = null, lng = null, source = "";

        // 1) جرب GPS مباشرة من المتصفح/الجهاز
        if (navigator.geolocation) {
          try {
            const pos = await new Promise((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, {
                enableHighAccuracy: true, timeout: 5000, maximumAge: 30000
              });
            });
            if (pos?.coords) {
              lat = pos.coords.latitude;
              lng = pos.coords.longitude;
              source = "live_gps";
            }
          } catch(e) {/* ignore — نستخدم بدائل */}
        }

        // 2) إن لم نحصل على GPS مباشر — موقع المندوب من reps
        if (!lat || !lng) {
          const myRep = reps.find(r => r.id === apt.repId);
          if (myRep?.lat && myRep?.lng) {
            lat = myRep.lat; lng = myRep.lng;
            source = "rep_last_seen";
          }
        }

        // 3) آخر مصدر — إحداثيات الموعد نفسه
        if (!lat || !lng) {
          if (apt.lat && apt.lng) {
            lat = apt.lat; lng = apt.lng;
            source = "appointment_coords";
          }
        }

        // إذا فشلت كل المصادر — نُسجّل تحذير
        if (!lat || !lng) {
          console.warn("⚠️ تعذّر حفظ موقع الطبيب — لا توجد إحداثيات", { aptId: apt.id, drName: apt.drName });
          // نحفظ سجل بدون إحداثيات (يظهر في الجدول لكن ليس على الخريطة)
          const locId = apt.drId || (apt.drName||"unknown").replace(/[^a-zA-Z0-9\u0600-\u06FF]/g,"_");
          const locRef = doc(db,`${govCode}_doctor_locations`, locId);
          const existing = await getDocs(query(collection(db,`${govCode}_doctor_locations`), where("__name__","==",locId))).catch(()=>null);
          const prev = existing && existing.docs[0] ? existing.docs[0].data() : null;
          await setDoc(locRef, {
            drId: apt.drId || locId,
            drName: apt.drName || "",
            address: apt.address || "",
            govCode, govName,
            repId: apt.repId || null,
            repName: apt.repName || "",
            lat: prev?.lat || null,  // احتفظ بالإحداثيات القديمة لو موجودة
            lng: prev?.lng || null,
            lastVisitDate: apt.date || (new Date()).toISOString().slice(0,10),
            lastVisitTime: apt.time || (new Date()).toTimeString().slice(0,5),
            lastVisitTs: serverTimestamp(),
            visitsCount: incrementVisit ? (prev?.visitsCount || 0) + 1 : (prev?.visitsCount || 0),
            noLocationWarning: !prev?.lat,
            lastSource: "no_gps",
          }, { merge: true });
          return false;
        }

        // ═══ حفظ كامل مع إحداثيات ═══
        const locId = apt.drId || (apt.drName||"unknown").replace(/[^a-zA-Z0-9\u0600-\u06FF]/g,"_");
        const locRef = doc(db,`${govCode}_doctor_locations`, locId);
        const existing = await getDocs(query(collection(db,`${govCode}_doctor_locations`), where("__name__","==",locId))).catch(()=>null);
        const prev = existing && existing.docs[0] ? existing.docs[0].data() : null;
        await setDoc(locRef, {
          drId: apt.drId || locId,
          drName: apt.drName || "",
          address: apt.address || "",
          govCode, govName,
          repId: apt.repId || null,
          repName: (reps.find(r=>r.id===apt.repId)?.name) || apt.repName || "",
          lat, lng,
          lastVisitDate: apt.date || (new Date()).toISOString().slice(0,10),
          lastVisitTime: apt.time || (new Date()).toTimeString().slice(0,5),
          lastVisitTs: serverTimestamp(),
          visitsCount: incrementVisit ? (prev?.visitsCount || 0) + 1 : (prev?.visitsCount || prev ? prev.visitsCount : 1),
          lastSource: source,
        }, { merge: true });
        console.log(`✅ موقع الطبيب محفوظ (${source}):`, apt.drName, lat, lng);
        return true;
      } catch(e) {
        console.error("خطأ في حفظ موقع الطبيب:", e);
        return false;
      }
    },

    // بدء التصوير مع عدد الحالات
    startShoot: async(id, casesCount)=>{
      const apt=apts.find(a=>a.id===id);
      // ═══ قاعدة صارمة: لا يمكن بدء تصوير لو في موعد آخر قيد التنفيذ ═══
      const EXEC = ["في الطريق", "وصلت", "جاري التصوير"];
      if (apt?.repId) {
        let liveApts = apts;
        try {
          const snap = await getDocs(collection(db, `${govCode}_appointments`));
          liveApts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch(e) {/* استخدم المحلّي */}
        const otherInExec = liveApts.find(a =>
          a.id !== id &&
          a.repId === apt.repId &&
          EXEC.includes(a.status)
        );
        if (otherInExec) {
          alert(
            `⚠️ لا يمكن بدء التصوير على هذا الموعد!\n\n` +
            `يوجد لديك موعد آخر قيد التنفيذ:\n` +
            `🦷 ${otherInExec.drName||"—"}\n` +
            `🏷 الحالة: ${otherInExec.status}\n\n` +
            `أنهِ ذلك الموعد أولاً، أو اضغط ⏸ تأجيل عليه.`
          );
          return;
        }
      }
      await updateDoc(doc(db,`${govCode}_appointments`,id),{
        status: "جاري التصوير",
        casesCount: Number(casesCount)||1,
        shootStartedAt: serverTimestamp(),
      });

      // ═══ حفظ موقع الطبيب — استخدام الـ helper الموحّد ═══
      // نزيد visitsCount هنا (بداية تصوير)
      await act.saveDoctorLocation(apt, true);

      // إشعار الكول سنتر والمدير
      const usersSnap = await getDocs(collection(db,`${govCode}_users`));
      const targets = ["admin","sec1"];
      usersSnap.docs.forEach(d=>{
        const u=d.data();
        if((u.role==="admin"||u.role==="sec")&&u.id) targets.push(u.id);
      });
      for(const tid of [...new Set(targets)]){
        await pushNotif(tid,"📷",`بدء تصوير ${casesCount} حالة — ${apt?.drName||""}`);
      }
    },

    complete: async(id,u)=>{
      const apt=apts.find(a=>a.id===id);
      await updateDoc(doc(db,`${govCode}_appointments`,id),{
        status:"مكتملة",
        completedAt: serverTimestamp(),
      });
      await updateDoc(doc(db,`${govCode}_reps`,u.id),{todayApts:(reps.find(r=>r.id===u.id)?.todayApts||0)+1});
      const drNm = apt?.drName || "—";
      await pushNotif("admin","✅",`${u.name} أكمل تصوير الطبيب ${drNm}`);
      await pushNotif("sec1","✅",`اكتمل تصوير الطبيب ${drNm}`);
      if(apt?.drId) await pushNotif(apt.drId,"✅",`اكتمل تصوير موعدك مع الطبيب ${drNm}`);
      // ملاحظة: حفظ موقع الطبيب يتم فقط عند الضغط على "بدء التصوير" (startShoot)

      // ═══ ترقية أقدم حالة "مقبولة قيد الانتظار" → "مقبولة" ═══
      // استعلام مباشر من Firebase (للحصول على آخر حالة دقيقة)
      try {
        const snap = await getDocs(collection(db, `${govCode}_appointments`));
        const queued = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(a => a.repId === u.id && a.status === "مقبولة قيد الانتظار")
          .sort((x, y) => {
            const tx = x.queuedAt?.toMillis?.() || x.queuedAt?.seconds * 1000 || 0;
            const ty = y.queuedAt?.toMillis?.() || y.queuedAt?.seconds * 1000 || 0;
            return tx - ty;
          });
        if (queued.length > 0) {
          const next = queued[0];
          await updateDoc(doc(db, `${govCode}_appointments`, next.id), {
            status: "مقبولة",
            queuedAt: null,
            promotedAt: serverTimestamp(),
          });
          await pushNotif(u.id, "▶️", `تم تفعيل الموعد التالي: ${next.drName || ""}`);
          console.log(`✅ تم ترقية الموعد ${next.id} (${next.drName}) من قيد الانتظار إلى مقبولة`);
        }
      } catch (e) { console.error("auto-promote queue error:", e); }
    },

    // ═══ تذكير "هل انتهيت من التصوير؟" — يُستدعى بعد ساعة من بدء التصوير ═══
    markShootReminderSent: async(id)=>{
      try {
        await updateDoc(doc(db,`${govCode}_appointments`,id),{
          shootReminderSent: true,
          shootReminderSentAt: serverTimestamp(),
        });
      } catch(e) { console.error("markShootReminderSent:", e); }
    },

    // المندوب اختار "لا، لم أنتهِ بعد" — نُعيد عدّاد التذكير لساعة جديدة
    dismissShootReminder: async(id)=>{
      try {
        await updateDoc(doc(db,`${govCode}_appointments`,id),{
          shootReminderSent: false,
          shootReminderSentAt: null,
          shootReminderDismissedAt: serverTimestamp(),
          // أعد ضبط شريحة التصوير لعدّ ساعة جديدة من الآن
          shootStartedAt: serverTimestamp(),
        });
        // إشعار مبسّط للكول سنتر/المدير حتى يعلموا أن التصوير قيد الاستمرار
        const apt = apts.find(a => a.id === id);
        await pushNotif("admin","⏳",`المندوب لم يُنهِ التصوير بعد — ${apt?.drName||""}`);
        await pushNotif("sec1","⏳",`المندوب لم يُنهِ التصوير بعد — ${apt?.drName||""}`);
      } catch(e) { console.error("dismissShootReminder:", e); }
    },

    addNote: async(id,note)=>{
      const apt=apts.find(a=>a.id===id);
      const notes=[...(apt?.notes||[]),note];
      await updateDoc(doc(db,`${govCode}_appointments`,id),{notes});
      // إشعار الكول سنتر والمدير بوجود ملاحظة جديدة
      try {
        const who = note?.user || "المندوب";
        const txt = (note?.text||"").substring(0,80);
        await pushNotif("sec1","📝",`ملاحظة من ${who} على ${apt?.drName||id}: ${txt}`);
        await pushNotif("admin","📝",`${who} أضاف ملاحظة على ${apt?.drName||id}: ${txt}`);
        // إشعار الطبيب أيضاً (لو موجود)
        if (apt?.drId) await pushNotif(apt.drId,"📝",`ملاحظة جديدة من ${who} على موعدك`);
      } catch(e) { console.error("addNote notif:", e); }
    },

    sendMsg: async(msgData)=>{
      await addDoc(collection(db,`${govCode}_messages`),{...msgData,ts:serverTimestamp()});
    },

    markAllNotifs: async()=>{
      const unreadNotifs = notifs.filter(x => !x.read);
      if (unreadNotifs.length === 0) return;
      // 1) حدّث الـ state المحلي فوراً لإسقاط العدّاد إلى صفر بدون انتظار الشبكة
      setOptimisticReadIds(prev => {
        const next = new Set(prev);
        for (const n of unreadNotifs) next.add(n.id);
        return next;
      });
      // 2) ثم نفّذ كل التحديثات على Firestore دفعة واحدة (writeBatch atomic)
      try {
        const batch = writeBatch(db);
        for (const n of unreadNotifs) {
          batch.update(doc(db, `${govCode}_notifs`, n.id), { read: true });
        }
        await batch.commit();
      } catch(e) {
        console.error("markAllNotifs batch:", e);
      }
    },

    // ═══ زيارات الأطباء (تيم ليدر / مندوب تسويق) ═══
    createVisit: async(v, byUser)=>{
      // v: {drName, clinic, address, reason, reasonOther, date, time, lastVisitDate, lat, lng, immediate, collectedAmount, visitType}

      // ═══ احفظ الطبيب في قاعدة الأطباء المشتركة (لو غير موجود) ═══
      // هذا يجعل الطبيب متاح لكل من التيم ليدر والكول سنتر
      try {
        const drName = (v.drName||"").trim();
        if (drName) {
          // اقرأ آخر الأطباء مباشرة من Firestore (تجنّب stale state)
          let liveDrs = drs || [];
          try {
            const snap = await getDocs(collection(db, `${govCode}_doctors`));
            liveDrs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          } catch(e) { console.warn("Could not refresh doctors list, using cache:", e.message); }

          // البحث المرن (تطبيع عربي + ahmamzat + الياء/الألف)
          const drNameNorm = arabicNormalize(drName);
          const existingDr = liveDrs.find(d => arabicNormalize(d.name) === drNameNorm);

          if (!existingDr) {
            // طبيب جديد → احفظه في collection الأطباء المشترك
            const drRef = await addDoc(collection(db, `${govCode}_doctors`), {
              name: drName,
              clinic: v.clinic||"عيادة",
              address: v.address||"",
              phone: "",
              city: GOV_CODES[govCode]||"",
              lat: v.lat ? parseFloat(v.lat) : null,
              lng: v.lng ? parseFloat(v.lng) : null,
              govCode,                              // ← مهم لتظهر للجميع في المحافظة
              govName: GOV_CODES[govCode] || "",
              createdAt: serverTimestamp(),
              createdBy: byUser?.id || "",
              createdByName: byUser?.name || "",
              createdBySource: "team_leader_visit",
            });
            await updateDoc(doc(db,`${govCode}_doctors`,drRef.id),{id:drRef.id});
            console.log(`✅ طبيب جديد أُضيف لقاعدة الأطباء (التيم ليدر): ${drName}`);
          } else {
            // طبيب موجود → حدّث العنوان لو كان مختلفاً (وغير فارغ)
            const upd = {};
            if (v.address && v.address.trim() && v.address !== existingDr.address) upd.address = v.address.trim();
            if (v.clinic && v.clinic.trim() && v.clinic !== existingDr.clinic) upd.clinic = v.clinic.trim();
            if (v.lat && v.lng && (!existingDr.lat || !existingDr.lng)) {
              upd.lat = parseFloat(v.lat);
              upd.lng = parseFloat(v.lng);
            }
            if (Object.keys(upd).length > 0) {
              upd.updatedAt = serverTimestamp();
              await updateDoc(doc(db,`${govCode}_doctors`,existingDr.id), upd);
              console.log(`🔄 تحديث بيانات الطبيب: ${drName}`);
            } else {
              console.log(`ℹ️ الطبيب ${drName} موجود سابقاً — لا تحديث مطلوب`);
            }
          }
        }
      } catch(e) { console.warn("ℹ️ لم يُحفظ الطبيب في قاعدة الأطباء:", e.message); }

      // ═══ احفظ الزيارة ═══
      const ref = await addDoc(collection(db,`${govCode}_visits`),{
        drName: v.drName||"",
        clinic: v.clinic||"",
        address: v.address||"",
        reason: v.reason||"",
        reasonOther: v.reasonOther||"",
        collectedAmount: v.collectedAmount ? parseFloat(v.collectedAmount) : null, // مبلغ مُستحصَل
        visitType: v.visitType || (byUser?.role==="marketing" ? "marketing" : "leader"), // نوع الزيارة
        date: v.date||"",
        time: v.time||"",
        lastVisitDate: v.lastVisitDate||"",
        lat: v.lat ? parseFloat(v.lat) : null,
        lng: v.lng ? parseFloat(v.lng) : null,
        leaderId: byUser?.id||"",
        leaderName: byUser?.name||"",
        leaderRole: byUser?.role||"",
        govCode,
        govName: GOV_CODES[govCode]||"",
        status: v.immediate ? "في الطريق" : "في الانتظار",
        startedAt: v.immediate ? serverTimestamp() : null,
        result: "",
        resultComment: "",
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db,`${govCode}_visits`,ref.id),{id:ref.id});
      // إشعار للمدير العام
      try { await addDoc(collection(db,`ADMIN-00_notifs`),{to:"admin",icon:"🦷",text:`[${GOV_CODES[govCode]||govCode}] زيارة جديدة: ${v.drName} — ${byUser?.name||""}`,time:Date.now()+"",ts:serverTimestamp(),read:false}); } catch(e){}
      // ═══ إشعار خاصّ لو في استحصال نقود ═══
      if (v.reason === "استحصال نقود" && v.collectedAmount) {
        const amount = parseFloat(v.collectedAmount);
        const dateStr = v.date || new Date().toISOString().slice(0,10);
        const moneyMsg = `💰 استحصل ${byUser?.fullName||byUser?.name||"مندوب"} مبلغ ${amount.toLocaleString()} د.ع من ${v.drName} بتاريخ ${dateStr}`;
        try {
          await pushNotif("admin", "💰", moneyMsg);
          await addDoc(collection(db,`ADMIN-00_notifs`),{
            to:"admin",icon:"💰",
            text:`[${GOV_CODES[govCode]||govCode}] ${moneyMsg}`,
            time:Date.now()+"",ts:serverTimestamp(),read:false
          });
        } catch(e){}
      }
      return ref.id;
    },
    visitStatus: async(id, status, byUser)=>{
      const update = {status};
      if (status==="في الطريق") update.startedAt = serverTimestamp();
      if (status==="انتهت الزيارة") update.endedAt = serverTimestamp();
      // عند التحرّك للزيارة → احفظ الموقع الحالي إذا متوفر
      if (status==="في الطريق" && navigator.geolocation) {
        try {
          const pos = await new Promise((res,rej)=>navigator.geolocation.getCurrentPosition(res,rej,{timeout:8000,enableHighAccuracy:true}));
          update.startLat = pos.coords.latitude;
          update.startLng = pos.coords.longitude;
        } catch(e){}
      }
      // عند "وصلت" → احفظ موقع العيادة (هذا الموقع يُثبَّت على الخريطة)
      if (status==="وصلت" && navigator.geolocation) {
        try {
          const pos = await new Promise((res,rej)=>navigator.geolocation.getCurrentPosition(res,rej,{timeout:8000,enableHighAccuracy:true}));
          update.lat = pos.coords.latitude;
          update.lng = pos.coords.longitude;
          update.arrivedAt = serverTimestamp();
        } catch(e){}
      }
      await updateDoc(doc(db,`${govCode}_visits`,id), update);
    },
    setVisitResult: async(id, result, comment)=>{
      const update = {
        result: result||"",
        resultComment: comment||"",
        status: "انتهت الزيارة",
        endedAt: serverTimestamp(),
      };
      // ═══ احفظ الموقع الحالي عند الإنهاء (لو لم يُحفظ سابقاً عند "وصلت") ═══
      // هذا يضمن ظهور الزيارة على الخريطة لكل من تيم ليدر + مشرف + مدير عام
      try {
        const visitsSnap = await getDocs(query(collection(db,`${govCode}_visits`), where("__name__","==",id)));
        const v = visitsSnap.docs[0]?.data();
        if (v && (!v.lat || !v.lng)) {
          // لم يُحفظ موقع سابقاً → احفظه الآن
          if (navigator.geolocation) {
            try {
              const pos = await new Promise((res,rej)=>navigator.geolocation.getCurrentPosition(res,rej,{timeout:8000,enableHighAccuracy:true}));
              update.lat = pos.coords.latitude;
              update.lng = pos.coords.longitude;
            } catch(e){ console.warn("لم يُحفظ موقع نهاية الزيارة:", e.message); }
          }
        }
      } catch(e) {}
      await updateDoc(doc(db,`${govCode}_visits`,id), update);
    },
    deleteVisit: async(visit)=>{
      const gc = visit.govCode || govCode;
      try { await deleteDoc(doc(db,`${gc}_visits`,visit.id)); } catch(e){ alert("خطأ: "+e.message); }
    },

    // ═══ تعديل معلومات الزيارة (للتيم ليدر / المشرف / مندوب التسويق) ═══
    // يُسمح به فقط لصاحب الزيارة وقبل اكتمالها (الزيارات المنتهية ثابتة)
    updateVisit: async(visit, updates)=>{
      const gc = visit.govCode || govCode;
      if (!gc || !visit.id) { alert("خطأ: معرّف الزيارة مفقود"); return; }
      try {
        const clean = {};
        // فقط الحقول المسموح بتعديلها
        const allowed = ["drName","clinic","address","reason","reasonOther","date","time","collectedAmount","notes"];
        for (const k of allowed) {
          if (updates[k] !== undefined) clean[k] = updates[k];
        }
        clean.editedAt = serverTimestamp();
        clean.editedBy = visit.leaderName || visit.leaderId || "";
        await updateDoc(doc(db,`${gc}_visits`,visit.id), clean);
      } catch(e) { alert("خطأ في التعديل: "+e.message); }
    },

    // ═══ إدارة الأجهزة ═══
    disableDevice: async(device)=>{
      const gc = device.govCode || govCode;
      if (!gc || !device.deviceId) return;
      try {
        await updateDoc(doc(db,`${gc}_devices`,device.deviceId),{
          active: false,
          disabledAt: serverTimestamp(),
        });
      } catch(e){ alert("خطأ: "+e.message); }
    },
    deleteDeviceRecord: async(device)=>{
      const gc = device.govCode || govCode;
      if (!gc || !device.deviceId) return;
      try {
        await deleteDoc(doc(db,`${gc}_devices`,device.deviceId));
      } catch(e){ alert("خطأ: "+e.message); }
    },

    // ═══════════════════════════════════════════════════════════════════════
    //  طلبات الحجز (مندوب التسويق / التيم ليدر → الكول سنتر)
    // ═══════════════════════════════════════════════════════════════════════

    // إنشاء طلب حجز جديد من مندوب التسويق أو التيم ليدر
    createBookingRequest: async(req, byUser)=>{
      // req: {drName, date, time, notes, address, clinic}
      if (!govCode) return null;
      try {
        const ref = await addDoc(collection(db,`${govCode}_booking_requests`), {
          drName: req.drName||"",
          clinic: req.clinic||"",
          address: req.address||"",
          date: req.date||"",
          time: req.time||"",
          notes: req.notes||"",
          fromUserId: byUser?.id||"",
          fromUserName: byUser?.fullName||byUser?.name||"",
          fromUserRole: byUser?.role||"",
          status: "pending",       // pending | approved | rejected
          rejectReason: "",
          govCode,
          govName: GOV_CODES[govCode]||"",
          createdAt: serverTimestamp(),
        });
        await updateDoc(doc(db,`${govCode}_booking_requests`,ref.id),{id:ref.id});
        // ═══ إشعار كل أعضاء الكول سنتر + المدراء في المحافظة ═══
        const senderName = byUser?.fullName||byUser?.name||"عضو";
        const roleLabel = byUser?.role==="marketing"?"مندوب التسويق":byUser?.role==="supervisor"?"التيم ليدر":byUser?.role==="mushref"?"المشرف":byUser?.role==="rep"?"مندوب السكنر":"العضو";
        const msg = `📨 ${roleLabel} ${senderName} يطلب حجز موعد للطبيب ${req.drName} (${req.date} ${fmt12(req.time)})`;

        // اجلب كل المستخدمين في المحافظة وأرسل لكل من له دور sec أو admin
        try {
          const usersSnap = await getDocs(collection(db,`${govCode}_users`));
          const targetIds = new Set(["sec1","admin"]); // الافتراضية القديمة (احتياط)
          usersSnap.docs.forEach(d => {
            const u = d.data();
            const uid = u.id || d.id;
            if ((u.role === "sec" || u.role === "admin") && uid) {
              targetIds.add(uid);
            }
          });
          for (const tid of targetIds) {
            await pushNotif(tid, "📨", msg);
          }
        } catch(e) {
          console.warn("notify sec/admin:", e);
          // كحدّ أدنى أرسل للأسماء الافتراضية
          await pushNotif("sec1", "📨", msg);
          await pushNotif("admin", "📨", msg);
        }
        return ref.id;
      } catch(e) { alert("خطأ في إرسال الطلب: "+e.message); return null; }
    },

    // الكول سنتر يوافق على الطلب → يحوّله لموعد حقيقي
    approveBookingRequest: async(reqId, aptData)=>{
      if (!govCode || !reqId) return;
      try {
        await updateDoc(doc(db,`${govCode}_booking_requests`,reqId), {
          status: "approved",
          approvedAt: serverTimestamp(),
        });
        // إشعار للمندوب الذي طلب الحجز
        const reqSnap = await getDocs(query(collection(db,`${govCode}_booking_requests`), where("__name__","==",reqId)));
        const req = reqSnap.docs[0]?.data();
        if (req?.fromUserId) {
          await pushNotif(req.fromUserId, "✅", `تم تثبيت الحجز للطبيب ${req.drName} — ${req.date} ${fmt12(req.time)}`);
        }
      } catch(e) { console.error("approveBookingRequest error:", e); }
    },

    // الكول سنتر يرفض الطلب → يرجع للمندوب مع سبب الرفض
    rejectBookingRequest: async(reqId, reason)=>{
      if (!govCode || !reqId) return;
      try {
        await updateDoc(doc(db,`${govCode}_booking_requests`,reqId), {
          status: "rejected",
          rejectReason: reason||"",
          rejectedAt: serverTimestamp(),
        });
        // إشعار للمندوب الذي طلب الحجز
        const reqSnap = await getDocs(query(collection(db,`${govCode}_booking_requests`), where("__name__","==",reqId)));
        const req = reqSnap.docs[0]?.data();
        if (req?.fromUserId) {
          await pushNotif(req.fromUserId, "↩", `طلبك للطبيب ${req.drName} يحتاج تعديل: ${reason||"بدون سبب"}`);
        }
      } catch(e) { console.error("rejectBookingRequest error:", e); }
    },

    // المندوب يعدّل طلبه المرفوض ويعيد إرساله
    updateBookingRequest: async(reqId, updates, byUser)=>{
      if (!govCode || !reqId) return;
      try {
        await updateDoc(doc(db,`${govCode}_booking_requests`,reqId), {
          ...updates,
          status: "pending",  // إعادة تفعيل
          rejectReason: "",
          updatedAt: serverTimestamp(),
        });
        // ═══ إشعار جديد لكل أعضاء الكول سنتر + المدراء ═══
        const senderName = byUser?.fullName||byUser?.name||"عضو";
        const roleLabel = byUser?.role==="marketing"?"مندوب التسويق":byUser?.role==="supervisor"?"التيم ليدر":byUser?.role==="mushref"?"المشرف":byUser?.role==="rep"?"مندوب السكنر":"العضو";
        const msg = `🔄 ${roleLabel} ${senderName} عدّل طلب حجز الطبيب ${updates.drName||"—"} وأعاد إرساله`;
        try {
          const usersSnap = await getDocs(collection(db,`${govCode}_users`));
          const targetIds = new Set(["sec1","admin"]);
          usersSnap.docs.forEach(d => {
            const u = d.data();
            const uid = u.id || d.id;
            if ((u.role === "sec" || u.role === "admin") && uid) {
              targetIds.add(uid);
            }
          });
          for (const tid of targetIds) {
            await pushNotif(tid, "🔄", msg);
          }
        } catch(e) {
          console.warn("notify sec/admin:", e);
          await pushNotif("sec1", "🔄", msg);
          await pushNotif("admin", "🔄", msg);
        }
      } catch(e) { console.error("updateBookingRequest error:", e); }
    },

    // حذف طلب حجز (للمندوب نفسه)
    deleteBookingRequest: async(reqId)=>{
      if (!govCode || !reqId) return;
      try {
        await deleteDoc(doc(db,`${govCode}_booking_requests`,reqId));
      } catch(e) { console.error("deleteBookingRequest error:", e); }
    },

    // ═══ صلاحيات المدير العام — حذف عبر المحافظات ═══
    adminDeleteApt: async(apt)=>{
      const gc = apt.govCode || govCode;
      if (!gc) return;
      try {
        await deleteDoc(doc(db,`${gc}_appointments`,apt.id));
        // تحديث محلي للقوائم cross-gov
        setApts(prev => prev.filter(x => !(x.id===apt.id && (x.govCode||govCode)===gc)));
      } catch(e) { alert("خطأ في الحذف: "+e.message); }
    },
    // الكول سنتر يستطيع حذف أي موعد (بما فيها المقبولة من المندوب)
    secDeleteApt: async(apt)=>{
      if (!govCode) return;
      try {
        await deleteDoc(doc(db,`${govCode}_appointments`,apt.id));
        // إشعار المندوب لو الموعد كان مُسنَداً له
        if (apt.repId) {
          await pushNotif(apt.repId, "🗑", `حُذف موعد ${apt.drName||apt.id} من قبل الكول سنتر`);
        }
        // إشعار المدير
        await pushNotif("admin", "🗑", `الكول سنتر حذف موعد ${apt.drName||apt.id} (الحالة: ${apt.status||"—"})`);
      } catch(e) { alert("خطأ في الحذف: "+e.message); }
    },
    // حذف موقع طبيب (للمدير العام فقط)
    deleteDocLoc: async(loc)=>{
      const gc = loc.govCode || govCode;
      if (!gc || !loc.id) return;
      try {
        await deleteDoc(doc(db,`${gc}_doctor_locations`,loc.id));
        setDocLocs(prev => prev.filter(x => !(x.id===loc.id && (x.govCode||govCode)===gc)));
      } catch(e) { alert("خطأ في الحذف: "+e.message); }
    },
    adminDeleteRepApts: async(rep)=>{
      // يحذف كل مواعيد مندوب معين في محافظته
      const gc = rep.govCode || govCode;
      if (!gc || !rep.id) return;
      try {
        const list = apts.filter(a => (a.govCode||govCode)===gc && a.repId===rep.id);
        for (const a of list) {
          await deleteDoc(doc(db,`${gc}_appointments`,a.id));
        }
        setApts(prev => prev.filter(a => !((a.govCode||govCode)===gc && a.repId===rep.id)));
      } catch(e) { alert("خطأ في الحذف: "+e.message); }
    },
    adminDeleteUser: async(rep)=>{
      const gc = rep.govCode || govCode;
      if (!gc || !rep.id) return;
      try {
        // احذف وثيقة المستخدم من _users و _reps
        await deleteDoc(doc(db,`${gc}_users`,rep.id)).catch(()=>{});
        await deleteDoc(doc(db,`${gc}_reps`,rep.id)).catch(()=>{});
        // احذف tokens FCM
        try {
          const toks = await getDocs(collection(db,`${gc}_fcm_tokens`));
          for (const t of toks.docs) {
            const d = t.data();
            if (d.userId===rep.id) await deleteDoc(doc(db,`${gc}_fcm_tokens`,t.id)).catch(()=>{});
          }
        } catch(e){}
        setReps(prev => prev.filter(r => !((r.govCode||govCode)===gc && r.id===rep.id)));
      } catch(e) { alert("خطأ في الحذف: "+e.message); }
    },
  };

  // ── Login / Logout ──
  const login=(u)=>{ setUser(u); localStorage.setItem("britao_user",JSON.stringify(u)); };
  const logout=()=>{ setUser(null); localStorage.removeItem("britao_user"); };

  // ── Build db object for child components ──
  const dbObj={appointments:apts,reps,messages:msgs,doctors:drs,docLocs,visits,bookingReqs,govCode,govName,isAdminAll};
  const unread=notifs.filter(n => !n.read && !optimisticReadIds.has(n.id)).length;

  // نظّف مجموعة الـ optimistic فور وصول تأكيد Firestore (المنطق: لو الإشعار صار read=true → ما عاد نحتاج بصمته)
  useEffect(() => {
    if (optimisticReadIds.size === 0) return;
    const stillNeeded = new Set();
    const knownIds = new Map(notifs.map(n => [n.id, n]));
    for (const id of optimisticReadIds) {
      const n = knownIds.get(id);
      // لو الإشعار ما زال موجود ومازال غير مقروء → نُبقي البصمة المحلية
      if (n && !n.read) stillNeeded.add(id);
    }
    if (stillNeeded.size !== optimisticReadIds.size) {
      setOptimisticReadIds(stillNeeded);
    }
  }, [notifs, optimisticReadIds]);

  // Modals عرض الطباعة (Native Android)
  const printModals = (
    <>
      {printMapModal && <NativePrintMapModal data={printMapModal} onClose={()=>setPrintMapModal(null)} govCode={govCode}/>}
      {printTableModal && <NativePrintTableModal data={printTableModal} onClose={()=>setPrintTableModal(null)}/>}
    </>
  );

  if(!user) return <>{printModals}<Login onLogin={login}/></>;

  // ── Splash Screen مع لوغو بريتاو ──
  if(loading&&DB_READY) return (
    <div style={{
      minHeight:"100vh",
      background:"radial-gradient(ellipse 800px 600px at 50% 50%, rgba(212,175,55,0.10), transparent 70%), #07070a",
      display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
      gap:24,
      animation:"fadeInUp 0.5s ease"
    }}>
      <style>{G}</style>
      <style>{`
        @keyframes britaoSplash {
          0%   { transform: scale(0.7) rotate(-8deg); opacity: 0; }
          50%  { transform: scale(1.08) rotate(2deg); opacity: 1; }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        @keyframes britaoGlow {
          0%, 100% { box-shadow: 0 0 60px #d4af3766, 0 0 120px #d4af3733; }
          50%      { box-shadow: 0 0 90px #f0c040aa, 0 0 180px #f0c04055; }
        }
        @keyframes britaoText {
          from { opacity: 0; transform: translateY(15px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes britaoLine {
          0% { transform: translateX(-100%); }
          50%, 100% { transform: translateX(100%); }
        }
      `}</style>
      <div style={{
        position:"relative",
        animation:"britaoSplash 0.9s cubic-bezier(.34,1.56,.64,1) both"
      }}>
        <div style={{
          width:170, height:170,
          borderRadius:30,
          padding:8,
          background:"linear-gradient(135deg,#1a1208,#000)",
          animation:"britaoGlow 2s ease-in-out infinite"
        }}>
          <img src="/logo192.png" alt="بريتاو"
            style={{width:"100%",height:"100%",objectFit:"contain",borderRadius:24}}/>
        </div>
      </div>
      <div style={{textAlign:"center", animation:"britaoText 1.2s 0.5s both"}}>
        <div style={{
          fontSize:32, fontWeight:900,
          background:"linear-gradient(135deg,#b8860b,#f0c040,#fff5d8)",
          WebkitBackgroundClip:"text",
          backgroundClip:"text",
          color:"transparent",
          letterSpacing:1,
          marginBottom:6,
          fontFamily:"Cairo,sans-serif"
        }}>
          بريتاو
        </div>
        <div style={{fontSize:11,color:"#8b6914",fontWeight:700,letterSpacing:2}}>
          PRETTAU DENTAL LAB
        </div>
      </div>
      {/* خط تحميل ذهبي */}
      <div style={{
        width:200, height:3,
        background:"#15100a",
        borderRadius:2,
        overflow:"hidden",
        position:"relative",
        marginTop:16
      }}>
        <div style={{
          position:"absolute", top:0, left:0,
          width:"60%", height:"100%",
          background:"linear-gradient(90deg, transparent, #f0c040, transparent)",
          animation:"britaoLine 1.5s ease-in-out infinite"
        }}/>
      </div>
    </div>
  );

  // ── Render correct app by role ──
  const props={user,db:dbObj,act,notifs,unread,onLogout:logout,onMarkAll:act.markAllNotifs,notifPerm,requestNotifs};

  const renderApp = () => {
    if(user.role==="admin")      return <AdminApp  {...props}/>;
    if(user.role==="supervisor") return <TeamLeaderApp {...props}/>;
    if(user.role==="mushref")    return <MushrefApp {...props}/>;
    if(user.role==="sec")        return <SecApp    {...props}/>;
    if(user.role==="rep")        return <RepApp    {...props}/>;
    if(user.role==="marketing")  return <MarketingApp {...props}/>;
    if(user.role==="doctor")     return <DoctorApp {...props}/>;
    return null;
  };

  return (
    <>
      {/* ═══ بانر تحذير عند انقطاع الإنترنت أو GPS ═══ */}
      <ConnectivityWatcher user={user}/>

      {renderApp()}
      {printModals}

      {/* ═══ TOAST إشعار منبثق داخل التطبيق ═══ */}
      {toast && (
        <div onClick={()=>setToast(null)}
          style={{position:"fixed",top:16,left:"50%",transform:"translateX(-50%)",
            zIndex:99999,background:"linear-gradient(135deg,#22d3a0,#0891b2)",
            color:"#fff",padding:"16px 20px",borderRadius:16,
            boxShadow:"0 10px 40px rgba(34,211,160,0.5), 0 0 80px rgba(34,211,160,0.3)",
            display:"flex",alignItems:"center",gap:14,
            width:"min(380px, 92vw)",
            fontFamily:"Cairo,sans-serif",direction:"rtl",cursor:"pointer",
            animation:"slideDownBounce .4s cubic-bezier(.34,1.56,.64,1), pulseBig 1.5s infinite",
            border:"2.5px solid rgba(255,255,255,0.25)"}}>
          <div style={{fontSize:36,filter:"drop-shadow(0 0 8px rgba(255,255,255,0.6))",animation:"ring 0.5s ease infinite alternate"}}>{toast.icon}</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:11,fontWeight:800,color:"#fff",opacity:.95,marginBottom:3,letterSpacing:"0.5px"}}>🔔 بريتاو — إشعار جديد</div>
            <div style={{fontSize:14,fontWeight:800,color:"#fff",lineHeight:1.4}}>{toast.text}</div>
          </div>
          <button onClick={(e)=>{e.stopPropagation();setToast(null);}}
            style={{background:"rgba(255,255,255,0.2)",border:"none",borderRadius:10,width:32,height:32,
              color:"#fff",fontSize:16,cursor:"pointer",fontWeight:900,flexShrink:0}}>✕</button>
        </div>
      )}
    </>
  );
}



/* ════ SCHEDULE TABLE — جدول المواعيد ════ */
function ScheduleTable({apts, title, showGov, canDelete=false, act=null, canEdit=false, reps=[], deleteFn=null}) {
  const [filter,setFilter] = useState("all");
  const [repFilter,setRepFilter] = useState(""); // فلتر المندوب — "" يعني الكل
  const [govFilter,setGovFilter] = useState("ALL"); // فلتر المحافظة (يظهر فقط لو showGov)
  const [search,setSearch] = useState("");
  const [dateFrom,setDateFrom] = useState("");
  const [dateTo,setDateTo]     = useState("");
  const [sortBy,setSortBy]     = useState("date_desc"); // date_desc, date_asc, dr, status
  const [editApt,setEditApt]   = useState(null); // الموعد قيد التعديل
  const [showNotesFor,setShowNotesFor] = useState(null); // عرض كل ملاحظات موعد محدد

  // ═══ قائمة المناديب المتاحة للفلترة ═══
  // أولوية لـ reps المُمرَّر من الـ parent، وإلا نستخرجها من المواعيد
  const repsForFilter = (() => {
    const fromProp = (reps||[]).filter(r => r.role==="rep" || r.role==="marketing" || !r.role);
    if (fromProp.length > 0) {
      // كذلك نُضيف أي مندوب موجود في المواعيد ولم يأتِ في reps (للتأمين)
      const knownIds = new Set(fromProp.map(r => r.id));
      const extras = [];
      apts.forEach(a => {
        if (a.repId && !knownIds.has(a.repId)) {
          knownIds.add(a.repId);
          extras.push({ id: a.repId, name: a.repName || a.repId });
        }
      });
      return [...fromProp, ...extras];
    }
    // Fallback: ابني القائمة من المواعيد
    const map = new Map();
    apts.forEach(a => {
      if (a.repId && !map.has(a.repId)) {
        map.set(a.repId, { id: a.repId, name: a.repName || a.repId });
      }
    });
    return Array.from(map.values());
  })();

  // أيّ دالة حذف نستعمل: المخصّصة (مثلاً secDeleteApt) أم الافتراضية adminDeleteApt
  const _delFn = deleteFn || act?.adminDeleteApt;

  const deleteOne = async (a) => {
    if (!canDelete || !_delFn) return;
    let msg = `حذف الموعد ${a.id} (${a.drName||""}) نهائياً؟`;
    // تحذير إضافي إذا كان قبل المندوب الموعد
    if (a.repId && ["مقبولة","في الطريق","وصلت","جاري التصوير","مقبولة قيد الانتظار"].includes(a.status)) {
      msg = `⚠️ تنبيه: هذا الموعد <مقبول> من قبل المندوب ${a.repName||"—"}\n\n${msg}\nسيُرسَل إشعار للمندوب بالحذف.`;
    }
    if (!window.confirm(msg)) return;
    await _delFn(a);
  };
  const deleteAll = async () => {
    if (!canDelete || !_delFn) return;
    const list = filtered;
    if (list.length === 0) return;
    if (!window.confirm(`حذف ${list.length} موعد ${filter==="all"?"كاملاً من المرشّح الحالي":`(${filter})`} نهائياً؟ لا يمكن التراجع.`)) return;
    if (!window.confirm("تأكيد ثاني — هل أنت متأكد تماماً؟")) return;
    for (const a of list) await _delFn(a);
  };

  // فلاتر سريعة بالتاريخ
  const todayStr = () => new Date().toISOString().slice(0,10);
  const dateNDaysAgo = (n) => { const d = new Date(); d.setDate(d.getDate()-n); return d.toISOString().slice(0,10); };
  const setQuickDate = (kind) => {
    if (kind === "today") { setDateFrom(todayStr()); setDateTo(todayStr()); }
    else if (kind === "yesterday") { const y = dateNDaysAgo(1); setDateFrom(y); setDateTo(y); }
    else if (kind === "week") { setDateFrom(dateNDaysAgo(7)); setDateTo(todayStr()); }
    else if (kind === "month") { setDateFrom(dateNDaysAgo(30)); setDateTo(todayStr()); }
    else if (kind === "all") { setDateFrom(""); setDateTo(""); }
  };

  const filtered = apts.filter(a=>{
    if(filter!=="all" && a.status!==filter) return false;
    // فلتر المحافظة (للمدير العام فقط)
    if(showGov && govFilter !== "ALL" && a.govCode !== govFilter) return false;
    // فلتر المندوب
    if(repFilter && a.repId !== repFilter) return false;
    // فلتر التاريخ
    const d = a.date || "";
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    if(search){
      const s = search.toLowerCase();
      return (a.drName||"").toLowerCase().includes(s)
        || (a.repName||"").toLowerCase().includes(s)
        || (a.address||"").toLowerCase().includes(s)
        || (a.id||"").toLowerCase().includes(s);
    }
    return true;
  }).sort((a,b)=>{
    if (sortBy === "date_asc")  { const x=a.date+" "+a.time; const y=b.date+" "+b.time; return x.localeCompare(y); }
    if (sortBy === "dr")        { return (a.drName||"").localeCompare(b.drName||"","ar"); }
    if (sortBy === "status")    { return (a.status||"").localeCompare(b.status||"","ar"); }
    // افتراضي: date_desc
    const da = a.date+" "+a.time;
    const db = b.date+" "+b.time;
    return db.localeCompare(da);
  });

  return (
    <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
        <div style={{fontSize:14,fontWeight:800,color:"#f0f9ff"}}>📋 {title||"جدول المواعيد"} ({filtered.length})</div>
        <div style={{display:"flex",gap:6}}>
          <PdfBtn onClick={()=>{
            const transferLabel = (a) => {
              if (!a.transferredFromUserName) return "—";
              const r = a.transferredFromUserRole;
              const lbl = r==="marketing"?"مندوب التسويق":r==="supervisor"?"تيم ليدر":r==="mushref"?"مشرف":r==="rep"?"مندوب سكنر":"";
              return `${lbl ? lbl+" — ":""}${a.transferredFromUserName}`;
            };
            const cols = ["التاريخ","الوقت","الطبيب","العيادة","العنوان","المندوب","الحالات","طلب من",...(showGov?["المحافظة"]:[]),"الحالة"];
            const rows = filtered.map(a=>[
              a.date||"—", fmt12(a.time)||"—", a.drName||"—", a.clinic||"—",
              a.address||"—", a.repName||"لم يُحدد", a.casesCount||"—",
              transferLabel(a),
              ...(showGov?[a._gov||(a.govCode?GOV_CODES[a.govCode]:"—")]:[]),
              a.status
            ]);
            const repLbl = repFilter ? (repsForFilter.find(r=>r.id===repFilter)?.fullName || repsForFilter.find(r=>r.id===repFilter)?.name || repFilter) : "";
            exportToPdf({title:title||"جدول المواعيد", subtitle:`الفلتر: ${filter==="all"?"الكل":filter}${repLbl?` • المندوب: ${repLbl}`:""}${search?` • بحث: "${search}"`:""}`, columns:cols, rows});
          }}/>
          {canDelete && filtered.length>0 && (
            <button onClick={deleteAll}
              style={{background:"#2e0a0a",border:"1px solid #f87171",borderRadius:8,padding:"6px 12px",color:"#f87171",fontSize:10,fontWeight:800,cursor:"pointer",fontFamily:"Cairo,sans-serif"}}>
              🗑 حذف الكل ({filtered.length})
            </button>
          )}
        </div>
      </div>

      {/* Search */}
      <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="ابحث باسم الطبيب / المندوب / العنوان..."
        style={{background:"#0d0a00",border:"1px solid #3d2f15",borderRadius:10,padding:"10px 14px",
          color:"#f0f9ff",fontSize:12,fontFamily:"Cairo,sans-serif",direction:"rtl"}} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} />

      {/* ═══ Gov filter — يظهر فقط للمدير العام (showGov=true) ═══ */}
      {showGov && (
        <div style={{background:"#15100a",border:"1px solid #3d2f15",borderRadius:10,padding:10,display:"flex",flexDirection:"column",gap:6}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
            <div style={{fontSize:11,color:"#d4af37",fontWeight:800}}>📍 فلتر المحافظة</div>
            {govFilter !== "ALL" && (
              <button onClick={()=>setGovFilter("ALL")}
                style={{background:"transparent",border:"1px solid #f87171",borderRadius:6,padding:"3px 10px",color:"#f87171",fontSize:9,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal','Inter',sans-serif"}}>
                ✕ مسح
              </button>
            )}
          </div>
          <select value={govFilter} onChange={e=>{setGovFilter(e.target.value); setRepFilter("");}}
            style={{width:"100%",background:"#0a0600",border:"1px solid #3d2f15",borderRadius:8,padding:"8px 10px",color:"#f0f9ff",fontSize:12,fontFamily:"'Tajawal','Inter',sans-serif",fontWeight:700,boxSizing:"border-box"}}>
            <option value="ALL">— كل المحافظات ({apts.length} موعد) —</option>
            {Object.keys(GOV_CODES).filter(c => c !== "ADMIN-00").map(c => {
              const cnt = apts.filter(a => a.govCode === c).length;
              if (cnt === 0) return null;
              return <option key={c} value={c}>{GOV_CODES[c]} ({cnt})</option>;
            })}
          </select>
        </div>
      )}

      {/* ═══ Rep filter — جديد ═══ */}
      {repsForFilter.length > 0 && (
        <div style={{background:"#15100a",border:"1px solid #3d2f15",borderRadius:10,padding:10,display:"flex",flexDirection:"column",gap:6}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
            <div style={{fontSize:11,color:"#d4af37",fontWeight:800}}>👤 فلتر المندوب</div>
            {repFilter && (
              <button onClick={()=>setRepFilter("")}
                style={{background:"transparent",border:"1px solid #f87171",borderRadius:6,padding:"3px 10px",color:"#f87171",fontSize:9,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal','Inter',sans-serif"}}>
                ✕ مسح
              </button>
            )}
          </div>
          <select value={repFilter} onChange={e=>setRepFilter(e.target.value)}
            style={{width:"100%",background:"#0a0600",border:"1px solid #3d2f15",borderRadius:8,padding:"8px 10px",color:"#f0f9ff",fontSize:12,fontFamily:"'Tajawal','Inter',sans-serif",fontWeight:700,boxSizing:"border-box"}}>
            <option value="">— كل المناديب ({repsForFilter.length}) —</option>
            {repsForFilter.map(r => {
              const cnt = apts.filter(a => a.repId === r.id).length;
              return (
                <option key={r.id} value={r.id}>
                  {r.fullName || r.name || r.id}{r.area ? ` — ${r.area}` : ""} ({cnt} موعد)
                </option>
              );
            })}
          </select>
        </div>
      )}

      {/* Date filter — جديد */}
      <div style={{background:"#15100a",border:"1px solid #3d2f15",borderRadius:10,padding:10,display:"flex",flexDirection:"column",gap:8}}>
        <div style={{fontSize:10,color:"#c9b37e",fontWeight:700}}>📅 فلتر التاريخ</div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {[["today","اليوم","#22d3a0"],["yesterday","أمس","#fbbf24"],["week","أسبوع","#60a5fa"],["month","شهر","#a78bfa"],["all","الكل","#94a3b8"]].map(([k,l,c])=>(
            <button key={k} onClick={()=>setQuickDate(k)}
              style={{padding:"5px 10px",borderRadius:8,border:`1px solid ${c}44`,background:`${c}11`,color:c,fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"Cairo,sans-serif"}}>{l}</button>
          ))}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
          <div>
            <div style={{fontSize:9,color:"#7a6841",marginBottom:3}}>من تاريخ</div>
            <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)}
              style={{width:"100%",background:"#0a0600",border:"1px solid #3d2f15",borderRadius:8,padding:"6px 8px",color:"#f0f9ff",fontSize:11,fontFamily:"Cairo,sans-serif",boxSizing:"border-box"}}/>
          </div>
          <div>
            <div style={{fontSize:9,color:"#7a6841",marginBottom:3}}>إلى تاريخ</div>
            <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)}
              style={{width:"100%",background:"#0a0600",border:"1px solid #3d2f15",borderRadius:8,padding:"6px 8px",color:"#f0f9ff",fontSize:11,fontFamily:"Cairo,sans-serif",boxSizing:"border-box"}}/>
          </div>
        </div>
        {/* Sort */}
        <div>
          <div style={{fontSize:9,color:"#7a6841",marginBottom:3}}>🔢 الفهرسة</div>
          <select value={sortBy} onChange={e=>setSortBy(e.target.value)}
            style={{width:"100%",background:"#0a0600",border:"1px solid #3d2f15",borderRadius:8,padding:"6px 8px",color:"#f0f9ff",fontSize:11,fontFamily:"Cairo,sans-serif"}}>
            <option value="date_desc">📅 الأحدث أولاً</option>
            <option value="date_asc">📅 الأقدم أولاً</option>
            <option value="dr">🦷 اسم الطبيب (أ-ي)</option>
            <option value="status">🏷 الحالة</option>
          </select>
        </div>
      </div>

      {/* Filter chips */}
      <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:4}}>
        {["all","جديدة","محوّلة","مقبولة","في الطريق","وصلت","جاري التصوير","مكتملة","مرفوضة"].map(s=>(
          <button key={s} onClick={()=>setFilter(s)}
            style={{padding:"6px 12px",borderRadius:20,
              border:`1px solid ${filter===s?(APTS[s]?.c||"#22d3a0"):"#3d2f15"}`,
              background:filter===s?(APTS[s]?.c||"#22d3a0")+"22":"#0d0a00",
              color:filter===s?(APTS[s]?.c||"#22d3a0"):"#94a3b8",
              fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"Cairo,sans-serif",whiteSpace:"nowrap",flexShrink:0}}>
            {s==="all"?"الكل":s}
          </button>
        ))}
      </div>

      {/* Table */}
      <div style={{overflowX:"auto",background:"#080f1a",borderRadius:12,border:"1px solid #3d2f15"}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:11,fontFamily:"Cairo,sans-serif",direction:"rtl"}}>
          <thead>
            <tr style={{background:"#15100a",color:"#94a3b8",fontSize:10}}>
              <th style={{padding:"10px 8px",textAlign:"right",fontWeight:800}}>التاريخ</th>
              <th style={{padding:"10px 8px",textAlign:"right",fontWeight:800}}>الوقت</th>
              <th style={{padding:"10px 8px",textAlign:"right",fontWeight:800}}>الطبيب</th>
              <th style={{padding:"10px 8px",textAlign:"right",fontWeight:800}}>العيادة</th>
              <th style={{padding:"10px 8px",textAlign:"right",fontWeight:800}}>العنوان</th>
              <th style={{padding:"10px 8px",textAlign:"right",fontWeight:800}}>المندوب</th>
              <th style={{padding:"10px 8px",textAlign:"right",fontWeight:800}}>الحالات</th>
              <th style={{padding:"10px 8px",textAlign:"right",fontWeight:800,color:"#38bdf8"}}>طلب من</th>
              {showGov && <th style={{padding:"10px 8px",textAlign:"right",fontWeight:800}}>المحافظة</th>}
              <th style={{padding:"10px 8px",textAlign:"right",fontWeight:800}}>الحالة</th>
              {canEdit && <th style={{padding:"10px 8px",textAlign:"center",fontWeight:800,color:"#fbbf24"}}>تعديل</th>}
              {canDelete && <th style={{padding:"10px 8px",textAlign:"center",fontWeight:800,color:"#f87171"}}>حذف</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.length===0 && (
              <tr><td colSpan={(showGov?10:9)+(canDelete?1:0)+(canEdit?1:0)} style={{padding:30,textAlign:"center",color:"#334155",fontSize:12}}>لا توجد مواعيد</td></tr>
            )}
            {filtered.map(a=>{
              const govName = a._gov || (a.govCode ? GOV_CODES[a.govCode] : "—");
              return (
                <tr key={a.id} style={{borderTop:"1px solid #3d2f1522"}}>
                  <td style={{padding:"10px 8px",color:"#60a5fa",fontWeight:700,whiteSpace:"nowrap"}}>{a.date||"—"}</td>
                  <td style={{padding:"10px 8px",color:"#a78bfa",fontWeight:700,whiteSpace:"nowrap"}}>{fmt12(a.time)||"—"}</td>
                  <td style={{padding:"10px 8px",color:"#f0f9ff",fontWeight:700}}>{a.drName||"—"}</td>
                  <td style={{padding:"10px 8px",color:"#94a3b8"}}>{a.clinic||"—"}</td>
                  <td style={{padding:"10px 8px",color:"#94a3b8",maxWidth:150,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.address||"—"}</td>
                  <td style={{padding:"10px 8px",color:a.repName?"#22d3a0":"#ef4444",fontWeight:700}}>{a.repName||"لم يُحدد"}</td>
                  <td style={{padding:"10px 8px",color:"#fbbf24",fontWeight:800,textAlign:"center"}}>{a.casesCount||"—"}</td>
                  <td style={{padding:"10px 8px",color:a.transferredFromUserName?"#38bdf8":"#475569",fontWeight:700,whiteSpace:"nowrap"}}>
                    {a.transferredFromUserName ? (() => {
                      const r = a.transferredFromUserRole;
                      const lbl = r==="marketing"?"مندوب التسويق":r==="supervisor"?"تيم ليدر":r==="mushref"?"مشرف":r==="rep"?"مندوب سكنر":"";
                      return `${lbl ? lbl+" — ":""}${a.transferredFromUserName}`;
                    })() : "—"}
                  </td>
                  {showGov && <td style={{padding:"10px 8px",color:"#f472b6",fontWeight:700}}>{govName}</td>}
                  <td style={{padding:"10px 8px"}}>
                    <div style={{display:"flex",flexDirection:"column",gap:4}}>
                      <SBadge s={a.status}/>
                      {/* ─── ملاحظات المندوب (لو موجودة) ─── */}
                      {Array.isArray(a.notes) && a.notes.length>0 && (
                        <button onClick={()=>setShowNotesFor(a)}
                          style={{background:"#1f1a08",border:"1px solid #fbbf2455",borderRadius:6,padding:"4px 6px",
                            color:"#fbbf24",fontSize:9,fontWeight:700,cursor:"pointer",fontFamily:"Cairo,sans-serif",
                            textAlign:"right",direction:"rtl",lineHeight:1.4,maxWidth:200}}
                          title="اضغط لعرض كل الملاحظات">
                          📝 {a.notes[a.notes.length-1]?.text?.substring(0,40) || ""}
                          {a.notes[a.notes.length-1]?.text?.length>40 ? "..." : ""}
                          {a.notes.length>1 && <span style={{color:"#7a6841",marginRight:4}}>(+{a.notes.length-1})</span>}
                        </button>
                      )}
                    </div>
                  </td>
                  {canEdit && (
                    <td style={{padding:"10px 8px",textAlign:"center"}}>
                      {!["مكتملة","مرفوضة","جاري التصوير"].includes(a.status) ? (
                        <button onClick={()=>setEditApt(a)}
                          style={{background:"#2a1f0a",border:"1px solid #fbbf2466",borderRadius:6,padding:"3px 8px",color:"#fbbf24",fontSize:10,fontWeight:800,cursor:"pointer",fontFamily:"Cairo,sans-serif"}}>
                          ✏️
                        </button>
                      ) : <span style={{color:"#475569",fontSize:9}}>—</span>}
                    </td>
                  )}
                  {canDelete && (
                    <td style={{padding:"10px 8px",textAlign:"center"}}>
                      <button onClick={()=>deleteOne(a)}
                        style={{background:"#2e0a0a",border:"1px solid #f8717155",borderRadius:6,padding:"3px 8px",color:"#f87171",fontSize:10,fontWeight:800,cursor:"pointer",fontFamily:"Cairo,sans-serif"}}>
                        🗑
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ═══ Modal تعديل الموعد (الكول سنتر) ═══ */}
      {editApt && (
        <EditAptModal
          apt={editApt}
          reps={reps}
          onClose={()=>setEditApt(null)}
          onSaveDateTime={async(d,t)=>{
            if (act?.updateAptDateTime) await act.updateAptDateTime(editApt.id, d, t);
          }}
          onSaveRep={async(repId)=>{
            if (act?.updateAptRep) await act.updateAptRep(editApt.id, repId);
          }}
        />
      )}

      {/* ═══ Modal عرض كل ملاحظات الموعد ═══ */}
      {showNotesFor && (
        <div onClick={()=>setShowNotesFor(null)} style={{position:"fixed",inset:0,background:"#000c",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16,direction:"rtl",fontFamily:"Cairo,sans-serif"}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"#0a0600",border:"2px solid #fbbf2455",borderRadius:16,padding:18,maxWidth:480,width:"100%",maxHeight:"85vh",overflow:"auto",boxShadow:"0 20px 60px rgba(251,191,36,.25)"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
              <div style={{fontSize:14,fontWeight:900,color:"#fbbf24"}}>📝 ملاحظات الموعد</div>
              <button onClick={()=>setShowNotesFor(null)} style={{background:"transparent",border:"none",color:"#94a3b8",fontSize:18,cursor:"pointer",padding:4}}>✕</button>
            </div>
            <div style={{background:"#15100a",borderRadius:10,padding:10,marginBottom:12,fontSize:11,color:"#c9b37e"}}>
              <div>🦷 <b style={{color:"#f0f9ff"}}>{showNotesFor.drName||"—"}</b></div>
              <div style={{marginTop:3}}>📅 {showNotesFor.date} — 🕐 {fmt12(showNotesFor.time)}</div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {(showNotesFor.notes||[]).map((n,i)=>(
                <div key={i} style={{background:"#1f1a08",border:"1px solid #fbbf2433",borderRadius:10,padding:"10px 12px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:4}}>
                    <div style={{fontSize:10,fontWeight:800,color:"#fbbf24"}}>👤 {n.user||"—"}</div>
                    <div style={{fontSize:9,color:"#7a6841"}}>{n.time||""}</div>
                  </div>
                  <div style={{fontSize:12,color:"#f0f9ff",lineHeight:1.6}}>{n.text||""}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ════ EDIT APT MODAL — تعديل تاريخ/وقت/مندوب الموعد ════ */
function EditAptModal({apt, reps, onClose, onSaveDateTime, onSaveRep}) {
  const [date,setDate] = useState(apt.date || "");
  const [time,setTime] = useState(apt.time || "");
  const [repId,setRepId] = useState(apt.repId || "");
  const [busy,setBusy] = useState(false);
  const [msg,setMsg]   = useState("");

  // فقط مناديب نفس المحافظة
  const myReps = (reps||[]).filter(r =>
    (r.role==="rep" || !r.role) &&
    (!r.govCode || !apt.govCode || r.govCode === apt.govCode)
  );

  const saveAll = async () => {
    setBusy(true); setMsg("");
    try {
      // 1) تاريخ ووقت لو تغيّر
      if (date !== apt.date || time !== apt.time) {
        await onSaveDateTime(date, time);
      }
      // 2) مندوب لو تغيّر
      if (repId !== (apt.repId||"")) {
        await onSaveRep(repId);
      }
      setMsg("✅ حُفظ التعديل");
      setTimeout(()=>onClose(), 800);
    } catch(e) {
      setMsg("⚠️ " + (e?.message || "خطأ"));
    } finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{
      position:"fixed",inset:0,background:"#000c",zIndex:9999,
      display:"flex",alignItems:"center",justifyContent:"center",padding:16,
      direction:"rtl",fontFamily:"Cairo,sans-serif"
    }}>
      <div onClick={e=>e.stopPropagation()} style={{
        background:"#0a0600",border:"2px solid #fbbf2455",borderRadius:16,
        padding:18,maxWidth:440,width:"100%",maxHeight:"90vh",overflow:"auto",
        boxShadow:"0 20px 60px rgba(251,191,36,.25)"
      }}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <div style={{fontSize:14,fontWeight:900,color:"#fbbf24"}}>✏️ تعديل الموعد</div>
          <button onClick={onClose} style={{background:"transparent",border:"none",color:"#94a3b8",fontSize:18,cursor:"pointer",padding:4}}>✕</button>
        </div>

        <div style={{background:"#15100a",borderRadius:10,padding:10,marginBottom:12,fontSize:11,color:"#c9b37e"}}>
          <div>🦷 <b style={{color:"#f0f9ff"}}>{apt.drName||"—"}</b></div>
          <div style={{marginTop:3}}>🏥 {apt.clinic||"—"}</div>
          <div style={{marginTop:3}}>📍 {apt.address||"—"}</div>
          <div style={{marginTop:3}}>🏷 الحالة: <SBadge s={apt.status}/></div>
        </div>

        {/* تاريخ ووقت */}
        <div style={{marginBottom:12}}>
          <div style={{fontSize:11,color:"#fbbf24",fontWeight:800,marginBottom:6}}>📅 التاريخ والوقت</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            <div>
              <div style={{fontSize:9,color:"#7a6841",marginBottom:3}}>التاريخ</div>
              <input type="date" value={date} onChange={e=>setDate(e.target.value)}
                style={{width:"100%",background:"#0d0a00",border:"1px solid #3d2f15",borderRadius:8,padding:"8px 10px",color:"#f0f9ff",fontSize:12,fontFamily:"Cairo,sans-serif",boxSizing:"border-box"}}/>
            </div>
            <div>
              <div style={{fontSize:9,color:"#7a6841",marginBottom:3}}>الوقت</div>
              <input type="time" value={time} onChange={e=>setTime(e.target.value)}
                style={{width:"100%",background:"#0d0a00",border:"1px solid #3d2f15",borderRadius:8,padding:"8px 10px",color:"#f0f9ff",fontSize:12,fontFamily:"Cairo,sans-serif",boxSizing:"border-box"}}/>
            </div>
          </div>
        </div>

        {/* مندوب */}
        <div style={{marginBottom:14}}>
          <div style={{fontSize:11,color:"#fbbf24",fontWeight:800,marginBottom:6}}>👤 المندوب الموجَّه</div>
          <select value={repId} onChange={e=>setRepId(e.target.value)}
            style={{width:"100%",background:"#0d0a00",border:"1px solid #3d2f15",borderRadius:8,padding:"8px 10px",color:"#f0f9ff",fontSize:12,fontFamily:"Cairo,sans-serif",boxSizing:"border-box"}}>
            <option value="">— لم يُحدَّد —</option>
            {myReps.map(r=>(
              <option key={r.id} value={r.id}>{r.name||r.id}</option>
            ))}
          </select>
          {apt.repName && (
            <div style={{fontSize:9,color:"#94a3b8",marginTop:4}}>
              الحالي: <span style={{color:"#22d3a0"}}>{apt.repName}</span>
            </div>
          )}
        </div>

        {msg && (
          <div style={{marginBottom:10,fontSize:11,color:msg.startsWith("✅")?"#22d3a0":"#f87171",textAlign:"center"}}>{msg}</div>
        )}

        <div style={{display:"flex",gap:8}}>
          <button onClick={onClose} disabled={busy}
            style={{flex:1,padding:"10px",borderRadius:10,border:"1px solid #3d2f15",background:"#0d0a00",color:"#94a3b8",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"Cairo,sans-serif"}}>
            إلغاء
          </button>
          <button onClick={saveAll} disabled={busy}
            style={{flex:1,padding:"10px",borderRadius:10,border:"1px solid #fbbf24",background:"linear-gradient(135deg,#fbbf24,#d4af37)",color:"#0a0600",fontSize:12,fontWeight:900,cursor:busy?"wait":"pointer",fontFamily:"Cairo,sans-serif",opacity:busy?.6:1}}>
            {busy?"جاري الحفظ...":"💾 حفظ التعديل"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ════ ADMIN TEAM PAGE ════ */
/* ════ DEVICE TRACK MODAL — خريطة لجهاز محدد ════ */
function DeviceTrackModal({device, onClose}) {
  if (!device) return null;
  const hasLoc = device.lat && device.lng;
  const accuracy = device.accuracy || 0;
  const isExact = device.gpsQuality === "exact";
  return (
    <div onClick={onClose} style={{
      position:"fixed",inset:0,background:"#000c",zIndex:9999,
      display:"flex",alignItems:"center",justifyContent:"center",padding:16
    }}>
      <div onClick={e=>e.stopPropagation()} style={{
        background:"#0a0600",border:"2px solid #d4af3744",borderRadius:16,
        padding:14,maxWidth:520,width:"100%",maxHeight:"90vh",overflow:"auto",
        boxShadow:"0 0 40px #d4af3733"
      }}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
          <div>
            <div style={{fontSize:13,fontWeight:800,color:"#f0c040"}}>📍 تتبّع الجهاز</div>
            <div style={{fontSize:11,color:"#c9b37e",marginTop:2}}>
              {device.deviceName} • {device.userName}
            </div>
          </div>
          <button onClick={onClose} style={{
            background:"#3d2f15",border:"none",borderRadius:8,padding:"6px 12px",
            color:"#f0c040",fontSize:14,cursor:"pointer",fontFamily:"Cairo,sans-serif"
          }}>✕</button>
        </div>

        {hasLoc ? (
          <>
            <LeafletMap
              reps={[{id:device.deviceId, name:device.userName||"المستخدم", lat:device.lat, lng:device.lng, status:"active", area:device.deviceName}]}
              appointments={[]}
              centerOn={{lat:device.lat,lng:device.lng}}
              filterRepId={device.deviceId}
              height={340}
            />
            <div style={{marginTop:10,background:"#15100a",border:"1px solid #3d2f15",borderRadius:10,padding:12,display:"flex",flexDirection:"column",gap:6}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:11}}>
                <span style={{color:"#7a6841"}}>🎯 الدقّة:</span>
                <span style={{color:isExact?"#22d3a0":"#fbbf24",fontWeight:800}}>
                  {accuracy}m {isExact?"(GPS دقيق)":"(تقريبي)"}
                </span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:11}}>
                <span style={{color:"#7a6841"}}>📍 الإحداثيات:</span>
                <span style={{color:"#f0f9ff",fontFamily:"monospace",fontSize:10}}>
                  {device.lat.toFixed(5)}, {device.lng.toFixed(5)}
                </span>
              </div>
              {device._govName && (
                <div style={{display:"flex",justifyContent:"space-between",fontSize:11}}>
                  <span style={{color:"#7a6841"}}>🏛 المحافظة:</span>
                  <span style={{color:"#f472b6"}}>{device._govName}</span>
                </div>
              )}
              <a href={`https://www.google.com/maps?q=${device.lat},${device.lng}`} target="_blank" rel="noreferrer"
                style={{textAlign:"center",padding:"8px",background:"#0d2035",border:"1px solid #38bdf855",borderRadius:8,color:"#38bdf8",fontSize:11,fontWeight:800,textDecoration:"none",marginTop:4}}>
                🗺 افتح في Google Maps
              </a>
            </div>
          </>
        ) : (
          <div style={{padding:30,textAlign:"center",color:"#7a6841",fontSize:12}}>
            ⚠️ لا يوجد موقع محفوظ لهذا الجهاز.<br/>
            انتظر حتى يفتح المستخدم التطبيق ويُرسل GPS.
          </div>
        )}
      </div>
    </div>
  );
}

/* ════ DEVICES MANAGER — إدارة الأجهزة المسجَّلة لكل مستخدم ════ */
function DevicesManagerPage({user, govCode, isAdminAll, act}) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterUser, setFilterUser] = useState("");
  const [filterGov, setFilterGov] = useState("ALL");
  const [tracking, setTracking] = useState(null); // الجهاز الذي نتبعه

  useEffect(()=>{
    if(!DB_READY) return;
    setLoading(true);

    const codes = isAdminAll
      ? Object.keys(GOV_CODES).filter(c=>c!=="ADMIN-00")
      : [govCode];

    if (codes.length === 0 || !codes[0]) { setLoading(false); return; }

    if (isAdminAll) {
      // المدير العام — يحمّل من كل المحافظات (snapshot واحد)
      Promise.all(codes.map(async c=>{
        try {
          const snap = await getDocs(collection(db, `${c}_devices`));
          return snap.docs.map(d=>({...d.data(),_docId:d.id,_govCode:c,_govName:GOV_CODES[c]||c}));
        } catch(e){ return []; }
      })).then(arrs=>{
        setDevices(arrs.flat());
        setLoading(false);
      });
      return;
    }
    // مدير المحافظة — listener حي
    const unsub = onSnapshot(collection(db, `${govCode}_devices`), snap=>{
      setDevices(snap.docs.map(d=>({...d.data(),_docId:d.id,_govCode:govCode,_govName:GOV_CODES[govCode]||""})));
      setLoading(false);
    });
    return ()=>unsub();
  },[govCode, isAdminAll]);

  // فلترة
  let filtered = devices;
  if (isAdminAll && filterGov!=="ALL") filtered = filtered.filter(d=>d._govCode===filterGov);
  if (filterUser) filtered = filtered.filter(d=>d.userId===filterUser);

  // تجميع حسب المستخدم
  const byUser = {};
  filtered.forEach(d=>{
    const uid = d.userId || "unknown";
    if (!byUser[uid]) byUser[uid] = { user: {id:uid, name:d.userName||"بدون اسم", role:d.userRole||""}, devices: [] };
    byUser[uid].devices.push(d);
  });
  const groups = Object.values(byUser).sort((a,b)=>a.user.name.localeCompare(b.user.name,"ar"));

  // قائمة المستخدمين للفلتر
  const users = [...new Map(devices.map(d=>[d.userId,{id:d.userId,name:d.userName||d.userId}])).values()];
  const govList = isAdminAll ? Object.keys(GOV_CODES).filter(c=>c!=="ADMIN-00") : [];

  // الوقت منذ آخر ظهور
  const timeAgo = (ts) => {
    if (!ts) return "—";
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    const diff = Date.now() - date.getTime();
    const min = Math.floor(diff/60000);
    if (min < 1) return "الآن";
    if (min < 60) return `منذ ${min} دقيقة`;
    const hr = Math.floor(min/60);
    if (hr < 24) return `منذ ${hr} ساعة`;
    return `منذ ${Math.floor(hr/24)} يوم`;
  };

  // online = ظهر خلال آخر 2 دقيقة (heartbeat كل دقيقة)
  const isOnline = (ts) => {
    if (!ts) return false;
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return (Date.now() - date.getTime()) < 2*60*1000;
  };
  // idle = ظهر خلال آخر 10 دقائق
  const isIdle = (ts) => {
    if (!ts) return false;
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    const diff = Date.now() - date.getTime();
    return diff >= 2*60*1000 && diff < 10*60*1000;
  };

  const ROLE_LBL = {rep:"مندوب سكنر",sec:"كول سنتر",supervisor:"تيم ليدر",doctor:"طبيب",admin:"مدير"};
  const ROLE_COLOR = {rep:"#60a5fa",sec:"#f472b6",supervisor:"#a78bfa",doctor:"#38bdf8",admin:"#22d3a0"};

  if(loading) return <div style={{padding:40,textAlign:"center",color:"#7a6841"}}>⏳ جاري التحميل...</div>;

  const totalActive = filtered.filter(d=>isOnline(d.lastSeen) && d.active!==false).length;
  const totalDisabled = filtered.filter(d=>d.active===false).length;

  return (
    <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap"}}>
        <div style={{fontSize:14,fontWeight:800,color:"#38bdf8"}}>📱 إدارة الأجهزة</div>
        <PdfBtn onClick={()=>{
          exportToPdf({
            title:"الأجهزة المسجَّلة",
            subtitle:`الإجمالي: ${filtered.length} • نشطة الآن: ${totalActive} • معطّلة: ${totalDisabled}`,
            columns:["المستخدم","الدور","المحافظة","اسم الجهاز","النوع","النظام","آخر ظهور","الحالة"],
            rows: filtered.map(d=>[
              d.userName||"—", ROLE_LBL[d.userRole]||d.userRole||"—",
              d._govName||"—", d.deviceName||"—", d.deviceType||"—",
              d.deviceOS||"—", timeAgo(d.lastSeen),
              d.active===false ? "معطّل" : isOnline(d.lastSeen) ? "🟢 نشط" : "⚪ غير متّصل"
            ])
          });
        }}/>
      </div>

      {/* بطاقات إحصائية */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
        <Card style={{padding:12,textAlign:"center",border:"1px solid #38bdf844"}}>
          <div style={{fontSize:20,fontWeight:900,color:"#38bdf8"}}>{filtered.length}</div>
          <div style={{fontSize:9,color:"#5a4518",marginTop:2}}>📱 إجمالي الأجهزة</div>
        </Card>
        <Card style={{padding:12,textAlign:"center",border:"1px solid #22d3a044"}}>
          <div style={{fontSize:20,fontWeight:900,color:"#22d3a0"}}>{totalActive}</div>
          <div style={{fontSize:9,color:"#5a4518",marginTop:2}}>🟢 نشط الآن</div>
        </Card>
        <Card style={{padding:12,textAlign:"center",border:"1px solid #f8717144"}}>
          <div style={{fontSize:20,fontWeight:900,color:"#f87171"}}>{totalDisabled}</div>
          <div style={{fontSize:9,color:"#5a4518",marginTop:2}}>🚫 معطّل</div>
        </Card>
      </div>

      {/* فلاتر */}
      <Card style={{padding:10,display:"flex",flexDirection:"column",gap:8}}>
        {isAdminAll && (
          <select value={filterGov} onChange={e=>setFilterGov(e.target.value)}
            style={{padding:"8px",borderRadius:8,background:"#0a0600",border:"1px solid #3d2f15",color:"#f0f9ff",fontSize:11,fontFamily:"Cairo,sans-serif"}}>
            <option value="ALL">— كل المحافظات —</option>
            {govList.map(c=><option key={c} value={c}>{GOV_CODES[c]} ({c})</option>)}
          </select>
        )}
        <select value={filterUser} onChange={e=>setFilterUser(e.target.value)}
          style={{padding:"8px",borderRadius:8,background:"#0a0600",border:"1px solid #3d2f15",color:"#f0f9ff",fontSize:11,fontFamily:"Cairo,sans-serif"}}>
          <option value="">— كل المستخدمين ({users.length}) —</option>
          {users.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </Card>

      {/* قائمة الأجهزة مجموعة بالمستخدم */}
      {groups.length===0 && (
        <Card style={{padding:30,textAlign:"center",color:"#5a4518"}}>
          لا توجد أجهزة مسجَّلة بعد. تظهر هنا تلقائياً عند فتح المستخدمين للتطبيق.
        </Card>
      )}

      {groups.map(g => (
        <Card key={g.user.id} style={{padding:14,border:`1px solid ${ROLE_COLOR[g.user.role]||"#3d2f15"}33`}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10,paddingBottom:10,borderBottom:"1px solid #3d2f15"}}>
            <div style={{width:36,height:36,borderRadius:10,background:`${ROLE_COLOR[g.user.role]||"#60a5fa"}22`,
              display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:14,fontWeight:800,color:ROLE_COLOR[g.user.role]||"#60a5fa"}}>
              {(g.user.name||"?")[0]}
            </div>
            <div style={{flex:1}}>
              <div style={{fontSize:12,fontWeight:800,color:"#f0f9ff"}}>{g.user.name}</div>
              <div style={{fontSize:9,color:"#7a6841"}}>{ROLE_LBL[g.user.role]||g.user.role} • {g.devices.length} جهاز</div>
            </div>
            {g.devices.filter(d=>isOnline(d.lastSeen)&&d.active!==false).length>1 && (
              <div style={{fontSize:9,fontWeight:800,padding:"3px 8px",borderRadius:12,background:"#fbbf2422",color:"#fbbf24"}}>
                ⚠️ {g.devices.filter(d=>isOnline(d.lastSeen)&&d.active!==false).length} متصلة
              </div>
            )}
          </div>

          {g.devices.sort((a,b)=>{
            const at = a.lastSeen?.toDate?.()?.getTime()||0;
            const bt = b.lastSeen?.toDate?.()?.getTime()||0;
            return bt-at;
          }).map(d => {
            const online = isOnline(d.lastSeen);
            const idle = !online && isIdle(d.lastSeen);
            const disabled = d.active === false;
            const icon = d.deviceType==="mobile" ? "📱" : d.deviceType==="tablet" ? "📱" : "🖥️";
            const stateColor = disabled?"#f87171":online?"#22d3a0":idle?"#fbbf24":"#7a6841";
            const stateBg    = disabled?"#1a0505":online?"#0a2e1e22":idle?"#2a1f0022":"#15100a";
            const stateBorder= disabled?"#f8717155":online?"#22d3a044":idle?"#fbbf2444":"#3d2f15";
            return (
              <div key={d._docId} style={{
                display:"flex",alignItems:"center",gap:10,padding:"10px",
                background: stateBg,
                borderRadius:8,marginBottom:6,
                border: `1px solid ${stateBorder}`,
                opacity: disabled ? 0.6 : 1
              }}>
                <div style={{fontSize:24}}>{icon}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:11,fontWeight:800,color:"#f0f9ff",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                    {d.deviceName || "جهاز غير معروف"}
                  </div>
                  <div style={{fontSize:9,color:"#7a6841",marginTop:2}}>
                    {d.deviceOS || ""} {d._govName?` • 📍 ${d._govName}`:""}
                  </div>
                  <div style={{fontSize:9,color:stateColor,marginTop:2,fontWeight:700}}>
                    {disabled ? "🚫 مُعطّل" :
                     online   ? "🟢 نشط الآن" :
                     idle     ? "🟡 خامل (آخر ظهور " + timeAgo(d.lastSeen) + ")" :
                                "🔴 غير متّصل (" + timeAgo(d.lastSeen) + ")"}
                  </div>
                  {d.lat && d.lng && (
                    <div style={{fontSize:9,marginTop:3,color:d.gpsQuality==="exact"?"#22d3a0":"#fbbf24"}}>
                      📡 {d.gpsQuality==="exact"?"GPS دقيق":"تقريبي"} ({d.accuracy||"?"}m)
                    </div>
                  )}
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:4,flexShrink:0}}>
                  {d.lat && d.lng && (
                    <button onClick={()=>setTracking(d)}
                      style={{background:"#0d2035",border:"1px solid #38bdf8",borderRadius:6,padding:"4px 10px",color:"#38bdf8",fontSize:9,fontWeight:800,cursor:"pointer",fontFamily:"Cairo,sans-serif",whiteSpace:"nowrap"}}>
                      📍 تتبّع
                    </button>
                  )}
                  {!disabled && (
                    <button onClick={()=>{
                      if(window.confirm(`إخراج هذا الجهاز (${d.deviceName})؟ سيتم تسجيل خروجه فوراً.`))
                        act.disableDevice(d);
                    }}
                      style={{background:"#2e0a0a",border:"1px solid #f87171",borderRadius:6,padding:"4px 10px",color:"#f87171",fontSize:9,fontWeight:800,cursor:"pointer",fontFamily:"Cairo,sans-serif",whiteSpace:"nowrap"}}>
                      🚪 إخراج
                    </button>
                  )}
                  <button onClick={()=>{
                    if(window.confirm(`حذف سجل هذا الجهاز نهائياً؟`))
                      act.deleteDeviceRecord(d);
                  }}
                    style={{background:"#15100a",border:"1px solid #5a4518",borderRadius:6,padding:"4px 10px",color:"#7a6841",fontSize:9,cursor:"pointer",fontFamily:"Cairo,sans-serif",whiteSpace:"nowrap"}}>
                    🗑 حذف
                  </button>
                </div>
              </div>
            );
          })}
        </Card>
      ))}

      {tracking && <DeviceTrackModal device={tracking} onClose={()=>setTracking(null)}/>}
    </div>
  );
}

function AdminTeamPage({user,govCode,apts,isAdminAll,reps,act}) {
  const [members,setMembers]   = useState([]);
  const [loading,setLoading]   = useState(true);
  const [busy,setBusy]         = useState({});
  const [filterGov,setFilterGov] = useState("ALL");

  // Load all members — single gov OR all govs (live listeners)
  useEffect(()=>{
    if(!DB_READY) return;
    setLoading(true);
    if (isAdminAll) {
      // ═══ المدير العام — listeners حيّة لكل المحافظات ═══
      // (التحديث فوري عند تسجيل أيّ حساب جديد بدل ما يحتاج إعادة تحميل)
      const allCodes = Object.keys(GOV_CODES).filter(c=>c!=="ADMIN-00");
      const byGov = {};
      const flush = () => {
        const all = [];
        for (const c of allCodes) {
          if (byGov[c]) all.push(...byGov[c]);
        }
        setMembers(all);
        setLoading(false);
      };
      const unsubs = allCodes.map(c => {
        return onSnapshot(
          collection(db, `${c}_users`),
          snap => {
            byGov[c] = snap.docs.map(d => ({
              ...d.data(),
              _docId: d.id,
              _govCode: c,
              _govName: GOV_CODES[c] || c,
            }));
            flush();
          },
          err => console.warn(`team page ${c}:`, err.message)
        );
      });
      return () => unsubs.forEach(u => { try { u(); } catch(e) {} });
    }
    if(!govCode){ setLoading(false); return; }
    const unsub = onSnapshot(collection(db,`${govCode}_users`), snap=>{
      setMembers(snap.docs.map(d=>({...d.data(),_docId:d.id,_govCode:govCode,_govName:GOV_CODES[govCode]||govCode})));
      setLoading(false);
    });
    return()=>unsub();
  },[govCode,isAdminAll]);

  const approve = async(m) => {
    const gc = m._govCode || govCode;
    setBusy(b=>({...b,[m._docId]:true}));
    await updateDoc(doc(db,`${gc}_users`,m._docId),{approved:true});
    setMembers(prev=>prev.map(x=>x._docId===m._docId&&x._govCode===gc?{...x,approved:true}:x));
    setBusy(b=>({...b,[m._docId]:false}));
  };
  const remove = async(m) => {
    const gc = m._govCode || govCode;
    if (isAdminAll && act && act.adminDeleteUser) {
      // حذف شامل: المستخدم + كل مواعيده + tokens
      if (!window.confirm(`حذف ${m.name} نهائياً مع كل مواعيده؟ لا يمكن التراجع.`)) return;
      await act.adminDeleteUser({id:m.id||m._docId, govCode:gc});
      await act.adminDeleteRepApts({id:m.id||m._docId, govCode:gc});
      setMembers(prev=>prev.filter(x=>!(x._docId===m._docId && x._govCode===gc)));
      return;
    }
    await deleteDoc(doc(db,`${gc}_users`,m._docId));
    setMembers(prev=>prev.filter(x=>!(x._docId===m._docId && x._govCode===gc)));
  };
  const removeApts = async(m) => {
    const gc = m._govCode || govCode;
    if (!window.confirm(`حذف كل مواعيد ${m.name} (${m._govName||""})؟`)) return;
    if (act && act.adminDeleteRepApts) {
      await act.adminDeleteRepApts({id:m.id||m._docId, govCode:gc});
    }
  };

  const filteredMembers = (isAdminAll && filterGov!=="ALL") ? members.filter(m=>m._govCode===filterGov) : members;
  const pending  = filteredMembers.filter(m=>!m.approved);
  const approved = filteredMembers.filter(m=>m.approved);
  const ROLE_COLOR = {rep:"#60a5fa",sec:"#f472b6",supervisor:"#a78bfa",doctor:"#38bdf8",admin:"#22d3a0"};
  const ROLE_LBL   = {rep:"مندوب سكنر",sec:"كول سنتر",supervisor:"تيم ليدر",doctor:"طبيب",admin:"مدير"};
  const govList = isAdminAll ? Object.keys(GOV_CODES).filter(c=>c!=="ADMIN-00") : [];

  if(loading) return <div style={{padding:40,textAlign:"center",color:"#334155"}}>⏳ جاري التحميل...</div>;

  return (
    <div style={{padding:16,display:"flex",flexDirection:"column",gap:14}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap"}}>
        <div style={{fontSize:14,fontWeight:800,color:"#f0f9ff"}}>👥 {isAdminAll?"إدارة كل الفرق (المدير العام)":"إدارة الفريق"}</div>
        <PdfBtn onClick={()=>{
          exportToPdf({
            title: isAdminAll?"كل الأعضاء — جميع المحافظات":`أعضاء ${GOV_CODES[govCode]||govCode}`,
            subtitle: isAdminAll && filterGov!=="ALL" ? `المحافظة: ${GOV_CODES[filterGov]||filterGov}` : "",
            columns: isAdminAll
              ? ["الاسم","الدور","الهاتف","المنطقة","المحافظة","الحالة"]
              : ["الاسم","الدور","الهاتف","المنطقة","الحالة"],
            rows: filteredMembers.map(m=>{
              const base = [m.name||"—", ROLE_LBL[m.role]||m.role, m.phone||"—", m.area||"—"];
              if (isAdminAll) base.push(m._govName||"—");
              base.push(m.approved?"موافق ✓":"بانتظار الموافقة ⏳");
              return base;
            })
          });
        }}/>
      </div>
      <div style={{fontSize:10,color:"#334155",background:"#15100a",borderRadius:10,padding:"8px 12px",border:"1px solid #3d2f15"}}>
        {isAdminAll ? `🌐 جميع المحافظات — ${members.length} عضو` : `📍 ${GOV_CODES[govCode]||""}`}
      </div>
      {isAdminAll && (
        <select value={filterGov} onChange={e=>setFilterGov(e.target.value)}
          style={{padding:"8px 12px",borderRadius:10,background:"#15100a",color:"#f0c040",border:"1px solid #3d2f15",fontSize:11,fontFamily:"Cairo,sans-serif"}}>
          <option value="ALL">— كل المحافظات —</option>
          {govList.map(c=><option key={c} value={c}>{GOV_CODES[c]} ({c})</option>)}
        </select>
      )}

      {/* Pending approval */}
      {pending.length>0&&(
        <div>
          <div style={{fontSize:12,fontWeight:800,color:"#fbbf24",marginBottom:10}}>
            ⏳ بانتظار الموافقة ({pending.length})
          </div>
          {pending.map((m,i)=>(
            <Card key={i} style={{padding:14,marginBottom:8,border:"1px solid #fbbf2444"}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:40,height:40,borderRadius:12,
                  background:`${ROLE_COLOR[m.role]||"#60a5fa"}22`,
                  display:"flex",alignItems:"center",justifyContent:"center",
                  fontSize:16,fontWeight:800,color:ROLE_COLOR[m.role]||"#60a5fa",flexShrink:0}}>
                  {(m.name||"?")[0]}
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:800,color:"#f0f9ff"}}>{m.name}</div>
                  <div style={{fontSize:10,color:"#334155"}}>{ROLE_LBL[m.role]||m.role} • {m.phone}</div>
                  {m.area&&<div style={{fontSize:9,color:"#5a4518"}}>{m.area}</div>}
                  {isAdminAll && m._govName && <div style={{fontSize:9,color:"#f0c040",marginTop:2}}>📍 {m._govName}</div>}
                </div>
                <div style={{display:"flex",gap:8,flexShrink:0}}>
                  <button onClick={()=>approve(m)} disabled={busy[m._docId]}
                    style={{padding:"7px 14px",background:"#0a2e1e",border:"1px solid #22d3a044",
                      borderRadius:8,color:"#22d3a0",fontSize:11,fontWeight:800,
                      cursor:"pointer",fontFamily:"Cairo,sans-serif"}}>
                    {busy[m._docId]?"...":"✓ موافقة"}
                  </button>
                  <button onClick={()=>remove(m)}
                    style={{padding:"7px 12px",background:"#2e0a0a",border:"1px solid #f8717133",
                      borderRadius:8,color:"#f87171",fontSize:11,fontWeight:800,
                      cursor:"pointer",fontFamily:"Cairo,sans-serif"}}>✕</button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      {pending.length===0&&(
        <div style={{background:"#0a2e1e",border:"1px solid #22d3a033",borderRadius:10,
          padding:"10px 14px",fontSize:11,fontWeight:700,color:"#22d3a0",textAlign:"center"}}>
          ✅ لا يوجد طلبات معلّقة
        </div>
      )}

      {/* Approved members */}
      {approved.length>0&&(
        <div>
          <div style={{fontSize:12,fontWeight:800,color:"#94a3b8",marginBottom:10}}>
            الأعضاء النشطون ({approved.length})
          </div>
          {approved.map((m,i)=>(
            <Card key={i} style={{padding:12,marginBottom:8}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:38,height:38,borderRadius:12,
                  background:`${ROLE_COLOR[m.role]||"#60a5fa"}22`,
                  display:"flex",alignItems:"center",justifyContent:"center",
                  fontSize:15,fontWeight:800,color:ROLE_COLOR[m.role]||"#60a5fa",flexShrink:0}}>
                  {(m.name||"?")[0]}
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:12,fontWeight:800,color:"#f0f9ff"}}>{m.name}</div>
                  <div style={{fontSize:10,color:"#334155"}}>{ROLE_LBL[m.role]||m.role} • {m.phone}</div>
                  {isAdminAll && m._govName && <div style={{fontSize:9,color:"#f0c040",marginTop:2}}>📍 {m._govName}</div>}
                </div>
                <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",justifyContent:"flex-end"}}>
                  <div style={{fontSize:9,fontWeight:700,padding:"3px 8px",borderRadius:20,
                    background:`${ROLE_COLOR[m.role]||"#60a5fa"}22`,
                    color:ROLE_COLOR[m.role]||"#60a5fa"}}>
                    {ROLE_LBL[m.role]||m.role}
                  </div>
                  {isAdminAll && m.role==="rep" && (
                    <button onClick={()=>removeApts(m)}
                      style={{background:"#2a1f00",border:"1px solid #fbbf2455",borderRadius:6,
                        padding:"3px 8px",color:"#fbbf24",fontSize:9,cursor:"pointer",fontFamily:"Cairo,sans-serif"}}>
                      🗑 مواعيد
                    </button>
                  )}
                  <button onClick={()=>remove(m)}
                    style={{background:"#2e0a0a",border:"1px solid #f8717133",borderRadius:6,
                      padding:"3px 8px",color:"#f87171",fontSize:9,cursor:"pointer",fontFamily:"Cairo,sans-serif"}}>
                    حذف
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {members.length===0&&(
        <div style={{textAlign:"center",padding:40,color:"#334155",fontSize:12}}>
          لا يوجد أعضاء مسجلون بعد
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════
//  TEAM LEADER APP — تيم ليدر مع قسم زيارة الأطباء
// ═══════════════════════════════════════════════
function TeamLeaderApp({user,db,act,notifs,unread,onLogout,onMarkAll,notifPerm,requestNotifs}) {
  const [pg,setPg]=useState("home");
  const [no,setNo]=useState(false);
  const [gps,setGps]=useState(null);
  const [myCoord,setMyCoord]=useState(null);

  // فتح لوحة الإشعارات تلقائياً عند الضغط على push notification
  useEffect(()=>{
    const open = () => setNo(true);
    window.addEventListener("britao:openNotifs", open);
    return () => window.removeEventListener("britao:openNotifs", open);
  },[]);

  // ═══ GPS — يطلب الإذن ويُتابع الموقع للتيم ليدر ═══
  useEffect(()=>{
    if(!navigator.geolocation){setGps("غير مدعوم");return;}
    const onPos = (p)=>{
      setGps(`${p.coords.latitude.toFixed(4)}, ${p.coords.longitude.toFixed(4)}`);
      setMyCoord({lat:p.coords.latitude, lng:p.coords.longitude});
    };
    navigator.geolocation.getCurrentPosition(onPos,()=>setGps("لم يُسمح بالوصول"),{enableHighAccuracy:true,timeout:10000});
    const wid = navigator.geolocation.watchPosition(onPos,()=>{},{enableHighAccuracy:true,maximumAge:15000});
    return ()=>{ try{ navigator.geolocation.clearWatch(wid);}catch(e){} };
  },[]);

  const [showForm,setShowForm]=useState(false);
  const sameGov = (r) => !r.govCode || r.govCode === user.govCode;
  // ═══ خصوصية التيم ليدر: يرى زياراته الخاصة فقط (مش زيارات تيم ليدرز آخرين) ═══
  const myVisits = (db.visits||[]).filter(v => v.leaderId === user.id);

  const NAV=[
    {id:"home",icon:"⬡",label:"الرئيسية",c:"#a78bfa"},
    {id:"visits",icon:"🦷",label:"زيارة الأطباء",c:"#a78bfa"},
    {id:"apts",icon:"📋",label:"جدول المناديب",c:"#a78bfa"},
    {id:"bookings",icon:"📨",label:"طلبات الحجز",c:"#a78bfa"},
    {id:"map",icon:"🗺",label:"الخريطة",c:"#a78bfa"},
    {id:"mymap",icon:"📍",label:"موقعي",c:"#a78bfa"},
  ];

  // كل مواعيد المناديب في محافظة التيم ليدر
  const govApts = (db.appointments||[]).filter(a => sameGov(a) && (!a.govCode || a.govCode === user.govCode));

  const REASON_OPTIONS = ["طبيب جديد","إعادة طبيب منقطع","تعديل موقف (مصالحة)","سبب تسويقي","استحصال نقود","أخرى"];

  const pending = myVisits.filter(v=>v.status==="في الانتظار");
  const active  = myVisits.filter(v=>["في الطريق","وصلت","جاري عمل الزيارة"].includes(v.status));
  const ended   = myVisits.filter(v=>v.status==="انتهت الزيارة");

  // ═══ طلبات الحجز للتيم ليدر ═══
  const myReqs       = (db.bookingReqs||[]).filter(r => r.fromUserId === user.id);
  const tlPendingReqs  = myReqs.filter(r=>r.status==="pending");
  const tlRejectedReqs = myReqs.filter(r=>r.status==="rejected");
  const tlApprovedReqs = myReqs.filter(r=>r.status==="approved");
  const [tlShowBookingForm,setTlShowBookingForm] = useState(false);
  const [tlEditingReq,setTlEditingReq] = useState(null);

  const rp = () => {
    if (pg === "home") return (
      <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{background:"linear-gradient(160deg,#1f1538,#0d0a1f)",borderRadius:18,padding:18}}>
          <div style={{fontSize:10,color:"#a78bfa",marginBottom:3}}>تيم ليدر</div>
          <div style={{fontSize:17,fontWeight:900,color:"#f0f9ff"}}>{user.fullName||user.name}</div>
          {user.govName && <div style={{fontSize:11,color:"#d4af37",fontWeight:800,marginTop:4}}>📍 {user.govName}{user.area?` — ${user.area}`:""}</div>}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
          {[
            {l:"في الانتظار",v:pending.length,c:"#fbbf24"},
            {l:"نشطة",v:active.length,c:"#22d3a0"},
            {l:"منتهية",v:ended.length,c:"#a78bfa"},
          ].map((s,i)=>(
            <Card key={i} style={{padding:"10px 8px",textAlign:"center",border:`1px solid ${s.c}22`}}>
              <div style={{fontSize:18,fontWeight:900,color:s.c}}>{s.v}</div>
              <div style={{fontSize:9,color:"#5a4518",marginTop:2}}>{s.l}</div>
            </Card>
          ))}
        </div>
        <Btn color="#a78bfa" onClick={()=>{setPg("visits");setShowForm(true);}} style={{width:"100%"}}>➕ إنشاء زيارة جديدة</Btn>
        {active.length>0 && (
          <div>
            <div style={{fontSize:11,fontWeight:800,color:"#22d3a0",marginBottom:8}}>🔴 نشطة الآن</div>
            {active.map(v => <VisitCard key={v.id} v={v} act={act} user={user}/>)}
          </div>
        )}
      </div>
    );
    if (pg === "visits") return (
      <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontSize:14,fontWeight:800,color:"#f0f9ff"}}>🦷 زيارات الأطباء ({myVisits.length})</div>
          <Btn small color="#a78bfa" onClick={()=>setShowForm(true)}>➕ زيارة جديدة</Btn>
        </div>
        {showForm && <NewVisitForm user={user} db={db} act={act} onClose={()=>setShowForm(false)} reasonOptions={REASON_OPTIONS}/>}
        {pending.length>0 && (<div><div style={{fontSize:11,fontWeight:800,color:"#fbbf24",margin:"6px 0"}}>⏳ في الانتظار</div>
          {pending.map(v=><VisitCard key={v.id} v={v} act={act} user={user}/>)}</div>)}
        {active.length>0 && (<div><div style={{fontSize:11,fontWeight:800,color:"#22d3a0",margin:"6px 0"}}>🔴 نشطة</div>
          {active.map(v=><VisitCard key={v.id} v={v} act={act} user={user}/>)}</div>)}
        {ended.length>0 && (<div><div style={{fontSize:11,fontWeight:800,color:"#a78bfa",margin:"6px 0"}}>✅ منتهية</div>
          {ended.map(v=><VisitCard key={v.id} v={v} act={act} user={user}/>)}</div>)}
        {myVisits.length===0 && !showForm && (
          <div style={{textAlign:"center",padding:40,color:"#5a4518",fontSize:12}}>لا توجد زيارات بعد. اضغط "زيارة جديدة" للبدء.</div>
        )}
      </div>
    );
    if (pg === "apts") {
      // جدول كل مواعيد المناديب في محافظة التيم ليدر (للمتابعة فقط)
      return <ScheduleTable apts={govApts} title={`📋 جدول مواعيد مناديب ${db.govName||""}`} showGov={false} reps={db.reps||[]}/>;
    }
    if (pg === "bookings") return (
      <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontSize:14,fontWeight:800,color:"#f0f9ff"}}>📨 طلبات الحجز</div>
          <Btn small color="#38bdf8" onClick={()=>{setTlEditingReq(null); setTlShowBookingForm(true);}}>+ طلب جديد</Btn>
        </div>

        {tlShowBookingForm && <BookingRequestForm user={user} db={db} act={act} existingReq={tlEditingReq} onClose={()=>{setTlShowBookingForm(false); setTlEditingReq(null);}}/>}

        {tlPendingReqs.length>0 && <div style={{fontSize:11,color:"#fbbf24"}}>⏳ في الانتظار ({tlPendingReqs.length})</div>}
        {tlPendingReqs.map(r => (
          <Card key={r.id} style={{padding:12,border:"1px solid #fbbf2455"}}>
            <div style={{fontSize:12,fontWeight:800,color:"#f0f9ff"}}>🦷 {r.drName}</div>
            <div style={{fontSize:10,color:"#94a3b8",marginTop:4}}>📅 {r.date} {fmt12(r.time)}</div>
            {r.notes && <div style={{fontSize:10,color:"#c9b37e",marginTop:4}}>📝 {r.notes}</div>}
            <div style={{fontSize:9,color:"#fbbf24",marginTop:6,fontWeight:700}}>⏳ بانتظار رد الكول سنتر</div>
          </Card>
        ))}

        {tlRejectedReqs.length>0 && <div style={{fontSize:11,color:"#f87171",marginTop:12}}>⚠️ مرفوضة - تحتاج تعديل ({tlRejectedReqs.length})</div>}
        {tlRejectedReqs.map(r => (
          <Card key={r.id} style={{padding:12,border:"2px solid #f87171",background:"#2a141488"}}>
            <div style={{fontSize:12,fontWeight:800,color:"#f0f9ff"}}>🦷 {r.drName}</div>
            <div style={{fontSize:10,color:"#94a3b8",marginTop:4}}>📅 {r.date} {fmt12(r.time)}</div>
            {r.rejectReason && <div style={{fontSize:10,color:"#fbbf24",marginTop:6,padding:8,background:"#1a0a0a",borderRadius:6}}>❌ {r.rejectReason}</div>}
            <Btn small color="#fbbf24" style={{marginTop:8,width:"100%"}}
              onClick={()=>{setTlEditingReq(r); setTlShowBookingForm(true);}}>
              ✏️ تعديل وإعادة الإرسال
            </Btn>
          </Card>
        ))}

        {tlApprovedReqs.length>0 && <div style={{fontSize:11,color:"#22d3a0",marginTop:12}}>✅ موافَق عليها ({tlApprovedReqs.length})</div>}
        {tlApprovedReqs.slice(0,10).map(r => (
          <Card key={r.id} style={{padding:12,border:"1px solid #22d3a055"}}>
            <div style={{fontSize:12,fontWeight:800,color:"#f0f9ff"}}>🦷 {r.drName}</div>
            <div style={{fontSize:10,color:"#94a3b8",marginTop:4}}>📅 {r.date} {fmt12(r.time)}</div>
            <div style={{fontSize:9,color:"#22d3a0",marginTop:6,fontWeight:700}}>✅ تم تثبيت الحجز</div>
          </Card>
        ))}

        {myReqs.length===0 && (
          <Card style={{padding:30,textAlign:"center",color:"#334155",fontSize:12}}>
            لا توجد طلبات حجز بعد — اضغط "+ طلب جديد"
          </Card>
        )}
      </div>
    );
    if (pg === "map") return (
      <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{fontSize:14,fontWeight:800,color:"#f0f9ff"}}>🗺 خريطة المحافظة</div>
        <div style={{background:"#15100a",borderRadius:10,padding:10,fontSize:10,color:"#c9b37e",lineHeight:1.7}}>
          ⚫ زيارات الفريق (تيم ليدر + مندوب تسويق) — يظهر بعد إنهاء الزيارة
        </div>
        <LeafletMap
          reps={db.reps.filter(sameGov)}
          appointments={db.appointments.filter(sameGov)}
          visitLocs={(db.visits||[]).filter(v => sameGov(v) && v.lat && v.lng)}
          height={400}
          govCode={user.govCode}/>
      </div>
    );

    // ═══ صفحة موقعي — للتيم ليدر ═══
    if (pg === "mymap") return (
      <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{fontSize:14,fontWeight:800,color:"#f0f9ff"}}>📍 موقعي الحقيقي</div>
        {gps && (
          <Card style={{padding:12}}>
            <div style={{fontSize:10,color:"#22d3a0",marginBottom:2}}>إحداثيات GPS الحقيقية</div>
            <div style={{fontSize:12,color:"#f0f9ff",fontWeight:700}}>{gps}</div>
            <div style={{fontSize:9,color:"#5a4518",marginTop:2}}>يتحدّث تلقائياً</div>
          </Card>
        )}
        {!gps && (
          <Card style={{padding:14,border:"1px solid #fbbf2444",background:"#1f1a08"}}>
            <div style={{fontSize:11,color:"#fbbf24",fontWeight:700,lineHeight:1.6}}>
              ⏳ جاري طلب إذن GPS...<br/>
              <span style={{color:"#c9b37e",fontWeight:500,fontSize:10}}>يجب السماح للتطبيق بالوصول لموقعك من إعدادات الجوال</span>
            </div>
          </Card>
        )}
        <LeafletMap reps={(db.reps||[]).filter(r=>r.id===user.id)} appointments={[]} height={360} filterRepId={user.id} centerOn={myCoord} govCode={user.govCode}/>
      </div>
    );

    return null;
  };

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100dvh",background:"transparent",fontFamily:"'Tajawal','Inter',sans-serif",direction:"rtl",maxWidth:"min(100%, 600px)",margin:"0 auto",width:"100%"}}>
      <style>{G}</style>
      <TopBar user={user} color="#a78bfa" nc={unread} onNotif={()=>{setNo(true); if(unread>0) onMarkAll();}} onLogout={onLogout} title="تيم ليدر"/>
      <NotifBanner notifPerm={notifPerm} requestNotifs={requestNotifs}/>
      <div style={{flex:1,overflowY:"auto"}}>{rp()}</div>
      <BNav items={NAV} active={pg} onChange={setPg}/>
      {no&&<NPanel notifs={notifs} color="#a78bfa" onClose={()=>setNo(false)} onMarkAll={onMarkAll}/>}
    </div>
  );
}

// ───── Visit form ─────
function NewVisitForm({user,db,act,onClose,reasonOptions}) {
  const [drMode,setDrMode] = useState("existing"); // existing | new
  const [selectedDr,setSelectedDr] = useState(""); // مفتاح الطبيب المختار من القائمة
  const [form,setForm] = useState({drName:"",clinic:"",address:"",reason:reasonOptions[0],reasonOther:"",collectedAmount:"",date:"",time:"",lastVisitDate:"",lat:null,lng:null});
  const [busy,setBusy] = useState(false);
  const [msg,setMsg]   = useState("");
  const [showSuggestions,setShowSuggestions] = useState(false); // إظهار قائمة الاقتراحات
  const INP = {width:"100%",background:"#0d0a00",border:"1px solid #3d2f15",borderRadius:10,padding:"10px",color:"#f0f9ff",fontSize:12,fontFamily:"Cairo,sans-serif",direction:"rtl",boxSizing:"border-box"};
  const LBL = {fontSize:10,color:"#c9b37e",marginBottom:4,fontWeight:700};

  // ── بناء قائمة الأطباء من _doctors و _doctor_locations لمحافظة المستخدم ──
  const myGov = user.govCode;
  const drsList = (db?.doctors||[]).filter(d => !d.govCode || d.govCode===myGov);
  const docLocsList = (db?.docLocs||[]).filter(l => !l.govCode || l.govCode===myGov);

  // دمج بدون تكرار — تطبيع قوي لأسماء الأطباء
  // يزيل: "د." "د " "دكتور " "دكتوره " والهمزات والتشكيل والمسافات الزائدة
  const norm = s => {
    if (!s) return "";
    let x = s.toString().trim();
    // إزالة التشكيل
    x = x.replace(/[\u064B-\u0652\u0670\u0640]/g,"");
    // توحيد الهمزات والألف
    x = x.replace(/[إأآا]/g,"ا").replace(/[ىي]/g,"ي").replace(/ة/g,"ه");
    // إزالة البادئات (دكتور / دكتوره / د.)
    x = x.replace(/^د\s*\.?\s*/,"").replace(/^دكتور(ه)?\s+/,"").replace(/^الدكتور(ه)?\s+/,"");
    // توحيد المسافات
    x = x.replace(/\s+/g," ").trim().toLowerCase();
    return x;
  };
  const merged = new Map();
  drsList.forEach(d => {
    const key = norm(d.name);
    if (!key) return;
    merged.set(key, {
      key,
      drName: d.name||"",
      clinic: d.clinic||"",
      lat: d.lat||null, lng: d.lng||null,
      lastVisitDate: "",
      source: "doctors",
    });
  });
  docLocsList.forEach(l => {
    const key = norm(l.drName);
    if (!key) return;
    const ex = merged.get(key);
    const item = {
      key,
      drName: (ex?.drName && ex.drName.length>=(l.drName||"").length) ? ex.drName : (l.drName||ex?.drName||""),
      clinic: l.clinic||(ex?.clinic||""),
      lat: l.lat||(ex?.lat||null), lng: l.lng||(ex?.lng||null),
      lastVisitDate: l.lastVisitDate||(ex?.lastVisitDate||""),
      source: ex ? "both" : "locations",
      visitsCount: l.visitsCount||1,
    };
    merged.set(key, item);
  });
  const allDoctors = Array.from(merged.values()).sort((a,b)=>a.drName.localeCompare(b.drName,"ar"));

  // عند اختيار طبيب من القائمة → ملء الحقول
  const onSelectDoctor = (key) => {
    setSelectedDr(key);
    if (!key) {
      setForm(f=>({...f, drName:"", clinic:"", address:"", lat:null, lng:null, lastVisitDate:""}));
      return;
    }
    const d = allDoctors.find(x=>x.key===key);
    if (!d) return;
    // اجلب العنوان من قاعدة بيانات الأطباء (drsList) أو docLocs
    const fullDr = drsList.find(x => (x.name||"") === d.drName) ||
                   docLocsList.find(x => (x.drName||"") === d.drName);
    setForm(f=>({
      ...f,
      drName: d.drName,
      clinic: d.clinic,
      address: fullDr?.address || f.address || "",
      lat: d.lat, lng: d.lng,
      lastVisitDate: d.lastVisitDate || f.lastVisitDate,
    }));
  };

  const save = async (immediate) => {
    // ═══ قراءة آخر القيم من الـ DOM (تحايل على lag الـ IME العربي على Android) ═══
    try { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); } catch(e){}
    await new Promise(r => setTimeout(r, 60));
    const readField = (placeholder) => {
      const el = document.querySelector(`input[placeholder="${placeholder}"]`);
      return el ? el.value : "";
    };
    let liveDrName    = readField("د. ...")  || form.drName;
    const liveAddress = readField("شارع، حي، بناية...") || form.address;
    // اسم العيادة لا placeholder له — استخدم قيمة الـ form كما هي بعد blur
    const liveClinic = form.clinic;
    const liveReasonOther = form.reasonOther;

    if (!liveDrName.trim()) { setMsg("اختر طبيباً أو أدخل اسماً جديداً"); return; }
    if (!liveClinic.trim()) { setMsg("أدخل اسم العيادة"); return; }
    if (form.reason==="أخرى" && !liveReasonOther.trim()) { setMsg("اكتب تعليق سبب الزيارة"); return; }
    if (form.reason==="استحصال نقود" && (!form.collectedAmount || parseFloat(form.collectedAmount)<=0)) { setMsg("أدخل المبلغ المُستحصَل"); return; }

    // ═══ التحقق من تشابه الاسم مع طبيب موجود (في وضع طبيب جديد فقط) ═══
    // مطابقة ذكيّة: تتحمّل الأخطاء الإملائية + توحيد الهمزات + Levenshtein
    if (drMode === "new") {
      const typedWords = drCleanWords(liveDrName);
      if (typedWords.length > 0 && typedWords[0].length >= 2) {
        // كل كلمة مُدخَلة يجب أن تطابق على الأقل كلمة واحدة في اسم الطبيب
        const matches = allDoctors.filter(d => {
          const drWords = drCleanWords(d.drName);
          return typedWords.every(typed => drWords.some(drWord => fuzzyArabicMatch(typed, drWord)));
        });
        if (matches.length > 0) {
          // اعرض رسالة تأكيد للمستخدم
          const matchedNames = matches.slice(0, 5).map(d =>
            `🦷 ${d.drName}${d.clinic ? ` — ${d.clinic}` : ""}`
          ).join("\n");
          const moreCount = matches.length > 5 ? `\n... و ${matches.length - 5} طبيب آخر` : "";
          const confirmMsg =
            `⚠️ يوجد طبيب باسم مشابه مسجَّل سابقاً:\n\n` +
            `${matchedNames}${moreCount}\n\n` +
            `❓ هل هو نفس الطبيب؟\n\n` +
            `• اضغط "موافق/OK" → سيُستخدم الاسم المحفوظ سابقاً\n` +
            `• اضغط "إلغاء/Cancel" → سيُحفظ الاسم الجديد كما هو`;

          const useExisting = window.confirm(confirmMsg);
          if (useExisting) {
            // استخدم اسم الطبيب الأول المتطابق
            liveDrName = matches[0].drName;
            console.log(`✅ تم استخدام الاسم المحفوظ: ${liveDrName}`);
          } else {
            console.log(`✅ سيُحفظ كاسم جديد: ${liveDrName}`);
          }
        }
      }
    }

    setBusy(true); setMsg("");
    try {
      // أحضر إحداثيات بداية الزيارة من GPS الحالي إن أمكن
      let lat=form.lat,lng=form.lng;
      if (immediate && navigator.geolocation) {
        try {
          const pos = await new Promise((res,rej)=>navigator.geolocation.getCurrentPosition(res,rej,{timeout:6000,enableHighAccuracy:true}));
          lat = pos.coords.latitude; lng = pos.coords.longitude;
        } catch(e){}
      }
      await act.createVisit({...form, drName: liveDrName, clinic: liveClinic, address: liveAddress, reasonOther: liveReasonOther, immediate, lat, lng}, user);
      setMsg("✅ تم الحفظ");
      setTimeout(()=>{ onClose(); }, 800);
    } catch(e) { setMsg("خطأ: "+e.message); }
    setBusy(false);
  };

  return (
    <Card style={{padding:16,border:"1px solid #a78bfa55",animation:"fadeUp .25s ease"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div style={{fontSize:13,fontWeight:800,color:"#a78bfa"}}>📝 زيارة جديدة</div>
        <button onClick={onClose} style={{background:"none",border:"none",color:"#5a4518",fontSize:18,cursor:"pointer"}}>✕</button>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:10}}>

        {/* اختيار الوضع: من القائمة الموجودة أم طبيب جديد */}
        <div style={{display:"flex",gap:6,background:"#0d0a00",padding:4,borderRadius:10,border:"1px solid #3d2f15"}}>
          <button onClick={()=>{setDrMode("existing");setForm(f=>({...f,drName:"",clinic:""}));setSelectedDr("");}}
            style={{flex:1,padding:"8px",borderRadius:8,border:"none",background:drMode==="existing"?"#a78bfa22":"transparent",color:drMode==="existing"?"#a78bfa":"#7a6841",fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"Cairo,sans-serif"}}>
            📋 من القائمة ({allDoctors.length})
          </button>
          <button onClick={()=>{setDrMode("new");setSelectedDr("");setForm(f=>({...f,drName:"",clinic:""}));}}
            style={{flex:1,padding:"8px",borderRadius:8,border:"none",background:drMode==="new"?"#fbbf2422":"transparent",color:drMode==="new"?"#fbbf24":"#7a6841",fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"Cairo,sans-serif"}}>
            ➕ طبيب جديد
          </button>
        </div>

        {drMode==="existing" ? (
          <>
            <div>
              <div style={LBL}>اختر الطبيب</div>
              <select value={selectedDr} onChange={e=>onSelectDoctor(e.target.value)} style={INP}>
                <option value="">— اختر طبيباً من المسجّلين ({allDoctors.length}) —</option>
                {allDoctors.length===0 && <option disabled>لا يوجد أطباء مسجّلون في محافظتك بعد</option>}
                {allDoctors.map(d => (
                  <option key={d.key} value={d.key}>
                    🦷 {d.drName}{d.clinic?` — ${d.clinic}`:""}
                  </option>
                ))}
              </select>
              {selectedDr && form.lat && form.lng && (
                <div style={{fontSize:9,color:"#60a5fa",marginTop:4}}>📍 موقع محفوظ</div>
              )}
            </div>
            {selectedDr && (
              <>
                <div><div style={LBL}>اسم العيادة</div>
                  <input value={form.clinic}
                    onChange={e=>setForm(f=>({...f,clinic:e.target.value}))}
                    onBlur={e=>setForm(f=>({...f,clinic:e.target.value}))}
                    style={INP} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} />
                </div>
                <div><div style={LBL}>عنوان الطبيب</div>
                  <input value={form.address}
                    onChange={e=>setForm(f=>({...f,address:e.target.value}))}
                    onBlur={e=>setForm(f=>({...f,address:e.target.value}))}
                    placeholder="شارع، حي، بناية..."
                    style={INP} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} />
                </div>
              </>
            )}
          </>
        ) : (
          <>
            {/* ═══ حقل اسم الطبيب الجديد + قائمة اقتراحات ذكيّة ═══ */}
            <div style={{position:"relative"}}>
              <div style={LBL}>اسم الطبيب الجديد</div>
              <SafeInput value={form.drName}
                onChange={e=>{ setForm(f=>({...f,drName:e.target.value})); setShowSuggestions(true); }}
                onBlur={e=>{ setForm(f=>({...f,drName:e.target.value})); setTimeout(()=>setShowSuggestions(false), 200); }}
                onFocus={()=>setShowSuggestions(true)}
                style={INP} placeholder="د. ..." />

              {/* القائمة المنسدلة للأطباء المتطابقين — مطابقة ذكيّة فاضلة (تتحمّل الأخطاء الإملائية) */}
              {showSuggestions && (() => {
                const typedWords = drCleanWords(form.drName);
                if (typedWords.length === 0 || typedWords[0].length < 2) return null;

                // ═══ مطابقة ذكيّة: تتحمّل الأخطاء الإملائية + توحيد الهمزات + Levenshtein ═══
                // مثال: "رنج" يطابق "رانج" — "احمد" يطابق "أحمد" — "شهاب" يطابق "شهاب"
                //       كل كلمة مُدخَلة يجب أن تطابق على الأقل كلمة واحدة من اسم الطبيب
                const matches = allDoctors.filter(d => {
                  const drWords = drCleanWords(d.drName);
                  return typedWords.every(typed => {
                    return drWords.some(drWord => fuzzyArabicMatch(typed, drWord));
                  });
                }).slice(0, 8);
                if (matches.length === 0) return null;

                return (
                  <div style={{
                    position:"absolute", top:"100%", left:0, right:0,
                    background:"#0d0a00", border:"1.5px solid #fbbf24",
                    borderRadius:10, maxHeight:280, overflowY:"auto",
                    zIndex:100, marginTop:4,
                    boxShadow:"0 8px 24px rgba(0,0,0,0.6), 0 0 12px rgba(251,191,36,0.2)"
                  }}>
                    <div style={{
                      padding:"8px 12px", fontSize:9, color:"#fbbf24", fontWeight:700,
                      borderBottom:"1px solid #3d2f15", background:"#1f1a08"
                    }}>
                      🔍 وُجد {matches.length} طبيب باسم مشابه — اضغط لاختيار، أو استمرّ في الكتابة لطبيب جديد
                    </div>
                    {matches.map((d,i) => (
                      <div key={d.key||i}
                        onMouseDown={(e)=>e.preventDefault()} // منع blur قبل click
                        onClick={()=>{
                          // املأ الحقول من الطبيب المختار
                          setForm(f=>({
                            ...f,
                            drName: d.drName,
                            clinic: d.clinic || f.clinic,
                            address: (allDoctors.find(x=>x.key===d.key) && drsList.find(x=>(x.name||"")===d.drName)?.address) || f.address,
                            lat: d.lat || f.lat,
                            lng: d.lng || f.lng,
                            lastVisitDate: d.lastVisitDate || f.lastVisitDate,
                          }));
                          setShowSuggestions(false);
                        }}
                        style={{
                          padding:"10px 12px", cursor:"pointer",
                          borderBottom: i < matches.length-1 ? "1px solid #15100a" : "none",
                          color:"#f0f9ff", fontSize:12,
                          transition:"background .15s ease"
                        }}
                        onMouseEnter={e=>e.currentTarget.style.background="#1f1a08"}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <div style={{fontWeight:700}}>🦷 {d.drName}</div>
                        {d.clinic && <div style={{fontSize:10, color:"#c9b37e", marginTop:2}}>🏥 {d.clinic}</div>}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            <div><div style={LBL}>اسم العيادة</div>
              <SafeInput value={form.clinic}
                onChange={e=>setForm(f=>({...f,clinic:e.target.value}))}
                style={INP} />
            </div>
            <div><div style={LBL}>عنوان الطبيب</div>
              <SafeInput value={form.address}
                onChange={e=>setForm(f=>({...f,address:e.target.value}))}
                placeholder="شارع، حي، بناية..."
                style={INP} />
            </div>
          </>
        )}

        <div><div style={LBL}>سبب الزيارة</div>
          <select value={form.reason} onChange={e=>setForm(f=>({...f,reason:e.target.value,reasonOther:""}))} style={INP}>
            {reasonOptions.map(r=><option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        {form.reason==="أخرى" && (
          <div><div style={LBL}>تعليق السبب</div>
            <SafeTextarea value={form.reasonOther}
              onChange={e=>setForm(f=>({...f,reasonOther:e.target.value}))}
              style={{...INP,minHeight:60}} placeholder="اكتب السبب..." />
          </div>
        )}
        {/* ═══ حقل المبلغ المُستحصَل (لمندوب التسويق / تيم ليدر / مشرف عند اختيار "استحصال نقود") ═══ */}
        {form.reason==="استحصال نقود" && (
          <div>
            <div style={{...LBL,color:"#22d3a0"}}>💰 المبلغ المُستحصَل (د.ع) *</div>
            <input type="number" inputMode="numeric" value={form.collectedAmount}
              onChange={e=>setForm(f=>({...f,collectedAmount:e.target.value}))}
              onBlur={e=>setForm(f=>({...f,collectedAmount:e.target.value}))}
              placeholder="مثال: 50000"
              style={{...INP,borderColor:"#22d3a0",fontSize:14,fontWeight:800,color:"#22d3a0"}}/>
            <div style={{fontSize:9,color:"#22d3a0",marginTop:4}}>سيُرسَل إشعار للمدير العام ومدير المحافظة باستحصال هذا المبلغ</div>
          </div>
        )}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <div><div style={LBL}>تاريخ الزيارة</div><input type="date" value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))} style={INP}/></div>
          <div><div style={LBL}>وقت الزيارة</div><input type="time" value={form.time} onChange={e=>setForm(f=>({...f,time:e.target.value}))} style={INP}/></div>
        </div>
        <div><div style={LBL}>تاريخ آخر زيارة للطبيب</div><input type="date" value={form.lastVisitDate} onChange={e=>setForm(f=>({...f,lastVisitDate:e.target.value}))} style={INP}/></div>
        {msg && <div style={{fontSize:11,color:msg.startsWith("✅")?"#22d3a0":"#f87171",textAlign:"center"}}>{msg}</div>}
        <div style={{marginTop:6}}>
          <Btn color="#22d3a0" onClick={()=>save(true)} disabled={busy} style={{width:"100%"}}>{busy?"...":"🚀 عمل الزيارة الآن"}</Btn>
        </div>
      </div>
    </Card>
  );
}

// ───── Visit card (workflow) ─────
function VisitCard({v,act,user}) {
  // الزيارات النشطة مفتوحة افتراضياً، المنتهية مغلقة
  const isActive = v.status !== "انتهت الزيارة";
  const [exp,setExp] = useState(isActive);
  const [resOpen,setResOpen] = useState(false);
  const [result,setResult] = useState("زيارة إيجابية");
  const [comment,setComment] = useState("");
  // ═══ حالة التعديل ═══
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    drName: v.drName || "",
    clinic: v.clinic || "",
    address: v.address || "",
    reason: v.reason || "",
    reasonOther: v.reasonOther || "",
    date: v.date || "",
    time: v.time || "",
    collectedAmount: v.collectedAmount || "",
  });
  const [editBusy, setEditBusy] = useState(false);

  // التحقّق: الزيارة هي المستخدم الحالي؟
  const isOwner = v.leaderId === user.id;
  const canEdit = isOwner; // المالك فقط يقدر يعدّل/يحذف

  const COL = {"في الانتظار":"#fbbf24","في الطريق":"#fbbf24","وصلت":"#38bdf8","جاري عمل الزيارة":"#e879f9","انتهت الزيارة":"#a78bfa"}[v.status]||"#94a3b8";

  // فتح نموذج النتيجة تلقائياً عند الوصول لمرحلة "جاري عمل الزيارة"
  React.useEffect(()=>{
    if (v.status === "جاري عمل الزيارة") setResOpen(true);
  },[v.status]);

  const submitResult = async () => {
    if (!comment.trim()) { alert("اكتب تعليقاً توضيحياً"); return; }
    await act.setVisitResult(v.id, result, comment.trim());
    setResOpen(false);
  };

  // مراحل الزيارة بالتسلسل
  const STAGES = [
    {id:"في الطريق",          icon:"🚗", label:"في الطريق",        color:"#fbbf24"},
    {id:"وصلت",               icon:"📍", label:"وصلت",             color:"#38bdf8"},
    {id:"جاري عمل الزيارة",   icon:"🦷", label:"جاري عمل الزيارة", color:"#e879f9"},
    {id:"انتهت الزيارة",      icon:"🏁", label:"انتهت الزيارة",    color:"#a78bfa"},
  ];
  const curIdx = STAGES.findIndex(s=>s.id===v.status);

  // الزر التالي في التسلسل
  const nextStage = () => {
    if (v.status==="في الانتظار")        return {id:"في الطريق",        label:"🚗 بدء — في الطريق",   color:"#fbbf24"};
    if (v.status==="في الطريق")          return {id:"وصلت",             label:"📍 وصلت للعيادة",      color:"#38bdf8"};
    if (v.status==="وصلت")               return {id:"جاري عمل الزيارة", label:"🦷 جاري عمل الزيارة", color:"#e879f9"};
    if (v.status==="جاري عمل الزيارة")   return {id:"انتهت الزيارة",    label:"🏁 إنهاء + النتيجة",   color:"#a78bfa", openResult:true};
    return null;
  };
  const nxt = nextStage();

  const handleNext = async () => {
    if (!nxt) return;
    if (nxt.openResult) { setResOpen(true); return; }
    await act.visitStatus(v.id, nxt.id, user);
  };

  return (
    <Card style={{padding:12,marginBottom:8,border:`2px solid ${COL}55`,boxShadow:isActive?`0 0 20px ${COL}22`:"none"}}>
      <div onClick={()=>setExp(!exp)} style={{cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
        <div>
          <div style={{fontSize:13,fontWeight:800,color:"#f0f9ff"}}>{v.drName}</div>
          <div style={{fontSize:10,color:"#c9b37e"}}>{v.clinic}</div>
          {v.address && <div style={{fontSize:9,color:"#94a3b8",marginTop:2}}>📍 {v.address}</div>}
          <div style={{fontSize:9,color:"#7a6841",marginTop:2}}>📌 {v.reason}{v.reason==="أخرى"&&v.reasonOther?`: ${v.reasonOther}`:""}</div>
          {v.date && <div style={{fontSize:9,color:"#60a5fa",marginTop:2}}>🗓 {v.date} {fmt12(v.time)}</div>}
        </div>
        <div style={{padding:"4px 12px",borderRadius:20,background:`${COL}22`,color:COL,fontSize:10,fontWeight:800,whiteSpace:"nowrap"}}>{v.status}</div>
      </div>

      {exp && (
        <div onClick={e=>e.stopPropagation()} style={{marginTop:12,borderTop:"1px solid #3d2f15",paddingTop:12,display:"flex",flexDirection:"column",gap:10}}>

          {/* شريط مراحل الزيارة بالتسلسل */}
          {v.status!=="في الانتظار" && (
            <div style={{display:"flex",alignItems:"center",gap:4}}>
              {STAGES.map((s,i)=>{
                const done = i < curIdx;
                const active = i === curIdx;
                return (
                  <React.Fragment key={s.id}>
                    {i>0 && <div style={{flex:1,height:3,background:done?s.color:"#15100a",borderRadius:2,transition:"background .3s"}}/>}
                    <div title={s.label} style={{
                      width:28,height:28,borderRadius:"50%",
                      background:done||active?s.color:"#15100a",
                      color:done||active?"#000":"#5a4518",
                      display:"flex",alignItems:"center",justifyContent:"center",
                      fontSize:13,fontWeight:800,
                      border:active?`2px solid ${s.color}`:"2px solid transparent",
                      boxShadow:active?`0 0 12px ${s.color}88`:"none",
                      flexShrink:0,
                      animation:active?"pulse 1.6s infinite":"none"
                    }}>{done?"✓":s.icon}</div>
                  </React.Fragment>
                );
              })}
            </div>
          )}

          {v.lastVisitDate && <div style={{fontSize:10,color:"#7a6841"}}>🕐 آخر زيارة سابقة: {v.lastVisitDate}</div>}
          {v.lat && v.lng && <div style={{fontSize:10,color:"#22d3a0"}}>📍 الموقع محفوظ ({v.lat.toFixed(4)}, {v.lng.toFixed(4)})</div>}

          {/* زر المرحلة التالية — كبير وواضح */}
          {nxt && !resOpen && (
            <Btn color={nxt.color} onClick={handleNext} style={{width:"100%",fontSize:13,padding:"12px"}}>
              {nxt.label}
            </Btn>
          )}

          {/* نموذج النتيجة — يفتح تلقائياً عند "جاري عمل الزيارة" */}
          {resOpen && (
            <div style={{background:"#0d0a00",border:"2px solid #a78bfa66",borderRadius:12,padding:12,display:"flex",flexDirection:"column",gap:10,animation:"fadeUp .25s ease"}}>
              <div style={{fontSize:12,fontWeight:800,color:"#a78bfa",textAlign:"center"}}>🏁 نتيجة الزيارة</div>
              <select value={result} onChange={e=>setResult(e.target.value)}
                style={{background:"#0a0600",border:"1px solid #3d2f15",borderRadius:8,padding:"10px",color:"#f0f9ff",fontSize:12,fontFamily:"Cairo,sans-serif",fontWeight:700}}>
                <option value="زيارة إيجابية">✅ زيارة إيجابية</option>
                <option value="زيارة سلبية">❌ زيارة سلبية</option>
              </select>
              <textarea value={comment} onChange={e=>setComment(e.target.value)} placeholder={`تعليق توضيحي عن ${result}...`}
                style={{background:"#0a0600",border:"1px solid #3d2f15",borderRadius:8,padding:10,color:"#f0f9ff",fontSize:11,fontFamily:"Cairo,sans-serif",minHeight:80,direction:"rtl"}} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} />
              <div style={{display:"flex",gap:8}}>
                <Btn color="#22d3a0" onClick={submitResult} style={{flex:1}}>✓ حفظ النتيجة</Btn>
                <Btn color="#5a4518" onClick={()=>setResOpen(false)} style={{flex:0.5}}>لاحقاً</Btn>
              </div>
            </div>
          )}

          {v.status==="انتهت الزيارة" && v.result && (
            <div style={{background:v.result==="زيارة إيجابية"?"#0a2e1e":"#2e0a0a",border:`1px solid ${v.result==="زيارة إيجابية"?"#22d3a044":"#f8717144"}`,borderRadius:10,padding:12}}>
              <div style={{fontSize:11,fontWeight:800,color:v.result==="زيارة إيجابية"?"#22d3a0":"#f87171",marginBottom:4}}>{v.result}</div>
              {v.resultComment && <div style={{fontSize:11,color:"#c9b37e"}}>{v.resultComment}</div>}
            </div>
          )}

          {/* ═══ أزرار التعديل/الحذف — لصاحب الزيارة فقط ═══ */}
          {canEdit && !editOpen && !resOpen && (
            <div style={{display:"flex",gap:8,marginTop:4}}>
              <button onClick={()=>setEditOpen(true)}
                style={{flex:1,background:"#1f1a08",border:"1px solid #fbbf2455",borderRadius:8,padding:"8px 10px",color:"#fbbf24",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal','Inter',sans-serif"}}>
                ✏️ تعديل المعلومات
              </button>
              <button onClick={()=>{
                if (window.confirm(`هل أنت متأكد من حذف زيارة "${v.drName}"؟\n\nهذا الإجراء غير قابل للتراجع.`)) {
                  act.deleteVisit(v);
                }
              }}
                style={{flex:1,background:"#2e0a0a",border:"1px solid #f8717155",borderRadius:8,padding:"8px 10px",color:"#f87171",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal','Inter',sans-serif"}}>
                🗑 حذف الزيارة
              </button>
            </div>
          )}

          {/* ═══ نموذج تعديل الزيارة ═══ */}
          {editOpen && (
            <div style={{background:"#15100a",border:"1.5px solid #fbbf24",borderRadius:12,padding:14,display:"flex",flexDirection:"column",gap:10,animation:"fadeUp .25s ease"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div style={{fontSize:12,fontWeight:800,color:"#fbbf24"}}>✏️ تعديل معلومات الزيارة</div>
                <button onClick={()=>setEditOpen(false)} style={{background:"transparent",border:"none",color:"#5a4518",fontSize:16,cursor:"pointer"}}>✕</button>
              </div>

              <div>
                <div style={{fontSize:9,color:"#7a6841",marginBottom:3}}>اسم الطبيب *</div>
                <SafeInput value={editForm.drName} onChange={e=>setEditForm(f=>({...f,drName:e.target.value}))}
                  style={{width:"100%",background:"#0a0600",border:"1px solid #3d2f15",borderRadius:8,padding:"8px 10px",color:"#f0f9ff",fontSize:12,fontFamily:"'Tajawal','Inter',sans-serif",direction:"rtl",boxSizing:"border-box"}}/>
              </div>
              <div>
                <div style={{fontSize:9,color:"#7a6841",marginBottom:3}}>اسم العيادة</div>
                <SafeInput value={editForm.clinic} onChange={e=>setEditForm(f=>({...f,clinic:e.target.value}))}
                  style={{width:"100%",background:"#0a0600",border:"1px solid #3d2f15",borderRadius:8,padding:"8px 10px",color:"#f0f9ff",fontSize:12,fontFamily:"'Tajawal','Inter',sans-serif",direction:"rtl",boxSizing:"border-box"}}/>
              </div>
              <div>
                <div style={{fontSize:9,color:"#7a6841",marginBottom:3}}>العنوان</div>
                <SafeInput value={editForm.address} onChange={e=>setEditForm(f=>({...f,address:e.target.value}))}
                  style={{width:"100%",background:"#0a0600",border:"1px solid #3d2f15",borderRadius:8,padding:"8px 10px",color:"#f0f9ff",fontSize:12,fontFamily:"'Tajawal','Inter',sans-serif",direction:"rtl",boxSizing:"border-box"}}/>
              </div>
              <div>
                <div style={{fontSize:9,color:"#7a6841",marginBottom:3}}>سبب الزيارة</div>
                <select value={editForm.reason} onChange={e=>setEditForm(f=>({...f,reason:e.target.value, reasonOther: e.target.value==="أخرى" ? f.reasonOther : ""}))}
                  style={{width:"100%",background:"#0a0600",border:"1px solid #3d2f15",borderRadius:8,padding:"8px 10px",color:"#f0f9ff",fontSize:12,fontFamily:"'Tajawal','Inter',sans-serif",direction:"rtl",boxSizing:"border-box"}}>
                  <option value="طبيب جديد">طبيب جديد</option>
                  <option value="إعادة طبيب منقطع">إعادة طبيب منقطع</option>
                  <option value="تعديل موقف (مصالحة)">تعديل موقف (مصالحة)</option>
                  <option value="سبب تسويقي">سبب تسويقي</option>
                  <option value="استلام عمل">استلام عمل</option>
                  <option value="تسليم عمل">تسليم عمل</option>
                  <option value="استحصال نقود">استحصال نقود</option>
                  <option value="أخرى">أخرى</option>
                </select>
              </div>
              {editForm.reason === "أخرى" && (
                <div>
                  <div style={{fontSize:9,color:"#7a6841",marginBottom:3}}>تفصيل السبب</div>
                  <SafeInput value={editForm.reasonOther} onChange={e=>setEditForm(f=>({...f,reasonOther:e.target.value}))}
                    style={{width:"100%",background:"#0a0600",border:"1px solid #3d2f15",borderRadius:8,padding:"8px 10px",color:"#f0f9ff",fontSize:12,fontFamily:"'Tajawal','Inter',sans-serif",direction:"rtl",boxSizing:"border-box"}}/>
                </div>
              )}
              {editForm.reason === "استحصال نقود" && (
                <div>
                  <div style={{fontSize:9,color:"#22d3a0",marginBottom:3}}>💰 المبلغ المُستحصَل (د.ع)</div>
                  <input type="number" inputMode="numeric" value={editForm.collectedAmount}
                    onChange={e=>setEditForm(f=>({...f,collectedAmount:e.target.value}))}
                    style={{width:"100%",background:"#0a0600",border:"1px solid #22d3a055",borderRadius:8,padding:"8px 10px",color:"#22d3a0",fontSize:13,fontFamily:"'Tajawal','Inter',sans-serif",direction:"ltr",fontWeight:800,boxSizing:"border-box"}}/>
                </div>
              )}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                <div>
                  <div style={{fontSize:9,color:"#7a6841",marginBottom:3}}>التاريخ</div>
                  <input type="date" value={editForm.date} onChange={e=>setEditForm(f=>({...f,date:e.target.value}))}
                    style={{width:"100%",background:"#0a0600",border:"1px solid #3d2f15",borderRadius:8,padding:"8px 10px",color:"#f0f9ff",fontSize:12,fontFamily:"'Tajawal','Inter',sans-serif",boxSizing:"border-box"}}/>
                </div>
                <div>
                  <div style={{fontSize:9,color:"#7a6841",marginBottom:3}}>الوقت</div>
                  <input type="time" value={editForm.time} onChange={e=>setEditForm(f=>({...f,time:e.target.value}))}
                    style={{width:"100%",background:"#0a0600",border:"1px solid #3d2f15",borderRadius:8,padding:"8px 10px",color:"#f0f9ff",fontSize:12,fontFamily:"'Tajawal','Inter',sans-serif",boxSizing:"border-box"}}/>
                </div>
              </div>

              <div style={{display:"flex",gap:8,marginTop:6}}>
                <button onClick={()=>setEditOpen(false)} disabled={editBusy}
                  style={{flex:1,background:"transparent",border:"1px solid #3d2f15",borderRadius:8,padding:"10px",color:"#94a3b8",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal','Inter',sans-serif"}}>
                  إلغاء
                </button>
                <button onClick={async()=>{
                  if (!editForm.drName.trim()) { alert("اسم الطبيب مطلوب"); return; }
                  setEditBusy(true);
                  try {
                    await act.updateVisit(v, editForm);
                    setEditOpen(false);
                  } finally { setEditBusy(false); }
                }} disabled={editBusy}
                  style={{flex:1.5,background:"linear-gradient(135deg,#fbbf24,#d4af37)",border:"none",borderRadius:8,padding:"10px",color:"#0a0600",fontSize:12,fontWeight:900,cursor:editBusy?"wait":"pointer",fontFamily:"'Tajawal','Inter',sans-serif",opacity:editBusy?0.6:1}}>
                  {editBusy ? "جاري الحفظ..." : "💾 حفظ التعديلات"}
                </button>
              </div>
            </div>
          )}

          {/* علامة "تم تعديل هذه الزيارة" */}
          {v.editedAt && !editOpen && (
            <div style={{fontSize:9,color:"#fbbf24",fontStyle:"italic",marginTop:4}}>
              ✏️ تم تعديل هذه الزيارة سابقاً
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  BOOKING REQUEST FORM — نموذج طلب حجز موعد
// ═══════════════════════════════════════════════════════════════════════════
function BookingRequestForm({user, db, act, onClose, existingReq}) {
  const [form, setForm] = useState({
    drName: existingReq?.drName || "",
    clinic: existingReq?.clinic || "",
    address: existingReq?.address || "",
    date: existingReq?.date || "",
    time: existingReq?.time || "",
    notes: existingReq?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  const INP = {width:"100%",background:"#0d0a00",border:"1px solid #3d2f15",borderRadius:10,padding:"10px",color:"#f0f9ff",fontSize:12,fontFamily:"Cairo,sans-serif",direction:"rtl",boxSizing:"border-box"};
  const LBL = {fontSize:10,color:"#c9b37e",marginBottom:4,fontWeight:700};

  const myGov = user.govCode;
  const myDocs = (db?.doctors||[]).filter(d => !d.govCode || d.govCode === myGov);

  const submit = async () => {
    if (!form.drName.trim()) { setMsg("اكتب اسم الطبيب"); return; }
    if (!form.date)          { setMsg("اختر تاريخ الموعد"); return; }
    if (!form.time)          { setMsg("اختر وقت الموعد"); return; }
    setBusy(true); setMsg("");
    try {
      if (existingReq && existingReq.id) {
        // تعديل طلب مرفوض → إعادة إرسال
        await act.updateBookingRequest(existingReq.id, form, user);
        setMsg("✅ أُعيد إرسال الطلب للكول سنتر");
      } else {
        // طلب جديد
        await act.createBookingRequest(form, user);
        setMsg("✅ أُرسل الطلب للكول سنتر");
      }
      setTimeout(()=>onClose(), 1000);
    } catch(e) { setMsg("خطأ: "+e.message); }
    setBusy(false);
  };

  return (
    <Card style={{padding:16,border:`1px solid #38bdf855`,animation:"fadeUp .25s ease"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div style={{fontSize:13,fontWeight:800,color:"#38bdf8"}}>📨 {existingReq ? "تعديل طلب الحجز" : "طلب حجز موعد جديد"}</div>
        <button onClick={onClose} style={{background:"none",border:"none",color:"#5a4518",fontSize:18,cursor:"pointer"}}>✕</button>
      </div>

      <div style={{display:"flex",flexDirection:"column",gap:10}}>

        {/* اسم الطبيب مع قائمة منسدلة ذكيّة — SafeInput لتجنّب IME composition issues */}
        <div style={{position:"relative"}}>
          <div style={LBL}>اسم الطبيب *</div>
          <SafeInput value={form.drName}
            onChange={e=>{setForm(f=>({...f,drName:e.target.value})); setShowSuggestions(true);}}
            onBlur={e=>{setForm(f=>({...f,drName:e.target.value})); setTimeout(()=>setShowSuggestions(false),200);}}
            onFocus={()=>setShowSuggestions(true)}
            placeholder="د. ..." style={INP} />

          {/* اقتراحات الأطباء */}
          {showSuggestions && (() => {
            const typedWords = drCleanWords(form.drName);
            if (typedWords.length === 0 || typedWords[0].length < 2) return null;
            const matches = myDocs.filter(d => {
              const drWords = drCleanWords(d.name);
              return typedWords.every(typed => drWords.some(drWord => fuzzyArabicMatch(typed, drWord)));
            }).slice(0, 8);
            if (matches.length === 0) return null;
            return (
              <div style={{position:"absolute",top:"100%",left:0,right:0,background:"#0d0a00",border:"1.5px solid #38bdf8",borderRadius:10,maxHeight:280,overflowY:"auto",zIndex:100,marginTop:4,boxShadow:"0 8px 24px rgba(0,0,0,0.6)"}}>
                <div style={{padding:"8px 12px",fontSize:9,color:"#38bdf8",fontWeight:700,borderBottom:"1px solid #3d2f15",background:"#0a1828"}}>
                  🔍 وُجد {matches.length} طبيب — اضغط لاختيار
                </div>
                {matches.map((d,i) => (
                  <div key={d.id||i}
                    onMouseDown={(e)=>e.preventDefault()}
                    onClick={()=>{
                      setForm(f=>({...f,drName:d.name||"",clinic:d.clinic||f.clinic,address:d.address||f.address}));
                      setShowSuggestions(false);
                    }}
                    style={{padding:"10px 12px",cursor:"pointer",borderBottom:i<matches.length-1?"1px solid #15100a":"none",color:"#f0f9ff",fontSize:12}}
                    onMouseEnter={e=>e.currentTarget.style.background="#0a1828"}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <div style={{fontWeight:700}}>🦷 {d.name}</div>
                    {d.clinic && <div style={{fontSize:10,color:"#c9b37e",marginTop:2}}>🏥 {d.clinic}</div>}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        <div><div style={LBL}>اسم العيادة (اختياري)</div>
          <SafeInput value={form.clinic} onChange={e=>setForm(f=>({...f,clinic:e.target.value}))} style={INP} />
        </div>
        <div><div style={LBL}>عنوان الطبيب (اختياري)</div>
          <SafeInput value={form.address} onChange={e=>setForm(f=>({...f,address:e.target.value}))} placeholder="شارع، حي، بناية..." style={INP} />
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <div><div style={LBL}>تاريخ الموعد *</div>
            <input type="date" value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))} style={INP}/>
          </div>
          <div><div style={LBL}>وقت الموعد *</div>
            <input type="time" value={form.time} onChange={e=>setForm(f=>({...f,time:e.target.value}))} style={INP}/>
          </div>
        </div>

        <div><div style={LBL}>ملاحظات</div>
          <SafeTextarea value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} rows={3} style={{...INP,resize:"none"}} placeholder="أضف أيّ تفاصيل للكول سنتر..." />
        </div>

        {existingReq?.rejectReason && (
          <div style={{background:"#2a1414",border:"1px solid #f87171",borderRadius:10,padding:10,fontSize:11,color:"#f87171"}}>
            ⚠️ <b>سبب الرفض السابق:</b> {existingReq.rejectReason}
          </div>
        )}

        {msg && <div style={{fontSize:11,color:msg.startsWith("✅")?"#22d3a0":"#f87171",textAlign:"center"}}>{msg}</div>}

        <Btn color="#38bdf8" onClick={submit} disabled={busy} style={{width:"100%"}}>
          {busy?"جاري...":(existingReq ? "🔄 إعادة توجيه الطلب" : "📨 توجيه الطلب للكول سنتر")}
        </Btn>
      </div>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  MARKETING APP — مندوب التسويق
// ═══════════════════════════════════════════════════════════════════════════
function MarketingApp({user,db,act,notifs,unread,onLogout,onMarkAll,notifPerm,requestNotifs}) {
  const [pg,setPg]=useState("home");
  const [no,setNo]=useState(false);
  const [showVisitForm,setShowVisitForm]=useState(false);
  const [showBookingForm,setShowBookingForm]=useState(false);
  const [editingReq,setEditingReq]=useState(null);
  const [gps,setGps]=useState(null);
  const [myCoord,setMyCoord]=useState(null);

  useEffect(()=>{
    const open = () => setNo(true);
    window.addEventListener("britao:openNotifs", open);
    return () => window.removeEventListener("britao:openNotifs", open);
  },[]);

  // ═══ GPS — يطلب الإذن ويُتابع الموقع لمندوب التسويق ═══
  useEffect(()=>{
    if(!navigator.geolocation){setGps("غير مدعوم");return;}
    const onPos = (p)=>{
      setGps(`${p.coords.latitude.toFixed(4)}, ${p.coords.longitude.toFixed(4)}`);
      setMyCoord({lat:p.coords.latitude, lng:p.coords.longitude});
    };
    navigator.geolocation.getCurrentPosition(onPos,()=>setGps("لم يُسمح بالوصول"),{enableHighAccuracy:true,timeout:10000});
    const wid = navigator.geolocation.watchPosition(onPos,()=>{},{enableHighAccuracy:true,maximumAge:15000});
    return ()=>{ try{ navigator.geolocation.clearWatch(wid);}catch(e){} };
  },[]);

  const sameGov = (r) => !r.govCode || r.govCode === user.govCode;
  const myVisits = (db.visits||[]).filter(v => v.leaderId === user.id);
  const myReqs   = (db.bookingReqs||[]).filter(r => r.fromUserId === user.id);
  const govApts  = (db.appointments||[]).filter(a => sameGov(a) && (!a.govCode || a.govCode === user.govCode));

  const NAV=[
    {id:"home",icon:"⬡",label:"الرئيسية",c:"#fb7185"},
    {id:"visits",icon:"🦷",label:"زيارة الأطباء",c:"#fb7185"},
    {id:"apts",icon:"📋",label:"جدول المناديب",c:"#fb7185"},
    {id:"bookings",icon:"📨",label:"طلبات الحجز",c:"#fb7185"},
    {id:"mymap",icon:"📍",label:"موقعي",c:"#fb7185"},
  ];

  // أسباب الزيارة الخاصّة بمندوب التسويق (مع خيارات إضافية)
  const REASON_OPTIONS = [
    "طبيب جديد","إعادة طبيب منقطع","تعديل موقف (مصالحة)","سبب تسويقي",
    "استلام عمل","تسليم عمل","استحصال نقود",
    "أخرى"
  ];

  const pending = myVisits.filter(v=>v.status==="في الانتظار");
  const active  = myVisits.filter(v=>["في الطريق","وصلت","جاري عمل الزيارة"].includes(v.status));
  const ended   = myVisits.filter(v=>v.status==="انتهت الزيارة");

  const rejectedReqs = myReqs.filter(r=>r.status==="rejected");
  const pendingReqs  = myReqs.filter(r=>r.status==="pending");
  const approvedReqs = myReqs.filter(r=>r.status==="approved");

  const rp = () => {
    if (pg === "home") return (
      <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{background:"linear-gradient(160deg,#3a0a14,#1a0507)",borderRadius:18,padding:18}}>
          <div style={{fontSize:10,color:"#fb7185",marginBottom:3}}>مندوب تسويق</div>
          <div style={{fontSize:17,fontWeight:900,color:"#f0f9ff"}}>{user.fullName||user.name}</div>
          {user.govName && <div style={{fontSize:11,color:"#d4af37",fontWeight:800,marginTop:4}}>📍 {user.govName}{user.area?` — ${user.area}`:""}</div>}
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
          <Card style={{padding:12,textAlign:"center",border:"1px solid #fbbf2444"}}>
            <div style={{fontSize:20,fontWeight:900,color:"#fbbf24"}}>{pending.length+active.length}</div>
            <div style={{fontSize:9,color:"#94a3b8"}}>زيارات نشطة</div>
          </Card>
          <Card style={{padding:12,textAlign:"center",border:"1px solid #38bdf844"}}>
            <div style={{fontSize:20,fontWeight:900,color:"#38bdf8"}}>{pendingReqs.length}</div>
            <div style={{fontSize:9,color:"#94a3b8"}}>طلبات معلّقة</div>
          </Card>
          <Card style={{padding:12,textAlign:"center",border:"1px solid #22d3a044"}}>
            <div style={{fontSize:20,fontWeight:900,color:"#22d3a0"}}>{approvedReqs.length}</div>
            <div style={{fontSize:9,color:"#94a3b8"}}>طلبات مقبولة</div>
          </Card>
        </div>

        {/* الطلبات المرفوضة (تحتاج تعديل) */}
        {rejectedReqs.length > 0 && (
          <div>
            <div style={{fontSize:12,fontWeight:800,color:"#f87171",marginBottom:8}}>⚠️ طلبات تحتاج تعديل ({rejectedReqs.length})</div>
            {rejectedReqs.map(r => (
              <Card key={r.id} style={{padding:12,marginBottom:8,border:"2px solid #f87171",background:"#2a1414"}}>
                <div style={{fontSize:12,fontWeight:800,color:"#fff"}}>🦷 {r.drName}</div>
                <div style={{fontSize:10,color:"#fecaca",marginTop:4}}>📅 {r.date} {fmt12(r.time)}</div>
                {r.rejectReason && <div style={{fontSize:10,color:"#fbbf24",marginTop:6,padding:8,background:"#1a0a0a",borderRadius:6}}>❌ {r.rejectReason}</div>}
                <Btn small color="#fbbf24" style={{marginTop:8,width:"100%"}}
                  onClick={()=>{setEditingReq(r); setShowBookingForm(true); setPg("bookings");}}>
                  ✏️ تعديل وإعادة الإرسال
                </Btn>
              </Card>
            ))}
          </div>
        )}

        <Btn color="#22d3a0" onClick={()=>setShowVisitForm(true)} style={{width:"100%"}}>+ زيارة جديدة</Btn>
        <Btn color="#38bdf8" onClick={()=>{setEditingReq(null); setShowBookingForm(true); setPg("bookings");}} style={{width:"100%"}}>📨 طلب حجز موعد</Btn>

        {showVisitForm && <NewVisitForm user={user} db={db} act={act} onClose={()=>setShowVisitForm(false)} reasonOptions={REASON_OPTIONS}/>}
      </div>
    );

    if (pg === "visits") return (
      <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontSize:14,fontWeight:800,color:"#f0f9ff"}}>🦷 زياراتي</div>
          <Btn small color="#22d3a0" onClick={()=>setShowVisitForm(true)}>+ جديدة</Btn>
        </div>

        {showVisitForm && <NewVisitForm user={user} db={db} act={act} onClose={()=>setShowVisitForm(false)} reasonOptions={REASON_OPTIONS}/>}

        {active.length>0 && <div style={{fontSize:11,color:"#fbbf24",marginTop:8}}>⚡ نشطة الآن ({active.length})</div>}
        {active.map(v => <VisitCard key={v.id} v={v} act={act} user={user}/>)}
        {pending.length>0 && <div style={{fontSize:11,color:"#94a3b8",marginTop:8}}>⏸ في الانتظار ({pending.length})</div>}
        {pending.map(v => <VisitCard key={v.id} v={v} act={act} user={user}/>)}
        {ended.length>0 && <div style={{fontSize:11,color:"#94a3b8",marginTop:8}}>✅ منتهية ({ended.length})</div>}
        {ended.slice(0,15).map(v => <VisitCard key={v.id} v={v} act={act} user={user}/>)}
      </div>
    );

    if (pg === "apts") {
      return <ScheduleTable apts={govApts} title={`📋 جدول مواعيد مناديب ${db.govName||""}`} showGov={false} reps={db.reps||[]}/>;
    }

    if (pg === "bookings") return (
      <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontSize:14,fontWeight:800,color:"#f0f9ff"}}>📨 طلبات الحجز</div>
          <Btn small color="#38bdf8" onClick={()=>{setEditingReq(null); setShowBookingForm(true);}}>+ طلب جديد</Btn>
        </div>

        {showBookingForm && <BookingRequestForm user={user} db={db} act={act} existingReq={editingReq} onClose={()=>{setShowBookingForm(false); setEditingReq(null);}}/>}

        {pendingReqs.length>0 && <div style={{fontSize:11,color:"#fbbf24",marginTop:8}}>⏳ في الانتظار ({pendingReqs.length})</div>}
        {pendingReqs.map(r => (
          <Card key={r.id} style={{padding:12,border:"1px solid #fbbf2455"}}>
            <div style={{fontSize:12,fontWeight:800,color:"#f0f9ff"}}>🦷 {r.drName}</div>
            <div style={{fontSize:10,color:"#94a3b8",marginTop:4}}>📅 {r.date} {fmt12(r.time)}</div>
            {r.notes && <div style={{fontSize:10,color:"#c9b37e",marginTop:4}}>📝 {r.notes}</div>}
            <div style={{fontSize:9,color:"#fbbf24",marginTop:6,fontWeight:700}}>⏳ بانتظار رد الكول سنتر</div>
            <button onClick={()=>{if(window.confirm("حذف هذا الطلب؟")) act.deleteBookingRequest(r.id);}}
              style={{background:"#2e0a0a",border:"1px solid #f8717155",borderRadius:6,padding:"4px 10px",color:"#f87171",fontSize:9,fontWeight:700,cursor:"pointer",fontFamily:"Cairo,sans-serif",marginTop:8}}>
              🗑 حذف
            </button>
          </Card>
        ))}

        {rejectedReqs.length>0 && <div style={{fontSize:11,color:"#f87171",marginTop:12}}>⚠️ مرفوضة - تحتاج تعديل ({rejectedReqs.length})</div>}
        {rejectedReqs.map(r => (
          <Card key={r.id} style={{padding:12,border:"2px solid #f87171",background:"#2a141488"}}>
            <div style={{fontSize:12,fontWeight:800,color:"#f0f9ff"}}>🦷 {r.drName}</div>
            <div style={{fontSize:10,color:"#94a3b8",marginTop:4}}>📅 {r.date} {fmt12(r.time)}</div>
            {r.rejectReason && <div style={{fontSize:10,color:"#fbbf24",marginTop:6,padding:8,background:"#1a0a0a",borderRadius:6}}>❌ {r.rejectReason}</div>}
            <Btn small color="#fbbf24" style={{marginTop:8,width:"100%"}}
              onClick={()=>{setEditingReq(r); setShowBookingForm(true);}}>
              ✏️ تعديل وإعادة الإرسال
            </Btn>
          </Card>
        ))}

        {approvedReqs.length>0 && <div style={{fontSize:11,color:"#22d3a0",marginTop:12}}>✅ موافَق عليها ({approvedReqs.length})</div>}
        {approvedReqs.slice(0,10).map(r => (
          <Card key={r.id} style={{padding:12,border:"1px solid #22d3a055"}}>
            <div style={{fontSize:12,fontWeight:800,color:"#f0f9ff"}}>🦷 {r.drName}</div>
            <div style={{fontSize:10,color:"#94a3b8",marginTop:4}}>📅 {r.date} {fmt12(r.time)}</div>
            <div style={{fontSize:9,color:"#22d3a0",marginTop:6,fontWeight:700}}>✅ تم تثبيت الحجز</div>
          </Card>
        ))}

        {myReqs.length===0 && (
          <Card style={{padding:30,textAlign:"center",color:"#334155",fontSize:12}}>
            لا توجد طلبات حجز بعد — اضغط "+ طلب جديد"
          </Card>
        )}
      </div>
    );

    // ═══ صفحة موقعي — لمندوب التسويق ═══
    if (pg === "mymap") return (
      <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{fontSize:14,fontWeight:800,color:"#f0f9ff"}}>📍 موقعي الحقيقي</div>
        {gps && (
          <Card style={{padding:12}}>
            <div style={{fontSize:10,color:"#22d3a0",marginBottom:2}}>إحداثيات GPS الحقيقية</div>
            <div style={{fontSize:12,color:"#f0f9ff",fontWeight:700}}>{gps}</div>
            <div style={{fontSize:9,color:"#5a4518",marginTop:2}}>يتحدّث تلقائياً كل ثانية</div>
          </Card>
        )}
        {!gps && (
          <Card style={{padding:14,border:"1px solid #fbbf2444",background:"#1f1a08"}}>
            <div style={{fontSize:11,color:"#fbbf24",fontWeight:700,lineHeight:1.6}}>
              ⏳ جاري طلب إذن GPS...<br/>
              <span style={{color:"#c9b37e",fontWeight:500,fontSize:10}}>يجب السماح للتطبيق بالوصول لموقعك من إعدادات الجوال</span>
            </div>
          </Card>
        )}
        <LeafletMap reps={(db.reps||[]).filter(r=>r.id===user.id)} appointments={[]} height={360} filterRepId={user.id} centerOn={myCoord} govCode={user.govCode}/>
      </div>
    );

    return null;
  };

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100dvh",background:"transparent",fontFamily:"'Tajawal','Inter',sans-serif",direction:"rtl",maxWidth:"min(100%, 600px)",margin:"0 auto",width:"100%"}}>
      <style>{G}</style>
      <TopBar user={user} color="#fb7185" nc={unread} onNotif={()=>{setNo(true); if(unread>0) onMarkAll();}} onLogout={onLogout} title="مندوب تسويق"/>
      <NotifBanner notifPerm={notifPerm} requestNotifs={requestNotifs}/>
      <div style={{flex:1,overflowY:"auto"}}>{rp()}</div>
      <BNav items={NAV} active={pg} onChange={setPg}/>
      {no&&<NPanel notifs={notifs} color="#fb7185" onClose={()=>setNo(false)} onMarkAll={onMarkAll}/>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  MUSHREF APP — المشرف (نسخة من التيم ليدر + خريطة موحّدة + طلبات حجز)
// ═══════════════════════════════════════════════════════════════════════════
function MushrefApp({user,db,act,notifs,unread,onLogout,onMarkAll,notifPerm,requestNotifs}) {
  const [pg,setPg]=useState("home");
  const [no,setNo]=useState(false);
  const [showVisitForm,setShowVisitForm]=useState(false);
  const [showBookingForm,setShowBookingForm]=useState(false);
  const [editingReq,setEditingReq]=useState(null);
  const [gps,setGps]=useState(null);
  const [myCoord,setMyCoord]=useState(null);

  useEffect(()=>{
    const open = () => setNo(true);
    window.addEventListener("britao:openNotifs", open);
    return () => window.removeEventListener("britao:openNotifs", open);
  },[]);

  // ═══ GPS — يطلب الإذن ويُتابع الموقع للمشرف ═══
  useEffect(()=>{
    if(!navigator.geolocation){setGps("غير مدعوم");return;}
    const onPos = (p)=>{
      setGps(`${p.coords.latitude.toFixed(4)}, ${p.coords.longitude.toFixed(4)}`);
      setMyCoord({lat:p.coords.latitude, lng:p.coords.longitude});
    };
    navigator.geolocation.getCurrentPosition(onPos,()=>setGps("لم يُسمح بالوصول"),{enableHighAccuracy:true,timeout:10000});
    const wid = navigator.geolocation.watchPosition(onPos,()=>{},{enableHighAccuracy:true,maximumAge:15000});
    return ()=>{ try{ navigator.geolocation.clearWatch(wid);}catch(e){} };
  },[]);

  const sameGov = (r) => !r.govCode || r.govCode === user.govCode;
  const myVisits = (db.visits||[]).filter(v => v.leaderId === user.id);
  const myReqs   = (db.bookingReqs||[]).filter(r => r.fromUserId === user.id);
  const govApts  = (db.appointments||[]).filter(a => sameGov(a) && (!a.govCode || a.govCode === user.govCode));

  // ═══ خريطة موحّدة: زيارات التيم ليدر + زيارات مندوب التسويق + مواقع الأطباء ═══
  // كل الزيارات التي عندها GPS (سواء من تيم ليدر أو من مندوب تسويق)
  const allTLandMarketingVisits = (db.visits||[]).filter(v =>
    sameGov(v) && v.lat && v.lng &&
    (v.leaderRole === "supervisor" || v.leaderRole === "marketing" || v.leaderRole === "mushref" || !v.leaderRole)
  );
  // كل أعضاء المحافظة الذين لديهم GPS — مندوب سكنر + تسويق + تيم ليدر
  const allMembersWithGPS = (db.reps||[]).filter(r =>
    sameGov(r) && r.lat && r.lng &&
    ["rep","marketing","supervisor"].includes(r.role)
  );
  const doctorLocs   = (db.docLocs||[]).filter(d => sameGov(d) && d.lat && d.lng);

  const NAV=[
    {id:"home",icon:"⬡",label:"الرئيسية",c:"#a78bfa"},
    {id:"visits",icon:"🦷",label:"زيارة الأطباء",c:"#a78bfa"},
    {id:"apts",icon:"📋",label:"جدول المناديب",c:"#a78bfa"},
    {id:"map",icon:"🗺",label:"خريطة موحّدة",c:"#a78bfa"},
    {id:"mymap",icon:"📍",label:"موقعي",c:"#a78bfa"},
    {id:"bookings",icon:"📨",label:"طلبات الحجز",c:"#a78bfa"},
  ];

  const REASON_OPTIONS = ["طبيب جديد","إعادة طبيب منقطع","تعديل موقف (مصالحة)","سبب تسويقي","استحصال نقود","أخرى"];

  const pending = myVisits.filter(v=>v.status==="في الانتظار");
  const active  = myVisits.filter(v=>["في الطريق","وصلت","جاري عمل الزيارة"].includes(v.status));
  const ended   = myVisits.filter(v=>v.status==="انتهت الزيارة");

  const rejectedReqs = myReqs.filter(r=>r.status==="rejected");
  const pendingReqs  = myReqs.filter(r=>r.status==="pending");
  const approvedReqs = myReqs.filter(r=>r.status==="approved");

  const rp = () => {
    if (pg === "home") return (
      <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{background:"linear-gradient(160deg,#1f1538,#0d0a1f)",borderRadius:18,padding:18}}>
          <div style={{fontSize:10,color:"#a78bfa",marginBottom:3}}>مشرف</div>
          <div style={{fontSize:17,fontWeight:900,color:"#f0f9ff"}}>{user.fullName||user.name}</div>
          {user.govName && <div style={{fontSize:11,color:"#d4af37",fontWeight:800,marginTop:4}}>📍 {user.govName}{user.area?` — ${user.area}`:""}</div>}
        </div>

        {rejectedReqs.length > 0 && (
          <div>
            <div style={{fontSize:12,fontWeight:800,color:"#f87171",marginBottom:8}}>⚠️ طلبات حجز تحتاج تعديل ({rejectedReqs.length})</div>
            {rejectedReqs.map(r => (
              <Card key={r.id} style={{padding:12,marginBottom:8,border:"2px solid #f87171",background:"#2a1414"}}>
                <div style={{fontSize:12,fontWeight:800,color:"#fff"}}>🦷 {r.drName}</div>
                <div style={{fontSize:10,color:"#fecaca",marginTop:4}}>📅 {r.date} {fmt12(r.time)}</div>
                {r.rejectReason && <div style={{fontSize:10,color:"#fbbf24",marginTop:6,padding:8,background:"#1a0a0a",borderRadius:6}}>❌ {r.rejectReason}</div>}
                <Btn small color="#fbbf24" style={{marginTop:8,width:"100%"}}
                  onClick={()=>{setEditingReq(r); setShowBookingForm(true); setPg("bookings");}}>
                  ✏️ تعديل وإعادة الإرسال
                </Btn>
              </Card>
            ))}
          </div>
        )}

        <Btn color="#22d3a0" onClick={()=>setShowVisitForm(true)} style={{width:"100%"}}>+ زيارة طبيب</Btn>
        <Btn color="#38bdf8" onClick={()=>{setEditingReq(null); setShowBookingForm(true); setPg("bookings");}} style={{width:"100%"}}>📨 طلب حجز موعد</Btn>

        {showVisitForm && <NewVisitForm user={user} db={db} act={act} onClose={()=>setShowVisitForm(false)} reasonOptions={REASON_OPTIONS}/>}
      </div>
    );

    if (pg === "visits") return (
      <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontSize:14,fontWeight:800,color:"#f0f9ff"}}>🦷 زياراتي</div>
          <Btn small color="#22d3a0" onClick={()=>setShowVisitForm(true)}>+ جديدة</Btn>
        </div>
        {showVisitForm && <NewVisitForm user={user} db={db} act={act} onClose={()=>setShowVisitForm(false)} reasonOptions={REASON_OPTIONS}/>}
        {active.length>0 && <div style={{fontSize:11,color:"#fbbf24"}}>⚡ نشطة ({active.length})</div>}
        {active.map(v => <VisitCard key={v.id} v={v} act={act} user={user}/>)}
        {pending.map(v => <VisitCard key={v.id} v={v} act={act} user={user}/>)}
        {ended.slice(0,15).map(v => <VisitCard key={v.id} v={v} act={act} user={user}/>)}
      </div>
    );

    if (pg === "apts") {
      return <ScheduleTable apts={govApts} title={`📋 جدول مواعيد مناديب ${db.govName||""}`} showGov={false} reps={db.reps||[]}/>;
    }

    if (pg === "map") return (
      <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{fontSize:14,fontWeight:800,color:"#f0f9ff"}}>🗺 خريطة موحّدة لكل النشاطات</div>
        <div style={{background:"#15100a",borderRadius:10,padding:10,fontSize:10,color:"#c9b37e",lineHeight:1.7}}>
          📍 مواقع كل الأعضاء (مندوب/تسويق/تيم ليدر) • ⚫ زيارات الفريق • 🦷 مواقع الأطباء
        </div>
        <div style={{fontSize:9,color:"#7a6841",display:"flex",gap:14,flexWrap:"wrap"}}>
          <span>👥 الأعضاء على الخريطة: <b style={{color:"#22d3a0"}}>{allMembersWithGPS.length}</b></span>
          <span>📍 الزيارات: <b style={{color:"#fbbf24"}}>{allTLandMarketingVisits.length}</b></span>
          <span>🦷 العيادات: <b style={{color:"#ef4444"}}>{doctorLocs.length}</b></span>
        </div>
        <LeafletMap
          reps={allMembersWithGPS}
          appointments={[]}
          doctorLocs={doctorLocs}
          visitLocs={allTLandMarketingVisits}
          height={500}
          govCode={user.govCode}/>
      </div>
    );

    // ═══ صفحة موقعي — للمشرف ═══
    if (pg === "mymap") return (
      <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{fontSize:14,fontWeight:800,color:"#f0f9ff"}}>📍 موقعي الحقيقي</div>
        {gps && (
          <Card style={{padding:12}}>
            <div style={{fontSize:10,color:"#22d3a0",marginBottom:2}}>إحداثيات GPS الحقيقية</div>
            <div style={{fontSize:12,color:"#f0f9ff",fontWeight:700}}>{gps}</div>
            <div style={{fontSize:9,color:"#5a4518",marginTop:2}}>يتحدّث تلقائياً</div>
          </Card>
        )}
        {!gps && (
          <Card style={{padding:14,border:"1px solid #fbbf2444",background:"#1f1a08"}}>
            <div style={{fontSize:11,color:"#fbbf24",fontWeight:700,lineHeight:1.6}}>
              ⏳ جاري طلب إذن GPS...<br/>
              <span style={{color:"#c9b37e",fontWeight:500,fontSize:10}}>يجب السماح للتطبيق بالوصول لموقعك من إعدادات الجوال</span>
            </div>
          </Card>
        )}
        <LeafletMap reps={(db.reps||[]).filter(r=>r.id===user.id)} appointments={[]} height={360} filterRepId={user.id} centerOn={myCoord} govCode={user.govCode}/>
      </div>
    );

    if (pg === "bookings") return (
      <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontSize:14,fontWeight:800,color:"#f0f9ff"}}>📨 طلبات الحجز</div>
          <Btn small color="#38bdf8" onClick={()=>{setEditingReq(null); setShowBookingForm(true);}}>+ طلب جديد</Btn>
        </div>

        {showBookingForm && <BookingRequestForm user={user} db={db} act={act} existingReq={editingReq} onClose={()=>{setShowBookingForm(false); setEditingReq(null);}}/>}

        {pendingReqs.length>0 && <div style={{fontSize:11,color:"#fbbf24"}}>⏳ في الانتظار ({pendingReqs.length})</div>}
        {pendingReqs.map(r => (
          <Card key={r.id} style={{padding:12,border:"1px solid #fbbf2455"}}>
            <div style={{fontSize:12,fontWeight:800,color:"#f0f9ff"}}>🦷 {r.drName}</div>
            <div style={{fontSize:10,color:"#94a3b8",marginTop:4}}>📅 {r.date} {fmt12(r.time)}</div>
            {r.notes && <div style={{fontSize:10,color:"#c9b37e",marginTop:4}}>📝 {r.notes}</div>}
            <div style={{fontSize:9,color:"#fbbf24",marginTop:6,fontWeight:700}}>⏳ بانتظار رد الكول سنتر</div>
          </Card>
        ))}

        {rejectedReqs.length>0 && <div style={{fontSize:11,color:"#f87171",marginTop:12}}>⚠️ مرفوضة ({rejectedReqs.length})</div>}
        {rejectedReqs.map(r => (
          <Card key={r.id} style={{padding:12,border:"2px solid #f87171",background:"#2a141488"}}>
            <div style={{fontSize:12,fontWeight:800,color:"#f0f9ff"}}>🦷 {r.drName}</div>
            <div style={{fontSize:10,color:"#94a3b8",marginTop:4}}>📅 {r.date} {fmt12(r.time)}</div>
            {r.rejectReason && <div style={{fontSize:10,color:"#fbbf24",marginTop:6,padding:8,background:"#1a0a0a",borderRadius:6}}>❌ {r.rejectReason}</div>}
            <Btn small color="#fbbf24" style={{marginTop:8,width:"100%"}}
              onClick={()=>{setEditingReq(r); setShowBookingForm(true);}}>
              ✏️ تعديل وإعادة الإرسال
            </Btn>
          </Card>
        ))}

        {approvedReqs.length>0 && <div style={{fontSize:11,color:"#22d3a0",marginTop:12}}>✅ موافَق عليها ({approvedReqs.length})</div>}
        {approvedReqs.slice(0,10).map(r => (
          <Card key={r.id} style={{padding:12,border:"1px solid #22d3a055"}}>
            <div style={{fontSize:12,fontWeight:800,color:"#f0f9ff"}}>🦷 {r.drName}</div>
            <div style={{fontSize:10,color:"#94a3b8",marginTop:4}}>📅 {r.date} {fmt12(r.time)}</div>
            <div style={{fontSize:9,color:"#22d3a0",marginTop:6,fontWeight:700}}>✅ تم تثبيت الحجز</div>
          </Card>
        ))}
      </div>
    );

    return null;
  };

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100dvh",background:"transparent",fontFamily:"'Tajawal','Inter',sans-serif",direction:"rtl",maxWidth:"min(100%, 600px)",margin:"0 auto",width:"100%"}}>
      <style>{G}</style>
      <TopBar user={user} color="#a78bfa" nc={unread} onNotif={()=>{setNo(true); if(unread>0) onMarkAll();}} onLogout={onLogout} title="مشرف"/>
      <NotifBanner notifPerm={notifPerm} requestNotifs={requestNotifs}/>
      <div style={{flex:1,overflowY:"auto"}}>{rp()}</div>
      <BNav items={NAV} active={pg} onChange={setPg}/>
      {no&&<NPanel notifs={notifs} color="#a78bfa" onClose={()=>setNo(false)} onMarkAll={onMarkAll}/>}
    </div>
  );
}

// ═══════════════════════════════════════════════
//  ADMIN APP
// ═══════════════════════════════════════════════
function AdminApp({user,db,act,notifs,unread,onLogout,onMarkAll,notifPerm,requestNotifs}) {
  const [pg,setPg]=useState("map");
  const [no,setNo]=useState(false);
  // فتح لوحة الإشعارات تلقائياً عند الضغط على push notification
  useEffect(()=>{
    const open = () => setNo(true);
    window.addEventListener("britao:openNotifs", open);
    return () => window.removeEventListener("britao:openNotifs", open);
  },[]);
  const [exp,setExp]=useState(null);
  const [docFilterRep,setDocFilterRep]=useState("");
  const [docFilterGov,setDocFilterGov]=useState("ALL");
  const [docDateFrom,setDocDateFrom]=useState("");
  const [docDateTo,setDocDateTo]=useState("");
  const [docSortBy,setDocSortBy]=useState("date_desc");
  const [visitFilterLeader,setVisitFilterLeader]=useState("");
  const [visitDateFrom,setVisitDateFrom]=useState("");
  const [visitDateTo,setVisitDateTo]=useState("");
  const [visitSortBy,setVisitSortBy]=useState("date_desc");
  const [cmpFilterGov,setCmpFilterGov]=useState("ALL");
  const [cmpShow,setCmpShow]=useState("ALL"); // ALL | visited | unvisited
  // فلتر الخريطة الرئيسية (محافظة + نوع العضو + الفرد)
  const [mapFilterGov, setMapFilterGov] = useState("ALL");
  const [mapFilterRep, setMapFilterRep] = useState("");
  const [mapFilterRole, setMapFilterRole] = useState("ALL"); // ALL | rep | marketing | supervisor | mushref
  // فلتر خريطة الزيارات (نوع الزيارة + المحافظة + الفرد)
  const [vmapFilterRole, setVmapFilterRole] = useState("ALL");
  const [vmapFilterGov, setVmapFilterGov] = useState("ALL");
  const [vmapFilterUser, setVmapFilterUser] = useState("");

  // فلترة حسب المحافظة عند مدير المحافظة (غير المدير العام)
  const sameGov = (r) => db.isAdminAll || !r.govCode || r.govCode === user.govCode;
  const visibleReps = db.reps.filter(sameGov);
  const visibleApts = db.appointments.filter(sameGov);

  const NAV = db.isAdminAll
    ? [{id:"map",icon:"🗺",label:"الخريطة",c:"#22d3a0"},{id:"docLocs",icon:"🦷",label:"مواقع الأطباء",c:"#ef4444"},{id:"visitsMap",icon:"📍",label:"خريطة الزيارات",c:"#a78bfa"},{id:"compare",icon:"📊",label:"المقارنة",c:"#22d3a0"},{id:"apts",icon:"📅",label:"المواعيد",c:"#60a5fa"},{id:"table",icon:"📋",label:"الجدول",c:"#f472b6"},{id:"reports",icon:"📈",label:"التقارير",c:"#c084fc"},{id:"reps",icon:"👥",label:"الفريق",c:"#fbbf24"},{id:"devices",icon:"📱",label:"الأجهزة",c:"#38bdf8"}]
    : [{id:"map",icon:"🗺",label:"الخريطة",c:"#22d3a0"},{id:"visitsMap",icon:"📍",label:"خريطة الزيارات",c:"#a78bfa"},{id:"apts",icon:"📅",label:"المواعيد",c:"#60a5fa"},{id:"table",icon:"📋",label:"الجدول",c:"#f472b6"},{id:"reports",icon:"📈",label:"التقارير",c:"#c084fc"},{id:"reps",icon:"👥",label:"الفريق",c:"#fbbf24"},{id:"devices",icon:"📱",label:"الأجهزة",c:"#38bdf8"}];

  const rp=()=>{
    if(pg==="map") {
      // ═══ تطبيق فلتر المحافظة + نوع العضو + الفرد ═══
      const govList = Object.keys(GOV_CODES).filter(c => c !== "ADMIN-00");
      // عدد الأعضاء لكل محافظة
      const repsCountByGov = govList.reduce((acc,c)=>{
        acc[c] = visibleReps.filter(r=>r.govCode===c).length;
        return acc;
      },{});

      // فلترة حسب المحافظة
      const govFilteredReps   = mapFilterGov === "ALL" ? visibleReps : visibleReps.filter(r=>r.govCode===mapFilterGov);
      const govFilteredApts   = mapFilterGov === "ALL" ? visibleApts : visibleApts.filter(a=>a.govCode===mapFilterGov);
      const govFilteredVisits = (db.visits||[]).filter(v => v.lat && v.lng && (mapFilterGov==="ALL" || v.govCode===mapFilterGov));

      // فلترة حسب نوع العضو (الدور)
      const roleFilteredReps = mapFilterRole === "ALL" ? govFilteredReps : govFilteredReps.filter(r => (r.role||"rep") === mapFilterRole);

      // فلترة حسب العضو الفرد (من ضمن المعروضين)
      const finalReps   = mapFilterRep ? roleFilteredReps.filter(r=>r.id===mapFilterRep) : roleFilteredReps;
      const finalApts   = mapFilterRep ? govFilteredApts.filter(a=>a.repId===mapFilterRep) : govFilteredApts;
      const finalVisits = mapFilterRep ? govFilteredVisits.filter(v=>v.leaderId===mapFilterRep) : govFilteredVisits;

      // مركز الخريطة الفعّال (govCode للمحافظة المختارة، أو محافظة المستخدم لو ALL)
      const effectiveGovCode = mapFilterGov !== "ALL" ? mapFilterGov : user.govCode;
      // لو اختار مندوب وعنده إحداثيات → نمرّر centerOn (مع إخفاء marker "موقعي" لأننا مدير)
      // الـ key يجبر إعادة بناء الخريطة عند تغيير المحافظة لتأخذ المركز الجديد
      const mapKey = `adm-map-${effectiveGovCode||"all"}-${mapFilterRep||"none"}`;

      return (
      <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
          <div style={{fontSize:13,fontWeight:800,color:"#f0f9ff"}}>🗺 خريطة مباشرة — حالة الأعضاء (GPS)</div>
          {(mapFilterGov!=="ALL" || mapFilterRep || mapFilterRole!=="ALL") && (
            <button onClick={()=>{setMapFilterGov("ALL");setMapFilterRep("");setMapFilterRole("ALL");}}
              style={{background:"#2e0a0a",border:"1px solid #f87171",borderRadius:8,padding:"6px 12px",color:"#f87171",fontSize:10,fontWeight:800,cursor:"pointer",fontFamily:"'Tajawal','Inter',sans-serif"}}>
              ✕ مسح الفلتر
            </button>
          )}
        </div>

        {/* ═══ فلاتر المحافظة + نوع العضو + الفرد (للمدير العام فقط) ═══ */}
        {db.isAdminAll && (
          <Card style={{padding:12,display:"flex",flexDirection:"column",gap:10}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              {/* فلتر المحافظة */}
              <div>
                <div style={{fontSize:10,color:"#94a3b8",marginBottom:5,fontWeight:700}}>📍 المحافظة</div>
                <select value={mapFilterGov} onChange={e=>{setMapFilterGov(e.target.value); setMapFilterRep("");}}
                  style={{width:"100%",padding:"9px 10px",borderRadius:10,background:"#0a0a0e",border:"1px solid #3d2f15",color:"#f0f9ff",fontSize:12,fontFamily:"'Tajawal','Inter',sans-serif",fontWeight:700}}>
                  <option value="ALL">— كل المحافظات ({visibleReps.length} عضو) —</option>
                  {govList.map(c=>{
                    if (repsCountByGov[c] === 0) return null;
                    return <option key={c} value={c}>{GOV_CODES[c]} ({repsCountByGov[c]})</option>;
                  })}
                </select>
              </div>
              {/* فلتر نوع العضو */}
              <div>
                <div style={{fontSize:10,color:"#94a3b8",marginBottom:5,fontWeight:700}}>🧩 نوع العضو</div>
                <select value={mapFilterRole} onChange={e=>{setMapFilterRole(e.target.value); setMapFilterRep("");}}
                  style={{width:"100%",padding:"9px 10px",borderRadius:10,background:"#0a0a0e",border:"1px solid #3d2f15",color:"#f0f9ff",fontSize:12,fontFamily:"'Tajawal','Inter',sans-serif",fontWeight:700}}>
                  <option value="ALL">— كل الأعضاء ({govFilteredReps.length}) —</option>
                  <option value="rep">🚗 مناديب السكنر ({govFilteredReps.filter(r=>(r.role||"rep")==="rep").length})</option>
                  <option value="marketing">🌹 مناديب التسويق ({govFilteredReps.filter(r=>r.role==="marketing").length})</option>
                  <option value="supervisor">👁 تيم ليدر ({govFilteredReps.filter(r=>r.role==="supervisor").length})</option>
                  <option value="mushref">🛡 مشرف ({govFilteredReps.filter(r=>r.role==="mushref").length})</option>
                </select>
              </div>
            </div>
            {/* فلتر العضو الفرد — صف ثاني */}
            <div>
              <div style={{fontSize:10,color:"#94a3b8",marginBottom:5,fontWeight:700}}>👤 عضو محدّد</div>
              <select value={mapFilterRep} onChange={e=>setMapFilterRep(e.target.value)}
                style={{width:"100%",padding:"9px 10px",borderRadius:10,background:"#0a0a0e",border:"1px solid #3d2f15",color:"#f0f9ff",fontSize:12,fontFamily:"'Tajawal','Inter',sans-serif",fontWeight:700,boxSizing:"border-box"}}>
                <option value="">— كل الأعضاء ({roleFilteredReps.length}) —</option>
                {roleFilteredReps.map(r => {
                  const roleIcon = r.role==="marketing"?"🌹":r.role==="supervisor"?"👁":r.role==="mushref"?"🛡":"🚗";
                  return (
                    <option key={r.id} value={r.id}>
                      {roleIcon} {r.fullName || r.name || r.id}{r.area ? ` — ${r.area}` : ""}
                    </option>
                  );
                })}
              </select>
            </div>
            {/* بطاقة إحصائيات الفلتر */}
            {(mapFilterGov !== "ALL" || mapFilterRep) && (
              <div style={{background:"rgba(34,211,160,.06)",border:"1px solid rgba(34,211,160,.25)",borderRadius:10,padding:"8px 12px",fontSize:11,color:"#86efac",display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                <span>🔍</span>
                <span>عرض:</span>
                {mapFilterGov !== "ALL" && <b style={{color:"#22d3a0"}}>{GOV_CODES[mapFilterGov]}</b>}
                {mapFilterRep && (
                  <>
                    {mapFilterGov !== "ALL" && <span>•</span>}
                    <b style={{color:"#fbbf24"}}>{(govFilteredReps.find(r=>r.id===mapFilterRep)?.fullName)||(govFilteredReps.find(r=>r.id===mapFilterRep)?.name)||mapFilterRep}</b>
                  </>
                )}
                <span style={{color:"#94a3b8",marginRight:"auto"}}>
                  {finalReps.length} عضو • {finalApts.length} موعد
                </span>
              </div>
            )}
          </Card>
        )}

        {/* بطاقات الأعضاء (مع مؤشّر اونلاين/اوفلاين) */}
        <div style={{display:"flex",gap:8,overflowX:"auto",paddingBottom:4}}>
          {finalReps.length === 0 ? (
            <div style={{padding:"12px 16px",color:"#71717a",fontSize:11,textAlign:"center",width:"100%"}}>
              لا يوجد أعضاء لعرضهم بهذه الفلاتر
            </div>
          ) : finalReps.map(r=>{
            // ═══ حساب حالة الاتصال (Online/Offline) ═══
            const ls = r.lastSeen?.toMillis?.() || (r.lastSeen?.seconds ? r.lastSeen.seconds*1000 : 0);
            const ageMs = ls ? Date.now() - ls : Infinity;
            const isOnline  = ageMs < 5 * 60 * 1000;     // < 5 دقائق
            const isIdle    = ageMs < 15 * 60 * 1000;    // 5-15 دقيقة
            const dotColor  = isOnline ? "#22d3a0" : isIdle ? "#fbbf24" : "#ef4444";
            const stateLbl  = isOnline ? "اونلاين" : isIdle ? "خامل" : "اوفلاين";
            const roleIcon  = r.role==="marketing"?"🌹":r.role==="supervisor"?"👁":r.role==="mushref"?"🛡":"🚗";
            const roleColor = r.role==="marketing"?"#fb7185":r.role==="supervisor"?"#a78bfa":r.role==="mushref"?"#22d3a0":"#60a5fa";
            return (
              <div key={r.id} onClick={()=>setMapFilterRep(mapFilterRep===r.id?"":r.id)}
                style={{
                  background: mapFilterRep===r.id ? "#1a3a2a" : "#15100a",
                  border:`1px solid ${mapFilterRep===r.id ? "#22d3a0" : roleColor+"44"}`,
                  borderRadius:12, padding:"8px 12px", flexShrink:0, cursor:"pointer",
                  transition:"all .2s ease", minWidth:140
                }}>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                  <div style={{width:8,height:8,borderRadius:"50%",background:dotColor,animation:isOnline?"pulse 2s infinite":"none"}}/>
                  <span style={{fontSize:9,color:dotColor,fontWeight:800}}>{stateLbl}</span>
                  <span style={{fontSize:11}}>{roleIcon}</span>
                </div>
                <div style={{fontSize:11,fontWeight:700,color:"#f0f9ff",marginBottom:2}}>{r.fullName||r.name}</div>
                <div style={{fontSize:9,color:"#d4af37",fontWeight:700}}>
                  {r.govCode && db.isAdminAll ? `${GOV_CODES[r.govCode]||""}` : (r.area||"")}
                </div>
              </div>
            );
          })}
        </div>

        {/* الخريطة — المواقع الحالية للأعضاء فقط (بدون زيارات — الزيارات في صفحة خريطة الزيارات) */}
        <LeafletMap
          key={mapKey}
          reps={finalReps}
          appointments={finalApts}
          height={360}
          govCode={effectiveGovCode}
        />

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
          {[
            {l:"نشطون", v:finalReps.filter(r=>r.status==="active").length, c:"#22d3a0"},
            {l:"مواعيد", v:finalApts.length, c:"#60a5fa"},
            {l:"مكتملة", v:finalApts.filter(a=>a.status==="مكتملة").length, c:"#22d3a0"},
          ].map((s,i)=>(
            <Card key={i} style={{padding:10,textAlign:"center",border:`1px solid ${s.c}22`}}>
              <div style={{fontSize:18,fontWeight:900,color:s.c}}>{s.v}</div>
              <div style={{fontSize:9,color:"#d4af37",fontWeight:800,marginTop:3}}>{s.l}</div>
            </Card>
          ))}
        </div>
      </div>
      );
    }
    if(pg==="docLocs") {
      const allLocs = db.docLocs || [];
      // فلتر بالمحافظة أولاً
      const govFiltered = (db.isAdminAll && docFilterGov!=="ALL")
        ? allLocs.filter(l=>l.govCode===docFilterGov)
        : allLocs;
      const repsList = [...new Map(govFiltered.filter(l=>l.repId).map(l=>[l.repId,{id:l.repId,name:l.repName||l.repId}])).values()];
      // فلتر بالمندوب
      let filtered = docFilterRep ? govFiltered.filter(l=>l.repId===docFilterRep) : govFiltered;
      // فلتر بالتاريخ
      filtered = filtered.filter(l=>{
        const d = l.lastVisitDate || "";
        if (docDateFrom && d < docDateFrom) return false;
        if (docDateTo && d > docDateTo) return false;
        return true;
      });
      const govList = Object.keys(GOV_CODES).filter(c=>c!=="ADMIN-00");
      // الفهرسة
      const sortedLocs = [...filtered].sort((a,b)=>{
        if (docSortBy==="date_asc")  return (a.lastVisitDate||"").localeCompare(b.lastVisitDate||"");
        if (docSortBy==="dr")        return (a.drName||"").localeCompare(b.drName||"","ar");
        if (docSortBy==="visits")    return (b.visitsCount||0) - (a.visitsCount||0);
        return (b.lastVisitDate||"").localeCompare(a.lastVisitDate||"");
      });

      const deleteAll = async () => {
        if (!window.confirm(`حذف ${filtered.length} موقع طبيب؟ لا يمكن التراجع.`)) return;
        if (!window.confirm("تأكيد ثاني — هل أنت متأكد؟")) return;
        for (const l of filtered) await act.deleteDocLoc(l);
      };

      return (
        <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap"}}>
            <div style={{fontSize:14,fontWeight:800,color:"#ef4444"}}>🦷 مواقع الأطباء — العيادات المُصوَّرة</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              <button onClick={()=>exportMapPdf({
                title:"خريطة مواقع الأطباء",
                subtitle: `${db.isAdminAll && docFilterGov!=="ALL"?GOV_CODES[docFilterGov]||docFilterGov:"كل المحافظات"} • ${filtered.length} موقع`,
                locations: filtered.filter(l=>l.lat&&l.lng)
              })}
                style={{background:"linear-gradient(135deg,#1a3a2a,#22d3a0)",color:"#000",border:"none",borderRadius:8,padding:"6px 14px",fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"Cairo,sans-serif",whiteSpace:"nowrap"}}>
                🗺 طباعة الخريطة
              </button>
              <PdfBtn onClick={()=>{
                exportToPdf({
                  title:"مواقع الأطباء — العيادات المُصوَّرة",
                  subtitle: `${db.isAdminAll && docFilterGov!=="ALL"?GOV_CODES[docFilterGov]||docFilterGov:"كل المحافظات"}${docFilterRep?` • المندوب: ${repsList.find(r=>r.id===docFilterRep)?.name||docFilterRep}`:""}${docDateFrom||docDateTo?` • التاريخ: ${docDateFrom||"البداية"} → ${docDateTo||"الآن"}`:""}`,
                  columns:["الطبيب","المحافظة","المندوب","التاريخ","الوقت","عدد الزيارات"],
                  rows: sortedLocs.map(l=>[l.drName||"—", l.govName||"—", l.repName||"—", l.lastVisitDate||"—", fmt12(l.lastVisitTime)||"—", l.visitsCount||1])
                });
              }}/>
              {db.isAdminAll && filtered.length>0 && (
                <button onClick={deleteAll}
                  style={{background:"#2e0a0a",border:"1px solid #f87171",borderRadius:8,padding:"6px 12px",color:"#f87171",fontSize:10,fontWeight:800,cursor:"pointer",fontFamily:"Cairo,sans-serif"}}>
                  🗑 حذف الكل ({filtered.length})
                </button>
              )}
            </div>
          </div>

          <DateSortFilter
            dateFrom={docDateFrom} setDateFrom={setDocDateFrom}
            dateTo={docDateTo}     setDateTo={setDocDateTo}
            sortBy={docSortBy}     setSortBy={setDocSortBy}
            sortOptions={[["date_desc","📅 الأحدث زيارة أولاً"],["date_asc","📅 الأقدم زيارة أولاً"],["dr","🦷 اسم الطبيب (أ-ي)"],["visits","🔢 عدد الزيارات الأكثر"]]}
          />
          <Card style={{padding:10,display:"flex",flexDirection:"column",gap:8}}>
            {db.isAdminAll && (
              <>
                <div style={{fontSize:10,color:"#94a3b8"}}>فلتر حسب المحافظة:</div>
                <select value={docFilterGov} onChange={e=>{setDocFilterGov(e.target.value);setDocFilterRep("");}}
                  style={{width:"100%",padding:"8px",borderRadius:8,background:"#0a0600",border:"1px solid #3d2f15",color:"#f0f9ff",fontSize:11,fontFamily:"Cairo,sans-serif"}}>
                  <option value="ALL">— كل المحافظات ({allLocs.length} عيادة) —</option>
                  {govList.map(c=>{
                    const cnt = allLocs.filter(l=>l.govCode===c).length;
                    if (cnt===0) return null;
                    return <option key={c} value={c}>{GOV_CODES[c]} ({cnt})</option>;
                  })}
                </select>
              </>
            )}
            <div style={{fontSize:10,color:"#94a3b8"}}>فلتر حسب المندوب:</div>
            <select value={docFilterRep} onChange={e=>setDocFilterRep(e.target.value)}
              style={{width:"100%",padding:"8px",borderRadius:8,background:"#0a0600",border:"1px solid #3d2f15",color:"#f0f9ff",fontSize:11,fontFamily:"Cairo,sans-serif"}}>
              <option value="">— كل المناديب ({govFiltered.length} عيادة) —</option>
              {repsList.map(r=>(
                <option key={r.id} value={r.id}>{r.name} ({govFiltered.filter(l=>l.repId===r.id).length})</option>
              ))}
            </select>
          </Card>
          <NumberedDoctorsMap
            locations={filtered}
            height={360}
            govCode={db.isAdminAll && docFilterGov!=="ALL" ? docFilterGov : (db.isAdminAll ? null : user.govCode)}
          />
          <div style={{fontSize:11,color:"#94a3b8",marginTop:4}}>📋 جدول العيادات ({filtered.length})</div>
          <div style={{background:"#15100a",border:"1px solid #3d2f15",borderRadius:12,overflow:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:10,fontFamily:"Cairo,sans-serif",direction:"rtl",minWidth:600}}>
              <thead>
                <tr style={{background:"#0d0a00",color:"#fbbf24"}}>
                  <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>الطبيب</th>
                  <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>المحافظة</th>
                  <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>المندوب</th>
                  <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>التاريخ والوقت</th>
                  <th style={{padding:"8px 6px",textAlign:"center",fontWeight:800}}>زيارات</th>
                  {db.isAdminAll && <th style={{padding:"8px 6px",textAlign:"center",fontWeight:800,color:"#f87171"}}>حذف</th>}
                </tr>
              </thead>
              <tbody>
                {sortedLocs.length===0 && (
                  <tr><td colSpan={db.isAdminAll?6:5} style={{padding:20,textAlign:"center",color:"#64748b"}}>لا توجد بيانات</td></tr>
                )}
                {sortedLocs.map(l=>(
                  <tr key={l.id} style={{borderTop:"1px solid #3d2f1522"}}>
                    <td style={{padding:"8px 6px",color:"#f0f9ff",fontWeight:700}}>🦷 {l.drName||"—"}</td>
                    <td style={{padding:"8px 6px",color:"#f472b6"}}>{l.govName||"—"}</td>
                    <td style={{padding:"8px 6px",color:"#60a5fa"}}>{l.repName||"—"}</td>
                    <td style={{padding:"8px 6px",color:"#94a3b8",fontSize:9}}>{l.lastVisitDate||"—"} {fmt12(l.lastVisitTime)}</td>
                    <td style={{padding:"8px 6px",textAlign:"center",color:"#fbbf24",fontWeight:800}}>{l.visitsCount||1}</td>
                    {db.isAdminAll && (
                      <td style={{padding:"8px 6px",textAlign:"center"}}>
                        <button onClick={()=>{ if(window.confirm(`حذف موقع "${l.drName}"؟`)) act.deleteDocLoc(l); }}
                          style={{background:"#2e0a0a",border:"1px solid #f8717155",borderRadius:6,padding:"3px 8px",color:"#f87171",fontSize:10,cursor:"pointer",fontFamily:"Cairo,sans-serif"}}>🗑</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    // ═══ صفحة خريطة الزيارات الموحَّدة (تيم ليدر + مندوب تسويق + مشرف) ═══
    if(pg==="visitsMap") {
      const allV = (db.visits||[]).filter(sameGov);
      // تصنيف حسب نوع الزيارة (الدور)
      const byRole = (v) => {
        if (v.leaderRole === "marketing")  return "marketing";
        if (v.leaderRole === "mushref")    return "mushref";
        return "supervisor"; // افتراضي = تيم ليدر
      };
      // فلتر المحافظة
      let filtered = vmapFilterGov === "ALL" ? allV : allV.filter(v => v.govCode === vmapFilterGov);
      // فلتر الدور
      if (vmapFilterRole !== "ALL") filtered = filtered.filter(v => byRole(v) === vmapFilterRole);
      // فلتر العضو الفرد
      if (vmapFilterUser) filtered = filtered.filter(v => v.leaderId === vmapFilterUser);

      // قوائم القيم المُتاحة بعد الفلاتر العليا (تتفاعل تتالياً)
      const govList = Object.keys(GOV_CODES).filter(c => c !== "ADMIN-00");
      const usersFromVisits = [...new Map(
        filtered.filter(v => v.leaderId).map(v => [v.leaderId, {
          id: v.leaderId,
          name: v.leaderName || v.leaderId,
          role: byRole(v),
          gov: v._gov || (v.govCode ? GOV_CODES[v.govCode] : "—"),
        }])
      ).values()];

      const withLoc = filtered.filter(v => v.lat && v.lng);

      // عداد لكل دور (يعكس الفلاتر العليا قبل فلتر الدور نفسه)
      const baseAfterGov = vmapFilterGov === "ALL" ? allV : allV.filter(v => v.govCode === vmapFilterGov);
      const cntRole = (role) => baseAfterGov.filter(v => byRole(v) === role).length;

      const effectiveGovCodeV = vmapFilterGov !== "ALL" ? vmapFilterGov : (db.isAdminAll ? null : user.govCode);
      const visitsMapKey = `vmap-${effectiveGovCodeV||"all"}-${vmapFilterRole}-${vmapFilterUser||"none"}`;

      return (
        <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
            <div style={{fontSize:14,fontWeight:800,color:"#a78bfa"}}>📍 خريطة الزيارات — كل الفِرَق</div>
            {(vmapFilterGov!=="ALL" || vmapFilterRole!=="ALL" || vmapFilterUser) && (
              <button onClick={()=>{setVmapFilterGov("ALL");setVmapFilterRole("ALL");setVmapFilterUser("");}}
                style={{background:"#2e0a0a",border:"1px solid #f87171",borderRadius:8,padding:"6px 12px",color:"#f87171",fontSize:10,fontWeight:800,cursor:"pointer",fontFamily:"'Tajawal','Inter',sans-serif"}}>
                ✕ مسح الفلتر
              </button>
            )}
          </div>

          {/* ═══ مفتاح الألوان ═══ */}
          <Card style={{padding:10,display:"flex",gap:14,flexWrap:"wrap",justifyContent:"center",alignItems:"center"}}>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <div style={{width:14,height:14,borderRadius:"50%",background:"#000",border:"2px solid #a78bfa",display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,color:"#a78bfa"}}>👁</div>
              <span style={{fontSize:10,color:"#a78bfa",fontWeight:700}}>تيم ليدر</span>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <div style={{width:14,height:14,borderRadius:"50%",background:"#1a0507",border:"2px solid #fb7185",display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,color:"#fb7185"}}>🌹</div>
              <span style={{fontSize:10,color:"#fb7185",fontWeight:700}}>مندوب تسويق</span>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <div style={{width:14,height:14,borderRadius:"50%",background:"#0d0a1f",border:"2px solid #22d3a0",display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,color:"#22d3a0"}}>🛡</div>
              <span style={{fontSize:10,color:"#22d3a0",fontWeight:700}}>مشرف</span>
            </div>
          </Card>

          {/* ═══ الفلاتر ═══ */}
          {db.isAdminAll && (
            <Card style={{padding:12,display:"flex",flexDirection:"column",gap:10}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <div>
                  <div style={{fontSize:10,color:"#94a3b8",marginBottom:5,fontWeight:700}}>📍 المحافظة</div>
                  <select value={vmapFilterGov} onChange={e=>{setVmapFilterGov(e.target.value); setVmapFilterUser("");}}
                    style={{width:"100%",padding:"9px 10px",borderRadius:10,background:"#0a0a0e",border:"1px solid #3d2f15",color:"#f0f9ff",fontSize:12,fontFamily:"'Tajawal','Inter',sans-serif",fontWeight:700}}>
                    <option value="ALL">— كل المحافظات ({allV.length} زيارة) —</option>
                    {govList.map(c=>{
                      const cnt = allV.filter(v=>v.govCode===c).length;
                      if (cnt===0) return null;
                      return <option key={c} value={c}>{GOV_CODES[c]} ({cnt})</option>;
                    })}
                  </select>
                </div>
                <div>
                  <div style={{fontSize:10,color:"#94a3b8",marginBottom:5,fontWeight:700}}>🧩 نوع الزيارة</div>
                  <select value={vmapFilterRole} onChange={e=>{setVmapFilterRole(e.target.value); setVmapFilterUser("");}}
                    style={{width:"100%",padding:"9px 10px",borderRadius:10,background:"#0a0a0e",border:"1px solid #3d2f15",color:"#f0f9ff",fontSize:12,fontFamily:"'Tajawal','Inter',sans-serif",fontWeight:700}}>
                    <option value="ALL">— كل الأنواع ({baseAfterGov.length}) —</option>
                    <option value="supervisor">👁 تيم ليدر ({cntRole("supervisor")})</option>
                    <option value="marketing">🌹 مندوب تسويق ({cntRole("marketing")})</option>
                    <option value="mushref">🛡 مشرف ({cntRole("mushref")})</option>
                  </select>
                </div>
              </div>
              <div>
                <div style={{fontSize:10,color:"#94a3b8",marginBottom:5,fontWeight:700}}>👤 عضو محدّد</div>
                <select value={vmapFilterUser} onChange={e=>setVmapFilterUser(e.target.value)}
                  style={{width:"100%",padding:"9px 10px",borderRadius:10,background:"#0a0a0e",border:"1px solid #3d2f15",color:"#f0f9ff",fontSize:12,fontFamily:"'Tajawal','Inter',sans-serif",fontWeight:700,boxSizing:"border-box"}}>
                  <option value="">— كل الأعضاء ({usersFromVisits.length}) —</option>
                  {usersFromVisits.map(u => {
                    const icon = u.role==="marketing"?"🌹":u.role==="mushref"?"🛡":"👁";
                    return <option key={u.id} value={u.id}>{icon} {u.name}{db.isAdminAll ? ` — ${u.gov}` : ""}</option>;
                  })}
                </select>
              </div>
              <div style={{background:"rgba(167,139,250,.06)",border:"1px solid rgba(167,139,250,.25)",borderRadius:10,padding:"8px 12px",fontSize:11,color:"#c4b5fd",display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                <span>📊</span>
                <span style={{color:"#94a3b8"}}>عرض:</span>
                <b style={{color:"#a78bfa"}}>{withLoc.length} زيارة على الخريطة</b>
                <span style={{color:"#94a3b8"}}>•</span>
                <b style={{color:"#fbbf24"}}>{filtered.length} إجمالي</b>
              </div>
            </Card>
          )}

          {/* الخريطة */}
          <LeafletMap key={visitsMapKey} reps={[]} appointments={[]} visitLocs={withLoc} height={420} govCode={effectiveGovCodeV}/>

          {/* جدول مختصر للزيارات */}
          <div style={{fontSize:11,color:"#94a3b8",marginTop:4}}>📋 جدول الزيارات ({filtered.length})</div>
          <div style={{background:"#15100a",border:"1px solid #3d2f15",borderRadius:12,overflow:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:10,fontFamily:"'Tajawal','Inter',sans-serif",direction:"rtl",minWidth:600}}>
              <thead>
                <tr style={{background:"#0d0a00",color:"#a78bfa"}}>
                  <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>الطبيب</th>
                  <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>العيادة</th>
                  {db.isAdminAll && <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>المحافظة</th>}
                  <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>العضو</th>
                  <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>النوع</th>
                  <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>السبب</th>
                  <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>التاريخ</th>
                  <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>الحالة</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length===0 && (
                  <tr><td colSpan={db.isAdminAll?8:7} style={{padding:30,textAlign:"center",color:"#5a4518"}}>لا توجد زيارات بهذه الفلاتر</td></tr>
                )}
                {filtered.slice(0,200).map(v=>{
                  const role = byRole(v);
                  const roleLbl = role==="marketing"?"🌹 تسويق":role==="mushref"?"🛡 مشرف":"👁 تيم ليدر";
                  const roleColor = role==="marketing"?"#fb7185":role==="mushref"?"#22d3a0":"#a78bfa";
                  return (
                    <tr key={v.id} style={{borderTop:"1px solid #3d2f1522"}}>
                      <td style={{padding:"8px 6px",color:"#f0f9ff",fontWeight:700}}>🦷 {v.drName||"—"}</td>
                      <td style={{padding:"8px 6px",color:"#c9b37e"}}>{v.clinic||"—"}</td>
                      {db.isAdminAll && <td style={{padding:"8px 6px",color:"#f472b6"}}>{v._gov||"—"}</td>}
                      <td style={{padding:"8px 6px",color:roleColor,fontWeight:700}}>{v.leaderName||"—"}</td>
                      <td style={{padding:"8px 6px",color:roleColor,fontWeight:700}}>{roleLbl}</td>
                      <td style={{padding:"8px 6px",color:"#d4d4d8"}}>{v.reason}{v.reason==="أخرى"&&v.reasonOther?": "+v.reasonOther:""}</td>
                      <td style={{padding:"8px 6px",color:"#60a5fa"}}>{v.date||"—"}</td>
                      <td style={{padding:"8px 6px",color:"#22d3a0"}}>{v.status||"—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    // ═══ الصفحة القديمة "زيارات التيم ليدر" — احتياط (لم تعد في NAV) ═══
    if(pg==="visits") {
      const allVisits = db.visits || [];
      const leadersList = [...new Map(allVisits.filter(v=>v.leaderId).map(v=>[v.leaderId,{id:v.leaderId,name:v.leaderName||v.leaderId}])).values()];
      let filteredVisits = visitFilterLeader ? allVisits.filter(v=>v.leaderId===visitFilterLeader) : allVisits;
      // فلتر التاريخ
      filteredVisits = filteredVisits.filter(v=>{
        const d = v.date || "";
        if (visitDateFrom && d < visitDateFrom) return false;
        if (visitDateTo && d > visitDateTo) return false;
        return true;
      });
      // الفهرسة
      const sortedVisits = [...filteredVisits].sort((a,b)=>{
        if (visitSortBy==="date_asc") return (a.date||"").localeCompare(b.date||"");
        if (visitSortBy==="dr")       return (a.drName||"").localeCompare(b.drName||"","ar");
        if (visitSortBy==="leader")   return (a.leaderName||"").localeCompare(b.leaderName||"","ar");
        return (b.date||"").localeCompare(a.date||"");
      });
      const withLoc = sortedVisits.filter(v=>v.lat&&v.lng);
      return (
        <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap"}}>
            <div style={{fontSize:14,fontWeight:800,color:"#a78bfa"}}>👁 زيارات التيم ليدر — العيادات المُزارة</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              <button onClick={()=>exportMapPdf({
                title:"خريطة زيارات التيم ليدر",
                subtitle: `${visitFilterLeader?leadersList.find(r=>r.id===visitFilterLeader)?.name:"كل التيم ليدرز"} • ${withLoc.length} موقع`,
                locations: withLoc
              })}
                style={{background:"linear-gradient(135deg,#1a3a2a,#22d3a0)",color:"#000",border:"none",borderRadius:8,padding:"6px 14px",fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"Cairo,sans-serif",whiteSpace:"nowrap"}}>
                🗺 طباعة الخريطة
              </button>
              <PdfBtn onClick={()=>{
                exportToPdf({
                  title:"زيارات التيم ليدر",
                  subtitle: `${visitFilterLeader ? leadersList.find(r=>r.id===visitFilterLeader)?.name||visitFilterLeader : "كل التيم ليدرز"}${visitDateFrom||visitDateTo?` • ${visitDateFrom||"البداية"} → ${visitDateTo||"الآن"}`:""}`,
                  columns:["الطبيب","العيادة","المحافظة","التيم ليدر","السبب","الموعد","آخر زيارة","الحالة","النتيجة","التعليق"],
                  rows: sortedVisits.map(v=>[
                    v.drName||"—", v.clinic||"—", v._gov||"—", v.leaderName||"—",
                    v.reason+(v.reason==="أخرى"&&v.reasonOther?": "+v.reasonOther:""),
                    (v.date||"—")+" "+fmt12(v.time), v.lastVisitDate||"—",
                    v.status, v.result||"—", v.resultComment||"—"
                  ])
                });
              }}/>
            </div>
          </div>
          <Card style={{padding:10}}>
            <div style={{fontSize:10,color:"#94a3b8",marginBottom:6}}>فلتر حسب التيم ليدر:</div>
            <select value={visitFilterLeader} onChange={e=>setVisitFilterLeader(e.target.value)} style={{width:"100%",padding:"8px",borderRadius:8,background:"#0a0600",border:"1px solid #3d2f15",color:"#f0f9ff",fontSize:11,fontFamily:"Cairo,sans-serif"}}>
              <option value="">— كل التيم ليدرز ({allVisits.length} زيارة) —</option>
              {leadersList.map(r=>(
                <option key={r.id} value={r.id}>{r.name} ({allVisits.filter(v=>v.leaderId===r.id).length})</option>
              ))}
            </select>
          </Card>
          <DateSortFilter
            dateFrom={visitDateFrom} setDateFrom={setVisitDateFrom}
            dateTo={visitDateTo}     setDateTo={setVisitDateTo}
            sortBy={visitSortBy}     setSortBy={setVisitSortBy}
            sortOptions={[["date_desc","📅 الأحدث أولاً"],["date_asc","📅 الأقدم أولاً"],["dr","🦷 اسم الطبيب"],["leader","👁 اسم التيم ليدر"]]}
          />
          <LeafletMap reps={[]} appointments={[]} visitLocs={withLoc} height={360} govCode={db.isAdminAll ? null : user.govCode}/>
          <div style={{fontSize:11,color:"#94a3b8",marginTop:4}}>📋 جدول الزيارات ({sortedVisits.length})</div>
          <div style={{background:"#15100a",border:"1px solid #3d2f15",borderRadius:12,overflow:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:10,fontFamily:"Cairo,sans-serif",direction:"rtl",minWidth:700}}>
              <thead>
                <tr style={{background:"#0d0a00",color:"#a78bfa"}}>
                  <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>الطبيب</th>
                  <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>العيادة</th>
                  <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>المحافظة</th>
                  <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>التيم ليدر</th>
                  <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>السبب</th>
                  <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>الموعد</th>
                  <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>آخر زيارة</th>
                  <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>الحالة</th>
                  <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>النتيجة</th>
                  <th style={{padding:"8px 6px",textAlign:"center",fontWeight:800,color:"#f87171"}}>حذف</th>
                </tr>
              </thead>
              <tbody>
                {sortedVisits.length===0 && (
                  <tr><td colSpan={10} style={{padding:30,textAlign:"center",color:"#5a4518"}}>لا توجد زيارات بعد</td></tr>
                )}
                {sortedVisits.map(v=>(
                  <tr key={v.id} style={{borderTop:"1px solid #3d2f1522"}}>
                    <td style={{padding:"8px 6px",color:"#f0f9ff",fontWeight:700}}>🦷 {v.drName||"-"}</td>
                    <td style={{padding:"8px 6px",color:"#c9b37e"}}>{v.clinic||"-"}</td>
                    <td style={{padding:"8px 6px",color:"#f472b6"}}>{v._gov||"-"}</td>
                    <td style={{padding:"8px 6px",color:"#a78bfa"}}>{v.leaderName||"-"}</td>
                    <td style={{padding:"8px 6px",color:"#94a3b8",fontSize:9}}>{v.reason}{v.reason==="أخرى"&&v.reasonOther?": "+v.reasonOther:""}</td>
                    <td style={{padding:"8px 6px",color:"#60a5fa",fontSize:9}}>{v.date||"-"} {fmt12(v.time)}</td>
                    <td style={{padding:"8px 6px",color:"#7a6841",fontSize:9}}>{v.lastVisitDate||"-"}</td>
                    <td style={{padding:"8px 6px"}}><span style={{padding:"2px 8px",borderRadius:20,background:"#0d0a00",color:"#a78bfa",fontSize:9,fontWeight:700}}>{v.status}</span></td>
                    <td style={{padding:"8px 6px",fontSize:9}}>
                      {v.result ? <div>
                        <div style={{color:v.result==="زيارة إيجابية"?"#22d3a0":"#f87171",fontWeight:700}}>{v.result}</div>
                        {v.resultComment && <div style={{color:"#7a6841",fontSize:8,marginTop:2}}>{v.resultComment}</div>}
                      </div> : <span style={{color:"#5a4518"}}>—</span>}
                    </td>
                    <td style={{padding:"8px 6px",textAlign:"center"}}>
                      <button onClick={()=>{ if(window.confirm(`حذف زيارة ${v.drName}؟`)) act.deleteVisit(v); }}
                        style={{background:"#2e0a0a",border:"1px solid #f8717155",borderRadius:6,padding:"3px 8px",color:"#f87171",fontSize:10,cursor:"pointer",fontFamily:"Cairo,sans-serif"}}>🗑</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    if(pg==="compare") {
      const allLocs = db.docLocs || [];
      const allVisits = db.visits || [];
      const govList = Object.keys(GOV_CODES).filter(c=>c!=="ADMIN-00");
      // مطابقة: لكل موقع طبيب — هل توجد زيارة من تيم ليدر بنفس الاسم/المحافظة؟
      const norm = s => (s||"").toString().trim().toLowerCase().replace(/\s+/g," ");
      const matchVisit = (loc) => {
        const ln = norm(loc.drName);
        const lc = norm(loc.clinic);
        return allVisits.find(v => v.govCode===loc.govCode && (
          (ln && norm(v.drName)===ln) ||
          (lc && norm(v.clinic)===lc) ||
          (ln && lc && norm(v.drName).includes(ln) && norm(v.clinic).includes(lc))
        ));
      };
      // ابنِ قائمة المقارنة (موقع طبيب + هل زاره تيم ليدر؟)
      let merged = allLocs.map(loc => {
        const v = matchVisit(loc);
        return {
          ...loc,
          _visitedByLeader: !!v,
          _visitId: v?.id || null,
          _leaderName: v?.leaderName || "",
          _visitDate: v?.date || (v?.startedAt ? "" : ""),
          _visitResult: v?.result || "",
        };
      });
      // فلتر بالمحافظة
      if (cmpFilterGov!=="ALL") merged = merged.filter(m=>m.govCode===cmpFilterGov);
      // فلتر بالحالة
      if (cmpShow==="visited")   merged = merged.filter(m=>m._visitedByLeader);
      if (cmpShow==="unvisited") merged = merged.filter(m=>!m._visitedByLeader);

      const visitedCount   = merged.filter(m=>m._visitedByLeader).length;
      const unvisitedCount = merged.length - visitedCount;
      const coverage = merged.length ? Math.round((visitedCount/merged.length)*100) : 0;

      return (
        <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap"}}>
            <div style={{fontSize:14,fontWeight:800,color:"#22d3a0"}}>📊 المقارنة — تغطية تيم ليدر للأطباء</div>
            <PdfBtn onClick={()=>{
              exportToPdf({
                title:"تقرير المقارنة — تغطية تيم ليدر للأطباء",
                subtitle:`${cmpFilterGov==="ALL"?"كل المحافظات":GOV_CODES[cmpFilterGov]||cmpFilterGov} • تغطية ${coverage}% (${visitedCount}/${merged.length})`,
                columns:["الطبيب","العيادة","المحافظة","المندوب الذي صوّر","الحالة","التيم ليدر","تاريخ الزيارة","النتيجة"],
                rows: merged.sort((a,b)=>(a._visitedByLeader===b._visitedByLeader?0:a._visitedByLeader?1:-1)).map(m=>[
                  m.drName||"—", m.clinic||"—", m.govName||"—", m.repName||"—",
                  m._visitedByLeader?"✓ مُزار":"! لم يُزَر بعد",
                  m._leaderName||"—", m._visitDate||"—", m._visitResult||"—"
                ])
              });
            }}/>
          </div>

          {/* بطاقات إحصائية */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
            <Card style={{padding:12,textAlign:"center",border:"1px solid #22d3a044"}}>
              <div style={{fontSize:22,fontWeight:900,color:"#22d3a0"}}>{visitedCount}</div>
              <div style={{fontSize:9,color:"#5a4518",marginTop:2}}>✓ مُزار</div>
            </Card>
            <Card style={{padding:12,textAlign:"center",border:"1px solid #ef444444"}}>
              <div style={{fontSize:22,fontWeight:900,color:"#ef4444"}}>{unvisitedCount}</div>
              <div style={{fontSize:9,color:"#5a4518",marginTop:2}}>! لم يُزَر</div>
            </Card>
            <Card style={{padding:12,textAlign:"center",border:"1px solid #f0c04044"}}>
              <div style={{fontSize:22,fontWeight:900,color:"#f0c040"}}>{coverage}%</div>
              <div style={{fontSize:9,color:"#5a4518",marginTop:2}}>التغطية</div>
            </Card>
          </div>

          {/* شريط التغطية */}
          <div style={{height:10,background:"#15100a",borderRadius:6,overflow:"hidden",border:"1px solid #3d2f15"}}>
            <div style={{height:"100%",width:`${coverage}%`,background:"linear-gradient(90deg,#22d3a0,#fbbf24)",transition:"width .4s"}}/>
          </div>

          {/* فلاتر */}
          <Card style={{padding:10,display:"flex",flexDirection:"column",gap:8}}>
            <div style={{fontSize:10,color:"#94a3b8"}}>فلتر بالمحافظة:</div>
            <select value={cmpFilterGov} onChange={e=>setCmpFilterGov(e.target.value)}
              style={{padding:"8px",borderRadius:8,background:"#0a0600",border:"1px solid #3d2f15",color:"#f0f9ff",fontSize:11,fontFamily:"Cairo,sans-serif"}}>
              <option value="ALL">— كل المحافظات —</option>
              {govList.map(c=><option key={c} value={c}>{GOV_CODES[c]} ({allLocs.filter(l=>l.govCode===c).length})</option>)}
            </select>
            <div style={{display:"flex",gap:6}}>
              {[
                ["ALL","الكل","#94a3b8"],
                ["visited","✓ مُزار فقط","#22d3a0"],
                ["unvisited","! لم يُزَر فقط","#ef4444"],
              ].map(([id,l,c])=>(
                <button key={id} onClick={()=>setCmpShow(id)} style={{
                  flex:1, padding:"6px", borderRadius:8,
                  border:`1px solid ${cmpShow===id?c:"#3d2f15"}`,
                  background:cmpShow===id?`${c}22`:"#0a0600",
                  color:cmpShow===id?c:"#94a3b8",
                  fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"Cairo,sans-serif"
                }}>{l}</button>
              ))}
            </div>
          </Card>

          {/* الخريطة */}
          <LeafletMap reps={[]} appointments={[]} compareLocs={merged.filter(m=>m.lat&&m.lng)} height={360} govCode={cmpFilterGov!=="ALL" ? cmpFilterGov : null}/>

          {/* الجدول */}
          <div style={{fontSize:11,color:"#94a3b8",marginTop:4}}>📋 جدول المقارنة ({merged.length})</div>
          <div style={{background:"#15100a",border:"1px solid #3d2f15",borderRadius:12,overflow:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:10,fontFamily:"Cairo,sans-serif",direction:"rtl",minWidth:700}}>
              <thead>
                <tr style={{background:"#0d0a00",color:"#22d3a0"}}>
                  <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>الطبيب</th>
                  <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>العيادة</th>
                  <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>المحافظة</th>
                  <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>المندوب الذي صوّر</th>
                  <th style={{padding:"8px 6px",textAlign:"center",fontWeight:800}}>الحالة</th>
                  <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>التيم ليدر</th>
                  <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>تاريخ الزيارة</th>
                  <th style={{padding:"8px 6px",textAlign:"right",fontWeight:800}}>النتيجة</th>
                </tr>
              </thead>
              <tbody>
                {merged.length===0 && (
                  <tr><td colSpan={8} style={{padding:30,textAlign:"center",color:"#5a4518"}}>لا توجد بيانات</td></tr>
                )}
                {merged.sort((a,b)=>(a._visitedByLeader===b._visitedByLeader?0:a._visitedByLeader?1:-1)).map(m=>(
                  <tr key={m.id} style={{borderTop:"1px solid #3d2f1522"}}>
                    <td style={{padding:"8px 6px",color:"#f0f9ff",fontWeight:700}}>🦷 {m.drName||"—"}</td>
                    <td style={{padding:"8px 6px",color:"#c9b37e"}}>{m.clinic||"—"}</td>
                    <td style={{padding:"8px 6px",color:"#f472b6"}}>{m.govName||"—"}</td>
                    <td style={{padding:"8px 6px",color:"#60a5fa"}}>{m.repName||"—"}</td>
                    <td style={{padding:"8px 6px",textAlign:"center"}}>
                      <span style={{padding:"3px 10px",borderRadius:20,
                        background:m._visitedByLeader?"#0a2e1e":"#2e0a0a",
                        color:m._visitedByLeader?"#22d3a0":"#ef4444",
                        fontSize:9,fontWeight:800
                      }}>{m._visitedByLeader?"✓ مُزار":"! لم يُزَر"}</span>
                    </td>
                    <td style={{padding:"8px 6px",color:"#a78bfa"}}>{m._leaderName||"—"}</td>
                    <td style={{padding:"8px 6px",color:"#7a6841",fontSize:9}}>{m._visitDate||"—"}</td>
                    <td style={{padding:"8px 6px",fontSize:9,color:m._visitResult==="زيارة إيجابية"?"#22d3a0":m._visitResult==="زيارة سلبية"?"#f87171":"#5a4518",fontWeight:700}}>{m._visitResult||"—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    if(pg==="apts") return <div style={{padding:16,display:"flex",flexDirection:"column",gap:10}}><div style={{fontSize:14,fontWeight:800,color:"#f0f9ff",marginBottom:4}}>جميع المواعيد</div>{visibleApts.map(a=><AptCard key={a.id} apt={a} user={user} role="admin" act={act} expanded={exp===a.id} onExpand={()=>setExp(exp===a.id?null:a.id)}/>)}</div>;
    if(pg==="table") return <ScheduleTable apts={visibleApts} title={db.isAdminAll?"جدول جميع المواعيد — كل المحافظات":`جدول مواعيد ${db.govName||""}`} showGov={db.isAdminAll} canDelete={db.isAdminAll} act={act} reps={visibleReps||[]}/>;
    if(pg==="reports") return <div style={{padding:16}}><ReportsPage apts={visibleApts} reps={visibleReps} color="#22d3a0"/></div>;
    if(pg==="reps") return <AdminTeamPage user={user} govCode={user.govCode} apts={visibleApts} isAdminAll={db.isAdminAll} reps={visibleReps} act={act}/>;
    if(pg==="devices") return <DevicesManagerPage user={user} govCode={user.govCode} isAdminAll={db.isAdminAll} act={act}/>;
    return null;
  };
  return (
    <div style={{display:"flex",flexDirection:"column",height:"100dvh",background:"transparent",fontFamily:"'Tajawal','Inter',sans-serif",direction:"rtl",maxWidth:"min(100%, 600px)",margin:"0 auto",width:"100%"}}>
      <style>{G}</style>
      <TopBar user={user} color="#22d3a0" nc={unread} onNotif={()=>{setNo(true); if(unread>0) onMarkAll();}} onLogout={onLogout} title="مدير"/>
      <NotifBanner notifPerm={notifPerm} requestNotifs={requestNotifs}/>
      <div style={{flex:1,overflowY:"auto"}}>{rp()}</div>
      <BNav items={NAV} active={pg} onChange={setPg}/>
      {no&&<NPanel notifs={notifs} color="#22d3a0" onClose={()=>setNo(false)} onMarkAll={onMarkAll}/>}
    </div>
  );
}

// ═══════════════════════════════════════════════
//  SECRETARY APP
// ═══════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════
//  DOCTOR EDIT MODAL — نافذة تعديل/حذف بيانات الطبيب (للكول سنتر)
// ═══════════════════════════════════════════════════════════════════════════
function DoctorEditModal({ doctor, onClose, onSave, onDelete, aptCount = 0 }) {
  const [form, setForm] = useState({
    name:    doctor.name    || "",
    clinic:  doctor.clinic  || "",
    phone:   doctor.phone   || "",
    address: doctor.address || "",
    city:    doctor.city    || "",
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg]   = useState("");

  const INP = {width:"100%",background:"#0a0600",border:"1px solid #3d2f15",borderRadius:10,
    padding:"10px 12px",color:"#f0f9ff",fontSize:13,fontFamily:"'Tajawal','Inter',sans-serif",
    direction:"rtl",boxSizing:"border-box"};
  const LBL = {fontSize:10,color:"#fbbf24",marginBottom:4,fontWeight:700};

  const save = async () => {
    if (!form.name.trim()) { setMsg("اسم الطبيب مطلوب"); return; }
    setBusy(true); setMsg("");
    try {
      await onSave({
        name:    form.name.trim(),
        clinic:  form.clinic.trim(),
        phone:   form.phone.trim(),
        address: form.address.trim(),
        city:    form.city.trim(),
      });
      setMsg("✅ تم الحفظ");
      setTimeout(onClose, 600);
    } catch(e) {
      setMsg("خطأ: " + (e?.message || "فشل الحفظ"));
    }
    setBusy(false);
  };

  return (
    <div onClick={onClose} style={{
      position:"fixed", inset:0, background:"#000c", zIndex:99999,
      display:"flex", alignItems:"center", justifyContent:"center", padding:16,
      direction:"rtl", fontFamily:"'Tajawal','Inter',sans-serif",
      animation:"fadeIn .2s ease",
    }}>
      <div onClick={e=>e.stopPropagation()} style={{
        background:"#0a0600",border:"2px solid #fbbf2455",borderRadius:16,
        padding:20,maxWidth:440,width:"100%",maxHeight:"90vh",overflow:"auto",
        boxShadow:"0 20px 60px rgba(251,191,36,.25)",
      }}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
          <div style={{fontSize:14,fontWeight:900,color:"#fbbf24"}}>✏️ تعديل بيانات الطبيب</div>
          <button onClick={onClose} style={{background:"transparent",border:"none",color:"#94a3b8",fontSize:18,cursor:"pointer",padding:4}}>✕</button>
        </div>

        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div>
            <div style={LBL}>اسم الطبيب *</div>
            <SafeInput value={form.name} onChange={e=>setForm(f=>({...f, name:e.target.value}))} style={INP} placeholder="د. ..."/>
          </div>
          <div>
            <div style={LBL}>اسم العيادة</div>
            <SafeInput value={form.clinic} onChange={e=>setForm(f=>({...f, clinic:e.target.value}))} style={INP} placeholder="عيادة..."/>
          </div>
          <div>
            <div style={LBL}>📱 رقم الهاتف</div>
            <input type="tel" inputMode="tel" value={form.phone}
              onChange={e=>setForm(f=>({...f, phone:e.target.value}))}
              style={{...INP, direction:"ltr", textAlign:"left", fontWeight:800, color:"#22d3a0"}}
              placeholder="07xxxxxxxxx"/>
          </div>
          <div>
            <div style={LBL}>📍 العنوان</div>
            <SafeInput value={form.address} onChange={e=>setForm(f=>({...f, address:e.target.value}))}
              style={INP} placeholder="شارع، حي، بناية..."/>
          </div>
          <div>
            <div style={LBL}>🏙 المدينة</div>
            <SafeInput value={form.city} onChange={e=>setForm(f=>({...f, city:e.target.value}))}
              style={INP} placeholder="بغداد، الموصل..."/>
          </div>

          {msg && (
            <div style={{
              fontSize:11,color:msg.startsWith("✅")?"#22d3a0":"#f87171",
              textAlign:"center",fontWeight:700,
              background: msg.startsWith("✅") ? "#0a2e1e" : "#2e0a0a",
              border:`1px solid ${msg.startsWith("✅")?"#22d3a044":"#f8717144"}`,
              borderRadius:8,padding:"8px",
            }}>{msg}</div>
          )}

          <div style={{display:"flex",gap:8,marginTop:6}}>
            <button onClick={onClose} disabled={busy}
              style={{flex:1,padding:"11px",borderRadius:10,border:"1px solid #3d2f15",
                background:"transparent",color:"#94a3b8",fontSize:12,fontWeight:700,
                cursor:busy?"wait":"pointer",fontFamily:"'Tajawal','Inter',sans-serif"}}>
              إلغاء
            </button>
            <button onClick={save} disabled={busy}
              style={{flex:1.5,padding:"11px",borderRadius:10,border:"none",
                background:"linear-gradient(135deg,#fbbf24,#d4af37)",
                color:"#0a0600",fontSize:13,fontWeight:900,
                cursor:busy?"wait":"pointer",fontFamily:"'Tajawal','Inter',sans-serif",
                opacity:busy?0.6:1}}>
              {busy ? "جاري الحفظ..." : "💾 حفظ التعديلات"}
            </button>
          </div>

          {/* ═══ منطقة الحذف الخطرة ═══ */}
          {onDelete && (
            <div style={{
              marginTop:14,paddingTop:14,borderTop:"1px dashed #2e0a0a",
              display:"flex",flexDirection:"column",gap:8,
            }}>
              <div style={{fontSize:10,color:"#f87171",fontWeight:700,textAlign:"center"}}>
                ⚠️ منطقة خطرة — الحذف نهائي
              </div>
              {aptCount > 0 && (
                <div style={{fontSize:10,color:"#fbbf24",textAlign:"center",
                  background:"#1f1a08",border:"1px solid #fbbf2433",
                  borderRadius:8,padding:"8px 10px",lineHeight:1.6}}>
                  ⚠️ هذا الطبيب مرتبط بـ <b>{aptCount}</b> موعد. الحذف لن يحذف المواعيد لكن قد تظهر بدون اسم طبيب.
                </div>
              )}
              <button
                disabled={busy}
                onClick={async () => {
                  const msg1 = `⚠️ تأكيد حذف الطبيب\n\n` +
                               `الاسم: ${doctor.name}\n` +
                               (doctor.clinic ? `العيادة: ${doctor.clinic}\n` : "") +
                               (aptCount > 0 ? `\n⚠️ مرتبط بـ ${aptCount} موعد\n` : "") +
                               `\nهل أنت متأكد؟ هذا الإجراء غير قابل للتراجع.`;
                  if (!window.confirm(msg1)) return;
                  // تأكيد ثاني للأمان
                  if (!window.confirm("تأكيد ثاني — هل أنت متأكد تماماً؟")) return;
                  setBusy(true);
                  try {
                    await onDelete();
                    onClose();
                  } catch(e) {
                    setMsg("خطأ في الحذف: " + (e?.message || ""));
                    setBusy(false);
                  }
                }}
                style={{
                  width:"100%",padding:"10px",borderRadius:10,
                  background:"#2e0a0a",border:"1px solid #f87171",
                  color:"#f87171",fontSize:11,fontWeight:800,
                  cursor:busy?"wait":"pointer",fontFamily:"'Tajawal','Inter',sans-serif",
                  opacity:busy?0.6:1,
                }}>
                🗑 حذف الطبيب نهائياً من قاعدة البيانات
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function SecApp({user,db,act,notifs,unread,onLogout,onMarkAll,notifPerm,requestNotifs}) {
  const [pg,setPg]=useState("home");
  const [no,setNo]=useState(false);
  // فتح لوحة الإشعارات تلقائياً عند الضغط على push notification
  useEffect(()=>{
    const open = () => setNo(true);
    window.addEventListener("britao:openNotifs", open);
    return () => window.removeEventListener("britao:openNotifs", open);
  },[]);
  const [exp,setExp]=useState(null);
  const [form,setForm]=useState({
    drMode:"new", // "existing" أو "new"
    drId:"",
    drName:"", drClinic:"", drPhone:"", drCity:"بغداد",
    address:"", lat:"", lng:"",
    date:"", time:"", notes:""
  });
  const [busy,setBusy]=useState(false);
  const [msg,setMsg]=useState("");
  const [showSuggestions,setShowSuggestions]=useState(false); // قائمة اقتراحات الأطباء
  const [docFilterRep,setDocFilterRep]=useState("");
  const [docDateFrom,setDocDateFrom]=useState("");
  const [docDateTo,setDocDateTo]=useState("");
  const [docSortBy,setDocSortBy]=useState("date_desc");
  const [activeReqId,setActiveReqId]=useState(null); // الطلب قيد المعاينة (للتحويل لموعد جديد)

  // ═══ طلبات الحجز المعلّقة (من مندوب التسويق + التيم ليدر + المشرف) ═══
  const incomingReqs = (db.bookingReqs||[]).filter(r => r.status === "pending");

  const NAV=[
    {id:"home",icon:"⬡",label:"الرئيسية",c:"#f472b6"},
    {id:"new",icon:"➕",label:"موعد جديد",c:"#f472b6"},
    {id:"repsStatus",icon:"🟢",label:"حالة المندوب",c:"#f472b6"},
    {id:"doctors",icon:"👨‍⚕️",label:"الأطباء",c:"#22d3a0"},
    {id:"table",icon:"📋",label:"الجدول",c:"#f472b6"},
    {id:"map",icon:"🗺",label:"الخريطة",c:"#f472b6"},
    {id:"docLocs",icon:"🦷",label:"مواقع الأطباء",c:"#ef4444"},
  ];

  // ═══ حالة لتعديل بيانات الأطباء ═══
  const [drSearch, setDrSearch] = useState("");
  const [editingDr, setEditingDr] = useState(null); // الطبيب قيد التعديل

  const submit = async () => {
    // ═══ قراءة آخر القيم مباشرة من الـ DOM ═══
    // السبب: على Android مع كيبورد عربي، الـ IME composition قد لا تنتهي قبل
    // الضغط على "حفظ" → React state قد يكون فارغاً بينما الحقل يحتوي قيمة.
    // الحلّ: نأخذ القيم من document.activeElement.blur() ثم نقرأ DOM مباشرة.
    try { if (document.activeElement && document.activeElement.blur) document.activeElement.blur(); } catch(e){}
    // انتظر tick صغير ليتم تحديث React state بعد blur (إنهاء composition)
    await new Promise(r => setTimeout(r, 60));

    // اقرأ أحدث قيم الحقول من الـ DOM مباشرة (تحايل على lag الـ IME)
    const readField = (placeholder) => {
      const el = document.querySelector(`input[placeholder="${placeholder}"]`);
      return el ? el.value : "";
    };
    let liveDrName     = readField("د. أحمد...")   || form.drName;
    const liveDrClinic = readField("عيادة...")     || form.drClinic;
    const liveDrPhone  = readField("07xx...")      || form.drPhone;
    const liveAddress  = readField("شارع، بناية...")|| form.address;
    const liveCity     = readField("بغداد")         || form.drCity || "بغداد";

    // تحقّق نهائي من الحقول الأساسية بعد القراءة الحيّة
    if(!form.date||!form.time||!liveAddress){setMsg("أكمل الحقول الأساسية");return;}

    // ═══ التحقق من تشابه اسم الطبيب مع طبيب موجود (في وضع طبيب جديد) ═══
    // مطابقة ذكيّة: تتحمّل الأخطاء الإملائية + توحيد الهمزات + Levenshtein
    let useExistingDrId = null; // إذا اختار "موافق" → نستخدم معرّف الطبيب الموجود
    if (form.drMode === "new" && liveDrName.trim()) {
      const typedWords = drCleanWords(liveDrName);
      if (typedWords.length > 0 && typedWords[0].length >= 2) {
        const myDocs = (db.doctors||[]).filter(d => !d.govCode || d.govCode === user.govCode);
        const matches = myDocs.filter(d => {
          const drWords = drCleanWords(d.name);
          return typedWords.every(typed => drWords.some(drWord => fuzzyArabicMatch(typed, drWord)));
        });
        if (matches.length > 0) {
          const matchedNames = matches.slice(0, 5).map(d =>
            `🦷 ${d.name}${d.clinic ? ` — ${d.clinic}` : ""}`
          ).join("\n");
          const moreCount = matches.length > 5 ? `\n... و ${matches.length - 5} طبيب آخر` : "";
          const confirmMsg =
            `⚠️ يوجد طبيب باسم مشابه مسجَّل سابقاً:\n\n` +
            `${matchedNames}${moreCount}\n\n` +
            `❓ هل هو نفس الطبيب؟\n\n` +
            `• اضغط "موافق/OK" → سيُستخدم الطبيب المحفوظ سابقاً\n` +
            `• اضغط "إلغاء/Cancel" → سيُحفظ كطبيب جديد`;
          const confirmed = window.confirm(confirmMsg);
          if (confirmed) {
            // استخدم الطبيب الأول المتطابق
            useExistingDrId = matches[0].id;
            liveDrName = matches[0].name;
            console.log(`✅ تم استخدام الطبيب المحفوظ: ${liveDrName}`);
          }
        }
      }
    }

    setBusy(true); setMsg("");
    try {
      let drId = form.drId;
      let drName = "";
      let drClinic = "";
      if(form.drMode === "new" && !useExistingDrId){
        if(!liveDrName.trim()){setMsg("أدخل اسم الطبيب");setBusy(false);return;}
        const newId = await act.addDoctor({
          name: liveDrName.trim(),
          clinic: liveDrClinic.trim() || "عيادة",
          phone: liveDrPhone.trim(),
          address: liveAddress.trim(),
          city: liveCity || "بغداد",
          lat: parseFloat(form.lat)||33.33,
          lng: parseFloat(form.lng)||44.40,
        });
        if(!newId){setBusy(false);return;}
        drId = newId;
        drName = liveDrName.trim();
        drClinic = liveDrClinic.trim() || "عيادة";
      } else if(useExistingDrId){
        // اختار طبيباً موجوداً من القائمة المنسدلة
        const dr = db.doctors.find(d=>d.id===useExistingDrId);
        if(!dr){setMsg("خطأ في الطبيب المُختار");setBusy(false);return;}
        drId = dr.id;
        drName = dr.name;
        drClinic = dr.clinic || "عيادة";
        // حدّث بيانات الطبيب لو تغيّرت
        const upd = {};
        if (liveDrPhone && liveDrPhone!==dr.phone)   upd.phone   = liveDrPhone;
        if (liveAddress && liveAddress!==dr.address) upd.address = liveAddress;
        if (liveCity && liveCity!==dr.city)          upd.city    = liveCity;
        if (Object.keys(upd).length > 0) await act.updateDoctor(dr.id, upd);
      } else {
        const dr = db.doctors.find(d=>d.id===form.drId);
        if(!dr){setMsg("اختر طبيباً");setBusy(false);return;}
        drName = dr.name;
        drClinic = dr.clinic || "عيادة";
        const upd = {};
        if (liveDrPhone && liveDrPhone!==dr.phone)   upd.phone   = liveDrPhone;
        if (liveAddress && liveAddress!==dr.address) upd.address = liveAddress;
        if (liveCity && liveCity!==dr.city)          upd.city    = liveCity;
        if (form.lat && parseFloat(form.lat)!==dr.lat) upd.lat   = parseFloat(form.lat);
        if (form.lng && parseFloat(form.lng)!==dr.lng) upd.lng   = parseFloat(form.lng);
        if (Object.keys(upd).length>0) await act.updateDoctor(form.drId, upd);
      }
      // اقرأ الملاحظات أيضاً من الـ DOM
      const notesEl = document.querySelector("textarea");
      const liveNotes = (notesEl && notesEl.value) || form.notes;

      // ═══ إذا كان الموعد مُنشَأ من طلب حجز → احفظ بيانات صاحب الطلب ═══
      let transferredFromUserId = null;
      let transferredFromUserName = null;
      let transferredFromUserRole = null;
      if (activeReqId) {
        const req = (db.bookingReqs||[]).find(r => r.id === activeReqId);
        if (req) {
          transferredFromUserId   = req.fromUserId   || null;
          transferredFromUserName = req.fromUserName || null;
          transferredFromUserRole = req.fromUserRole || null;
        }
      }

      await act.addApt({
        drId,
        drName,
        clinic: drClinic,
        address: liveAddress,
        city: liveCity || "بغداد",
        lat: parseFloat(form.lat)||33.33,
        lng: parseFloat(form.lng)||44.40,
        date: form.date,
        time: form.time,
        status: "جديدة",
        repId: null, repName: null,
        transferredFromUserId,
        transferredFromUserName,
        transferredFromUserRole,
        notes: liveNotes ? [{user:user.name,text:liveNotes,time:now()}] : []
      });
      setForm({drMode:"new",drId:"",drName:"",drClinic:"",drPhone:"",drCity:"بغداد",address:"",lat:"",lng:"",date:"",time:"",notes:""});

      // ═══ إذا كان الموعد جاي من طلب حجز → عَلِّم الطلب كـ "approved" ═══
      if (activeReqId) {
        try {
          await act.approveBookingRequest(activeReqId);
          setActiveReqId(null);
        } catch(e) { console.warn("approve booking req:", e.message); }
      }

      setMsg("✅ تم حفظ الموعد");
      setTimeout(()=>{setMsg("");setPg("apts");},1200);
    } catch(e) {
      setMsg("خطأ: " + e.message);
    }
    setBusy(false);
  };

  const pending = db.appointments.filter(a=>a.status==="جديدة");
  const active  = db.appointments.filter(a=>["مقبولة","في الطريق","وصلت","جاري التصوير"].includes(a.status));

  const INP = {width:"100%",background:"#0d0a00",border:"1px solid #3d2f15",borderRadius:10,
    padding:"10px",color:"#f0f9ff",fontSize:12,fontFamily:"Cairo,sans-serif",direction:"rtl",boxSizing:"border-box"};

  const rp = () => {
    if(pg === "home") return (
      <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{background:"linear-gradient(160deg,#1a0520,#0d0312)",borderRadius:18,padding:18}}>
          <div style={{fontSize:10,color:"#f472b6",marginBottom:3}}>كول سنتر</div>
          <div style={{fontSize:17,fontWeight:900,color:"#f0f9ff"}}>{user.fullName||user.name}</div>
          {user.govName && <div style={{fontSize:11,color:"#d4af37",fontWeight:800,marginTop:4}}>📍 {user.govName}</div>}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          {[
            {l:"تنتظر التوجيه",v:pending.length,c:"#f472b6",icon:"⚡"},
            {l:"نشطة الآن",v:active.length,c:"#22d3a0",icon:"🔴"},
            {l:"مكتملة",v:db.appointments.filter(a=>a.status==="مكتملة").length,c:"#60a5fa",icon:"✅"},
            {l:"الكلي",v:db.appointments.length,c:"#a78bfa",icon:"📅"},
          ].map((s,i)=>(
            <Card key={i} style={{padding:12,border:`1px solid ${s.c}22`}}>
              <div style={{display:"flex",justifyContent:"space-between"}}>
                <div>
                  <div style={{fontSize:10,color:"#d4af37",fontWeight:800,marginBottom:4}}>{s.l}</div>
                  <div style={{fontSize:20,fontWeight:900,color:s.c}}>{s.v}</div>
                </div>
                <span style={{fontSize:20}}>{s.icon}</span>
              </div>
            </Card>
          ))}
        </div>

        {/* ═══ طلبات الحجز الواردة (من مندوب التسويق / التيم ليدر / المشرف) ═══ */}
        {incomingReqs.length > 0 && (
          <div>
            <div style={{fontSize:13,fontWeight:800,color:"#fbbf24",marginBottom:8}}>
              📨 طلبات حجز جديدة ({incomingReqs.length})
            </div>
            {incomingReqs.map(r => {
              const roleLabel = r.fromUserRole==="marketing"?"مندوب التسويق":r.fromUserRole==="supervisor"?"التيم ليدر":r.fromUserRole==="mushref"?"المشرف":r.fromUserRole==="rep"?"مندوب السكنر":"عضو";
              return (
                <Card key={r.id} style={{padding:12,marginBottom:8,border:"2px solid #fbbf24",background:"#1f1a08aa",animation:"pulse 2s infinite"}}>
                  <div style={{fontSize:9,color:"#fbbf24",fontWeight:700,marginBottom:4}}>📨 طلب من {roleLabel}: <b>{r.fromUserName}</b></div>
                  <div style={{fontSize:13,fontWeight:800,color:"#f0f9ff"}}>🦷 {r.drName}</div>
                  {r.clinic && <div style={{fontSize:10,color:"#c9b37e",marginTop:2}}>🏥 {r.clinic}</div>}
                  {r.address && <div style={{fontSize:10,color:"#94a3b8",marginTop:2}}>📍 {r.address}</div>}
                  <div style={{fontSize:10,color:"#a78bfa",marginTop:4}}>📅 {r.date} {fmt12(r.time)}</div>
                  {r.notes && <div style={{fontSize:10,color:"#c9b37e",marginTop:4,padding:6,background:"#0d0a00",borderRadius:6}}>📝 {r.notes}</div>}
                  <div style={{display:"flex",gap:6,marginTop:8}}>
                    <Btn small color="#22d3a0" style={{flex:1}}
                      onClick={()=>{
                        // حوّل الطلب لموعد جديد: املأ الفورم تلقائياً وانتقل لصفحة موعد جديد
                        setForm(f=>({
                          ...f,
                          drMode:"new",
                          drId:"",
                          drName: r.drName||"",
                          drClinic: r.drClinic||r.clinic||"",
                          address: r.address||"",
                          date: r.date||"",
                          time: r.time||"",
                          notes: r.notes||"",
                        }));
                        setActiveReqId(r.id);
                        setPg("new");
                      }}>
                      👁 معاينة الطلب
                    </Btn>
                    <button onClick={()=>{
                      const reason = prompt("سبب رفض الطلب:");
                      if(reason && reason.trim()) {
                        act.rejectBookingRequest(r.id, reason.trim());
                      }
                    }}
                      style={{flex:1,background:"#2a1414",border:"1px solid #f87171",borderRadius:10,padding:"8px 12px",color:"#f87171",fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"Cairo,sans-serif"}}>
                      ✕ رفض
                    </button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        <Btn color="#f472b6" onClick={()=>setPg("new")} style={{width:"100%"}}>➕ تسجيل موعد جديد</Btn>
        {pending.length>0 && (
          <Card style={{padding:14,border:"1px solid #f472b633"}}>
            <div style={{fontSize:12,fontWeight:800,color:"#f472b6",marginBottom:10}}>⚡ تحتاج توجيه ({pending.length})</div>
            {pending.slice(0,5).map(a=>(
              <div key={a.id} onClick={()=>{setPg("apts");setExp(a.id);}}
                style={{padding:"8px 0",borderBottom:"1px solid #3d2f1522",cursor:"pointer"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <div style={{fontSize:12,fontWeight:700,color:"#f0f9ff"}}>{a.drName}</div>
                    <div style={{fontSize:10,color:"#334155"}}>{a.date} {fmt12(a.time)}</div>
                  </div>
                  {a.rejectReason
                    ? <span style={{padding:"2px 8px",borderRadius:12,background:"#2e0a0a",color:"#f87171",fontSize:9,fontWeight:800}}>↩ مرفوضة — أعد التوجيه</span>
                    : <SBadge s={a.status}/>}
                </div>
                {a.rejectReason && (
                  <div style={{marginTop:6,background:"#1a0505",border:"1px solid #f8717133",borderRadius:6,padding:"6px 8px"}}>
                    <div style={{fontSize:9,color:"#f87171",fontWeight:700}}>✕ {a.rejectedByName||"المندوب"} رفض:</div>
                    <div style={{fontSize:10,color:"#fecaca",marginTop:2}}>{a.rejectReason}</div>
                  </div>
                )}
              </div>
            ))}
          </Card>
        )}
      </div>
    );

    if(pg === "new") return (
      <div style={{padding:16}}>
        <div style={{fontSize:14,fontWeight:800,color:"#f0f9ff",marginBottom:14}}>➕ موعد جديد</div>
        <Card style={{padding:18,display:"flex",flexDirection:"column",gap:12}}>
          {/* اختيار نوع الطبيب */}
          <div>
            <div style={{fontSize:10,color:"#334155",marginBottom:8}}>الطبيب</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <button onClick={()=>setForm(f=>({...f,drMode:"new"}))}
                style={{padding:"10px",borderRadius:10,
                  border:`1.5px solid ${form.drMode==="new"?"#f472b6":"#3d2f15"}`,
                  background:form.drMode==="new"?"#f472b622":"#0d0a00",
                  color:form.drMode==="new"?"#f472b6":"#94a3b8",
                  fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"Cairo,sans-serif"}}>
                ➕ طبيب جديد
              </button>
              <button onClick={()=>setForm(f=>({...f,drMode:"existing"}))}
                style={{padding:"10px",borderRadius:10,
                  border:`1.5px solid ${form.drMode==="existing"?"#f472b6":"#3d2f15"}`,
                  background:form.drMode==="existing"?"#f472b622":"#0d0a00",
                  color:form.drMode==="existing"?"#f472b6":"#94a3b8",
                  fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"Cairo,sans-serif"}}>
                👨‍⚕️ طبيب مسجّل ({db.doctors.length})
              </button>
            </div>
          </div>

          {form.drMode === "new" ? (
            <>
              {/* ═══ حقل اسم الطبيب الجديد + قائمة اقتراحات ذكيّة ═══ */}
              <div style={{position:"relative"}}>
                <div style={{fontSize:10,color:"#334155",marginBottom:5}}>اسم الطبيب *</div>
                <input value={form.drName}
                  onChange={e=>{ setForm(f=>({...f,drName:e.target.value})); setShowSuggestions(true); }}
                  onBlur={e=>{ setForm(f=>({...f,drName:e.target.value})); setTimeout(()=>setShowSuggestions(false), 200); }}
                  onFocus={()=>setShowSuggestions(true)}
                  placeholder="د. أحمد..." style={INP} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} />

                {/* القائمة المنسدلة للأطباء المتطابقين — مطابقة ذكيّة */}
                {showSuggestions && (() => {
                  const typedWords = drCleanWords(form.drName);
                  if (typedWords.length === 0 || typedWords[0].length < 2) return null;
                  const myDocs = (db.doctors||[]).filter(d => !d.govCode || d.govCode === user.govCode);
                  const matches = myDocs.filter(d => {
                    const drWords = drCleanWords(d.name);
                    return typedWords.every(typed => drWords.some(drWord => fuzzyArabicMatch(typed, drWord)));
                  }).slice(0, 8);
                  if (matches.length === 0) return null;

                  return (
                    <div style={{
                      position:"absolute", top:"100%", left:0, right:0,
                      background:"#0d0a00", border:"1.5px solid #f472b6",
                      borderRadius:10, maxHeight:280, overflowY:"auto",
                      zIndex:100, marginTop:4,
                      boxShadow:"0 8px 24px rgba(0,0,0,0.6), 0 0 12px rgba(244,114,182,0.2)"
                    }}>
                      <div style={{
                        padding:"8px 12px", fontSize:9, color:"#f472b6", fontWeight:700,
                        borderBottom:"1px solid #3d2f15", background:"#1a0d12"
                      }}>
                        🔍 وُجد {matches.length} طبيب باسم مشابه — اضغط لاختيار، أو استمرّ بالكتابة لطبيب جديد
                      </div>
                      {matches.map((d,i) => (
                        <div key={d.id||i}
                          onMouseDown={(e)=>e.preventDefault()}
                          onClick={()=>{
                            // اختر الطبيب الموجود ← يبدّل الوضع لـ "existing" + يملأ كل البيانات
                            setForm(f=>({
                              ...f,
                              drMode:"existing",
                              drId: d.id,
                              drName: d.name||"",
                              drClinic: d.clinic||"",
                              drPhone: d.phone||"",
                              drCity: d.city||f.drCity||"بغداد",
                              address: d.address||f.address||"",
                              lat: d.lat ? String(d.lat) : f.lat,
                              lng: d.lng ? String(d.lng) : f.lng,
                            }));
                            setShowSuggestions(false);
                          }}
                          style={{
                            padding:"10px 12px", cursor:"pointer",
                            borderBottom: i < matches.length-1 ? "1px solid #15100a" : "none",
                            color:"#f0f9ff", fontSize:12,
                            transition:"background .15s ease"
                          }}
                          onMouseEnter={e=>e.currentTarget.style.background="#1a0d12"}
                          onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                          <div style={{fontWeight:700}}>🦷 {d.name}</div>
                          {d.clinic && <div style={{fontSize:10, color:"#c9b37e", marginTop:2}}>🏥 {d.clinic}</div>}
                          {d.phone && <div style={{fontSize:10, color:"#94a3b8", marginTop:2}}>📞 {d.phone}</div>}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              <div><div style={{fontSize:10,color:"#334155",marginBottom:5}}>اسم العيادة</div>
                <input value={form.drClinic}
                  onChange={e=>setForm(f=>({...f,drClinic:e.target.value}))}
                  onBlur={e=>setForm(f=>({...f,drClinic:e.target.value}))}
                  placeholder="عيادة..." style={INP} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} />
              </div>
              <div><div style={{fontSize:10,color:"#334155",marginBottom:5}}>رقم الهاتف</div>
                <input type="tel" inputMode="tel" value={form.drPhone}
                  onChange={e=>setForm(f=>({...f,drPhone:e.target.value}))}
                  onBlur={e=>setForm(f=>({...f,drPhone:e.target.value}))}
                  placeholder="07xx..." style={INP}/>
              </div>
            </>
          ) : (
            <DoctorPicker
              doctors={db.doctors||[]}
              selectedId={form.drId}
              onSelect={(d)=>{
                if (!d) { setForm(f=>({...f,drId:""})); return; }
                // املأ كل المعلومات من الطبيب الموجود
                setForm(f=>({
                  ...f,
                  drId: d.id,
                  drName: d.name||"",
                  drClinic: d.clinic||"",
                  drPhone: d.phone||"",
                  drCity: d.city||f.drCity||"بغداد",
                  address: d.address||f.address||"",
                  lat: d.lat ? String(d.lat) : f.lat,
                  lng: d.lng ? String(d.lng) : f.lng,
                }));
              }}
            />
          )}

          <div><div style={{fontSize:10,color:"#334155",marginBottom:5}}>العنوان *</div>
            <input value={form.address}
              onChange={e=>setForm(f=>({...f,address:e.target.value}))}
              onBlur={e=>setForm(f=>({...f,address:e.target.value}))}
              placeholder="شارع، بناية..." style={INP} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} />
          </div>
          <div><div style={{fontSize:10,color:"#334155",marginBottom:5}}>المدينة</div>
            <input value={form.drCity}
              onChange={e=>setForm(f=>({...f,drCity:e.target.value}))}
              onBlur={e=>setForm(f=>({...f,drCity:e.target.value}))}
              placeholder="بغداد" style={INP} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} />
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <div><div style={{fontSize:10,color:"#334155",marginBottom:5}}>التاريخ *</div>
              <input type="date" value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))} style={INP}/>
            </div>
            <div><div style={{fontSize:10,color:"#334155",marginBottom:5}}>الوقت *</div>
              <input type="time" value={form.time} onChange={e=>setForm(f=>({...f,time:e.target.value}))} style={INP}/>
            </div>
          </div>
          <div><div style={{fontSize:10,color:"#334155",marginBottom:5}}>ملاحظات</div>
            <textarea value={form.notes}
              onChange={e=>setForm(f=>({...f,notes:e.target.value}))}
              onBlur={e=>setForm(f=>({...f,notes:e.target.value}))}
              rows={3}
              style={Object.assign({},INP,{resize:"none"})} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} />
          </div>
          {msg && <div style={{fontSize:11,fontWeight:700,textAlign:"center",
            color:msg.startsWith("✅")?"#22d3a0":"#ef4444",
            background:msg.startsWith("✅")?"#22d3a015":"#ef444415",
            borderRadius:8,padding:"8px"}}>{msg}</div>}
          {/* لو فيه طلب قيد المعاينة، أظهر تنبيه */}
          {activeReqId && (
            <div style={{background:"#0a1828",border:"1px solid #fbbf24",borderRadius:10,padding:10,fontSize:11,color:"#fbbf24",fontWeight:700}}>
              👁 أنت تعاين طلب حجز — اضغط "حفظ الموعد" للموافقة، أو "رفض الطلب" للرفض
            </div>
          )}

          <Btn color="#f472b6" disabled={busy||!form.date||!form.time||(form.drMode==="existing" && !form.drId)}
            onClick={submit} style={{width:"100%"}}>
            {busy?<span style={{animation:"spin 1s linear infinite",display:"inline-block"}}>⟳</span>:(activeReqId?"✅ موافقة وحفظ الموعد":"حفظ الموعد 📅")}
          </Btn>

          {/* زرّ "رفض الطلب" — يظهر فقط عند معاينة طلب حجز */}
          {activeReqId && (
            <button onClick={()=>{
              const reason = prompt("اكتب سبب الرفض:");
              if(reason && reason.trim()) {
                act.rejectBookingRequest(activeReqId, reason.trim());
                setActiveReqId(null);
                setForm({drMode:"new",drId:"",drName:"",drClinic:"",drPhone:"",drCity:"بغداد",address:"",lat:"",lng:"",date:"",time:"",notes:""});
                setMsg("✅ تم رفض الطلب وأُعيد للمندوب للتعديل");
                setTimeout(()=>{setMsg("");setPg("home");}, 1500);
              }
            }}
              style={{width:"100%",background:"#2a1414",border:"1px solid #f87171",borderRadius:10,padding:"10px",color:"#f87171",fontSize:12,fontWeight:800,cursor:"pointer",fontFamily:"Cairo,sans-serif"}}>
              ✕ رفض الطلب وإعادته للمندوب
            </button>
          )}
        </Card>
      </div>
    );

    if(pg === "repsStatus") return (
      <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{fontSize:14,fontWeight:800,color:"#f0f9ff"}}>🟢 حالة المناديب الآن</div>
        <div style={{fontSize:10,color:"#334155"}}>تعرّفي على وضع كل مندوب قبل توجيه الحالة إليه</div>

        {(()=>{
          // جمع المناديب من db.reps (بما في ذلك المسجلون من users)
          const reps = (db.reps||[]).filter(r=>r.role==="rep"||!r.role); // أي rep أو بدون role (seed)

          if(reps.length===0) return (
            <Card style={{padding:30,textAlign:"center",color:"#334155",fontSize:12}}>
              لا يوجد مناديب مسجلون في محافظتك
            </Card>
          );

          // لكل مندوب، استخرج حالته الحالية من المواعيد
          return reps.map(r=>{
            const myApts = db.appointments.filter(a=>a.repId===r.id);
            // ابحث عن موعد نشط للمندوب
            const activeApt = myApts.find(a=>["مقبولة","في الطريق","وصلت","جاري التصوير"].includes(a.status));
            // ابحث عن موعد محوّل ينتظر القبول
            const pendingApt = !activeApt ? myApts.find(a=>a.status==="محوّلة") : null;

            let statusText = "شاغر — متاح للتوجيه";
            let statusColor = "#22d3a0";
            let statusIcon  = "🟢";
            let statusBg    = "#22d3a015";

            if(activeApt){
              if(activeApt.status==="في الطريق")         { statusText="في الطريق";          statusColor="#fbbf24"; statusIcon="🚗"; statusBg="#fbbf2415"; }
              else if(activeApt.status==="وصلت")         { statusText="وصل للموقع";         statusColor="#38bdf8"; statusIcon="📍"; statusBg="#38bdf815"; }
              else if(activeApt.status==="جاري التصوير") { statusText=`جاري التصوير (${activeApt.casesCount||1} حالة)`; statusColor="#e879f9"; statusIcon="📷"; statusBg="#e879f915"; }
              else if(activeApt.status==="مقبولة")       { statusText="قبل الموعد — لم ينطلق بعد"; statusColor="#a78bfa"; statusIcon="⏳"; statusBg="#a78bfa15"; }
            } else if(pendingApt){
              statusText = "قيد الموافقة/الرفض";
              statusColor = "#fb923c";
              statusIcon = "⌛";
              statusBg = "#fb923c15";
            }

            const completed = myApts.filter(a=>a.status==="مكتملة").length;
            const today = new Date().toISOString().slice(0,10);
            const todayApts = myApts.filter(a=>a.date===today).length;

            // آخر موقع معروف
            const lastLoc = r.lat && r.lng ? `${Number(r.lat).toFixed(4)}, ${Number(r.lng).toFixed(4)}` : "غير متوفر";

            return (
              <Card key={r.id} style={{padding:14,border:`1px solid ${statusColor}44`}}>
                <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:10}}>
                  <div style={{position:"relative",flexShrink:0}}>
                    <div style={{width:44,height:44,borderRadius:14,background:`${statusColor}22`,
                      display:"flex",alignItems:"center",justifyContent:"center",
                      fontSize:18,fontWeight:800,color:statusColor}}>
                      {(r.name||"?")[0]}
                    </div>
                    {/* نقطة حالة */}
                    <div style={{position:"absolute",bottom:-2,right:-2,width:14,height:14,
                      borderRadius:"50%",background:statusColor,border:"2.5px solid #080f1a",
                      animation: (activeApt||pendingApt)?"pulse 1.5s infinite":"none"}}/>
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:800,color:"#f0f9ff"}}>{r.fullName||r.name}</div>
                    <div style={{fontSize:10,color:"#334155"}}>{r.area||"—"} {r.phone?"• "+r.phone:""}</div>
                  </div>
                </div>

                {/* شريط الحالة */}
                <div style={{background:statusBg,border:`1px solid ${statusColor}33`,borderRadius:10,
                  padding:"10px 12px",display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                  <span style={{fontSize:16}}>{statusIcon}</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,fontWeight:800,color:statusColor}}>{statusText}</div>
                    {(activeApt||pendingApt) && (
                      <div style={{fontSize:10,color:"#94a3b8",marginTop:2}}>
                        مع {(activeApt||pendingApt).drName} — {(activeApt||pendingApt).clinic||""}
                      </div>
                    )}
                  </div>
                </div>

                {/* إحصائيات */}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
                  <div style={{background:"#0d0a00",borderRadius:8,padding:"8px",textAlign:"center"}}>
                    <div style={{fontSize:14,fontWeight:800,color:"#60a5fa"}}>{todayApts}</div>
                    <div style={{fontSize:9,color:"#d4af37",fontWeight:800,marginTop:3}}>اليوم</div>
                  </div>
                  <div style={{background:"#0d0a00",borderRadius:8,padding:"8px",textAlign:"center"}}>
                    <div style={{fontSize:14,fontWeight:800,color:"#22d3a0"}}>{completed}</div>
                    <div style={{fontSize:9,color:"#d4af37",fontWeight:800,marginTop:3}}>مكتملة</div>
                  </div>
                  <div style={{background:"#0d0a00",borderRadius:8,padding:"8px",textAlign:"center"}}>
                    <div style={{fontSize:14,fontWeight:800,color:"#fbbf24"}}>{myApts.length}</div>
                    <div style={{fontSize:9,color:"#d4af37",fontWeight:800,marginTop:3}}>الإجمالي</div>
                  </div>
                </div>

                {/* الموقع */}
                <div style={{marginTop:8,fontSize:9,color:"#5a4518",textAlign:"center"}}>
                  📍 {lastLoc}
                </div>
              </Card>
            );
          });
        })()}
      </div>
    );

    if(pg === "doctors") {
      // فلترة الأطباء بالبحث (اسم/عيادة/هاتف)
      const drNeedle = drSearch.trim().toLowerCase();
      const filteredDrs = !drNeedle ? db.doctors : db.doctors.filter(d => {
        const fields = [d.name, d.clinic, d.phone, d.address, d.city].map(x => (x||"").toLowerCase());
        return fields.some(f => f.includes(drNeedle));
      });
      return (
        <div style={{padding:16,display:"flex",flexDirection:"column",gap:10}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
            <div style={{fontSize:14,fontWeight:800,color:"#f0f9ff"}}>👨‍⚕️ إدارة الأطباء ({filteredDrs.length}/{db.doctors.length})</div>
            <Btn small color="#f472b6" onClick={()=>setPg("new")}>➕ موعد جديد</Btn>
          </div>

          {/* شريط البحث */}
          <SafeInput
            value={drSearch}
            onChange={e=>setDrSearch(e.target.value)}
            placeholder="🔍 ابحث باسم الطبيب / العيادة / الهاتف / العنوان..."
            style={{width:"100%",background:"#0d0a00",border:"1px solid #3d2f15",borderRadius:10,padding:"10px 14px",color:"#f0f9ff",fontSize:12,fontFamily:"'Tajawal','Inter',sans-serif",direction:"rtl",boxSizing:"border-box"}}/>

          {db.doctors.length === 0 ? (
            <Card style={{padding:30,textAlign:"center"}}>
              <div style={{fontSize:12,color:"#334155",marginBottom:10}}>لا يوجد أطباء مسجلون</div>
              <div style={{fontSize:10,color:"#5a4518"}}>سيتم إضافتهم تلقائياً عند تسجيل موعد جديد</div>
            </Card>
          ) : filteredDrs.length === 0 ? (
            <Card style={{padding:30,textAlign:"center"}}>
              <div style={{fontSize:12,color:"#334155"}}>لا نتائج بهذا البحث</div>
            </Card>
          ) : filteredDrs.map(d=>(
            <Card key={d.id} style={{padding:14}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                <div style={{width:42,height:42,borderRadius:14,background:"#38bdf822",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,color:"#38bdf8",flexShrink:0}}>👨‍⚕️</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:800,color:"#f0f9ff"}}>{d.name}</div>
                  <div style={{fontSize:10,color:"#94a3b8"}}>🏥 {d.clinic||"—"}</div>
                  {d.phone && <div style={{fontSize:10,color:"#22d3a0"}}>📱 {d.phone}</div>}
                  {d.address && <div style={{fontSize:10,color:"#7a6841"}}>📍 {d.address}</div>}
                </div>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:14,fontWeight:800,color:"#38bdf8"}}>{db.appointments.filter(a=>a.drId===d.id).length}</div>
                  <div style={{fontSize:8,color:"#94a3b8"}}>موعد</div>
                </div>
              </div>
              {/* زر التعديل */}
              <button onClick={()=>setEditingDr(d)}
                style={{width:"100%",background:"#1f1a08",border:"1px solid #fbbf2455",borderRadius:8,padding:"8px",color:"#fbbf24",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal','Inter',sans-serif"}}>
                ✏️ تعديل بيانات الطبيب
              </button>
            </Card>
          ))}

          {/* Modal تعديل/حذف الطبيب */}
          {editingDr && (
            <DoctorEditModal
              doctor={editingDr}
              aptCount={db.appointments.filter(a => a.drId === editingDr.id).length}
              onClose={()=>setEditingDr(null)}
              onSave={async (updates)=>{
                await act.updateDoctor(editingDr.id, updates);
                setEditingDr(null);
              }}
              onDelete={async ()=>{
                await act.deleteDoctor(editingDr.id);
                setEditingDr(null);
              }}/>
          )}
        </div>
      );
    }

    if(pg === "table") return <ScheduleTable apts={db.appointments} title={`جدول مواعيد ${user.govName||""}`} showGov={false} canEdit={true} canDelete={true} deleteFn={act.secDeleteApt} act={act} reps={db.reps||[]}/>;

    if(pg === "apts") return (
      <div style={{padding:16,display:"flex",flexDirection:"column",gap:10}}>
        <div style={{fontSize:14,fontWeight:800,color:"#f0f9ff",marginBottom:4}}>المواعيد ({db.appointments.length})</div>
        {db.appointments.length===0 && (
          <Card style={{padding:30,textAlign:"center",color:"#334155",fontSize:12}}>
            لا توجد مواعيد بعد
          </Card>
        )}
        {(()=>{
          // ترتيب: الجديدة أولاً (تحتاج توجيه) ثم النشطة ثم الباقي
          const sorted = [...db.appointments].sort((a,b)=>{
            const priority = s => {
              if(s==="جديدة") return 0;                 // أعلى أولوية
              if(s==="محوّلة") return 1;
              if(["مقبولة","في الطريق","وصلت","جاري التصوير"].includes(s)) return 2;
              if(s==="مكتملة") return 3;
              return 4;                                  // مرفوضة في الأخير
            };
            const pa = priority(a.status), pb = priority(b.status);
            if(pa !== pb) return pa - pb;
            // نفس الأولوية → الأحدث أولاً
            const da = (a.date||"") + " " + (a.time||"");
            const dbb = (b.date||"") + " " + (b.time||"");
            return dbb.localeCompare(da);
          });
          const newOnes = sorted.filter(a=>a.status==="جديدة");
          const rest    = sorted.filter(a=>a.status!=="جديدة");
          return (
            <>
              {/* قسم الحالات الجديدة — أولوية */}
              {newOnes.length>0 && (
                <>
                  <div style={{fontSize:11,fontWeight:800,color:"#f472b6",
                    background:"#f472b618",border:"1px solid #f472b644",
                    borderRadius:10,padding:"8px 14px",display:"flex",alignItems:"center",gap:8,
                    animation:"pulse 2s infinite"}}>
                    ⚡ تحتاج توجيه فوري ({newOnes.length})
                  </div>
                  {newOnes.map(a=>(
                    <AptCard key={a.id} apt={a} user={user} role="sec" act={act} allApts={db.appointments}
                      expanded={exp===a.id} onExpand={()=>setExp(exp===a.id?null:a.id)}/>
                  ))}
                </>
              )}
              {/* باقي الحالات */}
              {rest.length>0 && newOnes.length>0 && (
                <div style={{fontSize:10,fontWeight:700,color:"#334155",
                  borderTop:"1px solid #3d2f15",paddingTop:10,marginTop:6}}>
                  باقي المواعيد ({rest.length})
                </div>
              )}
              {rest.map(a=>(
                <AptCard key={a.id} apt={a} user={user} role="sec" act={act} allApts={db.appointments}
                  expanded={exp===a.id} onExpand={()=>setExp(exp===a.id?null:a.id)}/>
              ))}
            </>
          );
        })()}
      </div>
    );

    if(pg === "map") {
      // الكول سنتر ترى فقط مناديب ومواعيد محافظتها
      const myReps = db.reps.filter(r=>!r.govCode || r.govCode===user.govCode);
      const myApts = db.appointments.filter(a=>!a.govCode || a.govCode===user.govCode);
      return (
        <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
          <div style={{fontSize:14,fontWeight:800,color:"#f0f9ff"}}>🗺 خريطة محافظتي ({db.govName||""})</div>
          <LeafletMap reps={myReps} appointments={myApts} height={420} govCode={user.govCode}/>
        </div>
      );
    }

    if(pg === "docLocs") {
      // مواقع الأطباء — مقتصرة على محافظة الكول سنتر
      const myLocs = (db.docLocs||[]).filter(l => !l.govCode || l.govCode===user.govCode);
      const repsList = [...new Map(myLocs.filter(l=>l.repId).map(l=>[l.repId,{id:l.repId,name:l.repName||l.repId}])).values()];
      let filtered = docFilterRep ? myLocs.filter(l=>l.repId===docFilterRep) : myLocs;
      // فلتر التاريخ
      filtered = filtered.filter(l=>{
        const d = l.lastVisitDate || "";
        if (docDateFrom && d < docDateFrom) return false;
        if (docDateTo && d > docDateTo) return false;
        return true;
      });
      // الفهرسة
      const sortedLocs = [...filtered].sort((a,b)=>{
        if (docSortBy==="date_asc")  return (a.lastVisitDate||"").localeCompare(b.lastVisitDate||"");
        if (docSortBy==="dr")        return (a.drName||"").localeCompare(b.drName||"","ar");
        if (docSortBy==="visits")    return (b.visitsCount||0) - (a.visitsCount||0);
        return (b.lastVisitDate||"").localeCompare(a.lastVisitDate||"");
      });
      return (
        <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap"}}>
            <div style={{fontSize:14,fontWeight:800,color:"#ef4444"}}>🦷 مواقع الأطباء — {db.govName||""}</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              <button onClick={()=>exportMapPdf({
                title:`خريطة مواقع الأطباء — ${db.govName||""}`,
                subtitle: `${filtered.length} موقع${docFilterRep?` • المندوب: ${repsList.find(r=>r.id===docFilterRep)?.name||""}`:""}`,
                locations: filtered.filter(l=>l.lat&&l.lng)
              })}
                style={{background:"linear-gradient(135deg,#1a3a2a,#22d3a0)",color:"#000",border:"none",borderRadius:8,padding:"6px 14px",fontSize:11,fontWeight:800,cursor:"pointer",fontFamily:"Cairo,sans-serif",whiteSpace:"nowrap"}}>
                🗺 طباعة الخريطة
              </button>
              <PdfBtn onClick={()=>{
                exportToPdf({
                  title:`مواقع الأطباء — ${db.govName||""}`,
                  subtitle: `${docFilterRep ? `المندوب: ${repsList.find(r=>r.id===docFilterRep)?.name||docFilterRep}` : "كل المناديب"}${docDateFrom||docDateTo?` • ${docDateFrom||"البداية"} → ${docDateTo||"الآن"}`:""}`,
                  columns:["الطبيب","المندوب","التاريخ","الوقت","عدد الزيارات"],
                  rows: sortedLocs.map(l=>[l.drName||"—", l.repName||"—", l.lastVisitDate||"—", fmt12(l.lastVisitTime)||"—", l.visitsCount||1])
                });
              }}/>
            </div>
          </div>
          <Card style={{padding:10}}>
            <div style={{fontSize:10,color:"#94a3b8",marginBottom:6}}>فلتر حسب المندوب:</div>
            <select value={docFilterRep} onChange={e=>setDocFilterRep(e.target.value)} style={{width:"100%",padding:"8px",borderRadius:8,background:"#0a0600",border:"1px solid #3d2f15",color:"#f0f9ff",fontSize:11,fontFamily:"Cairo,sans-serif"}}>
              <option value="">— كل المناديب ({myLocs.length} عيادة) —</option>
              {repsList.map(r=>(
                <option key={r.id} value={r.id}>{r.name} ({myLocs.filter(l=>l.repId===r.id).length})</option>
              ))}
            </select>
          </Card>
          <DateSortFilter
            dateFrom={docDateFrom} setDateFrom={setDocDateFrom}
            dateTo={docDateTo}     setDateTo={setDocDateTo}
            sortBy={docSortBy}     setSortBy={setDocSortBy}
            sortOptions={[["date_desc","📅 الأحدث أولاً"],["date_asc","📅 الأقدم أولاً"],["dr","🦷 اسم الطبيب"],["visits","🔢 الأكثر زيارة"]]}
          />
          <NumberedDoctorsMap
            locations={filtered}
            height={360}
            govCode={user.govCode}
          />
          <div style={{fontSize:11,color:"#94a3b8",marginTop:4}}>📋 جدول العيادات ({filtered.length})</div>
          <div style={{background:"#15100a",border:"1px solid #3d2f15",borderRadius:12,overflow:"hidden"}}>
            <div style={{display:"grid",gridTemplateColumns:"1.7fr 1.2fr 1.2fr 0.6fr",background:"#0d0a00",padding:"8px 10px",fontSize:9,fontWeight:800,color:"#fbbf24",borderBottom:"1px solid #3d2f15"}}>
              <div>الطبيب</div><div>المندوب</div><div>التاريخ والوقت</div><div style={{textAlign:"center"}}>زيارات</div>
            </div>
            {filtered.length===0 ? (
              <div style={{padding:20,textAlign:"center",color:"#64748b",fontSize:11}}>لا توجد بيانات بعد — تظهر العيادات هنا تلقائياً عند بدء التصوير</div>
            ) : sortedLocs.map((l,i)=>(
              <div key={l.id} style={{display:"grid",gridTemplateColumns:"1.7fr 1.2fr 1.2fr 0.6fr",padding:"8px 10px",fontSize:10,color:"#f0f9ff",borderBottom:i<sortedLocs.length-1?"1px solid #1f1707":"none"}}>
                <div style={{fontWeight:700}}>🦷 {l.drName||"—"}</div>
                <div style={{color:"#60a5fa"}}>{l.repName||"—"}</div>
                <div style={{color:"#94a3b8",fontSize:9}}>{l.lastVisitDate||"—"} {fmt12(l.lastVisitTime)}</div>
                <div style={{textAlign:"center",color:"#fbbf24",fontWeight:800}}>{l.visitsCount||1}</div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100dvh",background:"transparent",fontFamily:"'Tajawal','Inter',sans-serif",direction:"rtl",maxWidth:"min(100%, 600px)",margin:"0 auto",width:"100%"}}>
      <style>{G}</style>
      <TopBar user={user} color="#f472b6" nc={unread} onNotif={()=>{setNo(true); if(unread>0) onMarkAll();}} onLogout={onLogout} title="كول سنتر"/>
      <NotifBanner notifPerm={notifPerm} requestNotifs={requestNotifs}/>
      <div style={{flex:1,overflowY:"auto"}}>{rp()}</div>
      <BNav items={NAV} active={pg} onChange={setPg}/>
      {no && <NPanel notifs={notifs} color="#f472b6" onClose={()=>setNo(false)} onMarkAll={onMarkAll}/>}
    </div>
  );
}


// ═══════════════════════════════════════════════
//  SHOOT COMPLETE REMINDER MODAL
//  يظهر للمندوب بعد ساعة من بدء التصوير ليتأكد إذا انتهى أو لا
// ═══════════════════════════════════════════════
function ShootCompleteReminderModal({apt, user, act, onClose}) {
  const [busy, setBusy] = useState(false);

  // اهتزاز قوي عند فتح الـ modal
  useEffect(() => {
    try {
      if (navigator.vibrate) {
        // نمط اهتزاز قوي ومتكرّر: اهتزاز/توقّف/اهتزاز...
        navigator.vibrate([400, 150, 400, 150, 400, 150, 600]);
      }
    } catch(e) { /* المتصفح لا يدعم */ }

    // صوت تنبيه (اختياري — beep بسيط لو الجهاز ما يهتز)
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.6);
      osc.start();
      osc.stop(ctx.currentTime + 0.6);
    } catch(e) { /* تجاهل */ }
  }, []);

  const onYes = async () => {
    setBusy(true);
    try {
      // اعتبر الموعد منتهياً تلقائياً
      await act.complete(apt.id, user);
      onClose();
    } catch(e) {
      console.error("complete from reminder:", e);
      setBusy(false);
    }
  };

  const onNo = async () => {
    setBusy(true);
    try {
      await act.dismissShootReminder(apt.id);
      onClose();
    } catch(e) {
      console.error("dismiss reminder:", e);
      setBusy(false);
    }
  };

  return (
    <div style={{
      position:"fixed", inset:0, background:"#000d", zIndex:99999,
      display:"flex", alignItems:"center", justifyContent:"center", padding:16,
      direction:"rtl", fontFamily:"'Tajawal','Inter',sans-serif",
      animation:"fadeIn .25s ease-out"
    }}>
      <style>{`
        @keyframes shootReminderPulse {
          0%,100% { transform:scale(1); box-shadow:0 0 0 0 rgba(232,121,249,.55), 0 20px 60px rgba(232,121,249,.35); }
          50%     { transform:scale(1.04); box-shadow:0 0 0 24px rgba(232,121,249,0), 0 30px 80px rgba(232,121,249,.55); }
        }
        @keyframes shootIconBounce {
          0%,100% { transform:translateY(0) rotate(-6deg); }
          50%     { transform:translateY(-8px) rotate(6deg); }
        }
      `}</style>

      <div style={{
        background:"linear-gradient(160deg,#1a0d30,#0a0418)",
        border:"2px solid rgba(232,121,249,.55)",
        borderRadius:24, padding:24, maxWidth:420, width:"100%",
        textAlign:"center",
        animation:"shootReminderPulse 1.6s ease-in-out infinite"
      }}>
        {/* أيقونة كاميرا تتحرّك */}
        <div style={{
          fontSize:64, marginBottom:8,
          animation:"shootIconBounce 1s ease-in-out infinite",
          display:"inline-block"
        }}>📷</div>

        <div style={{fontSize:18, fontWeight:900, color:"#f0c8ff", marginBottom:6}}>
          تذكير من النظام
        </div>
        <div style={{fontSize:11, color:"#c084fc", marginBottom:14, fontWeight:600}}>
          مرّت ساعة على بدء التصوير
        </div>

        {/* بيانات الموعد */}
        <div style={{
          background:"rgba(232,121,249,.08)",
          border:"1px solid rgba(232,121,249,.25)",
          borderRadius:14, padding:"12px 14px", marginBottom:18,
          textAlign:"right"
        }}>
          <div style={{fontSize:13, color:"#fafafa", fontWeight:800, marginBottom:4}}>
            🦷 {apt.drName || "—"}
          </div>
          {apt.clinic && <div style={{fontSize:11, color:"#d4d4d8", marginBottom:3}}>🏥 {apt.clinic}</div>}
          {apt.address && <div style={{fontSize:10, color:"#a1a1aa"}}>📍 {apt.address}</div>}
          <div style={{fontSize:10, color:"#e879f9", marginTop:6, fontWeight:700}}>
            📷 عدد الحالات: {apt.casesCount || 1}
          </div>
        </div>

        {/* السؤال الأساسي */}
        <div style={{
          fontSize:16, fontWeight:800, color:"#fafafa",
          marginBottom:18, lineHeight:1.5
        }}>
          هل انتهيت من تصوير هذه الحالة؟
        </div>

        {/* الأزرار */}
        <div style={{display:"flex", gap:10}}>
          <button
            onClick={onNo}
            disabled={busy}
            style={{
              flex:1, padding:"14px 0", borderRadius:14,
              background:"linear-gradient(135deg,#1f1933,#15101f)",
              border:"1.5px solid #6b21a8",
              color:"#e9d5ff", fontSize:15, fontWeight:800,
              cursor: busy ? "wait" : "pointer",
              fontFamily:"'Tajawal','Inter',sans-serif",
              opacity: busy ? .6 : 1,
            }}
          >
            ✕ لا، لم أنتهِ بعد
          </button>
          <button
            onClick={onYes}
            disabled={busy}
            style={{
              flex:1, padding:"14px 0", borderRadius:14,
              background:"linear-gradient(135deg,#22d3a0,#10b981)",
              border:"1.5px solid #34d399",
              color:"#022c22", fontSize:15, fontWeight:900,
              cursor: busy ? "wait" : "pointer",
              fontFamily:"'Tajawal','Inter',sans-serif",
              opacity: busy ? .6 : 1,
              boxShadow:"0 6px 20px rgba(34,211,160,.35)",
            }}
          >
            ✓ نعم، انتهيت
          </button>
        </div>

        <div style={{
          fontSize:9, color:"#71717a", marginTop:12, lineHeight:1.6
        }}>
          ضغط <b style={{color:"#22d3a0"}}>نعم</b> = اعتبار الموعد مكتملاً تلقائياً<br/>
          ضغط <b style={{color:"#c084fc"}}>لا</b> = سيتمّ تذكيرك مرّة أخرى بعد ساعة
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════
//  REP APP
// ═══════════════════════════════════════════════
function RepApp({user,db,act,notifs,unread,onLogout,onMarkAll,notifPerm,requestNotifs}) {
  const [pg,setPg]=useState("home");
  const [no,setNo]=useState(false);
  // فتح لوحة الإشعارات تلقائياً عند الضغط على push notification
  useEffect(()=>{
    const open = () => setNo(true);
    window.addEventListener("britao:openNotifs", open);
    return () => window.removeEventListener("britao:openNotifs", open);
  },[]);
  const [exp,setExp]=useState(null);
  const [gps,setGps]=useState(null);
  const [myCoord,setMyCoord]=useState(null); // {lat,lng} GPS الحقيقي
  const [shootReminderApt, setShootReminderApt] = useState(null); // الموعد الذي مرّ عليه ساعة بدون إنهاء
  const [showBookingForm,setShowBookingForm]=useState(false); // نموذج طلب حجز موعد
  const [editingReq,setEditingReq]=useState(null); // طلب حجز قيد التعديل (لو مرفوض)
  const myInfo=db.reps.find(r=>r.id===user.id)||{};
  // المندوب يرى فقط الطلبات المُوجَّهة له شخصياً
  const incoming=db.appointments.filter(a=>a.status==="محوّلة" && a.repId===user.id);
  const mine=db.appointments.filter(a=>a.repId===user.id);
  // ═══ طلبات حجز المواعيد التي أرسلها هذا المندوب ═══
  const myReqs       = (db.bookingReqs||[]).filter(r => r.fromUserId === user.id);
  const pendingReqs  = myReqs.filter(r=>r.status==="pending");
  const approvedReqs = myReqs.filter(r=>r.status==="approved");
  const rejectedReqs = myReqs.filter(r=>r.status==="rejected");

  // ═══ تذكير "هل انتهيت من التصوير؟" — كل دقيقة نفحص المواعيد التي بقيت "جاري التصوير" أكثر من ساعة ═══
  useEffect(() => {
    if (!user || !act) return;

    const checkShootReminder = async () => {
      // لو الـ modal مفتوح أصلاً → لا تتدخّل
      if (shootReminderApt) return;

      const ONE_HOUR_MS = 60 * 60 * 1000;
      const nowMs = Date.now();

      // ابحث عن أوّل موعد جاري التصوير ومرّت عليه ساعة بدون تذكير
      const pending = mine.find(a => {
        if (a.status !== "جاري التصوير") return false;
        // لو سبق وأرسلنا تذكير وما رد المندوب عليه → لا تكرّر (الـ modal سيظهر تلقائياً عبر الفحص التالي)
        if (a.shootReminderSent) return true;
        const startedAt = a.shootStartedAt?.toMillis?.() || (a.shootStartedAt?.seconds ? a.shootStartedAt.seconds * 1000 : 0);
        if (!startedAt) return false;
        return (nowMs - startedAt) >= ONE_HOUR_MS;
      });

      if (pending) {
        // إذا لم يُرسَل التذكير بعد → أرسله (Firestore + إشعار محلي)
        if (!pending.shootReminderSent) {
          try {
            await act.markShootReminderSent(pending.id);
          } catch(e) { console.error("mark reminder:", e); }

          // اهتزاز قوي فوراً عند فتح الـ modal
          try {
            if (navigator.vibrate) {
              navigator.vibrate([500, 200, 500, 200, 500, 200, 800]);
            }
          } catch(e) {}

          // إشعار محلي عبر Capacitor (لو متوفّر) — يصل للمندوب حتى لو التطبيق بالخلفية
          try {
            const cap = window.Capacitor;
            if (cap?.isNativePlatform?.() && window.Capacitor?.Plugins?.LocalNotifications) {
              await window.Capacitor.Plugins.LocalNotifications.schedule({
                notifications: [{
                  id: Math.floor(Math.random() * 100000),
                  title: "📷 هل انتهيت من التصوير؟",
                  body: `مرّت ساعة على بدء تصوير ${pending.drName || ""} — افتح التطبيق للرد`,
                  smallIcon: "ic_stat_icon",
                  sound: null,
                  schedule: { at: new Date(Date.now() + 500) },
                }]
              });
            }
          } catch(e) { console.warn("LocalNotifications:", e); }
        }

        // افتح الـ modal
        setShootReminderApt(pending);
      }
    };

    // فحص فوري + فحص دوري كل دقيقة
    checkShootReminder();
    const id = setInterval(checkShootReminder, 60 * 1000);
    return () => clearInterval(id);
  }, [mine, user, shootReminderApt]);

  // ═══ ترقية تلقائية للمواعيد قيد الانتظار إذا لم يكن هناك حالة نشطة ═══
  // يعمل بشكل دوري كاحتياط في حال فشل الترقية وقت إنهاء التصوير
  // ملاحظة: يتجاهل المواعيد المؤجَّلة يدوياً (pausedByUser=true) — فقط المُحوَّلة آلياً
  useEffect(() => {
    if (!user || !act) return;
    const ACTIVE = ["مقبولة", "في الطريق", "وصلت", "جاري التصوير"];
    const myActiveCount = mine.filter(a => ACTIVE.includes(a.status)).length;
    const queued = mine
      .filter(a => a.status === "مقبولة قيد الانتظار" && !a.pausedByUser)
      .sort((x, y) => {
        const tx = x.queuedAt?.toMillis?.() || x.queuedAt?.seconds * 1000 || 0;
        const ty = y.queuedAt?.toMillis?.() || y.queuedAt?.seconds * 1000 || 0;
        return tx - ty;
      });

    // إذا لا توجد حالة نشطة وعنده مواعيد قيد الانتظار (غير مؤجَّلة يدوياً) → فعّل أقدم واحدة
    if (myActiveCount === 0 && queued.length > 0 && act.promoteQueued) {
      console.log(`🔄 لا توجد حالة نشطة + ${queued.length} في الانتظار → ترقية ${queued[0].drName}`);
      act.promoteQueued(queued[0]);
    }
  }, [mine, user]);

  // Show GPS status + watch موقع المندوب الحقيقي
  useEffect(()=>{
    if(!navigator.geolocation){setGps("غير مدعوم");return;}
    const onPos = (p)=>{
      setGps(`${p.coords.latitude.toFixed(4)}, ${p.coords.longitude.toFixed(4)}`);
      setMyCoord({lat:p.coords.latitude, lng:p.coords.longitude});
    };
    navigator.geolocation.getCurrentPosition(onPos,()=>setGps("لم يُسمح بالوصول"),{enableHighAccuracy:true,timeout:10000});
    const wid = navigator.geolocation.watchPosition(onPos,()=>{},{enableHighAccuracy:true,maximumAge:15000});
    return ()=>{ try{ navigator.geolocation.clearWatch(wid);}catch(e){} };
  },[]);

  // ═══ ميزة خاصة بمناديب السليمانية فقط: جدول المواعيد ═══
  const isSulayRep = user.govCode === "SLM-9J2C";

  const NAV = isSulayRep
    ? [{id:"home",icon:"⬡",label:"الرئيسية",c:"#60a5fa"},{id:"apts",icon:"📅",label:"مواعيدي",c:"#60a5fa"},{id:"table",icon:"📋",label:"الجدول",c:"#60a5fa"},{id:"bookings",icon:"📨",label:"طلبات الحجز",c:"#60a5fa"},{id:"mymap",icon:"📍",label:"موقعي",c:"#60a5fa"}]
    : [{id:"home",icon:"⬡",label:"الرئيسية",c:"#60a5fa"},{id:"apts",icon:"📅",label:"مواعيدي",c:"#60a5fa"},{id:"bookings",icon:"📨",label:"طلبات الحجز",c:"#60a5fa"},{id:"mymap",icon:"📍",label:"موقعي",c:"#60a5fa"}];
  const rp=()=>{
    if(pg==="home") return (
      <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{background:"linear-gradient(160deg,#071a2e,#0d0a00)",borderRadius:18,padding:"18px",position:"relative",overflow:"hidden"}}>
          <div style={{fontSize:10,color:"#60a5fa",marginBottom:3}}>مندوب سكنر</div>
          <div style={{fontSize:17,fontWeight:900,color:"#f0f9ff"}}>{user.fullName||user.name}</div>
          <div style={{fontSize:11,color:"#d4af37",fontWeight:800,marginTop:4}}>📍 {user.govName||""}{user.area||myInfo.area?` — ${user.area||myInfo.area}`:""}</div>
          {gps&&<div style={{marginTop:10,background:"#0d2035",borderRadius:10,padding:"6px 12px",fontSize:9,color:"#22d3a0"}}>📍 GPS: {gps}</div>}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
          {[{l:"طلبات جديدة",v:incoming.length,c:"#f472b6"},{l:"مقبولة",v:mine.filter(a=>!["مكتملة","مرفوضة"].includes(a.status)).length,c:"#22d3a0"},{l:"مكتملة",v:mine.filter(a=>a.status==="مكتملة").length,c:"#60a5fa"}].map((s,i)=>(
            <Card key={i} style={{padding:"10px 8px",textAlign:"center",border:`1px solid ${s.c}22`}}><div style={{fontSize:18,fontWeight:900,color:s.c}}>{s.v}</div><div style={{fontSize:9,color:"#d4af37",fontWeight:800,marginTop:3}}>{s.l}</div></Card>
          ))}
        </div>

        {/* ═══ زر طلب حجز موعد + تنبيه طلبات مرفوضة ═══ */}
        <Btn color="#38bdf8" onClick={()=>{setEditingReq(null); setShowBookingForm(true); setPg("bookings");}} style={{width:"100%"}}>📨 طلب حجز موعد</Btn>

        {/* الطلبات المرفوضة (تحتاج تعديل) — تنبيه على الصفحة الرئيسية */}
        {rejectedReqs.length > 0 && (
          <div>
            <div style={{fontSize:12,fontWeight:800,color:"#f87171",marginBottom:8}}>⚠️ طلبات حجز تحتاج تعديل ({rejectedReqs.length})</div>
            {rejectedReqs.map(r => (
              <Card key={r.id} style={{padding:12,marginBottom:8,border:"2px solid #f87171",background:"#2a1414"}}>
                <div style={{fontSize:12,fontWeight:800,color:"#fff"}}>🦷 {r.drName}</div>
                <div style={{fontSize:10,color:"#fecaca",marginTop:4}}>📅 {r.date} {fmt12(r.time)}</div>
                {r.rejectReason && <div style={{fontSize:10,color:"#fbbf24",marginTop:6,padding:8,background:"#1a0a0a",borderRadius:6}}>❌ {r.rejectReason}</div>}
                <Btn small color="#fbbf24" style={{marginTop:8,width:"100%"}}
                  onClick={()=>{setEditingReq(r); setShowBookingForm(true); setPg("bookings");}}>
                  ✏️ تعديل وإعادة الإرسال
                </Btn>
              </Card>
            ))}
          </div>
        )}

        {incoming.map(a=>(
          <Card key={a.id} style={{padding:14,border:"1px solid #f472b633",animation:"blink 2s infinite"}}>
            <div style={{fontSize:10,color:"#f472b6",fontWeight:700,marginBottom:6}}>📅 طلب موعد جديد!</div>
            <div style={{fontSize:13,fontWeight:800,color:"#f0f9ff",marginBottom:4}}>{a.drName} — {a.clinic}</div>
            <div style={{fontSize:11,color:"#334155",marginBottom:2}}>📍 {a.address}</div>
            <div style={{fontSize:11,color:"#a78bfa",marginBottom:10}}>🗓 {a.date} — 🕐 {fmt12(a.time)}</div>
            <div style={{display:"flex",gap:8}}><Btn color="#22d3a0" onClick={()=>act.accept(a.id,user)} style={{flex:1}}>✓ قبول</Btn><Btn color="#f87171" onClick={()=>{ const r=prompt("يرجى كتابة سبب الرفض:"); if(r&&r.trim()) act.reject(a.id, r.trim(), user); }} style={{flex:1}}>✕ رفض</Btn></div>
          </Card>
        ))}
        {mine.filter(a=>!["مكتملة","مرفوضة"].includes(a.status)).map(a=>{
          const isQueued = a.status === "مقبولة قيد الانتظار";
          return (
          <Card key={a.id} style={{padding:14,border:`1px solid ${APTS[a.status]?.c||"#3d2f15"}33`,opacity:isQueued?0.75:1}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}><div><div style={{fontSize:12,fontWeight:800,color:"#f0f9ff"}}>{a.drName}</div><div style={{fontSize:10,color:"#334155"}}>{a.id}</div></div><SBadge s={a.status}/></div>
            <div style={{fontSize:11,color:"#334155",marginBottom:10}}>📍 {a.address}</div>
            {isQueued && (
              <div style={{background:"#1f2a00",border:"1px solid #fbbf2444",borderRadius:8,padding:"8px 10px",marginBottom:10,display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:14}}>⏸</span>
                <div style={{fontSize:10,color:"#fbbf24",fontWeight:700,lineHeight:1.5}}>
                  هذا الموعد <b>قيد الانتظار</b>{a.pausedByUser ? " (مؤجَّل يدوياً)":""}<br/>
                  <span style={{color:"#c9b37e",fontWeight:500}}>اضغط <b>▶️ تفعيل الآن</b> للبدء فوراً (سيُؤجَّل الموعد النشط لو موجود)</span>
                </div>
              </div>
            )}
            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
              {a.status==="مقبولة"&&<Btn small color="#fbbf24" onClick={()=>act.upStatus(a.id,"في الطريق")}>🚗 في الطريق</Btn>}
              {/* ⏸ تأجيل أي موعد فعّال (مقبولة/في الطريق/وصلت) — يظهر فقط لو في موعد آخر منتظر */}
              {["مقبولة","في الطريق","وصلت"].includes(a.status) && mine.some(x=>x.id!==a.id && x.status==="مقبولة قيد الانتظار") && (
                <Btn small color="#94a3b8" onClick={()=>{
                  if(window.confirm(`تأجيل ${a.drName||"الموعد"} ووضعه قيد الانتظار؟\n\nسيتم تفعيل أحد المواعيد المنتظرة تلقائياً (لو موجود).`)) {
                    act.pauseAccepted(a.id, user.id);
                  }
                }}>⏸ تأجيل</Btn>
              )}
              {a.status==="في الطريق"&&<Btn small color="#38bdf8" onClick={()=>act.upStatus(a.id,"وصلت")}>📍 وصلت</Btn>}
              {a.status==="وصلت"&&<Btn small color="#e879f9" onClick={()=>{ const n=prompt("كم عدد الحالات التي ستصورها لهذا الطبيب؟","1"); if(n&&!isNaN(n)&&Number(n)>0){ act.startShoot(a.id,Number(n)); } }}>📷 بدء التصوير</Btn>}
              {a.status==="جاري التصوير"&&<Btn small color="#22d3a0" onClick={()=>act.complete(a.id,user)}>✅ انتهى التصوير</Btn>}
              {a.status==="جاري التصوير"&&<Btn small color="#fbbf24" onClick={()=>{
                const t = prompt("📝 اكتب ملاحظتك على هذه الحالة:\n(مثال: حالة معقّدة، الطبيب طلب تأجيل، إلخ)");
                if(t && t.trim()) {
                  act.addNote(a.id, {user:user.name, text:t.trim(), time:now()});
                  alert("✅ تم إرسال الملاحظة للكول سنتر والمدير");
                }
              }}>📝 إضافة ملاحظة</Btn>}
              {/* ▶️ تفعيل الآن — للمواعيد قيد الانتظار */}
              {isQueued && (
                <Btn small color="#22d3a0" onClick={()=>{
                  // ابحث عن أي موعد فعّال (ما عدا التصوير الجاري)
                  const activeOther = mine.find(x=>x.id!==a.id && ["مقبولة","في الطريق","وصلت"].includes(x.status));
                  const isShooting  = mine.some(x=>x.id!==a.id && x.status==="جاري التصوير");
                  if(isShooting){
                    alert(`⚠️ لا يمكن التبديل الآن — يوجد تصوير قيد التنفيذ. أنهِ التصوير أولاً.`);
                    return;
                  }
                  if(activeOther){
                    if(!window.confirm(`تفعيل ${a.drName||"هذا الموعد"} الآن؟\n\nسيُؤجَّل ${activeOther.drName||"الموعد الفعّال"} (${activeOther.status}) تلقائياً.`)) return;
                  }
                  act.promoteQueued(a);
                }}>▶️ تفعيل الآن</Btn>
              )}
            </div>
            {/* عرض آخر ملاحظة لو موجودة (أثناء جاري التصوير) */}
            {a.status==="جاري التصوير" && Array.isArray(a.notes) && a.notes.length>0 && (
              <div style={{marginTop:8,background:"#1f1a08",border:"1px solid #fbbf2433",borderRadius:8,padding:"8px 10px",fontSize:10,color:"#fbbf24"}}>
                <div style={{fontWeight:700,marginBottom:3}}>📝 آخر ملاحظة:</div>
                <div style={{color:"#c9b37e",lineHeight:1.5}}>{a.notes[a.notes.length-1]?.text||""}</div>
                {a.notes.length>1 && <div style={{color:"#7a6841",marginTop:3,fontSize:9}}>+ {a.notes.length-1} ملاحظة سابقة</div>}
              </div>
            )}
          </Card>
        );})}
      </div>
    );
    if(pg==="apts") return <div style={{padding:16,display:"flex",flexDirection:"column",gap:10}}><div style={{fontSize:14,fontWeight:800,color:"#f0f9ff",marginBottom:4}}>مواعيدي</div>{[...incoming,...mine].map(a=><AptCard key={a.id} apt={a} user={user} role="rep" act={act} expanded={exp===a.id} onExpand={()=>setExp(exp===a.id?null:a.id)}/>)}</div>;
    // ═══ جدول المواعيد — لكل مناديب السليمانية ═══
    // كل مندوب يرى كل مواعيد المحافظة (مثل الكول سنتر)
    if(pg==="table" && isSulayRep) {
      return <ScheduleTable apts={db.appointments} title={`📋 جدول كل مواعيد — ${db.govName||"السليمانية"}`} showGov={false} reps={db.reps||[]}/>;
    }
    if(pg==="mymap") return (
      <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{fontSize:14,fontWeight:800,color:"#f0f9ff"}}>📍 موقعي الحقيقي</div>
        {gps&&<Card style={{padding:12}}><div style={{fontSize:10,color:"#22d3a0",marginBottom:2}}>إحداثيات GPS الحقيقية</div><div style={{fontSize:12,color:"#f0f9ff",fontWeight:700}}>{gps}</div><div style={{fontSize:9,color:"#334155",marginTop:2}}>يتحدّث تلقائياً</div></Card>}
        <LeafletMap reps={db.reps.filter(r=>r.id===user.id)} appointments={mine} height={360} filterRepId={user.id} centerOn={myCoord} govCode={user.govCode}/>
      </div>
    );

    // ═══ صفحة طلبات الحجز — نفس آلية مندوب التسويق ═══
    if(pg==="bookings") return (
      <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontSize:14,fontWeight:800,color:"#f0f9ff"}}>📨 طلبات الحجز</div>
          <Btn small color="#38bdf8" onClick={()=>{setEditingReq(null); setShowBookingForm(true);}}>+ طلب جديد</Btn>
        </div>

        {showBookingForm && <BookingRequestForm user={user} db={db} act={act} existingReq={editingReq} onClose={()=>{setShowBookingForm(false); setEditingReq(null);}}/>}

        {pendingReqs.length>0 && <div style={{fontSize:11,color:"#fbbf24",marginTop:8}}>⏳ في الانتظار ({pendingReqs.length})</div>}
        {pendingReqs.map(r => (
          <Card key={r.id} style={{padding:12,border:"1px solid #fbbf2455"}}>
            <div style={{fontSize:12,fontWeight:800,color:"#f0f9ff"}}>🦷 {r.drName}</div>
            <div style={{fontSize:10,color:"#94a3b8",marginTop:4}}>📅 {r.date} {fmt12(r.time)}</div>
            {r.notes && <div style={{fontSize:10,color:"#c9b37e",marginTop:4}}>📝 {r.notes}</div>}
            <div style={{fontSize:9,color:"#fbbf24",marginTop:6,fontWeight:700}}>⏳ بانتظار رد الكول سنتر</div>
            <button onClick={()=>{if(window.confirm("حذف هذا الطلب؟")) act.deleteBookingRequest(r.id);}}
              style={{background:"#2e0a0a",border:"1px solid #f8717155",borderRadius:6,padding:"4px 10px",color:"#f87171",fontSize:9,fontWeight:700,cursor:"pointer",fontFamily:"Cairo,sans-serif",marginTop:8}}>
              🗑 حذف
            </button>
          </Card>
        ))}

        {rejectedReqs.length>0 && <div style={{fontSize:11,color:"#f87171",marginTop:12}}>⚠️ مرفوضة - تحتاج تعديل ({rejectedReqs.length})</div>}
        {rejectedReqs.map(r => (
          <Card key={r.id} style={{padding:12,border:"2px solid #f87171",background:"#2a141488"}}>
            <div style={{fontSize:12,fontWeight:800,color:"#f0f9ff"}}>🦷 {r.drName}</div>
            <div style={{fontSize:10,color:"#94a3b8",marginTop:4}}>📅 {r.date} {fmt12(r.time)}</div>
            {r.rejectReason && <div style={{fontSize:10,color:"#fbbf24",marginTop:6,padding:8,background:"#1a0a0a",borderRadius:6}}>❌ {r.rejectReason}</div>}
            <Btn small color="#fbbf24" style={{marginTop:8,width:"100%"}}
              onClick={()=>{setEditingReq(r); setShowBookingForm(true);}}>
              ✏️ تعديل وإعادة الإرسال
            </Btn>
          </Card>
        ))}

        {approvedReqs.length>0 && <div style={{fontSize:11,color:"#22d3a0",marginTop:12}}>✅ موافَق عليها ({approvedReqs.length})</div>}
        {approvedReqs.slice(0,10).map(r => (
          <Card key={r.id} style={{padding:12,border:"1px solid #22d3a055"}}>
            <div style={{fontSize:12,fontWeight:800,color:"#f0f9ff"}}>🦷 {r.drName}</div>
            <div style={{fontSize:10,color:"#94a3b8",marginTop:4}}>📅 {r.date} {fmt12(r.time)}</div>
            <div style={{fontSize:9,color:"#22d3a0",marginTop:6,fontWeight:700}}>✅ تم تثبيت الحجز</div>
          </Card>
        ))}

        {myReqs.length===0 && (
          <Card style={{padding:30,textAlign:"center",color:"#334155",fontSize:12}}>
            لا توجد طلبات حجز بعد — اضغط "+ طلب جديد"
          </Card>
        )}
      </div>
    );

    return null;
  };
  return (
    <div style={{display:"flex",flexDirection:"column",height:"100dvh",background:"transparent",fontFamily:"'Tajawal','Inter',sans-serif",direction:"rtl",maxWidth:"min(100%, 600px)",margin:"0 auto",width:"100%"}}>
      <style>{G}</style>
      <TopBar user={user} color="#60a5fa" nc={unread} onNotif={()=>{setNo(true); if(unread>0) onMarkAll();}} onLogout={onLogout} title="مندوب سكنر"/>
      <NotifBanner notifPerm={notifPerm} requestNotifs={requestNotifs}/>
      <div style={{flex:1,overflowY:"auto"}}>{rp()}</div>
      <BNav items={NAV} active={pg} onChange={setPg}/>
      {no&&<NPanel notifs={notifs} color="#60a5fa" onClose={()=>setNo(false)} onMarkAll={onMarkAll}/>}
      {/* تذكير "هل انتهيت من التصوير؟" — يظهر بعد ساعة من بدء التصوير */}
      {shootReminderApt && (
        <ShootCompleteReminderModal
          apt={shootReminderApt}
          user={user}
          act={act}
          onClose={() => setShootReminderApt(null)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════
//  DOCTOR APP
// ═══════════════════════════════════════════════
function DoctorApp({user,db,act,notifs,unread,onLogout,onMarkAll,notifPerm,requestNotifs}) {
  const [pg,setPg]=useState("home");
  const [no,setNo]=useState(false);
  // فتح لوحة الإشعارات تلقائياً عند الضغط على push notification
  useEffect(()=>{
    const open = () => setNo(true);
    window.addEventListener("britao:openNotifs", open);
    return () => window.removeEventListener("britao:openNotifs", open);
  },[]);
  const [exp,setExp]=useState(null);
  const [form,setForm]=useState({address:"",city:"بغداد",lat:"",lng:"",date:"",time:"",note:""});
  const [done,setDone]=useState(false);
  const my=db.appointments.filter(a=>a.drId===user.id);
  const drInfo=db.doctors.find(d=>d.id===user.id)||{};
  const NAV=[{id:"home",icon:"⬡",label:"الرئيسية",c:"#38bdf8"},{id:"book",icon:"📅",label:"حجز موعد",c:"#38bdf8"},{id:"track",icon:"📋",label:"مواعيدي",c:"#38bdf8"}];
  const book=()=>{
    act.addApt({drId:user.id,drName:user.name,clinic:drInfo.clinic||"عيادة",address:form.address,city:form.city,lat:parseFloat(form.lat)||33.33,lng:parseFloat(form.lng)||44.40,date:form.date,time:form.time,status:"جديدة",repId:null,repName:null,notes:form.note?[{user:user.name,text:form.note,time:now()}]:[]});
    setDone(true); setForm({address:"",city:"بغداد",lat:"",lng:"",date:"",time:"",note:""});
    setTimeout(()=>setDone(false),4000);
  };
  const rp=()=>{
    if(pg==="home") return (
      <div style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
        <div style={{background:"linear-gradient(160deg,#071a2e,#040f1c)",borderRadius:18,padding:"18px"}}>
          <div style={{fontSize:10,color:"#38bdf8",marginBottom:3}}>طبيب 👨‍⚕️</div>
          <div style={{fontSize:17,fontWeight:900,color:"#f0f9ff"}}>{user.name}</div>
          <div style={{fontSize:10,color:"#5a4518",marginTop:2}}>{drInfo.clinic} — {drInfo.city}</div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          {[{l:"مواعيدي الكلية",v:my.length,c:"#38bdf8",icon:"📅"},{l:"مكتملة",v:my.filter(a=>a.status==="مكتملة").length,c:"#22d3a0",icon:"✅"}].map((s,i)=>(
            <Card key={i} style={{padding:12,border:`1px solid ${s.c}22`}}><div style={{display:"flex",justifyContent:"space-between"}}><div><div style={{fontSize:10,color:"#d4af37",fontWeight:800,marginBottom:4}}>{s.l}</div><div style={{fontSize:20,fontWeight:900,color:s.c}}>{s.v}</div></div><span style={{fontSize:20}}>{s.icon}</span></div></Card>
          ))}
        </div>
        <Btn color="#38bdf8" onClick={()=>setPg("book")} style={{width:"100%"}}>📅 حجز موعد سكنر جديد</Btn>
        {my.filter(a=>["في الطريق","وصلت","جاري التصوير"].includes(a.status)).map(a=>(
          <Card key={a.id} style={{padding:14,border:`1px solid ${APTS[a.status]?.c}44`}}>
            <div style={{fontSize:10,color:APTS[a.status]?.c,fontWeight:700,marginBottom:4}}>🔴 مندوب نشط الآن</div>
            <div style={{fontSize:12,fontWeight:800,color:"#f0f9ff",marginBottom:4}}>{a.repName}</div>
            <SBadge s={a.status}/>
          </Card>
        ))}
        <div style={{fontSize:12,fontWeight:800,color:"#f0f9ff",marginBottom:2}}>آخر مواعيدي</div>
        {my.slice(0,3).map((a,i)=>(
          <Card key={i} style={{padding:12,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div><div style={{fontSize:11,fontWeight:700,color:"#f0f9ff"}}>{a.id}</div><div style={{fontSize:10,color:"#334155"}}>{a.date} — {fmt12(a.time)}</div></div>
            <SBadge s={a.status}/>
          </Card>
        ))}
      </div>
    );
    if(pg==="book") return (
      <div style={{padding:16}}>
        <div style={{fontSize:14,fontWeight:800,color:"#f0f9ff",marginBottom:14}}>📅 طلب موعد سكنر</div>
        {done&&<div style={{background:"#0a2e1e",border:"1px solid #22d3a033",borderRadius:12,padding:"12px",marginBottom:14,color:"#22d3a0",fontSize:12,fontWeight:700}}>✓ تم الإرسال — ستتصل بك الكول سنتر قريباً</div>}
        <Card style={{padding:16,display:"flex",flexDirection:"column",gap:12}}>
          {[{l:"العنوان",k:"address",ph:"شارع، بناية...",t:"text"},{l:"المدينة",k:"city",ph:"بغداد",t:"text"},{l:"التاريخ",k:"date",t:"date"},{l:"الوقت",k:"time",t:"time"}].map(f=>(
            <div key={f.k}><div style={{fontSize:10,color:"#334155",marginBottom:5}}>{f.l}</div>
              <input type={f.t} value={form[f.k]} onChange={e=>setForm(x=>({...x,[f.k]:e.target.value}))} placeholder={f.ph} style={{width:"100%",background:"#0d0a00",border:"1px solid #3d2f15",borderRadius:10,padding:"10px",color:"#f0f9ff",fontSize:12,fontFamily:"Cairo,sans-serif",direction:"rtl",boxSizing:"border-box"}} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} />
            </div>
          ))}
          <div><div style={{fontSize:10,color:"#334155",marginBottom:5}}>ملاحظات للمندوب</div>
            <textarea value={form.note} onChange={e=>setForm(x=>({...x,note:e.target.value}))} rows={3} style={{width:"100%",background:"#0d0a00",border:"1px solid #3d2f15",borderRadius:10,padding:"10px",color:"#f0f9ff",fontSize:12,fontFamily:"Cairo,sans-serif",direction:"rtl",resize:"none",boxSizing:"border-box"}} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} />
          </div>
          <div style={{background:"#0d2035",borderRadius:10,padding:"10px",fontSize:10,color:"#60a5fa"}}>💡 GPS: افتح Google Maps → اضغط على موقعك → انسخ الأرقام هنا</div>
          <Btn color="#38bdf8" disabled={!form.address||!form.date||!form.time} onClick={book} style={{width:"100%"}}>إرسال طلب الموعد 📤</Btn>
        </Card>
      </div>
    );
    if(pg==="track") return <div style={{padding:16,display:"flex",flexDirection:"column",gap:10}}><div style={{fontSize:14,fontWeight:800,color:"#f0f9ff",marginBottom:4}}>مواعيدي</div>{my.map(a=><AptCard key={a.id} apt={a} user={user} role="doctor" act={act} expanded={exp===a.id} onExpand={()=>setExp(exp===a.id?null:a.id)}/>)}</div>;
    return null;
  };
  return (
    <div style={{display:"flex",flexDirection:"column",height:"100dvh",background:"transparent",fontFamily:"'Tajawal','Inter',sans-serif",direction:"rtl",maxWidth:"min(100%, 600px)",margin:"0 auto",width:"100%"}}>
      <style>{G}</style>
      <TopBar user={user} color="#38bdf8" nc={unread} onNotif={()=>{setNo(true); if(unread>0) onMarkAll();}} onLogout={onLogout} title="طبيب"/>
      <NotifBanner notifPerm={notifPerm} requestNotifs={requestNotifs}/>
      <div style={{flex:1,overflowY:"auto"}}>{rp()}</div>
      <BNav items={NAV} active={pg} onChange={setPg}/>
      {no&&<NPanel notifs={notifs} color="#38bdf8" onClose={()=>setNo(false)} onMarkAll={onMarkAll}/>}
    </div>
  );
}

// ═══════════ الجذر الحقيقي مع Error Boundary ═══════════
export default function App() {
  return React.createElement(ErrorBoundary, null, React.createElement(AppInner, null));
}